const {
  useState,
  useEffect
} = React;

// Load html5-qrcode scanner library eagerly
(function() {
  if (window.Html5Qrcode) return;
  var s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js";
  document.head.appendChild(s);
})();

// ============================================================
// API
// ============================================================
const API_URL = "https://script.google.com/a/macros/filament.coffee/s/AKfycbxLQnDxEfR9cMPitd6FSvyyiOm6JAb71H0ItEQbmCy2HK9NjlV-oLTW6_WbMjFyBz2A2g/exec";

// Startup used to be one un-timed loadAll, and a failed loadAll rendered the
// whole app empty with the error hidden inside the (by then unmounted) loading
// overlay. Three guards now: a hard timeout, one automatic retry, and a
// localStorage copy of the last good payload so a failed load shows this
// morning's data with a banner instead of a blank screen in the van.
const LOAD_TIMEOUT_MS = 30000;
const RETRY_DELAY_MS = 3000;
const CACHE_KEY = "filament_cache_v1";

// Apps Script answers with an HTML sign-in or quota page rather than JSON when
// the script is busy or the session has lapsed. Parsing that as JSON throws
// "Unexpected token <", which tells the driver nothing — translate it.
// timeoutMs === 0 means no timeout: used for writes, where aborting the request
// client-side would not stop the sheet write and could produce a duplicate.
async function fetchJson(url, opts, timeoutMs) {
  const ms = timeoutMs === undefined ? LOAD_TIMEOUT_MS : timeoutMs;
  const useTimer = ms > 0 && typeof AbortController !== "undefined";
  const ctrl = useTimer ? new AbortController() : null;
  const timer = ctrl ? setTimeout(function() { ctrl.abort(); }, ms) : null;
  try {
    const res = await fetch(url, Object.assign({ redirect: "follow" }, opts || {}, ctrl ? { signal: ctrl.signal } : {}));
    if (!res.ok) throw new Error("Spreadsheet returned HTTP " + res.status);
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error("The spreadsheet sent a page instead of data \u2014 the script is probably busy or over quota.");
    }
  } catch (e) {
    if (e && e.name === "AbortError") {
      throw new Error("The spreadsheet took more than " + Math.round(ms / 1000) + "s to answer.");
    }
    throw e;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

const _isUnknownAction = d => !!(d && d.error && /unknown action/i.test(String(d.error)));

function readCache() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    if (!parsed || !parsed.data || !parsed.ts) return null;
    if (!Array.isArray(parsed.data.customers) || parsed.data.customers.length === 0) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function writeCache(data) {
  if (!data || !Array.isArray(data.customers) || data.customers.length === 0) return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
  } catch (e) {
    // Over quota — keep the core the driver needs on the road, drop the history.
    try {
      const slim = { ...data };
      ["deliveryLog", "gasLog", "maintLog", "actionItems", "conversations", "kegRegister", "contacts"].forEach(k => { delete slim[k]; });
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: slim }));
    } catch (e2) {
      /* no cache this session — the app still works while online */
    }
  }
}

function mergeCache(extra) {
  const cached = readCache();
  if (cached) writeCache({ ...cached.data, ...extra });
}

function fmtCacheTime(ts) {
  try {
    const d = new Date(ts);
    const time = d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
    if (d.toDateString() === new Date().toDateString()) return time;
    return time + " on " + d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  } catch (e) {
    return "earlier";
  }
}

// ============================================================
// TRAINING MODE (Oct 2026) — for practice deliveries with a new driver.
// A user with training: true sees the real customers and every screen as
// normal, under a "Training Mode" banner, but nothing is written to any
// sheet. Remove training: true from the user to make the login live.
// ============================================================
var TRAINING_MODE = false;

const api = {
  // loadCore is the trimmed startup payload. An older Code.gs answers
  // {error:"Unknown action: loadCore"}, so fall back to loadAll — that way the
  // Shopify asset and the Apps Script deployment can go out in either order.
  async load() {
    let data = await fetchJson(`${API_URL}?action=loadCore`);
    if (_isUnknownAction(data)) {
      data = await fetchJson(`${API_URL}?action=loadAll`, null, 60000);
      if (data && !data.error) data._full = true;
    }
    return data;
  },
  // Only called when the backend served a core payload. On an older deployment
  // loadAll already carried the history, so this is skipped.
  async loadHistory() {
    const data = await fetchJson(`${API_URL}?action=loadHistory`, null, 60000);
    if (_isUnknownAction(data)) return null;
    return data;
  },
  async post(action, data) {
    // Training mode: every write is swallowed here, so nothing reaches any
    // sheet — deliveries, gas, notes, run ticks, all of it. The app carries on
    // as if the save worked; a refresh puts the real data back.
    if (TRAINING_MODE) return { success: true, training: true };
    return fetchJson(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain;charset=utf-8"
      },
      body: JSON.stringify({
        action,
        ...data
      })
    }, 0);
  }
};

// ============================================================
// DATA
// ============================================================
const CUSTOMERS = [{
  id: "117",
  name: "Adisyn Cyber Pty Ltd",
  suburb: "Perth",
  address: "2/234 Pier St, Perth WA",
  category: "Professional Firm/Office"
}, {
  id: "31",
  name: "Avada CrossFit",
  suburb: "O'Connor",
  address: "2/4 Zeta Cres, O'Connor WA",
  category: "Gym/Fitness"
}, {
  id: "247",
  name: "Be Free Cafe",
  suburb: "East Perth",
  address: "Perth City Farm - 1 City Farm Pl, East Perth WA",
  category: "Cafe"
}, {
  id: "149",
  name: "Bike Bar - Claremont",
  suburb: "Claremont",
  address: "273 Stirling Hwy, Claremont WA",
  category: "Not Listed"
}, {
  id: "32",
  name: "Brew-Ha Kings Square",
  suburb: "Perth",
  address: "Shop 2/562 Wellington Street, Perth WA",
  category: "Cafe"
}, {
  id: "229",
  name: "Cafe Duo Restaurant",
  suburb: "Baldivis",
  address: "20 Settlers Ave, Baldivis WA",
  category: "Cafe"
}, {
  id: "246",
  name: "Coco Belle Espresso Bar",
  suburb: "Mount Pleasant",
  address: "21 Queens Rd, Mount Pleasant WA",
  category: "Cafe"
}, {
  id: "136",
  name: "Cranked Coffee",
  suburb: "Leederville",
  address: "5/106 Oxford St, Leederville WA",
  category: "Cafe"
}, {
  id: "157",
  name: "Daisies Cottesloe",
  suburb: "Cottesloe",
  address: "305 Marmion St, Cottesloe WA",
  category: "Cafe"
}, {
  id: "46",
  name: "Drift Kitchen",
  suburb: "Scarborough",
  address: "16A Calais Rd, Scarborough WA",
  category: "Cafe"
}, {
  id: "42",
  name: "Dropinz Coffee",
  suburb: "Mullaloo",
  address: "Shop 10/11 Koorana Rd, Mullaloo WA",
  category: "Cafe"
}, {
  id: "238",
  name: "Emmerson's Cafe",
  suburb: "Joondanna",
  address: "75 Powell St, Joondanna WA",
  category: "Cafe"
}, {
  id: "33",
  name: "Eric Chamberlain",
  suburb: "Cottesloe",
  address: "Shop 5/36 Eric St, Cottesloe WA",
  category: "Cafe"
}, {
  id: "6",
  name: "Fields Of Vincent",
  suburb: "Mt Hawthorn",
  address: "135 Scarborough Beach Rd, Mt Hawthorn WA",
  category: "Cafe"
}, {
  id: "245",
  name: "Finns Espresso",
  suburb: "Perth",
  address: "Shop 13 Plaza Arcade, 650 Hay Street Mall, Perth WA",
  category: "Cafe"
}, {
  id: "3",
  name: "Flo Espresso",
  suburb: "Perth",
  address: "168/15 Aberdeen St, Perth WA",
  category: "Cafe"
}, {
  id: "121",
  name: "Garage Grind (Midland Kia)",
  suburb: "Midland",
  address: "194 Great Eastern Hwy, Midland WA",
  category: "Cafe"
}, {
  id: "55",
  name: "Go Coffee Go",
  suburb: "Doubleview",
  address: "222 Scarborough Beach Rd, Doubleview WA",
  category: "Cafe"
}, {
  id: "217",
  name: "Grain Bakery (Shenton Park)",
  suburb: "Shenton Park",
  address: "4/155 Onslow Rd, Shenton Park WA",
  category: "Retail Food - Other"
}, {
  id: "216",
  name: "Grain Bakery WA",
  suburb: "Lathlain",
  address: "19 Lathlain Pl, Lathlain WA",
  category: "Retail Food - Other"
}, {
  id: "236",
  name: "Grounds Brew House",
  suburb: "Yokine",
  address: "61 Woodrow Ave, Yokine WA",
  category: "Cafe"
}, {
  id: "227",
  name: "Hank's Corner",
  suburb: "North Perth",
  address: "64A Scarborough Beach Rd, North Perth WA",
  category: "Cafe"
}, {
  id: "22",
  name: "He Brew Espresso",
  suburb: "Maida Vale",
  address: "Shop 4/31 Maida Vale Rd, Maida Vale WA",
  category: "Cafe"
}, {
  id: "232",
  name: "Jaffelato",
  suburb: "Rockingham",
  address: "3A/1 Rockingham Beach Rd, Rockingham WA",
  category: "Cafe"
}, {
  id: "7",
  name: "Koda Capital",
  suburb: "West Perth",
  address: "Level 3, 66 Kings Park Rd, West Perth WA",
  category: "Professional Firm/Office"
}, {
  id: "237",
  name: "KR Surveys",
  suburb: "Osborne Park",
  address: "54 McDonald St, Osborne Park WA",
  category: "Professional Firm/Office"
}, {
  id: "148",
  name: "Kuld Creamery",
  suburb: "Fremantle",
  address: "11 Essex St, Fremantle WA",
  category: "Not Listed"
}, {
  id: "210",
  name: "La Patisserie South Perth",
  suburb: "South Perth",
  address: "3/35 Mends St, South Perth WA",
  category: "Cafe"
}, {
  id: "78",
  name: "Liquor Shed Jandakot",
  suburb: "Jandakot",
  address: "8/628 Karel Ave, Jandakot WA",
  category: "Bottleshop"
}, {
  id: "105",
  name: "Little Rokeby",
  suburb: "Subiaco",
  address: "269 Rokeby Rd, Subiaco WA",
  category: "Cafe"
}, {
  id: "182",
  name: "MBS Insurance",
  suburb: "West Perth",
  address: "Ground Level/146 Colin St, West Perth WA",
  category: "Professional Firm/Office"
}, {
  id: "226",
  name: "Melt Slimming Studios",
  suburb: "Subiaco",
  address: "Unit 8/1 Forrest St, Subiaco WA",
  category: "Gym/Fitness"
}, {
  id: "8",
  name: "MJA Studio",
  suburb: "Subiaco",
  address: "Level 1, 23 Railway Rd, Subiaco WA",
  category: "Professional Firm/Office"
}, {
  id: "239",
  name: "Moose Claremont",
  suburb: "Claremont",
  address: "40R Bay View Terrace, Claremont WA",
  category: "Cafe"
}, {
  id: "126",
  name: "Mount Street Breakfast Bar",
  suburb: "West Perth",
  address: "42 Mount St, West Perth WA",
  category: "Cafe"
}, {
  id: "219",
  name: "Noggos Kitchen and Coffee",
  suburb: "Maylands",
  address: "38 Peninsula Rd, Maylands WA",
  category: "Cafe"
}, {
  id: "242",
  name: "Ol' Buoy Cott Main",
  suburb: "Cottesloe",
  address: "Indiana Teahouse - 99 Marine Pde, Cottesloe WA",
  category: "Cafe"
}, {
  id: "173",
  name: "OMG Decadent Donuts Perth Hills",
  suburb: "Mount Richon",
  address: "9 Hillside Rd, Mount Richon WA",
  category: "Not Listed"
}, {
  id: "249",
  name: "Oxford Yard",
  suburb: "Leederville",
  address: "2/196 Oxford St, Leederville WA",
  category: "Cafe"
}, {
  id: "18",
  name: "Pinsani (Wotton & Kearney)",
  suburb: "Perth",
  address: "Level 49, 108 St Georges Tce, Perth WA",
  category: "Professional Firm/Office"
}, {
  id: "82",
  name: "Playa Coffee",
  suburb: "Perth",
  address: "15-17 William St, Perth WA",
  category: "Cafe"
}, {
  id: "141",
  name: "Primero Group",
  suburb: "Osborne Park",
  address: "1 Pearson Way, Osborne Park WA",
  category: "Professional Firm/Office"
}, {
  id: "251",
  name: "Ricky's",
  suburb: "Mt Lawley",
  address: "141 Central Ave, Mt Lawley WA",
  category: "Cafe"
}, {
  id: "211",
  name: "Rise&Grind Espresso Bar",
  suburb: "Doubleview",
  address: "255A Scarborough Beach Rd, Doubleview WA",
  category: "Cafe"
}, {
  id: "248",
  name: "Roasted N Toasted",
  suburb: "Leederville",
  address: "35B/285 Vincent St, Leederville WA",
  category: "Cafe"
}, {
  id: "218",
  name: "safestyle",
  suburb: "Osborne Park",
  address: "3/17 Pearson Way, Osborne Park WA",
  category: "Professional Firm/Office"
}, {
  id: "233",
  name: "Soak Laundry Lounge",
  suburb: "West Perth",
  address: "3/1297 Hay St, West Perth WA",
  category: "Cafe"
}, {
  id: "123",
  name: "Someday Coffee Co",
  suburb: "Floreat",
  address: "445 Cambridge St, Floreat WA",
  category: "Cafe"
}, {
  id: "172",
  name: "Split on Angelo",
  suburb: "South Perth",
  address: "53 Angelo St, South Perth WA",
  category: "Cafe"
}, {
  id: "235",
  name: "The Herdsman Market - Cafe",
  suburb: "Churchlands",
  address: "9 Flynn St, Churchlands WA",
  category: "Cafe"
}, {
  id: "162",
  name: "TST Social Club",
  suburb: "Swanbourne",
  address: "Campbell Barracks - West Coast Hwy, Swanbourne WA",
  category: "Not Listed"
}, {
  id: "53",
  name: "Urban Kitchen Cafe",
  suburb: "Balcatta",
  address: "216 Balcatta Rd, Balcatta WA",
  category: "Cafe"
}, {
  id: "181",
  name: "Venn Coffee - Bicton",
  suburb: "Bicton",
  address: "1/258 Canning Hwy, Bicton WA",
  category: "Cafe"
}, {
  id: "26",
  name: "Venn Coffee - East Victoria Park",
  suburb: "East Victoria Park",
  address: "965 Albany Hwy, East Victoria Park WA",
  category: "Cafe"
}, {
  id: "240",
  name: "Wholefood Circus",
  suburb: "Shenton Park",
  address: "107 Nicholson Rd, Shenton Park WA",
  category: "Cafe"
}, {
  id: "241",
  name: "Your Daily Grind",
  suburb: "Maddington",
  address: "5/11 Attfield Street, Maddington WA",
  category: "Cafe"
}, {
  id: "159",
  name: "Yours Truly Co WA",
  suburb: "Subiaco",
  address: "284 Hay Street, Subiaco WA",
  category: "Cafe"
}];
const INITIAL_KEGS = {};
const PRODUCTS = ["Classic (Nitro)", "Choc Banana (Nitro)", "Lamington (Nitro)", "Choc Mint (Nitro)", "Choc Orange (Nitro)", "Summer (Nitro)", "Iced Espresso"];
// Product colours/labels are keyed off a NORMALISED base name. The Ops app and
// Brewery app write product names in different forms — e.g. the Brewery writes
// "Lamington (Nitro)" while Ops historically used "Lamington (N)". Looking up the
// raw string used to miss for these, falling back to grey/green. buildVariantMap
// expands each base into every name form ("X", "X (N)", "X (Nitro)") so any
// variant resolves to the correct colour/label. Colours match the Brewery app.
function buildVariantMap(base) {
  var out = {};
  Object.keys(base).forEach(function(name) {
    out[name] = base[name];
    out[name + " (N)"] = base[name];
    out[name + " (Nitro)"] = base[name];
  });
  return out;
}
const PRODUCT_SHORT_BASE = {
  "Classic": "Classic",
  "Choc Banana": "Choc Ban",
  "Lamington": "Laming",
  "Choc Mint": "Choc Mnt",
  "Choc Orange": "Choc Org",
  "Summer": "Summer",
  "Iced Espresso": "Ice Esp"
};
const PRODUCT_COLOR_BASE = {
  "Classic": "#C43E2A",
  "Choc Banana": "#F0B816",
  "Lamington": "#6B3A2A",
  "Choc Mint": "#2A8B6A",
  "Choc Orange": "#D4870A",
  "Summer": "#D4598A",
  "Iced Espresso": "#2563A8"
};
const PRODUCT_SHORT = buildVariantMap(PRODUCT_SHORT_BASE);
const PRODUCT_COLOR = buildVariantMap(PRODUCT_COLOR_BASE);
const KEG_SIZES = ["9L", "18L"];

// ============================================================
// GAS LOG ORDER — newest reading first, always
// ============================================================
// The sheet hands the log back oldest-first (row order). It used to be flipped
// with an in-place reverse(), but that mutated the payload, and the same
// payload was then written to the localStorage cache. Next launch the app
// painted from that already-flipped cache and flipped it again, so the history
// came up oldest-first until (and unless) the fresh history arrived. A reading
// entered with an earlier date was also prepended to the top regardless.
// Sorting by the reading's own date + time fixes both, and never mutates.
const _GAS_MON = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
function gasRecordKey(r) {
  if (!r) return -Infinity;
  let day = null;
  const m = String(r.date || "").match(/(\d{1,2})\s+([A-Za-z]{3})[A-Za-z]*\s+(\d{4})/);
  if (m && _GAS_MON[m[2].toLowerCase()] != null) day = new Date(+m[3], _GAS_MON[m[2].toLowerCase()], +m[1]).getTime();
  if (day == null && r.ts) { const d = new Date(r.ts); day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); }
  if (day == null) return -Infinity;
  let mins = 0;
  const t = String(r.time || "").match(/(\d{1,2}):(\d{2})\s*([ap]\.?m\.?)?/i);
  if (t) {
    let hh = +t[1];
    const ap = t[3] ? t[3].charAt(0).toLowerCase() : "";
    if (ap === "p" && hh < 12) hh += 12;
    if (ap === "a" && hh === 12) hh = 0;
    mins = hh * 60 + (+t[2]);
  }
  return day + mins * 60000;
}
// fromSheet: the list is in sheet (oldest-first) order, so a tie on date and
// time goes to the later row. Otherwise the list is already newest-first and a
// tie keeps its place, so re-sorting on every update can't shuffle it.
function sortGasList(list, fromSheet) {
  if (!Array.isArray(list)) return list;
  return list.map((r, i) => ({ r, i, k: gasRecordKey(r) }))
    .sort((a, b) => (b.k - a.k) || (fromSheet ? b.i - a.i : a.i - b.i))
    .map(x => x.r);
}
function sortGasLogMap(map, fromSheet) {
  const out = {};
  for (const k in (map || {})) out[k] = sortGasList(map[k], fromSheet);
  return out;
}

// ============================================================
// RUN MODE — Field Ops picks up the run the driver started in the Runs app
// ============================================================
// Who sees run mode. Rolled out to the drivers (Sept 2026) after testing on
// Brendan's login. Anyone not listed gets the app exactly as it was: no prompt,
// no run list, no run banner. A driver's run is found by matching this login's
// name ("Aaron") against the Driver column of the Runs tab.
const RUN_MODE_USERS = ["brendan", "aaron", "ollie"];
const runModeEnabledFor = u => !!(u && RUN_MODE_USERS.indexOf(String(u.username || "").toLowerCase()) !== -1);

// Remembers the answer to "Are you on run X?" for the day, so the question is
// asked once per run rather than every time the app comes back to the front.
const RUN_ACK_KEY = "filament_run_ack_v1";
function readRunAck() {
  try {
    const a = JSON.parse(localStorage.getItem(RUN_ACK_KEY) || "null");
    return a && a.day === new Date().toDateString() ? a : null;
  } catch (e) { return null; }
}
// manual: the run was picked by ID through "I'm on a run" rather than found
// as On the road. The app then keeps re-reading that run by its ID, so it
// survives a refresh or reload instead of disappearing.
function writeRunAck(runId, answer, manual) {
  try { localStorage.setItem(RUN_ACK_KEY, JSON.stringify({ runId: runId, answer: answer, manual: !!manual, day: new Date().toDateString() })); } catch (e) {}
}
// Run IDs are the driver's initials + the run's date, e.g. JC-300926 — the
// same scheme RunsCode.gs uses. Keep in step with DRIVER_INITIALS there.
const RUN_DRIVER_INITIALS = { brendan: "BG", aaron: "AE", ollie: "ON" };
function guessRunId(user) {
  const u = String((user && (user.username || user.name)) || "").trim().toLowerCase();
  const ini = RUN_DRIVER_INITIALS[u] || RUN_DRIVER_INITIALS[u.split(/\s+/)[0]] ||
    String((user && user.name) || "X").split(/\s+/).map(w => w.charAt(0)).join("").toUpperCase().slice(0, 3);
  const d = new Date(), p = n => String(n).padStart(2, "0");
  return ini + "-" + p(d.getDate()) + p(d.getMonth() + 1) + String(d.getFullYear()).slice(2);
}
// Mirrors isKegLine in filament-runs.js / isKegLine_ in RunsCode.gs — coffee
// bags, squealers, glasses and one-off gear ride on the order but aren't kegs.
function runIsKegLine(l) {
  if (!l) return false;
  const size = String(l.size || "").trim().toLowerCase();
  if (size === "bag" || size === "extra") return false;
  if (size && !/^\d+(\.\d+)?\s*l$/.test(size)) return false;
  return !runIsNonKegName(l.product);
}
// A product name that can never be a keg. "Extra" / "Extras - ..." is the
// wholesale sheet's catch-all for one-off gear. Mirrors isNonKegName in
// filament-runs.js and isNonKegLine_ in RunsCode.gs.
function runIsNonKegName(prod) {
  const p = String(prod || "").toLowerCase().trim();
  if (p.indexOf("bags") === 0 || p.indexOf("coffee bag") !== -1) return true;
  if (/^extras?\b/.test(p)) return true;
  return /squealer|glass|box of|merch|sticker|tap badge|corflute|a-?frame|gas (bottle|cylinder)|regulator/.test(p);
}
// ---- Extras named from their notes (Oct 2026) ----
// The wholesale Item column for one-off gear is a generic "Extra" / "Extras -
// Squealers, Glasses etc."; what's actually going out is typed in the notes
// ("A-FRAME ONLY", "GAS CYLINDER"). The slip printed the generic name, and an
// extra with its quantity left blank didn't show at all. Mirrors extraName /
// lineQty in filament-runs.js and extraName_ / lineQty_ in RunsCode.gs.
function runSourceNote(notes) {
  return String(notes || "").split(/\s*\u00B7\s*/).filter(p => {
    p = p.trim();
    return p && !/^(day|run|deliver)\s/i.test(p) && p.charAt(0) !== "#" && p.charAt(0) !== "\u26A0";
  }).join(" \u00B7 ");
}
function runExtraName(l) {
  const p = String((l && l.product) || "").trim();
  if (!/^extras?\b/i.test(p)) return p;
  let s = runSourceNote(l && l.notes).replace(/^\s*only\s+/i, "").replace(/\s+only\s*$/i, "").trim();
  if (!s || s.length > 40) return p || "Extra";
  if (s === s.toUpperCase()) s = s.toLowerCase().replace(/(^|[\s\-\/(])([a-z])/g, (m, a, b) => a + b.toUpperCase());
  return s;
}
// A blank quantity on an extra means one of it.
const runLineQty = l => { const q = +(l && l.qty) || 0; return (q <= 0 && l && !runIsKegLine(l)) ? 1 : q; };
const runProdBase = p => String(p || "").replace(/\s*\((?:n|nitro)\)\s*$/i, "").trim();
const runProdKey = p => runProdBase(p).toLowerCase();
// A 7L is a 9L keg filled short and scans as KEG-09-, so it counts as 9L here.
const runSizeKey = sz => { const s = String(sz || "").replace(/\s/g, "").toUpperCase(); return s === "7L" ? "9L" : (s || "18L"); };
const runStopDone = st => /deliver|done|complete/i.test(String((st && st.status) || ""));
const runOmoList = no => String(no || "").split("/").map(x => x.trim()).filter(Boolean);
// The OMO box on the delivery screen takes four digits after a fixed "OMO".
const runOmoDigits = no => { const m = String(no || "").match(/\d{4}/); return m ? m[0] : ""; };
function runQtyLabel(n) {
  const q = +n || 0;
  if (q === Math.floor(q)) return String(q);
  const whole = Math.floor(q), frac = q - whole;
  const f = Math.abs(frac - 0.5) < 0.01 ? "\u00BD" : Math.abs(frac - 0.25) < 0.01 ? "\u00BC" : Math.abs(frac - 0.75) < 0.01 ? "\u00BE" : null;
  return f ? (whole ? String(whole) : "") + f : String(Math.round(q * 100) / 100);
}
// Keg lines grouped product + size: [{product, size, qty, key}]
function runKegTally(lines) {
  const map = {}, order = [];
  (lines || []).forEach(l => {
    if (!runIsKegLine(l)) return;
    const size = runSizeKey(l.size);
    const key = runProdKey(l.product) + "|" + size;
    if (!map[key]) { map[key] = { product: runProdBase(l.product) || "Keg", size: String(l.size || size).toUpperCase(), qty: 0, key: key }; order.push(key); }
    map[key].qty += +l.qty || 0;
  });
  return order.map(k => map[k]);
}
function runExtraLines(lines) {
  return (lines || []).filter(l => l && !runIsKegLine(l) && String(l.product || "").trim())
    .map(l => ({ product: runExtraName(l).replace(/\s*-\s*box of\s*(\d+)\s*$/i, " (box of $1)"), qty: runLineQty(l) }));
}
// Tap cards / corflute as stored on the run stop: [{product, qty, reason}],
// older rows a plain list or a comma string.
function runSignageItems(v) {
  let raw = v;
  if (raw == null || raw === "") return [];
  if (typeof raw === "string") { const t = raw.trim(); if (!t) return []; try { raw = t.charAt(0) === "[" ? JSON.parse(t) : t.split(","); } catch (e) { raw = t.split(","); } }
  if (!Array.isArray(raw)) return [];
  const out = [], at = {};
  raw.forEach(x => {
    const p = String(x && typeof x === "object" ? x.product || "" : x || "").trim(); if (!p || runIsNonKegName(p)) return;
    const q = x && typeof x === "object" ? Math.max(1, +x.qty || 1) : 1;
    if (at[p] == null) { at[p] = out.length; out.push({ product: p, qty: 0, reason: (x && x.reason) || "" }); }
    out[at[p]].qty += q;
  });
  return out;
}
function runKegCount(st) {
  const t = runKegTally(st && st.lines);
  return t.length ? t.reduce((a, r) => a + r.qty, 0) : (+(st && st.kegCount) || 0);
}
function runDayLabel(d) {
  const m = String(d || "").match(/(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})/);
  if (!m) return String(d || "");
  const MON = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
  const dt = new Date(+m[3], MON[m[2].slice(0, 3)] || 0, +m[1]);
  return dt.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}
const TAP_LEVELS = ["Full", "Medium", "Low"];
const SCHED_MAINT = ["Line Clean", "Full System Clean"];
// Line clean cadence, per venue (8 Oct 2026). Every 28 days by default; a
// venue can have its own number in the Equipment tab's "Line Clean Days"
// column (col AD) — e.g. long insulated lines where a clean purges a lot of
// coffee. Blank = the default. The delivery screen and slip ask from the
// venue's number on ("Line clean today"); the Maintenance screen is green
// before it, amber ON the day it falls due, red (overdue) any day after it —
// one rule, used by every screen through lcStatusOf (8 Oct 2026; there was a
// 7-day grace that read "due soon" while already past the venue's number).
// Code.gs writes the same rule into new Fleet Overview rows — change both.
const LC_DEFAULT_DAYS = 28;
// days since the last clean -> { level: "ok"|"due"|"overdue"|"none", label }
function lcStatusOf(days, due) {
  if (days === null || days === undefined) return { level: "none", label: "No record" };
  if (days < due) return { level: "ok", label: `${days} days ago` };
  if (days === due) return { level: "due", label: `${days} days ago \u2014 due today` };
  const over = days - due;
  return { level: "overdue", label: `${days} days ago \u2014 ${over} day${over === 1 ? "" : "s"} overdue` };
}
// "NA" in that column switches the prompt off for the venue (they clean their
// own lines, e.g. Venn weekly, or it's collect-only). Returns null when off.
function lcOffFor(eq) { return !!(eq && eq.lineCleanOff); }
function lcDueDaysFor(eq) {
  if (lcOffFor(eq)) return null;
  const v = Number(eq && eq.lineCleanDays);
  return (isFinite(v) && v > 0) ? Math.round(v) : LC_DEFAULT_DAYS;
}
const LC_UNABLE_TYPE = "Line Clean - Unable";

// Sheets stores a typed "0412 345 678" as the number 412345678, dropping the
// leading zero. An Australian number with exactly 9 digits that would start
// 2/3/4/7/8 has lost its 0 — put it back. Mirrors normPhone_ in Code.gs.
function normPhone(v) {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  const digits = s.replace(/[\s()-]/g, "");
  if (/^[23478]\d{8}$/.test(digits)) return "0" + s;
  return s;
}
// For tel:/sms: links — digits and a leading + only.
const phoneHref = v => normPhone(v).replace(/[^\d+]/g, "");
const REPAIR_TASKS = ["Taps", "Liquid line and disconnects", "Gas line and disconnects", "Gas regulator", "Fridge", "Other"];
const NOTE_TYPES = ["General", "Sales Lead", "Issue", "Feedback", "New Location", "Closing", "Menu Change", "Reorder", "Competitor Intel"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

// ============================================================
// USERS — simple credential store (passwords should be changed on deploy)
// ============================================================
const USERS = [{
  username: "brendan",
  password: "filament1",
  name: "Brendan",
  role: "admin"
}, {
  username: "aaron",
  password: "filament2026",
  name: "Aaron",
  role: "driver"
}, {
  username: "ollie",
  password: "nolan2026",
  name: "Ollie",
  role: "driver"
}];
const C = {
  bg: "#FAFAF8",
  surface: "#FFFFFF",
  surfaceAlt: "#F5F4F0",
  border: "#E8E5DE",
  borderLight: "#F0EDE6",
  text: "#1A1714",
  textSec: "#706B63",
  textTer: "#A9A49B",
  accent: "#C17817",
  accentSoft: "#FFF5E6",
  accentBorder: "#F0D4A0",
  green: "#2D7A4F",
  greenSoft: "#EEFBF3",
  greenBorder: "#B8E4CA",
  blue: "#2563A8",
  blueSoft: "#EDF4FF",
  blueBorder: "#B0D0F0",
  red: "#C43E2A",
  redSoft: "#FFF0ED",
  purple: "#6D4BA0",
  cyan: "#1A7A7A",
  slate: "#4A4640"
};
const levelColor = l => l === "Full" ? C.green : l === "Medium" ? C.accent : l === "Low" ? C.red : C.textTer;
const font = `'Libre Franklin','Helvetica Neue',-apple-system,sans-serif`;
const serif = `'Newsreader','Georgia',serif`;
const LOGO_URL = "data:image/png;base64,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";

// ============================================================
// KEG SVG — visual keg with product colour, size proportional
// ============================================================
function KegVisual({
  size,
  product,
  level,
  small,
  ghost
}) {
  const is9 = size === "9L" || size === "09L";
  const h = small ? is9 ? 48 : 64 : is9 ? 72 : 96;
  const w = small ? is9 ? 32 : 36 : is9 ? 48 : 52;
  const col = PRODUCT_COLOR[product] || "#888";
  const opacity = ghost ? 0.2 : 1;
  const fillH = level === "Full" ? 0.85 : level === "Medium" ? 0.55 : level === "Low" ? 0.25 : 0;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      width: w,
      height: h,
      position: "relative",
      opacity
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 52 96",
    style: {
      width: "100%",
      height: "100%"
    }
  }, /*#__PURE__*/React.createElement("rect", {
    x: "8",
    y: "16",
    width: "36",
    height: "68",
    rx: "6",
    fill: col,
    fillOpacity: ".08",
    stroke: col,
    strokeWidth: "2"
  }), fillH > 0 && /*#__PURE__*/React.createElement("rect", {
    x: "9",
    y: 16 + 68 * (1 - fillH),
    width: "34",
    height: 68 * fillH,
    rx: "5",
    fill: col,
    fillOpacity: ".2"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "26",
    cy: "16",
    rx: "18",
    ry: "6",
    fill: col,
    fillOpacity: ".12",
    stroke: col,
    strokeWidth: "2"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "26",
    cy: "84",
    rx: "18",
    ry: "6",
    fill: "none",
    stroke: col,
    strokeWidth: "2"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "22",
    y: "6",
    width: "8",
    height: "10",
    rx: "3",
    fill: col,
    fillOpacity: ".3"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "12",
    y1: "45",
    x2: "40",
    y2: "45",
    stroke: col,
    strokeWidth: "1",
    opacity: ".3"
  }), /*#__PURE__*/React.createElement("text", {
    x: "26",
    y: "62",
    textAnchor: "middle",
    fontSize: is9 ? "16" : "14",
    fontWeight: "700",
    fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
    fill: col,
    opacity: ".45"
  }, is9 ? "9" : "18")));
}

// ============================================================
// A-FRAME SVG — tappable sides
// ============================================================
function AFrameVisual({
  sideA,
  sideB,
  onTapA,
  onTapB,
  productsA,
  productsB,
  activeA,
  activeB,
  singleSide
}) {
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 16,
      justifyContent: "center",
      padding: "16px 0"
    }
  }, [{
    side: "A",
    products: productsA,
    onTap: onTapA,
    active: activeA
  }, {
    side: "B",
    products: productsB,
    onTap: onTapB,
    active: activeB
  }].filter(s => !(singleSide && s.side === "B")).map(s => /*#__PURE__*/React.createElement("button", {
    key: s.side,
    onClick: s.onTap,
    style: {
      width: 140,
      padding: 0,
      background: "none",
      border: "none",
      cursor: "pointer",
      position: "relative"
    }
  }, s.active && /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      inset: -4,
      borderRadius: 12,
      border: `2.5px solid ${C.accent}`,
      background: `${C.accent}08`,
      zIndex: 0,
      animation: "pulse 1.5s ease-in-out infinite"
    }
  }), /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 140 180",
    style: {
      width: "100%",
      display: "block",
      position: "relative",
      zIndex: 1
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: "M20 170 L70 10 L120 170",
    stroke: s.active ? C.accent : C.border,
    strokeWidth: s.active ? "4" : "3",
    fill: "none",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "35",
    y1: "120",
    x2: "105",
    y2: "120",
    stroke: s.active ? C.accent : C.border,
    strokeWidth: "2"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "38",
    y: "35",
    width: "64",
    height: "78",
    rx: "2",
    fill: s.products ? PRODUCT_COLOR[s.products] || C.accent : s.active ? C.accentSoft : C.surfaceAlt,
    fillOpacity: s.products ? 0.12 : s.active ? 1 : 0.5,
    stroke: s.products ? PRODUCT_COLOR[s.products] || C.accent : s.active ? C.accent : C.border,
    strokeWidth: s.active ? "2.5" : "1.5"
  }), s.products ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("text", {
    x: "70",
    y: "66",
    textAnchor: "middle",
    fontSize: "16",
    fontWeight: "700",
    fill: PRODUCT_COLOR[s.products] || C.text,
    fontFamily: font
  }, PRODUCT_SHORT[s.products] || s.products), /*#__PURE__*/React.createElement("text", {
    x: "70",
    y: "84",
    textAnchor: "middle",
    fontSize: "13",
    fill: C.textSec,
    fontFamily: font
  }, "Corflute")) : /*#__PURE__*/React.createElement("text", {
    x: "70",
    y: "76",
    textAnchor: "middle",
    fontSize: "14",
    fill: s.active ? C.accent : C.textTer,
    fontFamily: font,
    fontWeight: s.active ? "700" : "400"
  }, s.active ? "Select ▼" : "Tap to set")), /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: "center",
      fontSize: 13,
      fontWeight: 600,
      color: s.active ? C.accent : C.textSec,
      marginTop: 2
    }
  }, singleSide ? "Single face" : "Side " + s.side))));
}

// ============================================================
// TAP CARD VISUAL
// ============================================================
function TapCardVisual({
  product,
  onTap,
  active
}) {
  const col = PRODUCT_COLOR[product] || C.border;
  return /*#__PURE__*/React.createElement("button", {
    onClick: onTap,
    style: {
      padding: 0,
      background: "none",
      border: "none",
      cursor: "pointer",
      position: "relative"
    }
  }, active && /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      inset: -4,
      borderRadius: 12,
      border: `2.5px solid ${C.accent}`,
      background: `${C.accent}08`,
      zIndex: 0
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      width: 100,
      height: 64,
      borderRadius: 8,
      border: `2px solid ${active ? C.accent : product ? col : C.border}`,
      background: active && !product ? C.accentSoft : product ? `${col}10` : C.surfaceAlt,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      position: "relative",
      zIndex: 1
    }
  }, product ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 700,
      color: col
    }
  }, PRODUCT_SHORT[product] || product), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      color: C.textTer
    }
  }, "tap card")) : /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: active ? 600 : 400,
      color: active ? C.accent : C.textTer
    }
  }, active ? "Select ▼" : "Tap to set")));
}

// ============================================================
// KEG CELL — individual cell for delivered/collected kegs
// ============================================================
function KegCell({
  keg,
  onRemove,
  color
}) {
  const col = PRODUCT_COLOR[keg.product] || "#888";
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      background: color === "green" ? C.greenSoft : C.accentSoft,
      border: `1px solid ${color === "green" ? C.greenBorder : C.accentBorder}`,
      borderRadius: 12,
      marginBottom: 6
    }
  }, /*#__PURE__*/React.createElement(KegVisual, {
    size: keg.size,
    product: keg.product,
    small: true,
    level: "Full"
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 13,
      fontWeight: 600,
      color: C.text
    }
  }, keg.kegId), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      color: C.textSec
    }
  }, keg.size, " \xB7 ", keg.product)), /*#__PURE__*/React.createElement("button", {
    onClick: onRemove,
    style: {
      background: "none",
      border: "none",
      color: C.textTer,
      cursor: "pointer",
      fontSize: 18,
      padding: "0 4px"
    }
  }, "\xD7"));
}

// ============================================================
// SHARED
// ============================================================
function Select({
  value,
  onChange,
  options,
  placeholder
}) {
  return /*#__PURE__*/React.createElement("select", {
    value: value,
    onChange: e => { var _st = window.pageYOffset || document.documentElement.scrollTop; onChange(e.target.value); requestAnimationFrame(function() { window.scrollTo(0, _st); }); },
    style: {
      width: "100%",
      padding: "10px 12px",
      fontSize: 16,
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      color: value ? C.text : C.textTer,
      fontFamily: font,
      boxSizing: "border-box"
    }
  }, /*#__PURE__*/React.createElement("option", {
    value: ""
  }, placeholder), options.map(o => /*#__PURE__*/React.createElement("option", {
    key: o,
    value: o
  }, o)));
}
function TextArea({
  value,
  onChange,
  placeholder,
  rows
}) {
  return /*#__PURE__*/React.createElement("textarea", {
    value: value,
    onChange: e => onChange(e.target.value),
    placeholder: placeholder,
    rows: rows || 3,
    style: {
      width: "100%",
      padding: "10px 12px",
      fontSize: 16,
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      color: C.text,
      fontFamily: font,
      resize: "vertical",
      boxSizing: "border-box"
    }
  });
}
function BackBtn({
  label,
  onClick,
  screenName
}) {
  return /*#__PURE__*/React.createElement("div", {
    style: {
      position: "sticky",
      top: TRAINING_MODE ? 30 : 0,
      zIndex: 20,
      background: C.bg,
      padding: "12px 0 10px",
      marginBottom: 8,
      borderBottom: `1px solid ${C.borderLight}`
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: onClick,
    style: {
      background: "none",
      border: "none",
      color: C.accent,
      fontSize: 15,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: font,
      padding: 0,
      display: "flex",
      alignItems: "center",
      gap: 6
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 20,
      lineHeight: 1
    }
  }, "\u2039"), " ", label));
}
const icons = {
  keg: c => /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 48 48",
    fill: "none",
    style: {
      width: "100%",
      height: "100%"
    }
  }, /*#__PURE__*/React.createElement("rect", {
    x: "14",
    y: "10",
    width: "20",
    height: "28",
    rx: "3",
    fill: c,
    fillOpacity: ".08",
    stroke: c,
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "24",
    cy: "10",
    rx: "10",
    ry: "4",
    fill: c,
    fillOpacity: ".12",
    stroke: c,
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "24",
    cy: "38",
    rx: "10",
    ry: "4",
    fill: "none",
    stroke: c,
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "22",
    y: "5",
    width: "4",
    height: "5",
    rx: "1.5",
    fill: c,
    fillOpacity: ".5"
  })),
  tap: c => /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 48 48",
    fill: "none",
    style: {
      width: "100%",
      height: "100%"
    }
  }, /*#__PURE__*/React.createElement("rect", {
    x: "4",
    y: "4",
    width: "22",
    height: "30",
    rx: "2",
    fill: c,
    fillOpacity: ".12",
    stroke: c,
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "33",
    y: "4",
    width: "5",
    height: "18",
    rx: "2.5",
    fill: c,
    fillOpacity: ".2",
    stroke: c,
    strokeWidth: "1.3"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "28",
    y: "22",
    width: "16",
    height: "5",
    rx: "2",
    fill: c,
    fillOpacity: ".15",
    stroke: c,
    strokeWidth: "1.3"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M40 27 L44 35 L41 36 L37 28",
    fill: c,
    fillOpacity: ".2",
    stroke: c,
    strokeWidth: "1.2",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "42",
    cy: "40",
    rx: "3",
    ry: "2",
    fill: c,
    fillOpacity: ".15",
    stroke: c,
    strokeWidth: "1"
  })),
  aframe: c => /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 48 48",
    fill: "none",
    style: {
      width: "100%",
      height: "100%"
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: "M10 42 L24 6 L38 42",
    stroke: c,
    strokeWidth: "1.5",
    strokeLinejoin: "round",
    fill: c,
    fillOpacity: ".05"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "15",
    y1: "28",
    x2: "33",
    y2: "28",
    stroke: c,
    strokeWidth: "1.2"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "18",
    y: "14",
    width: "12",
    height: "12",
    rx: "1",
    fill: c,
    fillOpacity: ".08",
    stroke: c,
    strokeWidth: "1.2"
  })),
  fridge: c => /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 48 48",
    fill: "none",
    style: {
      width: "100%",
      height: "100%"
    }
  }, /*#__PURE__*/React.createElement("rect", {
    x: "13",
    y: "5",
    width: "22",
    height: "36",
    rx: "2.5",
    fill: c,
    fillOpacity: ".06",
    stroke: c,
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "13",
    y1: "19",
    x2: "35",
    y2: "19",
    stroke: c,
    strokeWidth: "1.2"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "30",
    y: "10",
    width: "2",
    height: "6",
    rx: "1",
    fill: c,
    fillOpacity: ".4"
  })),
  spanner: c => /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 48 48",
    fill: "none",
    style: {
      width: "100%",
      height: "100%"
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: "M22 4 L26 4 L27 8 L29 9 L33 7 L36 10 L34 14 L35 16 L39 17 L39 21 L35 22 L34 24 L37 27 L34 31 L30 29 L28 30 L27 34 L23 34 L22 30 L20 29 L16 31 L13 28 L15 24 L14 22 L10 21 L10 17 L14 16 L15 14 L12 11 L15 7 L19 9 L21 8 Z",
    fill: c,
    fillOpacity: ".1",
    stroke: c,
    strokeWidth: "1.5",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "24.5",
    cy: "19",
    r: "5",
    fill: "none",
    stroke: c,
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "24.5",
    cy: "19",
    r: "1.5",
    fill: c,
    fillOpacity: ".3"
  })),
  notes: c => /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 48 48",
    fill: "none",
    style: {
      width: "100%",
      height: "100%"
    }
  }, /*#__PURE__*/React.createElement("rect", {
    x: "11",
    y: "6",
    width: "26",
    height: "36",
    rx: "2.5",
    fill: c,
    fillOpacity: ".06",
    stroke: c,
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "17",
    y1: "15",
    x2: "31",
    y2: "15",
    stroke: c,
    strokeWidth: "1.2",
    opacity: ".4"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "17",
    y1: "21",
    x2: "28",
    y2: "21",
    stroke: c,
    strokeWidth: "1.2",
    opacity: ".4"
  })),
  gas: c => /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 48 48",
    fill: "none",
    style: {
      width: "100%",
      height: "100%"
    }
  }, /*#__PURE__*/React.createElement("rect", {
    x: "16",
    y: "12",
    width: "16",
    height: "30",
    rx: "4",
    fill: c,
    fillOpacity: ".08",
    stroke: c,
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "20",
    y: "6",
    width: "8",
    height: "6",
    rx: "2",
    fill: c,
    fillOpacity: ".12",
    stroke: c,
    strokeWidth: "1.3"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "24",
    cy: "8",
    r: "1.5",
    fill: c,
    fillOpacity: ".3"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "22",
    y: "3",
    width: "4",
    height: "4",
    rx: "1.5",
    fill: c,
    fillOpacity: ".2",
    stroke: c,
    strokeWidth: "1"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "20",
    y1: "35",
    x2: "28",
    y2: "35",
    stroke: c,
    strokeWidth: "1",
    opacity: ".3"
  }))
};

// ============================================================
// APP
// ============================================================
function App() {
  // Auth
  const [currentUser, setCurrentUser] = useState(null);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Loading
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Non-null when what is on screen came from the localStorage cache because
  // the sheet could not be reached. Drives the amber staleness banner.
  const [cacheTs, setCacheTs] = useState(null);
  // Bumped by the Retry buttons to re-run the load effect without a full page
  // reload, which on a weak connection would re-download the entire app.
  const [loadNonce, setLoadNonce] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [screen, setScreen] = useState("home");
  // Run mode (see RUN_MODE_USERS). fieldRun is the run on the road for this
  // driver today, onRun whether they said yes to it, runStopCtx the sequence
  // number of the stop being worked (slip + delivery), runActiveSeq the stop
  // they last opened — it shows green until delivered.
  const [fieldRun, setFieldRun] = useState(null);
  const [onRun, setOnRun] = useState(false);
  const [runAsk, setRunAsk] = useState(false);
  const [runStopCtx, setRunStopCtx] = useState(null);
  const [runActiveSeq, setRunActiveSeq] = useState(null);
  const [runChecking, setRunChecking] = useState(false);
  // "I'm on a run" cover: null when closed, else { phase, id, busy, error, found }
  const [runFind, setRunFind] = useState(null);
  const runCheckAtRef = React.useRef(0);
  // Sept 2026 — faster "Are you on run X?". A check already on the wire isn't
  // repeated, and fieldRunRef lets the background poll know whether a run has
  // already been found without re-creating its timer on every render.
  const runCheckBusyRef = React.useRef(false);
  const fieldRunRef = React.useRef(null);
  useEffect(() => { fieldRunRef.current = fieldRun; }, [fieldRun]);
  const runFirstCheckRef = React.useRef(false);
  const runLocalDoneRef = React.useRef({});
  const screenRef = React.useRef("home");
  useEffect(() => { screenRef.current = screen; }, [screen]);
  const [customer, setCust] = useState(null);
  const [search, setSearch] = useState("");
  const toastRef = React.useRef(null);
  const toastTimerRef = React.useRef(null);
  useEffect(() => {
    try {
      if (screen === "delivery" && _delivReturnY.current != null) {
        var _dy = _delivReturnY.current;
        _delivReturnY.current = null;
        requestAnimationFrame(function() { requestAnimationFrame(function() {
          window.scrollTo(0, _dy);
          [16, 50, 100, 200].forEach(function(d) { setTimeout(function() { window.scrollTo(0, _dy); }, d); });
        }); });
      } else if (screen === "home" && _homeScrollY.current > 0) {
        var _sy = _homeScrollY.current;
        requestAnimationFrame(function() { requestAnimationFrame(function() {
          window.scrollTo(0, _sy);
          [16, 50, 100, 200].forEach(function(d) { setTimeout(function() { window.scrollTo(0, _sy); }, d); });
        }); });
      } else if (screen !== "home") {
        window.scrollTo({ top: 0, behavior: "instant" }); document.documentElement.scrollTop = 0; document.body.scrollTop = 0;
        var el = document.getElementById("root"); if (el) el.scrollTop = 0;
      }
    } catch(e) {}
  }, [screen]);
  useEffect(() => {
    var _onScroll = function() { setShowBackToTop(screen === "home" && (window.pageYOffset || document.documentElement.scrollTop || 0) > window.innerHeight); };
    window.addEventListener("scroll", _onScroll, { passive: true });
    return function() { window.removeEventListener("scroll", _onScroll); };
  }, [screen]);
  const [sessionLog, setSessionLog] = useState(() => {
    // Retain "Visited today" across a logout/login (or refresh) on the same day.
    // Stored per-date so it clears automatically when the day rolls over.
    try {
      const raw = localStorage.getItem("filament_sessionLog");
      if (raw) {
        const parsed = JSON.parse(raw);
        const today = new Date().toISOString().split("T")[0];
        if (parsed && parsed.date === today && parsed.log) return parsed.log;
      }
    } catch (e) {}
    return {};
  });
  // Persist sessionLog (date-stamped) so today's visits survive a logout/login.
  useEffect(() => {
    try {
      const today = new Date().toISOString().split("T")[0];
      if (TRAINING_MODE) return;
      localStorage.setItem("filament_sessionLog", JSON.stringify({ date: today, log: sessionLog }));
    } catch (e) {}
  }, [sessionLog]);
  const [liveKegs, setLiveKegs] = useState({
    ...INITIAL_KEGS
  });

  // Keg Register — product lookup for scanned kegs
  const [kegRegister, setKegRegister] = useState([]);
  // Refs so the scanner useEffect closure always reads current data
  const liveKegsRef = React.useRef(liveKegs);
  const kegRegisterRef = React.useRef(kegRegister);
  useEffect(() => { liveKegsRef.current = liveKegs; }, [liveKegs]);
  useEffect(() => { kegRegisterRef.current = kegRegister; }, [kegRegister]);

  // Keg search
  const [kegSearch, setKegSearch] = useState("");
  const [kegSearchResults, setKegSearchResults] = useState([]);
  const [gasFromSummary, setGasFromSummary] = useState(false);
  const [gasFilter, setGasFilter] = useState("ours");
  const [gasActionCust, setGasActionCust] = useState(null);
  const [gasActionNote, setGasActionNote] = useState("");
  const [gasActionType, setGasActionType] = useState("Notified customer");
  const [gasActionDate, setGasActionDate] = useState("");
  const [gasViewAction, setGasViewAction] = useState(null);

  // Gas level (kPa) helpers
  const GAS_FULL = 20000;
  const GAS_STEPS = [0,1000,2000,3000,4000,5000,6000,7000,8000,9000,10000,11000,12000,13000,14000,15000,16000,17000,18000,19000,20000];
  const getGasColor = kpa => { if(kpa==null)return'#9E9E9E'; if(kpa===-1)return'#9E9E9E'; if(kpa<=0)return'#C62828'; if(kpa<=4000)return'#E65100'; if(kpa<=10000)return'#1565C0'; if(kpa<=16000)return'#2E7D32'; return'#1B5E20'; };
  const getGasStatus = kpa => { if(kpa===-1)return{label:"Can't read",cls:'gs-nr',warn:false}; if(kpa<=0)return{label:'Empty',cls:'gs-empty',warn:true}; if(kpa<=4000)return{label:'Low',cls:'gs-low',warn:true}; if(kpa<=10000)return{label:'OK',cls:'gs-mid',warn:false}; if(kpa<=16000)return{label:'Good',cls:'gs-good',warn:false}; return{label:'Full',cls:'gs-full',warn:false}; };
  const getGasShort = kpa => { if(kpa==null)return'\u2014'; if(kpa===-1)return'N/R'; if(kpa>=1000)return Math.round(kpa/1000)+'k'; return kpa+''; };
  const getLatestGasKpa = custId => { const logs = gasLog[custId] || []; for(let i=0;i<logs.length;i++){if(logs[i].kpa!=null&&logs[i].kpa!==undefined&&logs[i].kpa!=='')return logs[i].kpa;} return null; };
  const getLatestGasAction = custId => { const logs = gasLog[custId] || []; for(let i=0;i<logs.length;i++){ if(logs[i].reason === 'Status update') return logs[i]; if(logs[i].reason === 'Level check' || logs[i].reason === 'Gas empty' || logs[i].reason === 'Gas lost' || logs[i].reason === 'New cylinder') return null; } return null; };

  // Remote data (loaded from sheet)
  const [remoteCustomers, setRemoteCustomers] = useState(null);
  const [syncingCustomers, setSyncingCustomers] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Apply a payload to state. Every key is optional, so this handles the core
  // payload, the deferred history payload and a cached full payload alike.
  const applyData = data => {
    if (!data) return;
    // Customers
    if (data.customers && data.customers.length > 0) {
      setRemoteCustomers(data.customers.map(c => ({ ...c, syncStatus: c.syncStatus || '' })));
    }
    // Equipment
    if (data.equipment) {
      // Convert sheet format (active: true/false) to app format (inactive: true/false)
      // and trim taps array to match tower configuration
      const TOWER_COUNTS = {
        "Single tap tower": 1,
        "Twin tap tower": 2,
        "Triple tap tower": 3
      };
      const converted = {};
      Object.entries(data.equipment).forEach(([id, eq]) => {
        const towerCount = TOWER_COUNTS[eq.tower] || 1;
        const taps = (eq.taps || []).map((t, i) => ({
          ...t,
          inactive: t.active === false || !t.type && i >= towerCount
        }));
        // Only keep taps up to tower count (inactive ones beyond tower size are irrelevant)
        converted[id] = {
          ...eq,
          taps: eq.tower ? taps.slice(0, towerCount) : taps
        };
      });
      setSavedEquipment(converted);
    }
    // Corflute & Tap Cards
    if (data.corflute) {
      setSavedCorflute(data.corflute);
      setSavedTapCards(Object.fromEntries(Object.entries(data.corflute).map(([id, d]) => [id, {
        cards: d.cards || [],
        backup: d.backup || []
      }])));
    }
    // Kegs on hand
    if (data.kegsOnHand) setLiveKegs(data.kegsOnHand);
    // Keg Register
    if (data.kegRegister) setKegRegister(data.kegRegister);
    // Gas log
    // Sorted into a NEW object — never reversed in place (see sortGasList).
    if (data.gasLog) setGasLog(sortGasLogMap(data.gasLog, true));
    // Maintenance & repair logs
    if (data.maintLog) {
      const addTs = logs => {
        const result = {};
        for (const [cid, entries] of Object.entries(logs || {})) {
          result[cid] = entries.map(e => ({
            ...e,
            ts: e.date ? new Date(e.date).getTime() : 0
          }));
        }
        return result;
      };
      if (data.maintLog.maint) setMaintLog(addTs(data.maintLog.maint));
      if (data.maintLog.repair) setRepairLog(addTs(data.maintLog.repair));
    }
    if (data.deliveryLog) setDeliveryLogData(data.deliveryLog);
    if (data.actionItems) setActionItems(data.actionItems);
    if (data.contacts) setContacts(data.contacts.map(c => ({ ...c, phone: normPhone(c.phone) })));
    if (data.conversations) setConversations(data.conversations);
  };

  // Load data from Google Sheet on startup
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    // Paint the last good payload straight away so the app is usable before the
    // network answers — and stays usable if it never does.
    const cached = readCache();
    if (cached) {
      applyData(cached.data);
      setCacheTs(cached.ts);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setLoadError(null);

    const attempt = async retriesLeft => {
      try {
        const data = await api.load();
        if (cancelled) return;
        if (data && data.error) throw new Error(data.error);
        // A payload with no customers is a failed load wearing a success
        // costume. Without this check the app silently falls back to the
        // hardcoded CUSTOMERS list and renders every other screen empty.
        if (!data || !Array.isArray(data.customers) || data.customers.length === 0) {
          throw new Error("The spreadsheet returned no customer data.");
        }
        applyData(data);
        writeCache(data);
        setCacheTs(null);
        setLoadError(null);
        setLoading(false);
        // Core payload — fetch the history behind it so the driver can start
        // work without waiting on the log tabs.
        if (!data._full) {
          api.loadHistory().then(hist => {
            if (cancelled || !hist || hist.error) return;
            applyData(hist);
            mergeCache(hist);
          }).catch(() => {
            /* history is non-critical — the core is already on screen */
          });
        }
      } catch (err) {
        if (cancelled) return;
        if (retriesLeft > 0) {
          setTimeout(function() { if (!cancelled) attempt(retriesLeft - 1); }, RETRY_DELAY_MS);
          return;
        }
        setLoadError(err && err.message ? err.message : String(err));
        // With cached data on screen the app stays usable and the banner
        // explains itself. With nothing to show, hold the overlay up so the
        // error and its Retry button stay mounted — the old code dropped the
        // overlay here, which is what produced a blank app with no explanation.
        setLoading(!cached);
      }
    };

    attempt(1);
    return () => { cancelled = true; };
  }, [currentUser, loadNonce]);

  const retryLoad = () => { setLoadError(null); setLoadNonce(n => n + 1); };

  // Shown when the app is running on cached data because the sheet could not be
  // reached. Carries the timestamp: silent staleness is worse than a blank screen.
  const staleBanner = loadError && cacheTs ? /*#__PURE__*/React.createElement("div", {
    key: "stale",
    style: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      background: C.accentSoft,
      borderBottom: `1px solid ${C.accentBorder}`,
      fontSize: 13,
      color: C.text
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: { flex: 1 }
  }, "Showing data from ", fmtCacheTime(cacheTs), " \u2014 couldn't reach the spreadsheet."), /*#__PURE__*/React.createElement("button", {
    onClick: retryLoad,
    style: {
      padding: "5px 12px",
      background: C.accent,
      color: "#fff",
      border: "none",
      borderRadius: 7,
      fontFamily: font,
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      flexShrink: 0
    }
  }, "Retry")) : null;

  // ── GAS GAUGE CARD (horizontal pill picker) ──
  const GasGaugeCard = React.memo(function GasGaugeCard(props) {
    const { custId, gasLog, setGasLog, syncToSheet, customer, equip, currentUser, showToast, formVal, setFormVal } = props;
    const canvasRef = React.useRef(null);
    const pillRef = React.useRef(null);
    const [pickerIdx, setPickerIdx] = React.useState(null);
    const [savedState, setSavedState] = React.useState(false);
    const [undoPrev, setUndoPrev] = React.useState(null);
    const undoTimerRef = React.useRef(null);

    const latestKpa = getLatestGasKpa(custId);
    const hasReading = latestKpa != null;
    const isCantRead = pickerIdx === -1 || (pickerIdx == null && latestKpa === -1);
    const initIdx = hasReading && latestKpa !== -1 ? GAS_STEPS.indexOf(latestKpa) : -1;
    const activeIdx = isCantRead ? 0 : (pickerIdx != null ? pickerIdx : (initIdx >= 0 ? initIdx : 0));
    const activeKpa = isCantRead ? -1 : (GAS_STEPS[activeIdx] || 0);
    const userPicked = pickerIdx != null || hasReading;
    const status = getGasStatus(activeKpa);
    const color = userPicked ? getGasColor(activeKpa) : '#9E9E9E';

    const logs = gasLog[custId] || [];
    const lastReading = logs.find(function(l) { return l.kpa != null && l.kpa !== undefined && l.kpa !== ''; });
    const lastReadingText = lastReading ? (lastReading.kpa === -1 ? "Last: Can't read \xB7 " + lastReading.date : 'Last: ' + lastReading.kpa.toLocaleString() + ' kPa \xB7 ' + getGasStatus(lastReading.kpa).label + ' \xB7 ' + lastReading.date) : 'No reading recorded';

    const todayISO = new Date().toISOString().split('T')[0];
    const selDate = formVal.gaugeDate || todayISO;
    const displayDate = new Date(selDate + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

    // Canvas gauge removed -- pills + kPa display only

    // Scroll active pill into view on mount
    React.useEffect(function() {
      var el = pillRef.current;
      if (!el) return;
      if (isCantRead) {
        if (el.children[0]) el.children[0].scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' });
      } else {
        var pill = el.children[activeIdx + 1];
        if (pill) pill.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' });
      }
    }, []);

    var selectPill = function(idx) {
      setPickerIdx(idx);
      if (idx >= 0) {
        var el = pillRef.current;
        if (el && el.children[idx + 1]) el.children[idx + 1].scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }
    };

    // Auto-save: pick + save in one tap (no separate Save button)
    var selectAndSave = function(idx) {
      setPickerIdx(idx);
      if (idx >= 0) {
        var el = pillRef.current;
        if (el && el.children[idx + 1]) el.children[idx + 1].scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }
      var kpa = idx === -1 ? -1 : GAS_STEPS[idx];
      var useDate = new Date(selDate + 'T00:00:00');
      var record = {
        date: useDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
        time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }),
        reason: 'Level check', notes: idx === -1 ? "Can't read" : '', kpa: kpa, ts: useDate.getTime()
      };
      setUndoPrev({ logs: [].concat(gasLog[custId] || []) });
      setGasLog(function(prev) { var n = {}; for(var k in prev) n[k]=prev[k]; n[custId] = [record].concat(prev[custId] || []); return n; });
      syncToSheet('logGas', { data: { customerId: custId, customerName: customer.name, reason: 'Level check', notes: idx === -1 ? "Can't read" : '', kpa: kpa, cylOwner: equip && equip.cylOwner || '', user: currentUser && currentUser.name || 'Unknown', date: record.date, time: record.time } });
      setSavedState(true);
      setFormVal(function(p) { var n = {}; for(var k in p) n[k]=p[k]; n.gasConfirmedThisSession = true; return n; });
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(function() { setSavedState(false); setUndoPrev(null); }, 8000);
    };

    var saveLevel = function() {
      var kpa = isCantRead ? -1 : GAS_STEPS[activeIdx];
      var useDate = new Date(selDate + 'T00:00:00');
      var record = {
        date: useDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
        time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }),
        reason: 'Level check', notes: isCantRead ? "Can't read" : '', kpa: kpa, ts: useDate.getTime()
      };
      setUndoPrev({ logs: [].concat(gasLog[custId] || []) });
      setGasLog(function(prev) { var n = {}; for(var k in prev) n[k]=prev[k]; n[custId] = [record].concat(prev[custId] || []); return n; });
      syncToSheet('logGas', { data: { customerId: custId, customerName: customer.name, reason: 'Level check', notes: isCantRead ? "Can't read" : '', kpa: kpa, cylOwner: equip && equip.cylOwner || '', user: currentUser && currentUser.name || 'Unknown', date: record.date, time: record.time } });
      setSavedState(true);
      setFormVal(function(p) { var n = {}; for(var k in p) n[k]=p[k]; n.gasConfirmedThisSession = true; return n; });
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(function() { setSavedState(false); setUndoPrev(null); }, 8000);
      if (isCantRead) {
        showToast("Gas level saved: Can't read");
      } else {
        var s = getGasStatus(kpa);
        showToast('Gas level saved: ' + kpa.toLocaleString() + ' kPa \u2014 ' + s.label + (s.warn ? ' \u26A0\uFE0F' : ''));
      }
    };

    var undoLevel = function() {
      clearTimeout(undoTimerRef.current);
      if (undoPrev) setGasLog(function(prev) { var n = {}; for(var k in prev) n[k]=prev[k]; n[custId] = undoPrev.logs; return n; });
      setSavedState(false); setUndoPrev(null);
      setFormVal(function(p) { var n = {}; for(var k in p) n[k]=p[k]; n.gasConfirmedThisSession = false; return n; });
      showToast('Gas level reverted');
    };

    var BS = { 'gs-nr': { bg: '#F5F4F0', co: '#9E9E9E', bd: '#E8E5DE' }, 'gs-empty': { bg: '#FFEBEE', co: '#C62828', bd: '#EF9A9A' }, 'gs-low': { bg: '#FFF3E0', co: '#E65100', bd: '#FFCC80' }, 'gs-mid': { bg: '#E3F2FD', co: '#1565C0', bd: '#90CAF9' }, 'gs-good': { bg: '#E8F5E9', co: '#2E7D32', bd: '#A5D6A7' }, 'gs-full': { bg: '#E8F5E9', co: '#1B5E20', bd: '#66BB6A' } };

    var saveSame = function() {
      if (latestKpa == null) return;
      var useDate = new Date(selDate + 'T00:00:00');
      var record = {
        date: useDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
        time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }),
        reason: 'Level check', notes: 'Same as last reading', kpa: latestKpa, ts: useDate.getTime()
      };
      setUndoPrev({ logs: [].concat(gasLog[custId] || []) });
      setGasLog(function(prev) { var n = {}; for(var k in prev) n[k]=prev[k]; n[custId] = [record].concat(prev[custId] || []); return n; });
      syncToSheet('logGas', { data: { customerId: custId, customerName: customer.name, reason: 'Level check', notes: 'Same as last reading', kpa: latestKpa, cylOwner: equip && equip.cylOwner || '', user: currentUser && currentUser.name || 'Unknown', date: record.date, time: record.time } });
      setSavedState(true);
      setFormVal(function(p) { var n = {}; for(var k in p) n[k]=p[k]; n.gasConfirmedThisSession = true; return n; });
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(function() { setSavedState(false); setUndoPrev(null); }, 8000);
      if (latestKpa === -1) {
        showToast("Gas level saved: Can't read");
      } else {
        var s = getGasStatus(latestKpa);
        showToast('Gas same: ' + latestKpa.toLocaleString() + ' kPa \u2014 ' + s.label);
      }
    };

    var saveSwap = function() {
      var useDate = new Date(selDate + 'T00:00:00');
      var record = {
        date: useDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
        time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }),
        reason: 'New cylinder', notes: '', kpa: 20000, ts: useDate.getTime()
      };
      setUndoPrev({ logs: [].concat(gasLog[custId] || []) });
      setGasLog(function(prev) { var n = {}; for(var k in prev) n[k]=prev[k]; n[custId] = [record].concat(prev[custId] || []); return n; });
      syncToSheet('logGas', { data: { customerId: custId, customerName: customer.name, reason: 'New cylinder', notes: '', kpa: 20000, cylOwner: equip && equip.cylOwner || '', user: currentUser && currentUser.name || 'Unknown', date: record.date, time: record.time } });
      setSavedState(true);
      setPickerIdx(GAS_STEPS.indexOf(20000));
      setFormVal(function(p) { var n = {}; for(var k in p) n[k]=p[k]; n.gasConfirmedThisSession = true; return n; });
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(function() { setSavedState(false); setUndoPrev(null); }, 8000);
      showToast('New cylinder logged \u2014 20,000 kPa');
    };
    var bs = BS[status.cls] || BS['gs-mid'];

    var ce = React.createElement;
    return ce("div", { style: { background: C.surface, border: "1.5px solid " + C.border, borderRadius: 14, padding: "14px", marginBottom: 12 } },
      ce("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 } },
        ce("div", { style: { minWidth: 0 } },
          ce("div", { style: { fontSize: 10, fontWeight: 600, letterSpacing: .5, textTransform: "uppercase", color: C.purple, marginBottom: 2 } }, "Gas Level"),
          ce("div", { style: { fontSize: 26, fontWeight: 800, color: color, lineHeight: 1, transition: "color .3s" } }, isCantRead ? "Can't read" : !userPicked ? 'Select level' : activeKpa === 0 ? '0 kPa' : activeKpa.toLocaleString() + ' kPa')
        ),
        ce("div", { style: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 } },
          ce("span", { style: { display: "inline-block", padding: "3px 12px", borderRadius: 14, fontSize: 11, fontWeight: 700, letterSpacing: .3, background: !userPicked ? '#F5F4F0' : bs.bg, color: !userPicked ? '#9E9E9E' : bs.co, border: "1.5px solid " + (!userPicked ? '#E8E5DE' : bs.bd), whiteSpace: "nowrap" } }, !userPicked ? 'No reading' : status.label),
          savedState && ce("span", { style: { fontSize: 11, fontWeight: 700, color: "#2E7D32" } }, "\u2713 Saved")
        )
      ),
      ce("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 8, fontSize: 11, color: C.textTer } },
        ce("span", { style: { fontWeight: selDate !== todayISO ? 600 : 500, color: selDate !== todayISO ? C.accent : C.textTer } }, displayDate),
        ce("input", { type: "date", value: selDate, max: todayISO, onChange: function(e) { if(e.target.value) setFormVal(function(p) { var n = {}; for(var k in p) n[k]=p[k]; n.gaugeDate = e.target.value; return n; }); }, style: { fontSize: 11, fontWeight: 600, color: C.blue, padding: "3px 8px", background: C.blueSoft, border: "1px solid " + C.blueBorder, borderRadius: 6, fontFamily: font, WebkitAppearance: "none", width: 70, textAlign: "center" } }),
        selDate !== todayISO && ce("button", { onClick: function() { setFormVal(function(p) { var n = {}; for(var k in p) n[k]=p[k]; n.gaugeDate = todayISO; return n; }); }, style: { fontSize: 11, fontWeight: 600, color: C.textTer, background: "none", border: "none", cursor: "pointer", fontFamily: font, textDecoration: "underline" } }, "Today")
      ),
      ce("div", { ref: pillRef, onTouchStart: function(e) { pillRef._tx = e.touches[0].clientX; pillRef._ty = e.touches[0].clientY; pillRef._moved = false; }, onTouchMove: function(e) { var dx = Math.abs(e.touches[0].clientX - (pillRef._tx||0)), dy = Math.abs(e.touches[0].clientY - (pillRef._ty||0)); if(dx > 6 || dy > 6) pillRef._moved = true; }, style: { display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6, WebkitOverflowScrolling: "touch", msOverflowStyle: "none", scrollbarWidth: "none" } },
        ce("button", { key: "nr", onClick: function() { selectAndSave(-1); }, onTouchEnd: function(e) { if(pillRef._moved) return; if(e.cancelable) e.preventDefault(); selectAndSave(-1); }, style: { flexShrink: 0, padding: isCantRead ? "8px 14px" : "6px 10px", fontSize: isCantRead ? 13 : 12, fontWeight: isCantRead ? 700 : 500, color: isCantRead ? "#fff" : "#9E9E9E", background: isCantRead ? "#9E9E9E" : "#9E9E9E12", border: isCantRead ? "2px solid #9E9E9E" : "1.5px solid #9E9E9E40", borderRadius: 20, cursor: "pointer", fontFamily: font, whiteSpace: "nowrap", transition: "all .15s" } }, "N/R"),
        GAS_STEPS.map(function(kpa, i) {
          var sel = !isCantRead && i === activeIdx;
          var pillColor = getGasColor(kpa);
          return ce("button", { key: kpa, onClick: function() { selectAndSave(i); }, onTouchEnd: function(e) { if(pillRef._moved) return; if(e.cancelable) e.preventDefault(); selectAndSave(i); }, style: { flexShrink: 0, padding: sel ? "8px 14px" : "6px 10px", fontSize: sel ? 13 : 12, fontWeight: sel ? 700 : 500, color: sel ? "#fff" : pillColor, background: sel ? pillColor : pillColor + "12", border: sel ? "2px solid " + pillColor : "1.5px solid " + pillColor + "40", borderRadius: 20, cursor: "pointer", fontFamily: font, whiteSpace: "nowrap", transition: "all .15s" } }, kpa === 0 ? "0" : (kpa / 1000) + "k");
        })
      ),
      status.warn && ce("div", { style: { marginTop: 8, padding: "7px 10px", background: "#FFF3E0", border: "1.5px solid #FFCC80", borderRadius: 10, fontSize: 11, fontWeight: 600, color: "#E65100" } }, "\u26A0\uFE0F Cylinder low \u2014 schedule replacement"),
      ce("div", { style: { display: "flex", gap: 8, marginTop: 10 } },
        ce("button", { onClick: saveSame, disabled: latestKpa == null, style: { flex: 1, padding: "10px", fontSize: 13, fontWeight: 700, fontFamily: font, border: "2px solid " + (latestKpa != null ? C.accent : C.borderLight), borderRadius: 11, cursor: latestKpa != null ? "pointer" : "default", background: C.surface, color: latestKpa != null ? C.accent : C.textTer, opacity: latestKpa != null ? 1 : 0.5 } }, "Same as last"),
        ce("button", { onClick: saveSwap, style: { flex: 1, padding: "10px", fontSize: 13, fontWeight: 700, fontFamily: font, border: "2px solid #1B5E20", borderRadius: 11, cursor: "pointer", background: "#E8F5E9", color: "#1B5E20" } }, "Swap cylinder"),
        savedState && undoPrev && ce("button", { onClick: undoLevel, style: { padding: "10px 14px", fontSize: 13, fontWeight: 700, fontFamily: font, border: "2px solid " + C.border, borderRadius: 11, cursor: "pointer", background: C.surface, color: C.textSec } }, "Undo")
      )
    );
  });

  // Delivery
  const [delivered, setDelivered] = useState([]);
  const [collected, setCollected] = useState([]);
  const [onHandTap, setOnHandTap] = useState([]);
  const [onHandBackup, setOnHandBackup] = useState([]);
  const [scanInput, setScanInput] = useState("");
  const scanInputRef = React.useRef(null);
  const scanQtyRef = React.useRef(1);
  const scanQtyDisplayRef = React.useRef(null);
  const collectQtyRef = React.useRef(1);
  const collectQtyDisplayRef = React.useRef(null);
  const [collectInput, setCollectInput] = useState("");
  const collectInputRef = React.useRef(null);
  const actionDescRef = React.useRef(null);
  const deliveryNoteRef = React.useRef(null);
  const delivInstrRef = React.useRef(null);
  const [scanProduct, setScanProduct] = useState("");
  const [scanSize, setScanSize] = useState("18L");
  const [scanQty, setScanQty] = useState(1);
  const [collectQty, setCollectQty] = useState(1);
  const [orderRef, setOrderRef] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [backdateDate, setBackdateDate] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  // Transfer keg (customer -> customer). Lives on the Adjust screen, not the
  // Delivery screen: a delivery must match its order, and a transfer has no order.
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferMode, setTransferMode] = useState("in");
  const [transferDigits, setTransferDigits] = useState("");
  const [transferSize, setTransferSize] = useState("18L");
  const [transferOutKeg, setTransferOutKeg] = useState("");
  const [transferDest, setTransferDest] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const transferInputRef = React.useRef(null);
  const [expandedBackup, setExpandedBackup] = useState({});
  const _scrollLock = React.useRef(null);
  const _homeScrollY = React.useRef(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const scrollSafe = (fn) => {
    if (document.activeElement && document.activeElement !== document.body) { try { document.activeElement.blur(); } catch(e){} }
    const now = Date.now();
    const lock = _scrollLock.current;
    const wy = (lock && now - lock.t < 400) ? lock.y : (window.pageYOffset || document.documentElement.scrollTop || 0);
    if (lock && lock.timers) lock.timers.forEach(clearTimeout);
    fn();
    const restore = () => { window.scrollTo(0, wy); };
    restore();
    const timers = [0, 16, 50, 120, 250, 400].map(d => setTimeout(restore, d));
    requestAnimationFrame(() => { restore(); requestAnimationFrame(() => { restore(); requestAnimationFrame(restore); }); });
    _scrollLock.current = { y: wy, t: now, timers };
  };

  // Corflute
  const [corSideA, setCorSideA] = useState("");
  const [corSideB, setCorSideB] = useState("");
  const [cor2SideA, setCor2SideA] = useState("");
  const [cor2SideB, setCor2SideB] = useState("");
  const [corBackups, setCorBackups] = useState([]);
  const [corBackupAdding, setCorBackupAdding] = useState(false);
  // How this venue RUNS its A-frame, as distinct from what is in it today.
  // "" = let the planner infer it from the faces (the old behaviour).
  const [corDisplayMode, setCorDisplayMode] = useState("");
  const [corSelecting, setCorSelecting] = useState(null);

  // Tap cards
  const [tapCards, setTapCards] = useState([]);
  const [tapBackupCards, setTapBackupCards] = useState([]);
  const [tapSelecting, setTapSelecting] = useState(null);
  const [tapBackupSelecting, setTapBackupSelecting] = useState(null);

  // Equipment
  const [equipFridge, setEquipFridge] = useState("");
  const [equipTower, setEquipTower] = useState("");
  const [equipTaps, setEquipTaps] = useState([]);
  const [equipGasLine, setEquipGasLine] = useState("");
  const [equipRegulator, setEquipRegulator] = useState("");
  const [equipSelecting, setEquipSelecting] = useState(null);
  const [equipNotes, setEquipNotes] = useState("");
  const [equipAframes, setEquipAframes] = useState(0);

  // Saved state per customer (persists after logging)
  const [savedCorflute, setSavedCorflute] = useState({});
  const [savedTapCards, setSavedTapCards] = useState({});
  const [savedEquipment, setSavedEquipment] = useState({});
  const [gasLog, _setGasLogRaw] = useState({}); // { custId: [{ date, time, reason, notes, kpa }] } newest first
  // Every write goes through here so the list stays newest-first even when a
  // reading is backdated — the add/undo/edit code all just prepends.
  const setGasLog = React.useCallback(upd => _setGasLogRaw(prev => {
    const next = typeof upd === "function" ? upd(prev) : upd;
    if (!next || next === prev) return next;
    const out = {};
    for (const k in next) out[k] = (prev && next[k] === prev[k]) ? next[k] : sortGasList(next[k], false);
    return out;
  }), []);
  const [maintLog, setMaintLog] = useState({}); // { custId: [{ date, time, type, notes }] }
  const [repairLog, setRepairLog] = useState({}); // { custId: [{ date, time, task, notes }] }
  const [deliveryLogData, setDeliveryLogData] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [crmMode, setCrmMode] = useState("view");
  const [crmEditContact, setCrmEditContact] = useState(null);
  const [crmConvoContact, setCrmConvoContact] = useState("");
  const [crmConvoNote, setCrmConvoNote] = useState("");
  const [noteMode, setNoteMode] = useState("note"); // "note" | "action"
  const [noteSaved, setNoteSaved] = useState(false);
  const [actionDesc, setActionDesc] = useState("");
  const [actionAssignee, setActionAssignee] = useState("");
  const [actionDueDate, setActionDueDate] = useState("");
  const [crmTab, setCrmTab] = useState("notes");
  const [expandedNote, setExpandedNote] = useState(null); // { type: "note"|"action"|"delivery", index }
  const [editingDelivInstr, setEditingDelivInstr] = useState(false);
  const [deliveryEntryMode, setDeliveryEntryMode] = useState("manual");
  const [delivSection, setDelivSection] = useState(""); // "" | "kegs" | "gas" | "notes"
  // Tap cards / corflute opened from the delivery screen come back to it, at
  // the same scroll position. null = opened from the customer screen as before.
  const [signReturn, setSignReturn] = useState(null);
  // Line clean prompt on the delivery screen: null | "unable" (typing a
  // reason) | { outcome: "done"|"unable", record }.
  const [lcAction, setLcAction] = useState(null);
  // Maintenance screen: editing this venue's line clean days (Equipment col AD).
  const [lcDaysEdit, setLcDaysEdit] = useState(false);
  const lcDaysRef = React.useRef(null);
  const lcReasonRef = React.useRef(null);
  const _delivReturnY = React.useRef(null);
  const [scanModalMode, setScanModalMode] = useState(""); // "" | "deliver" | "collect" -- when non-empty modal is open
  const scanModeRef = React.useRef(""); // mirrors scanModalMode so scanner closure reads live value
  React.useEffect(function() { scanModeRef.current = scanModalMode; }, [scanModalMode]);
  const scannerMountRef = React.useRef(null);
  const modeToggleRef = React.useRef(null);
  const modeTogglePrevRef = React.useRef(deliveryEntryMode);
  useEffect(() => { if (modeTogglePrevRef.current !== deliveryEntryMode) { modeTogglePrevRef.current = deliveryEntryMode; requestAnimationFrame(function() { var el = modeToggleRef.current; if (el) { var rect = el.getBoundingClientRect(); var offset = rect.top + window.pageYOffset - 50; window.scrollTo({ top: Math.max(0, offset), behavior: "instant" }); } }); } }, [deliveryEntryMode]);
  const scannerInstanceRef = React.useRef(null);

  // --- QR Scanner: plain-DOM approach (mirrors keg-scanner-test.html) ---
  const parseSizeFromId = (id) => { var m = id.match(/^KEG-(\d+)-/i); return m ? parseInt(m[1], 10) + "L" : "18L"; };

  useEffect(() => {
    if (!scanModalMode) return;
    var el = scannerMountRef.current;
    if (!el) return;

    // --- Inject CSS ---
    var styleId = "filament-scanner-css";
    if (!document.getElementById(styleId)) {
      var sty = document.createElement("style");
      sty.id = styleId;
      sty.textContent = [
        "#fsc-mode-wrap{display:flex;gap:8px;margin-bottom:12px}",
        "#fsc-mode-wrap button{flex:1;padding:13px 8px;border-radius:12px;font-size:14px;font-weight:800;letter-spacing:1.2px;cursor:pointer;transition:all .15s;border:2px solid #E8E5DE;background:#F5F4F0;color:#A9A49B;font-family:'" + font.split("'")[1] + "',sans-serif}",
        "#fsc-mode-wrap .fsc-active-deliver{border-color:#2D7A4F;background:#EEFBF3;color:#2D7A4F}",
        "#fsc-mode-wrap .fsc-active-collect{border-color:#C17817;background:#FFF5E6;color:#C17817}",
        "#fsc-scan-btn{width:100%;padding:13px 8px;background:#EBF2FB;color:#2563A8;border:2px solid #2563A8;border-radius:12px;font-size:14px;font-weight:800;letter-spacing:1.2px;cursor:pointer;margin-bottom:16px;display:flex;align-items:center;justify-content:center;font-family:'" + font.split("'")[1] + "',sans-serif}",
        "#fsc-reader-wrap{display:none;border-radius:14px;position:relative;background:#111;margin-bottom:16px;overflow:hidden}",
        "#fsc-reader{width:100%;min-height:300px;position:relative}",
        "#fsc-reader video{width:100%!important;height:auto!important;display:block!important;object-fit:cover!important}",
        "#fsc-reader img{display:none!important}",
        "#fsc-reader>div{width:100%!important}",
        "#fsc-close-btn{position:absolute;top:10px;right:10px;background:rgba(0,0,0,.6);color:#fff;border:none;border-radius:8px;padding:6px 12px;font-size:14px;font-weight:700;cursor:pointer;z-index:50}",
        "#fsc-pending{display:none;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:60;min-width:240px;border-radius:16px;text-align:center;padding:18px 22px;box-shadow:0 6px 24px rgba(0,0,0,.5)}",
        "#fsc-pending .fsc-pending-id{font-size:22px;font-weight:900;letter-spacing:1px;color:#fff;margin-bottom:2px}",
        "#fsc-pending .fsc-pending-size{font-size:14px;font-weight:700;color:rgba(255,255,255,.85);margin-bottom:14px}",
        "#fsc-pending .fsc-pending-btn{width:100%;padding:14px;border:none;border-radius:12px;font-size:15px;font-weight:800;letter-spacing:1px;cursor:pointer;color:#fff;margin-bottom:6px}",
        "#fsc-pending .fsc-pending-cancel{width:100%;padding:8px;border:none;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;background:rgba(255,255,255,.15);color:rgba(255,255,255,.7)}",
        "#fsc-reject-warn{display:none;padding:11px 14px;border-radius:10px;background:#FFF0ED;border:1px solid #F0C4BB;color:#C43E2A;font-size:13px;font-weight:600;margin-bottom:12px}",
        ".fsc-cam-mode-wrap{position:absolute;bottom:14px;left:50%;transform:translateX(-50%);display:flex;gap:6px;z-index:50}",
        ".fsc-cam-mode-btn{padding:9px 20px;border-radius:30px;font-size:13px;font-weight:800;letter-spacing:1px;border:2px solid rgba(255,255,255,.3);background:rgba(0,0,0,.45);color:#fff;cursor:pointer}",
        ".fsc-cam-deliver{border-color:#2D7A4F!important;background:#2D7A4F!important}",
        ".fsc-cam-collect{border-color:#C17817!important;background:#C17817!important}",
        "#fsc-reader__scan_region{border:2px solid rgba(255,255,255,.3)!important;border-radius:4px!important;box-shadow:0 0 0 4000px rgba(0,0,0,.3)!important}",
        "#fsc-reader__dashboard_section{display:none!important}",
        ".fsc-list-section{margin-bottom:16px}",
        ".fsc-list-label{font-size:11px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;margin-bottom:8px;display:flex;align-items:center;gap:6px}",
        ".fsc-badge{font-size:11px;font-weight:700;padding:1px 8px;border-radius:20px}",
        ".fsc-badge-green{background:#EEFBF3;color:#2D7A4F;border:1px solid #B8E4CA}",
        ".fsc-badge-amber{background:#FFF5E6;color:#C17817;border:1px solid #F0D4A0}",
        ".fsc-none-yet{font-size:12px;color:#A9A49B;font-style:italic;margin-bottom:8px}",
        ".fsc-swipe-outer{position:relative;border-radius:12px;margin-bottom:6px;overflow:hidden;height:52px}",
        ".fsc-swipe-bg{position:absolute;inset:0;background:#C43E2A;display:flex;align-items:center;justify-content:flex-end;padding-right:16px;border-radius:12px;font-size:24px;opacity:0}",
        ".fsc-keg-row{position:absolute;inset:0;display:flex;align-items:center;gap:10px;padding:0 12px;border-radius:12px;background:#F5F4F0;border:1.5px solid #F0EDE6;cursor:grab;user-select:none;transition:transform .15s ease;will-change:transform}",
        ".fsc-keg-row.dragging{transition:none}",
        ".fsc-keg-bar{width:10px;align-self:stretch;border-radius:4px;opacity:.25;flex-shrink:0}",
        ".fsc-keg-bar-green{background:#2D7A4F}",
        ".fsc-keg-bar-amber{background:#C17817}",
        ".fsc-keg-info{flex:1}",
        ".fsc-keg-id{font-size:14px;font-weight:800;color:#1A1714}",
        ".fsc-keg-size{font-size:12px;color:#A9A49B}",
        ".fsc-swipe-hint{display:flex;flex-direction:column;align-items:center;flex-shrink:0;opacity:.3;line-height:1.2}",
        ".fsc-swipe-hint-emoji{font-size:13px}",
        ".fsc-swipe-hint-text{font-size:10px;font-weight:600;color:#706B63;letter-spacing:.3px}",
        "@keyframes fscPopIn{0%{transform:scale(.92);opacity:0}100%{transform:scale(1);opacity:1}}",
        "@keyframes fscFadeIn{0%{opacity:0}100%{opacity:1}}"
      ].join("\n");
      document.head.appendChild(sty);
    }

    // --- Inject HTML (mirrors test HTML structure) ---
    el.innerHTML = [
      '<button id="fsc-scan-btn"><span id="fsc-scan-label">SCAN KEG TO DELIVER</span></button>',
      '<div id="fsc-reader-wrap">',
      '  <div id="fsc-reader"></div>',
      '  <button id="fsc-close-btn">\u2715</button>',
      '  <div id="fsc-pending" style="display:none"></div>',
      '  <div class="fsc-cam-mode-wrap">',
      '    <button class="fsc-cam-mode-btn fsc-cam-deliver" id="fsc-cam-btn-deliver">DELIVER</button>',
      '    <button class="fsc-cam-mode-btn" id="fsc-cam-btn-collect">COLLECT</button>',
      '  </div>',
      '</div>',
      '<div id="fsc-reject-warn"></div>',
      '<div class="fsc-list-section">',
      '  <div class="fsc-list-label" style="color:#2D7A4F">DELIVERED <span class="fsc-badge fsc-badge-green" id="fsc-count-deliver">0</span></div>',
      '  <div class="fsc-none-yet" id="fsc-none-deliver">None yet</div>',
      '  <div id="fsc-list-deliver"></div>',
      '</div>',
      '<div class="fsc-list-section">',
      '  <div class="fsc-list-label" style="color:#C17817">COLLECTED <span class="fsc-badge fsc-badge-amber" id="fsc-count-collect">0</span></div>',
      '  <div class="fsc-none-yet" id="fsc-none-collect">None yet</div>',
      '  <div id="fsc-list-collect"></div>',
      '</div>'
    ].join("");

    // --- Load html5-qrcode lib ---
    function ensureLib(cb) {
      if (window.Html5Qrcode) { cb(); return; }
      // Check if script tag already exists (from eager load) and wait for it
      var existing = document.querySelector('script[src*="html5-qrcode"]');
      if (existing) {
        var prev = existing.onload;
        existing.onload = function() { if (prev) prev(); cb(); };
        existing.onerror = function() { showToast("Scanner library failed to load"); };
        return;
      }
      var s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js";
      s.onload = cb;
      s.onerror = function() { showToast("Scanner library failed to load"); };
      document.head.appendChild(s);
    }

    // --- Scanner state (plain vars, not React) ---
    var sc = null, mode = (scanModalMode === "collect" ? "collecting" : "delivering");
    var scDelivered = [], scCollected = [];
    var audioCtx = null, rejectTimer = null;
    var pendingId = null;

    // Scroll lock helper — captures scroll, runs fn, restores across React re-renders
    function scanScrollLock(fn) {
      var sy = window.pageYOffset || document.documentElement.scrollTop || 0;
      var restore = function() { window.scrollTo(0, sy); };
      fn();
      restore();
      requestAnimationFrame(restore);
      setTimeout(restore, 50);
      setTimeout(restore, 150);
      setTimeout(restore, 300);
    }

    function primeAudio() {
      try {
        if (audioCtx) return;
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        var buf = audioCtx.createBuffer(1, 1, 22050);
        var src = audioCtx.createBufferSource();
        src.buffer = buf; src.connect(audioCtx.destination); src.start(0);
      } catch(e) {}
    }
    function beep(ok) {
      try {
        if (!audioCtx) return;
        if (audioCtx.state === "suspended") audioCtx.resume();
        var osc = audioCtx.createOscillator();
        var gain = audioCtx.createGain();
        osc.connect(gain); gain.connect(audioCtx.destination);
        osc.frequency.value = ok ? 880 : 220; osc.type = "sine";
        gain.gain.setValueAtTime(0.3, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + (ok ? 0.15 : 0.3));
        osc.start(audioCtx.currentTime); osc.stop(audioCtx.currentTime + (ok ? 0.15 : 0.3));
      } catch(e) {}
    }
    function fscSetMode(m) {
      dismissPending();
      mode = m; var d = m === "delivering";
      // Bridge back to React so modal header pill stays in sync
      var wantReactMode = d ? "deliver" : "collect";
      if (scanModeRef.current !== wantReactMode) setScanModalMode(wantReactMode);
      var safeSet = function(id, prop, val) { var n = document.getElementById(id); if (n) n[prop] = val; };
      safeSet("fsc-btn-deliver", "className", d ? "fsc-active-deliver" : "");
      safeSet("fsc-btn-collect", "className", !d ? "fsc-active-collect" : "");
      safeSet("fsc-scan-label", "textContent", d ? "SCAN KEG TO DELIVER" : "SCAN KEG TO COLLECT");
      safeSet("fsc-cam-btn-deliver", "className", "fsc-cam-mode-btn" + (d ? " fsc-cam-deliver" : ""));
      safeSet("fsc-cam-btn-collect", "className", "fsc-cam-mode-btn" + (!d ? " fsc-cam-collect" : ""));
      var rw = document.getElementById("fsc-reader-wrap");
      if (rw) rw.style.border = "2.5px solid " + (d ? "#B8E4CA" : "#F0D4A0");
    }
    function startScanner() {
      primeAudio();
      try {
        document.getElementById("fsc-scan-btn").style.display = "none";
        document.getElementById("fsc-reader-wrap").style.display = "block";
        sc = new window.Html5Qrcode("fsc-reader");
        scannerInstanceRef.current = sc;
        sc.start(
          { facingMode: "environment" },
          {
            fps: 15,
            qrbox: { width: 260, height: 260 },
            videoConstraints: {
              facingMode: "environment",
              advanced: [{ focusMode: "continuous" }]
            }
          },
          function(text) { onScan(text); },
          function() {}
        ).catch(function(err) { showToast("Camera: " + err); stopScannerFn(); });
      } catch(e) { showToast("Scanner: " + e.message); stopScannerFn(); }
    }
    function stopScannerFn() {
      dismissPending();
      scanScrollLock(function() {
        if (sc) {
          sc.stop().then(function() { sc.clear(); sc = null; scannerInstanceRef.current = null; }).catch(function(){ sc = null; scannerInstanceRef.current = null; });
        }
        var rw = document.getElementById("fsc-reader-wrap");
        if (rw) rw.style.display = "none";
        var sb = document.getElementById("fsc-scan-btn");
        if (sb) sb.style.display = "flex";
      });
    }
    function showReject(msg) {
      beep(false);
      var rw = document.getElementById("fsc-reject-warn");
      if (!rw) return;
      rw.textContent = "\u26A0\uFE0F " + msg;
      rw.style.display = "block";
      rw.style.animation = "none";
      void rw.offsetWidth;
      rw.style.animation = "fscPopIn .2s ease";
      if (rejectTimer) clearTimeout(rejectTimer);
      rejectTimer = setTimeout(function() { rw.style.display = "none"; }, 3000);
      var scanRegion = document.getElementById("fsc-reader__scan_region");
      if (scanRegion) {
        scanRegion.style.border = "3px solid #C43E2A";
        scanRegion.style.boxShadow = "0 0 20px rgba(196,62,42,.6), inset 0 0 20px rgba(196,62,42,.15)";
        scanRegion.style.transition = "border .15s, box-shadow .15s";
        setTimeout(function() { scanRegion.style.border = "2px solid rgba(255,255,255,.3)"; scanRegion.style.boxShadow = "none"; }, 800);
      }
    }
    function lookupKegProduct(id) {
      // Check liveKegs for all customers to find this keg's product
      var kegsObj = typeof liveKegsRef.current === "object" ? liveKegsRef.current : {};
      var keys = Object.keys(kegsObj);
      for (var k = 0; k < keys.length; k++) {
        var data = kegsObj[keys[k]];
        var all = [].concat(data.onTap || [], data.backup || []);
        for (var j = 0; j < all.length; j++) {
          if (all[j].kegId === id && all[j].product) return all[j].product;
        }
      }
      // Fallback: check Keg Register (product from last fill)
      var reg = kegRegisterRef.current || [];
      for (var r = 0; r < reg.length; r++) {
        if (reg[r].kegId === id && reg[r].product) return reg[r].product;
      }
      return "";
    }
    function showPending(id, size, forceMsg) {
      pendingId = id;
      var pe = document.getElementById("fsc-pending");
      if (!pe) return;
      var product = lookupKegProduct(id);
      var prodCol = product ? PRODUCT_COLOR[product] : null;
      var isDeliver = mode === "delivering";
      var defaultCol = isDeliver ? "#2D7A4F" : "#C17817";
      var bgCol = prodCol || defaultCol;
      var label = isDeliver ? "DELIVER" : "COLLECT";
      var sizeLabel = size + (product ? " \u00B7 " + (PRODUCT_SHORT[product] || product) : "");
      if (forceMsg) {
        // "Already scanned" display — no confirm button, just dismiss on tap
        pe.style.background = "#555";
        pe.innerHTML = '<div class="fsc-pending-id">' + id + '</div>' +
          '<div class="fsc-pending-size">' + sizeLabel + '</div>' +
          '<div style="font-size:13px;font-weight:700;color:rgba(255,255,255,.7);padding:8px 0">' + forceMsg + '</div>' +
          '<button class="fsc-pending-cancel" id="fsc-pending-cancel">OK</button>';
        pe.style.display = "block";
        pe.style.animation = "none";
        void pe.offsetWidth;
        pe.style.animation = "fscFadeIn .15s ease";
        document.getElementById("fsc-pending-cancel").addEventListener("click", function() { dismissPending(); });
        return;
      }
      pe.style.background = bgCol;
      pe.innerHTML = '<div class="fsc-pending-id">' + id + '</div>' +
        '<div class="fsc-pending-size">' + sizeLabel + '</div>' +
        '<button class="fsc-pending-btn" id="fsc-pending-confirm" style="background:rgba(255,255,255,.2)">' + label + '</button>' +
        '<button class="fsc-pending-cancel" id="fsc-pending-cancel">CANCEL</button>';
      pe.style.display = "block";
      pe.style.animation = "none";
      void pe.offsetWidth;
      pe.style.animation = "fscFadeIn .15s ease";
      document.getElementById("fsc-pending-confirm").addEventListener("click", function() { confirmPending(); });
      document.getElementById("fsc-pending-cancel").addEventListener("click", function() { dismissPending(); });
    }
    function dismissPending() {
      pendingId = null;
      var pe = document.getElementById("fsc-pending");
      if (pe) pe.style.display = "none";
    }
    function confirmPending() {
      var id = pendingId;
      if (!id) return;
      var size = parseSizeFromId(id);
      scanScrollLock(function() {
        if (mode === "collecting") {
          var custKegs = (liveKegsRef.current || {})[customer.id] || { onTap: [], backup: [] };
          var allAtCust = [].concat(custKegs.onTap, custKegs.backup);
          var found = null;
          for (var i = 0; i < allAtCust.length; i++) { if (allAtCust[i].kegId === id) { found = allAtCust[i]; break; } }
          if (!found) { dismissPending(); showReject(id + " is not logged at this customer"); return; }
          beep(true);
          scCollected.push(id);
          setCollected(function(prev) { return prev.concat([{ kegId: id, product: found.product || "", size: found.size || size }]); });
          setOnHandTap(function(prev) { var x = -1; for(var j=0;j<prev.length;j++){if(prev[j].kegId===id){x=j;break;}} return x>=0?prev.filter(function(_,j){return j!==x;}):prev; });
          setOnHandBackup(function(prev) { var x = -1; for(var j=0;j<prev.length;j++){if(prev[j].kegId===id){x=j;break;}} return x>=0?prev.filter(function(_,j){return j!==x;}):prev; });
        } else {
          beep(true);
          scDelivered.push(id);
          var regProduct = lookupKegProduct(id);
          setDelivered(function(prev) { return prev.concat([{ kegId: id, product: regProduct, size: size, location: "fridge" }]); });
          setOnHandBackup(function(prev) { return prev.concat([{ kegId: id, product: regProduct, size: size }]); });
        }
      });
      dismissPending();
      // Cooldown: ignore re-scans of this keg for 2s after confirming
      var cooldownId = id;
      cooldownUntil = Date.now() + 2000;
      cooldownKeg = cooldownId;
      renderScanLists();
    }
    var cooldownUntil = 0, cooldownKeg = null;
    var lastDupeId = null;
    function onScan(raw) {
      var id = raw.trim().toUpperCase();
      if (!id.match(/^KEG-\d+-\d+/i)) return;
      // Ignore scans while a pending confirmation is showing
      if (pendingId) return;
      // Cooldown: silently ignore re-scans of just-confirmed keg
      if (cooldownKeg === id && Date.now() < cooldownUntil) return;
      // Duplicate: silently ignore but remember for tap-to-reveal
      if (scDelivered.concat(scCollected).indexOf(id) !== -1) {
        lastDupeId = id; return;
      }
      // Collect mode: validate keg is at this customer before showing pending
      if (mode === "collecting") {
        var custKegs = (liveKegsRef.current || {})[customer.id] || { onTap: [], backup: [] };
        var allAtCust = [].concat(custKegs.onTap, custKegs.backup);
        var found = false;
        for (var i = 0; i < allAtCust.length; i++) { if (allAtCust[i].kegId === id) { found = true; break; } }
        if (!found) { showReject(id + " is not logged at this customer"); return; }
      }
      lastDupeId = null;
      var size = parseSizeFromId(id);
      beep(true);
      showPending(id, size);
    }
    function renderScanLists() {
      renderScanList("deliver", scDelivered, "green");
      renderScanList("collect", scCollected, "amber");
    }
    function renderScanList(key, arr, colour) {
      var ce = document.getElementById("fsc-count-" + key); if (ce) ce.textContent = arr.length;
      var ne = document.getElementById("fsc-none-" + key); if (ne) ne.style.display = arr.length ? "none" : "block";
      var container = document.getElementById("fsc-list-" + key); if (!container) return;
      container.innerHTML = "";
      arr.forEach(function(id, idx) {
        var size = parseSizeFromId(id);
        var outer = document.createElement("div"); outer.className = "fsc-swipe-outer";
        if (idx === arr.length - 1) outer.style.animation = "fscPopIn .2s ease";
        var bg = document.createElement("div"); bg.className = "fsc-swipe-bg"; bg.textContent = "\u274C";
        var row = document.createElement("div"); row.className = "fsc-keg-row";
        row.innerHTML = '<div class="fsc-keg-bar fsc-keg-bar-' + colour + '"></div><div class="fsc-keg-info"><div class="fsc-keg-id">' + id + '</div><div class="fsc-keg-size">' + size + '</div></div><div class="fsc-swipe-hint"><span class="fsc-swipe-hint-emoji">\u23EA</span><span class="fsc-swipe-hint-text">remove</span></div>';
        outer.appendChild(bg); outer.appendChild(row); container.appendChild(outer);
        addScanSwipe(row, outer, key, id);
      });
    }
    function addScanSwipe(row, outer, listKey, id) {
      var startX = 0, currentX = 0, dragging = false, THRESHOLD = -80;
      function onStart(e) { startX = e.touches ? e.touches[0].clientX : e.clientX; currentX = 0; dragging = true; row.classList.add("dragging"); }
      function onMove(e) { if (!dragging) return; var x = (e.touches ? e.touches[0].clientX : e.clientX) - startX; if (x > 0) x = 0; currentX = x; row.style.transform = "translateX(" + x + "px)"; outer.querySelector(".fsc-swipe-bg").style.opacity = Math.min(Math.abs(x) / Math.abs(THRESHOLD), 1); if (e.cancelable) e.preventDefault(); }
      function onEnd() { if (!dragging) return; dragging = false; row.classList.remove("dragging"); if (currentX <= THRESHOLD) { row.style.transition = "transform .2s ease"; row.style.transform = "translateX(-110%)"; setTimeout(function() { removeScanItem(listKey, id); }, 200); } else { row.style.transform = "translateX(0)"; outer.querySelector(".fsc-swipe-bg").style.opacity = 0; } }
      row.addEventListener("touchstart", onStart, { passive: true });
      row.addEventListener("touchmove", onMove, { passive: false });
      row.addEventListener("touchend", onEnd);
      row.addEventListener("mousedown", onStart);
      window.addEventListener("mousemove", function(e) { if (dragging) onMove(e); });
      window.addEventListener("mouseup", onEnd);
    }
    function removeScanItem(key, id) {
      scanScrollLock(function() {
        if (key === "deliver") {
          scDelivered = scDelivered.filter(function(k) { return k !== id; });
          setDelivered(function(prev) { return prev.filter(function(k) { return k.kegId !== id; }); });
          setOnHandBackup(function(prev) { var x = -1; for(var j=0;j<prev.length;j++){if(prev[j].kegId===id){x=j;break;}} return x>=0?prev.filter(function(_,j){return j!==x;}):prev; });
        } else {
          scCollected = scCollected.filter(function(k) { return k !== id; });
          setCollected(function(prev) { return prev.filter(function(k) { return k.kegId !== id; }); });
        }
        renderScanLists();
      });
    }

    // --- Attach event handlers ---
    document.getElementById("fsc-scan-btn").onclick = function() { ensureLib(startScanner); };
    document.getElementById("fsc-close-btn").onclick = stopScannerFn;
    document.getElementById("fsc-cam-btn-deliver").onclick = function() { fscSetMode("delivering"); };
    document.getElementById("fsc-cam-btn-collect").onclick = function() { fscSetMode("collecting"); };
    // Tap on camera area: if a duplicate was silently ignored, show "already scanned" popup
    document.getElementById("fsc-reader-wrap").addEventListener("click", function(e) {
      // Don't intercept button clicks
      if (e.target.closest("#fsc-close-btn") || e.target.closest(".fsc-cam-mode-wrap") || e.target.closest("#fsc-pending")) return;
      if (pendingId) return;
      if (lastDupeId) {
        var size = parseSizeFromId(lastDupeId);
        beep(false);
        showPending(lastDupeId, size, "Already scanned");
        lastDupeId = null;
      }
    });

    // --- Sync initial UI to current mode, then auto-start the camera ---
    // Skips the explicit "SCAN KEG TO DELIVER" tap and fixes the wrong label
    // when entering in collect mode. The button still shows again if the user
    // closes the scanner via the × button, so they can re-open it.
    fscSetMode(mode);
    ensureLib(startScanner);

    // --- Bridge React modal-mode toggle -> scanner internal `mode` ---
    var _modeSyncTimer = setInterval(function() {
      var wantMode = scanModeRef.current === "collect" ? "collecting" : "delivering";
      if (mode !== wantMode) fscSetMode(wantMode);
    }, 50);

    // --- Cleanup on unmount ---
    return function() {
      clearInterval(_modeSyncTimer);
      var s = scannerInstanceRef.current;
      if (s) { try { s.stop().then(function() { try { s.clear(); } catch(e){} }).catch(function(){}); } catch(e) {} scannerInstanceRef.current = null; }
      el.innerHTML = "";
    };
  }, [!!scanModalMode]);
  const [notesTabMode, setNotesTabMode] = useState("note");
  const notesTabNoteRef = React.useRef(null);
  const notesTabActionRef = React.useRef(null);

  const [formVal, setFormVal] = useState({});
  const [editingLog, setEditingLog] = useState(null); // { source: "maint"|"repair"|"gas", index, record }


  const _keepScroll = fn => { var _st = window.pageYOffset || document.documentElement.scrollTop; fn(); requestAnimationFrame(function() { requestAnimationFrame(function() { window.scrollTo(0, _st); }); }); };

  // --- CRM tag button ---
  const [crmTagged, setCrmTagged] = useState(false);
  const [stickyTagged, setStickyTagged] = useState(false);
  const [editingConvo, setEditingConvo] = useState(null);
  const [editConvoNote, setEditConvoNote] = useState("");
  const [editingDelivery, setEditingDelivery] = useState(null); // { index, record, ... }
  const [editingHistoryNote, setEditingHistoryNote] = useState(null); // { index, text }

  const _crmTagButtons = () => {
    if (!customer) return null;
    return /*#__PURE__*/React.createElement("div", { style: { marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" } },
      /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setCrmTagged(prev => !prev); if (!crmTagged) showToast("Will save to CRM"); }), style: { padding: "7px 10px", fontSize: 12, fontWeight: 600, background: crmTagged ? "#EEFBF3" : "#EBF2FB", border: "1.5px solid " + (crmTagged ? "#B8E4CA" : "#2563A8"), borderRadius: 8, cursor: "pointer", fontFamily: font, color: crmTagged ? "#2D7A4F" : "#2563A8" } }, crmTagged ? "\u2713 Will save to CRM" : "Log to CRM"),
      /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setStickyTagged(prev => !prev); if (!stickyTagged) showToast("Will make sticky note"); }), style: { padding: "7px 10px", fontSize: 12, fontWeight: 600, background: stickyTagged ? "#FFF8E1" : "#F5F5F5", border: "1.5px solid " + (stickyTagged ? "#FFD54F" : "#CCC"), borderRadius: 8, cursor: "pointer", fontFamily: font, color: stickyTagged ? "#F57F17" : "#666" } }, stickyTagged ? "\u2713 Will make sticky" : "Make sticky"));
  };

  const ROLE_WORDS = ["barista", "manager", "owner", "chef", "bartender", "supervisor", "staff", "trainee", "director", "receptionist", "admin", "head", "lead", "assistant", "coordinator", "cashier", "waiter", "waitress", "server", "kitchen", "front of house", "foh", "boh"];
  const NAME_STOP_WORDS = ["about", "regarding", "concerning", "that", "who", "the", "this", "their", "there", "they", "from", "with", "and", "for", "has", "had", "was", "were", "will", "would", "could", "should", "been", "being", "have", "does", "said", "says", "told", "asked", "wants", "needs", "today", "yesterday", "tomorrow", "again", "also", "just", "still", "already", "always", "never", "here", "back", "down", "into", "over", "after", "before"];

  const _extractFromNote = (text) => {
    const triggers = ["spoke to", "spoke with", "chatted with", "chatted to", "talked to", "talked with", "met with", "met ", "discussed with", "discussing with", "catch up with", "catching up with", "caught up with", "following up with", "checked in with", "call with", "called ", "informed ", "told ", "mentioned to"];
    const lower = text.toLowerCase();
    var name = "";
    var role = "";

    // Extract name from trigger phrases
    for (let t = 0; t < triggers.length; t++) {
      const idx = lower.indexOf(triggers[t]);
      if (idx !== -1) {
        const after = text.substring(idx + triggers[t].length).trim();
        // Split on punctuation to get the clause after the trigger
        const clause = after.split(/[,.;:!?\n]/)[0].trim();
        const words = clause.split(/\s+/);
        // Take first name only — stop at stop words, role words, or lowercase non-name words
        var nameWords = [];
        for (let w = 0; w < words.length && w < 3; w++) {
          const wLower = words[w].toLowerCase();
          if (NAME_STOP_WORDS.indexOf(wLower) !== -1) break;
          if (ROLE_WORDS.indexOf(wLower) !== -1) break;
          // If word is all lowercase and not the first word, likely not a name
          if (w > 0 && words[w] === wLower && wLower.length > 1) break;
          nameWords.push(words[w]);
        }
        if (nameWords.length > 0) {
          name = nameWords.map(function(w) { return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(); }).join(" ");
          // Check if next word after name is a role
          var restAfterName = clause.substring(nameWords.join(" ").length).trim().toLowerCase();
          // Strip leading comma/dash
          restAfterName = restAfterName.replace(/^[,\-\s]+/, "").trim();
          for (let r = 0; r < ROLE_WORDS.length; r++) {
            if (restAfterName.indexOf(ROLE_WORDS[r]) === 0 || restAfterName.indexOf("new " + ROLE_WORDS[r]) === 0 || restAfterName.indexOf("the " + ROLE_WORDS[r]) === 0) {
              // Grab the role phrase (up to 3 words)
              var rolePhrase = restAfterName.replace(/^(new |the )/, "").split(/\s+/).slice(0, 2).join(" ");
              role = rolePhrase.charAt(0).toUpperCase() + rolePhrase.slice(1);
              break;
            }
          }
        }
        break;
      }
    }

    // If no trigger found, check for existing contact names in text
    if (!name && customer) {
      const custContacts = contacts.filter(function(c) { return String(c.customerId) === String(customer.id) && c.active !== "No"; });
      for (let i = 0; i < custContacts.length; i++) {
        if (lower.indexOf(custContacts[i].contactName.toLowerCase()) !== -1) {
          name = custContacts[i].contactName;
          break;
        }
      }
    }

    // Also scan entire text for role words if no role found yet
    if (!role) {
      for (let r = 0; r < ROLE_WORDS.length; r++) {
        var rIdx = lower.indexOf(ROLE_WORDS[r]);
        if (rIdx !== -1) {
          // Check it's preceded by "new " or similar context
          var before = lower.substring(Math.max(0, rIdx - 5), rIdx).trim();
          if (before === "new" || before === "the" || before === "a" || before === "head" || before === "") {
            var rPhrase = text.substring(rIdx, rIdx + ROLE_WORDS[r].length);
            role = rPhrase.charAt(0).toUpperCase() + rPhrase.slice(1).toLowerCase();
            break;
          }
        }
      }
    }

    return { name: name, role: role };
  };

  const _sendCrmOnSave = (noteText) => {
    if (!customer || !noteText || !crmTagged) { _keepScroll(() => setCrmTagged(false)); return; }
    const extracted = _extractFromNote(noteText);
    const contactName = extracted.name || "General";

    // Log conversation
    syncToSheet("saveConversation", { data: { customerId: customer.id, customerName: customer.name, contactName: contactName, note: noteText, loggedBy: currentUser?.name || "Unknown" } });
    const nowDate = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
    _keepScroll(() => setConversations(prev => [...prev, { date: nowDate, customerId: String(customer.id), customerName: customer.name, contactName: contactName, note: noteText, loggedBy: currentUser?.name || "Unknown", linkedType: "", linkedDescription: "" }]));

    // Auto-create or update contact if name found
    if (extracted.name) {
      var existing = contacts.find(function(c) { return String(c.customerId) === String(customer.id) && c.contactName.toLowerCase() === extracted.name.toLowerCase(); });
      if (!existing) {
        syncToSheet("saveContact", { data: { customerId: customer.id, customerName: customer.name, contactName: extracted.name, role: extracted.role || "", phone: "" } });
        _keepScroll(() => setContacts(prev => [...prev, { customerId: String(customer.id), customerName: customer.name, contactName: extracted.name, role: extracted.role || "", phone: "", active: "Yes" }]));
      } else if (extracted.role && !existing.role) {
        // Update existing contact with detected role if they don't have one
        syncToSheet("saveContact", { data: { customerId: customer.id, customerName: customer.name, contactName: extracted.name, role: extracted.role, phone: existing.phone || "", originalName: extracted.name } });
        _keepScroll(() => setContacts(prev => prev.map(function(c) { return String(c.customerId) === String(customer.id) && c.contactName.toLowerCase() === extracted.name.toLowerCase() ? { ...c, role: extracted.role } : c; })));
      }
    }

    showToast("Saved to CRM" + (extracted.name ? " \u2014 " + extracted.name + (extracted.role ? " (" + extracted.role + ")" : "") : ""));
    _keepScroll(() => setCrmTagged(false));
  };
  const showToast = m => {
    if (TRAINING_MODE) m = "Training \u2014 not saved \u00B7 " + m;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    var el = toastRef.current;
    if (el) { el.textContent = m; el.style.visibility = 'visible'; el.style.pointerEvents = 'auto'; el.style.opacity = '1'; }
    toastTimerRef.current = setTimeout(function() { if (el) { el.style.opacity = '0'; setTimeout(function() { if (el) { el.style.visibility = 'hidden'; el.style.pointerEvents = 'none'; } }, 300); } }, 2500);
  };
  const handleLogin = () => {
    const user = USERS.find(u => u.username.toLowerCase() === loginUsername.toLowerCase() && u.password === loginPassword);
    if (user) {
      TRAINING_MODE = !!user.training;
      setCurrentUser(user);
      setLoginError("");
      setLoginUsername("");
      setLoginPassword("");
    } else {
      setLoginError("Invalid username or password");
    }
  };
  const handleLogout = () => {
    setFieldRun(null); setOnRun(false); setRunAsk(false); setRunStopCtx(null); setRunActiveSeq(null);
    runFirstCheckRef.current = false; runCheckAtRef.current = 0;
    TRAINING_MODE = false;
    setCurrentUser(null);
    setScreen("home");
    setCust(null);
    setSearch("");
  };

  // Search all kegs across all customers
  const searchKegs = query => {
    setKegSearch(query);
    if (!query.trim()) {
      setKegSearchResults([]);
      return;
    }
    const q = query.trim().toUpperCase();
    const results = [];
    Object.entries(liveKegs).forEach(([custId, data]) => {
      const cust = activeCustomers.find(c => c.id === custId);
      if (!cust) return;
      (data.onTap || []).forEach(k => {
        if (k.kegId && k.kegId.toUpperCase().includes(q)) {
          results.push({
            ...k,
            location: "On Tap",
            customer: cust
          });
        }
      });
      (data.backup || []).forEach(k => {
        if (k.kegId && k.kegId.toUpperCase().includes(q)) {
          results.push({
            ...k,
            location: "Backup",
            customer: cust
          });
        }
      });
    });
    setKegSearchResults(results);
  };
  const addLog = (type, data) => {
    setSessionLog(prev => ({
      ...prev,
      [customer.id]: {
        ...(prev[customer.id] || {}),
        [type]: [...(prev[customer.id]?.[type] || []), {
          ...data,
          time: new Date().toLocaleTimeString('en-AU', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          user: currentUser?.name || "Unknown"
        }]
      }
    }));
  };

  // Sync helper — fires POST to Apps Script, shows syncing indicator
  const syncToSheet = async (action, payload) => {
    setSyncing(true);
    try {
      const result = await api.post(action, payload);
      if (result.error) {
        showToast(`Sync error: ${result.error}`);
      } else if (result.warning) {
        // Partial success — e.g. the Delivery Log row saved but one or more
        // kegs failed to update in the Keg Register. Never fail silently.
        showToast(`\u26A0 ${result.warning}`);
      }
      setSyncing(false);
      return result;
    } catch (err) {
      showToast(`Sync failed: ${err.message}`);
      setSyncing(false);
      return {
        error: err.message
      };
    }
  };

  // Use remote customers if loaded, otherwise fall back to hardcoded
  const activeCustomers = (remoteCustomers || CUSTOMERS).filter(c => c.active !== 'No');
  const archivedCustomers = (remoteCustomers || []).filter(c => c.active === 'No');

  // Count active taps from equipment (excludes inactive positions)
  const getActiveTapCount = custId => {
    const equip = savedEquipment[custId];
    if (!equip?.tower) return 1; // default 1 tap until equipment is configured
    const TOWER_COUNTS = {
      "Single tap tower": 1,
      "Twin tap tower": 2,
      "Triple tap tower": 3
    };
    const totalPositions = TOWER_COUNTS[equip.tower] || 1;
    if (!equip.taps || !Array.isArray(equip.taps)) return totalPositions;
    const activeTaps = equip.taps.filter(t => !t.inactive).length;
    return activeTaps || 1; // at least 1
  };
  const openCustomer = c => {
    if (screen === "home") { _homeScrollY.current = window.pageYOffset || document.documentElement.scrollTop || 0; }
    setCust(c);
    setLcDaysEdit(false);
    setScreen("customer");
    setSearch("");
  };
  // Called bare from the customer screen's button (with a click event), or with
  // an explicit customer + run stop from the delivery slip.
  const openDelivery = (custArg, stopArg) => {
    const cust = (custArg && typeof custArg === "object" && custArg.id !== undefined && !custArg.nativeEvent) ? custArg : customer;
    if (cust && cust !== customer) setCust(cust);
    // Run context: the slip passes its stop. A delivery opened the ordinary way
    // for a venue that's still to be done on today's run picks it up too, so
    // the OMO and the kegs banner are there however the driver got here.
    let runStop = (stopArg && stopArg.seq !== undefined) ? stopArg : null;
    if (!runStop && onRun && fieldRun && cust) {
      runStop = (fieldRun.stops || []).find(s => String(s.customerId) === String(cust.id) && !runStopDone(s)) || null;
    }
    setRunStopCtx(runStop ? runStop.seq : null);
    const ex = cust ? liveKegs[cust.id] || {
      onTap: [],
      backup: []
    } : {
      onTap: [],
      backup: []
    };
    setOnHandTap(ex.onTap.map(k => ({
      ...k
    })));
    setOnHandBackup(ex.backup.map(k => ({
      ...k
    })));
    setDelivered([]);
    setCollected([]);
    setScanInput("");
    setScanProduct("");
    setScanSize("18L");
    setOrderRef("OMO" + (runStop ? runOmoDigits(runStop.orderNo) : ""));
    setDeliveryNote("");
    setNoteSaved(false);
    setNoteMode("note");
    setActionDesc("");
    setActionAssignee(currentUser?.name || "");
    setActionDueDate("");
    setCrmTagged(false);
    setStickyTagged(false);
    setConfirmed(false);
    setBackdateDate("");
    setEditingDelivInstr(false);
    setDeliveryEntryMode("manual");
    setDelivSection("");
    setScanModalMode("");
    setFormVal(function(p) { var n = {}; for(var k in p) n[k]=p[k]; delete n.gasConfirmedThisSession; return n; });
    setLcAction(null);
    setScreen("delivery");
  };
  const openAdjust = () => {
    const ex = customer ? liveKegs[customer.id] || {
      onTap: [],
      backup: []
    } : {
      onTap: [],
      backup: []
    };
    setOnHandTap(ex.onTap.map(k => ({
      ...k
    })));
    setOnHandBackup(ex.backup.map(k => ({
      ...k
    })));
    setCollected([]);
    setScanInput("");
    setScanProduct("");
    setScanSize("18L");
    setScanQty(1);
    scanQtyRef.current = 1;
    setAdjustReason("");
    setExpandedBackup({});
    setTransferOpen(false);
    setTransferMode("in");
    setTransferDigits("");
    setTransferSize("18L");
    setTransferOutKeg("");
    setTransferDest("");
    setTransferReason("");
    setTransferBusy(false);
    if (transferInputRef.current) transferInputRef.current.value = "";
    setScreen("adjust");
  };
  // Fires immediately on confirm rather than batching into Save Adjustment —
  // it writes another customer's row, so it must not be left stranded if the
  // driver backs out of the screen. Local state is updated in lockstep so a
  // later Save Adjustment can't clobber the keg back out again.
  const doTransfer = async (t) => {
    if (transferBusy) return;
    setTransferBusy(true);
    const res = await syncToSheet("transferKegBetweenCustomers", {
      data: {
        kegId: t.kegId,
        toCustomerId: t.toCustomerId,
        toCustomerName: t.toCustomerName,
        product: t.product || "",
        size: t.size || "",
        reason: t.reason,
        expectFromCustomerId: t.expectFromCustomerId || undefined,
        user: currentUser?.name || "Unknown"
      }
    });
    setTransferBusy(false);
    if (!res || res.error) return; // syncToSheet already surfaced it

    const kid = String(res.kegId || t.kegId).toUpperCase().trim();
    const _n = s => String(s || "").toUpperCase().trim();
    const moved = { product: res.product || t.product || "", size: res.size || t.size || "", kegId: res.kegId, location: "fridge" };

    setLiveKegs(prev => {
      const next = { ...prev };
      const src = next[String(res.fromId)] || { onTap: [], backup: [] };
      next[String(res.fromId)] = { ...src,
        onTap: (src.onTap || []).filter(k => _n(k.kegId) !== kid),
        backup: (src.backup || []).filter(k => _n(k.kegId) !== kid) };
      const dst = next[String(res.toId)] || { onTap: [], backup: [] };
      const dup = [].concat(dst.onTap || [], dst.backup || []).some(k => _n(k.kegId) === kid);
      next[String(res.toId)] = { ...dst, onTap: dst.onTap || [],
        backup: dup ? (dst.backup || []) : [].concat(dst.backup || [], [moved]) };
      return next;
    });

    setKegRegister(prev => prev.map(r => _n(r.kegId) === kid
      ? { ...r, location: res.toName, lastMovedBy: currentUser?.name || "", notes: "Transferred from " + res.fromName + " \u2014 " + t.reason }
      : r));

    // Keep the Adjust screen's working copy in step with what the server did
    if (String(customer.id) === String(res.toId)) {
      setOnHandBackup(p => p.some(k => _n(k.kegId) === kid) ? p : [].concat(p, [moved]));
    }
    if (String(customer.id) === String(res.fromId)) {
      setOnHandTap(p => p.filter(k => _n(k.kegId) !== kid));
      setOnHandBackup(p => p.filter(k => _n(k.kegId) !== kid));
    }

    _keepScroll(() => {
      setTransferDigits("");
      if (transferInputRef.current) transferInputRef.current.value = "";
      setTransferOutKeg("");
      setTransferDest("");
      setTransferReason("");
    });
    showToast(res.kegId + ": " + res.fromName + " \u2192 " + res.toName);
  };

  const submitAdjust = () => {
    addLog("adjust", {
      onHandTap,
      onHandBackup,
      collected,
      reason: adjustReason
    });
    // Capture pre-adjust kegs on hand for the undo snapshot — read BEFORE setLiveKegs overwrites it
    const _prevKegs = (() => {
      const _ex = liveKegs[customer.id] || { onTap: [], backup: [] };
      return {
        onTap: (_ex.onTap || []).map(k => ({ product: k.product || '', size: k.size || '', kegId: k.kegId || '', level: k.level || '' })),
        backup: (_ex.backup || []).map(k => ({ product: k.product || '', size: k.size || '', kegId: k.kegId || '', location: k.location || 'fridge' }))
      };
    })();
    const newKegs = {
      onTap: [...onHandTap],
      backup: [...onHandBackup]
    };
    setLiveKegs(prev => ({
      ...prev,
      [customer.id]: newKegs
    }));
    syncToSheet("saveKegsOnHand", {
      customerId: customer.id,
      data: newKegs
    });
    // Item 1: an adjust-screen collect must also update the Keg Register, via the
    // existing logDelivery backend path (writes Movement Log + register + Delivery Log audit)
    if (collected.length > 0) {
      syncToSheet("logDelivery", {
        data: {
          customerId: customer.id,
          customerName: customer.name,
          delivered: [],
          collected,
          orderRef: "",
          note: adjustReason || "",
          user: currentUser?.name || "Unknown",
          prevKegs: _prevKegs
        }
      });
    }
    showToast("Kegs on hand updated");
    setScreen("customer");
  };
  const addDelivered = () => {
    const raw = scanInputRef.current ? scanInputRef.current.value : scanInput;
    const digits = raw.trim();
    if (!digits) return;
    // Compose full keg ID: KEG-{size prefix}-{digits}
    const baseId = digits.match(/^KEG-/) ? digits.toUpperCase() : ("KEG-" + (scanSize === "9L" ? "09" : "18") + "-" + digits.padStart(3, "0")).toUpperCase();
    // Auto-lookup product from register if not manually selected
    const regEntry = kegRegister.find(r => r.kegId === baseId);
    const product = scanProduct || (regEntry ? regEntry.product : "");
    // Previously a silent `return` — the keg vanished with no feedback.
    if (!product) { showToast("No product for " + baseId + " \u2014 pick one"); return; }
    const newKeg = { kegId: baseId, product: product, size: scanSize, location: "fridge" };
    setDelivered(prev => [...prev, newKeg]);
    setOnHandBackup(prev => [...prev, { ...newKeg }]);
    setScanInput("");
    if (scanInputRef.current) scanInputRef.current.value = "";
    setScanProduct("");
    showToast("1\xD7 delivered");
  };
  const addCollected = () => {
    const raw = collectInputRef.current ? collectInputRef.current.value : collectInput;
    const baseId = raw.trim().toUpperCase();
    if (!baseId) return;
    const ids = [];
    const _cqty = collectQtyRef.current || 1;
    for (let i = 0; i < _cqty; i++) {
      ids.push(_cqty === 1 ? baseId : incrementKegId(baseId, i));
    }
    setCollected(prev => [...prev, ...ids.map(id => ({ kegId: id }))]);
    ids.forEach(id => {
      setOnHandTap(prev => { const x = prev.findIndex(k => k.kegId === id); return x >= 0 ? prev.filter((_, j) => j !== x) : prev; });
      setOnHandBackup(prev => { const x = prev.findIndex(k => k.kegId === id); return x >= 0 ? prev.filter((_, j) => j !== x) : prev; });
    });
    const nextId = incrementKegId(baseId, _cqty);
    setCollectInput(nextId);
    if (collectInputRef.current) collectInputRef.current.value = nextId;
    collectQtyRef.current = 1; setCollectQty(1);
    if (collectQtyDisplayRef.current) collectQtyDisplayRef.current.textContent = "1";
    showToast(`${ids.length}\xD7 collected`);
  };
  const _collectFromTap = i => {
    const k = onHandTap[i];
    setCollected(prev => [...prev, {
      kegId: k.kegId,
      product: k.product,
      size: k.size
    }]);
    setOnHandTap(prev => prev.filter((_, j) => j !== i));
    showToast(`${PRODUCT_SHORT[k.product] || k.product} collected from tap`);
  };
  const collectFromTap = i => scrollSafe(() => _collectFromTap(i));
  const _collectFromBackup = i => {
    const k = onHandBackup[i];
    setCollected(prev => [...prev, {
      kegId: k.kegId,
      product: k.product,
      size: k.size
    }]);
    setOnHandBackup(prev => prev.filter((_, j) => j !== i));
    showToast(`${PRODUCT_SHORT[k.product] || k.product} collected from backup`);
  };
  const collectFromBackup = i => scrollSafe(() => _collectFromBackup(i));
  const _promoteToTap = i => {
    const k = onHandBackup[i];
    setOnHandBackup(prev => prev.filter((_, j) => j !== i));
    setOnHandTap(prev => [...prev, {
      ...k,
      level: "Full"
    }]);
    showToast(`${PRODUCT_SHORT[k.product] || k.product} moved to tap`);
  };
  const promoteToTap = i => scrollSafe(() => _promoteToTap(i));
  // Untap: the reverse of promoteToTap. Moves an on-tap keg back into the
  // fridge backups (level dropped). From there "\u2192 Backup" moves it on to
  // the backup fridge / cool room if that's where it actually went.
  const _untapToFridge = i => {
    const k = onHandTap[i];
    if (!k) return;
    setOnHandTap(prev => prev.filter((_, j) => j !== i));
    setOnHandBackup(prev => [...prev, {
      product: k.product,
      size: k.size,
      kegId: k.kegId,
      location: "fridge"
    }]);
    showToast(`${PRODUCT_SHORT[k.product] || k.product} untapped \u2014 back in fridge`);
  };
  const untapToFridge = i => scrollSafe(() => _untapToFridge(i));
  const incrementKegId = (baseId, offset) => {
    const match = baseId.match(/^(.*?)(\d+)$/);
    if (!match) return baseId + "-" + (offset + 1);
    const prefix = match[1];
    const num = parseInt(match[2], 10) + offset;
    return prefix + String(num).padStart(match[2].length, "0");
  };
  const submitDelivery = () => {
    // Resolve effective date — use backdate if set, otherwise today
    const _bdUseDate = backdateDate ? new Date(backdateDate + 'T00:00:00') : new Date();
    const _bdISO = backdateDate || new Date().toLocaleDateString('en-CA');
    const _bdDisplay = _bdUseDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    // Capture pre-delivery kegs on hand for the undo snapshot — read BEFORE setLiveKegs overwrites it
    const _prevKegs = (() => {
      const _ex = liveKegs[customer.id] || { onTap: [], backup: [] };
      return {
        onTap: (_ex.onTap || []).map(k => ({ product: k.product || '', size: k.size || '', kegId: k.kegId || '', level: k.level || '' })),
        backup: (_ex.backup || []).map(k => ({ product: k.product || '', size: k.size || '', kegId: k.kegId || '', location: k.location || 'fridge' }))
      };
    })();
    addLog("deliveries", {
      delivered,
      collected,
      onHandTap,
      onHandBackup,
      orderRef,
      notes: deliveryNote
    });
    // Persist kegs on hand so customer dashboard and home screen reflect the delivery
    const newKegs = {
      onTap: [...onHandTap],
      backup: [...onHandBackup],
      lastDelivery: _bdISO
    };
    setLiveKegs(prev => ({
      ...prev,
      [customer.id]: newKegs
    }));
    // Sync to sheet: kegs on hand + movement log
    syncToSheet("saveKegsOnHand", {
      customerId: customer.id,
      data: newKegs
    });
    syncToSheet("logDelivery", {
      data: {
        customerId: customer.id,
        customerName: customer.name,
        delivered,
        collected,
        orderRef,
        note: noteMode === "note" ? (deliveryNoteRef.current ? deliveryNoteRef.current.value : deliveryNote) : "",
        stickyNote: noteMode === "note" && stickyTagged ? true : undefined,
        user: currentUser?.name || "Unknown",
        backdateDate: backdateDate || undefined,
        prevKegs: _prevKegs,
        // Ticks the stop off on the run in the Runs Master.
        runId: (runCtxStop && fieldRun) ? fieldRun.runId : undefined,
        runSeq: runCtxStop ? runCtxStop.seq : undefined
      }
    });
    // If sticky tagged, also update local active note state
    if (noteMode === "note" && stickyTagged) {
      const _stickyVal = (deliveryNoteRef.current ? deliveryNoteRef.current.value : deliveryNote).trim();
      if (_stickyVal) {
        setActionItems(prev => [...prev, { dateCreated: _bdDisplay, customerId: customer.id, customerName: customer.name, description: _stickyVal, assignedTo: currentUser?.name || "", dueDate: "", status: "Note", completedDate: "", completedBy: "", createdBy: currentUser?.name || "Unknown" }]);
        setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, activeNote: _stickyVal, noteCleared: false } : c) : prev);
        setCust(prev => prev && prev.id === customer.id ? { ...prev, activeNote: _stickyVal, noteCleared: false } : prev);
      }
    }
    // If action item mode and description provided, log the action item
    if (noteMode === "action" && actionDesc.trim()) {
      syncToSheet("logActionItem", {
        data: {
          customerId: customer.id,
          customerName: customer.name,
          description: actionDesc.trim(),
          assignedTo: actionAssignee || currentUser?.name || "",
          dueDate: actionDueDate || "",
          createdBy: currentUser?.name || "Unknown"
        }
      });
      setActionItems(prev => [...prev, {
        dateCreated: _bdDisplay,
        customerId: customer.id,
        customerName: customer.name,
        description: actionDesc.trim(),
        assignedTo: actionAssignee || currentUser?.name || "",
        dueDate: actionDueDate || "",
        status: "Open",
        completedDate: "",
        completedBy: "",
        createdBy: currentUser?.name || "Unknown"
      }]);
    }
    // Update local delivery log so note appears immediately
    const _dlNote = noteMode === "note" ? (deliveryNoteRef.current ? deliveryNoteRef.current.value : deliveryNote) : "";
    setDeliveryLogData(prev => [...prev, {
      date: _bdDisplay,
      time: backdateDate ? "" : new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }),
      customerId: customer.id,
      customerName: customer.name,
      orderRef,
      kegsDelivered: delivered.length,
      kegsCollected: collected.length,
      note: _dlNote.trim(),
      loggedBy: currentUser?.name || "Unknown",
      hasSnapshot: true
    }]);
    if (runCtxStop && fieldRun) {
      runMarkStopLocal(runCtxStop.seq);
      const _left = (fieldRun.stops || []).filter(x => !runStopDone(x) && x.seq !== runCtxStop.seq).length;
      setRunStopCtx(null);
      setRunActiveSeq(null);
      showToast(`${customer.name} delivered \u2014 ${_left ? _left + (_left === 1 ? " stop" : " stops") + " to go" : "that's the last stop"}`);
      setScreen("runlist");
      return;
    }
    showToast(backdateDate ? `Delivery backdated to ${_bdDisplay}` : `Delivery complete — ${onHandTap.length} on tap, ${onHandBackup.length} backup`);
    setScreen("customer");
  };
  // ============================================================
  // RUN MODE — logic
  // ============================================================
  const runStops = (fieldRun && fieldRun.stops) || [];
  const runCtxStop = runStopCtx != null ? runStops.find(s => s.seq === runStopCtx) || null : null;
  const runNextStop = () => {
    if (runActiveSeq != null) { const a = runStops.find(s => s.seq === runActiveSeq && !runStopDone(s)); if (a) return a; }
    return runStops.find(s => !runStopDone(s)) || null;
  };
  const runFindCustomer = st => st && st.customerId ? (remoteCustomers || CUSTOMERS).find(c => String(c.id) === String(st.customerId)) || null : null;
  const runNowHHMM = () => new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });

  // Optimistic tick. Kept in a ref as well so a refresh that lands before the
  // sheet write has gone through can't un-tick it.
  const runMarkStopLocal = seq => {
    if (!fieldRun) return;
    const at = runNowHHMM();
    runLocalDoneRef.current[fieldRun.runId + "|" + seq] = at;
    setFieldRun(fr => fr ? { ...fr, stops: (fr.stops || []).map(x => x.seq === seq ? { ...x, status: "Delivered", deliveredAt: at } : x) } : fr);
  };

  const checkMyRun = async opts => {
    opts = opts || {};
    if (!currentUser || !runModeEnabledFor(currentUser)) return;
    const now = Date.now();
    // Was 60s, which is exactly the gap that bit: open Field Ops, switch to the
    // Runs app, tap Start run, switch back within a minute — and the check was
    // skipped. 10s still stops the focus + visibilitychange pair double-firing.
    if (!opts.force && now - runCheckAtRef.current < 10000) return;
    if (runCheckBusyRef.current) return;
    runCheckAtRef.current = now;
    runCheckBusyRef.current = true;
    if (opts.manual) setRunChecking(true);
    try {
      // A run picked by ID ("I'm on a run") is re-read by that ID — it may never
      // have been set On the road, so the normal lookup wouldn't find it.
      const ackNow = readRunAck();
      const pinned = (fieldRunRef.current && fieldRunRef.current.manual) ? fieldRunRef.current.runId
        : (ackNow && ackNow.answer === "yes" && ackNow.manual ? ackNow.runId : "");
      const r = await fetchJson(`${API_URL}?action=loadMyRun&user=${encodeURIComponent(currentUser.name)}` + (pinned ? `&runId=${encodeURIComponent(pinned)}` : ""), null, 30000);
      // An older Code.gs answers "Unknown action" — run mode just stays off.
      if (!r || r.error) return;
      if (!r.run && pinned) {
        // The picked run has gone (renamed or deleted in the Runs app). Drop the
        // pin; the next check goes back to looking for a run On the road.
        writeRunAck(pinned, "no");
        runCheckAtRef.current = 0;
      }
      if (!r.run) {
        setFieldRun(null); setOnRun(false); setRunAsk(false);
        if (screenRef.current === "runlist" || screenRef.current === "runslip") setScreen("home");
        return;
      }
      const run = r.run;
      const local = runLocalDoneRef.current;
      run.stops = (run.stops || []).map(st => (!runStopDone(st) && local[run.runId + "|" + st.seq]) ? { ...st, status: "Delivered", deliveredAt: local[run.runId + "|" + st.seq] } : st);
      setFieldRun(run);
      const ack = readRunAck();
      if (ack && ack.runId === run.runId) {
        setRunAsk(false);
        setOnRun(ack.answer === "yes");
        if (ack.answer === "yes" && opts.first && screenRef.current === "home") setScreen("runlist");
      } else {
        setOnRun(false);
        setRunAsk(true);
      }
    } catch (e) {
      if (opts.manual) showToast("Couldn't check the run \u2014 " + (e && e.message ? e.message : "no signal"));
    } finally {
      runCheckBusyRef.current = false;
      if (opts.manual) setRunChecking(false);
    }
  };

  const answerRunPrompt = yes => {
    if (!fieldRun) { setRunAsk(false); return; }
    writeRunAck(fieldRun.runId, yes ? "yes" : "no", fieldRun.manual);
    setRunAsk(false);
    setOnRun(yes);
    if (yes) { setRunActiveSeq(null); setRunStopCtx(null); setScreen("runlist"); }
  };
  const enterRunMode = () => {
    if (!fieldRun) return;
    writeRunAck(fieldRun.runId, "yes", fieldRun.manual);
    setOnRun(true); setRunAsk(false); setScreen("runlist");
  };
  const leaveRunMode = () => {
    if (fieldRun) writeRunAck(fieldRun.runId, "no");
    setOnRun(false); setRunStopCtx(null); setRunActiveSeq(null); setCust(null); setScreen("home");
  };

  // ── "I'm on a run" ──────────────────────────────────────────
  // For when the prompt was dismissed by accident, never appeared, or the run
  // wasn't started in the Runs app. Looks for a run On the road first; failing
  // that, tries the ID it would be (initials + today, e.g. JC-300926) and
  // offers it. The ID box is prefilled and editable — a second run today ends in B.
  const enterFoundRun = (run, manual) => {
    const local = runLocalDoneRef.current;
    run.stops = (run.stops || []).map(st => (!runStopDone(st) && local[run.runId + "|" + st.seq]) ? { ...st, status: "Delivered", deliveredAt: local[run.runId + "|" + st.seq] } : st);
    run.manual = !!manual;
    writeRunAck(run.runId, "yes", manual);
    setFieldRun(run); setOnRun(true); setRunAsk(false);
    setRunActiveSeq(null); setRunStopCtx(null);
    setRunFind(null);
    setScreen("runlist");
  };
  const runFindById = async id => {
    id = String(id || "").trim().toUpperCase().replace(/\s+/g, "");
    if (!id) { setRunFind(f => ({ ...(f || {}), error: "Type the run ID, e.g. " + guessRunId(currentUser) })); return; }
    setRunFind(f => ({ ...(f || {}), id: id, busy: true, error: "", found: null }));
    try {
      const r = await fetchJson(`${API_URL}?action=loadMyRun&user=${encodeURIComponent(currentUser?.name || "")}&runId=${encodeURIComponent(id)}`, null, 30000);
      if (r && r.error) { setRunFind(f => f && ({ ...f, busy: false, error: r.error })); return; }
      if (!r || !r.run) { setRunFind(f => f && ({ ...f, busy: false, error: `No run called ${id} in the Runs app.` })); return; }
      setRunFind(f => f && ({ ...f, busy: false, found: r.run }));
    } catch (e) {
      setRunFind(f => f && ({ ...f, busy: false, error: "Couldn't reach the spreadsheet \u2014 " + (e && e.message ? e.message : "check signal") }));
    }
  };
  const openRunFind = async () => {
    const guess = guessRunId(currentUser);
    setRunFind({ phase: "checking", id: guess, busy: true, error: "", found: null });
    try {
      const r = await fetchJson(`${API_URL}?action=loadMyRun&user=${encodeURIComponent(currentUser?.name || "")}`, null, 30000);
      if (r && r.run) { enterFoundRun(r.run, false); showToast("Run " + r.run.runId + " found"); return; }
    } catch (e) { /* fall through to the ID box */ }
    setRunFind(f => f && ({ ...f, phase: "pick" }));
    runFindById(guess);
  };
  const openRunSlip = st => {
    if (!st) return;
    setRunStopCtx(st.seq);
    if (!runStopDone(st)) setRunActiveSeq(st.seq);
    const c = runFindCustomer(st);
    if (c) setCust(c);
    setScreen("runslip");
  };
  const markRunStopDoneNoDelivery = st => {
    if (!fieldRun || !st) return;
    runMarkStopLocal(st.seq);
    api.post("markRunStop", { runId: fieldRun.runId, seq: st.seq, customerId: st.customerId, status: "Delivered", user: currentUser?.name || "" })
      .then(r => { if (r && r.error) showToast("Run not updated: " + r.error); })
      .catch(() => showToast("No signal \u2014 stop ticked here, the run sheet will be behind"));
    setRunStopCtx(null); setRunActiveSeq(null);
    showToast(st.name + " done");
    setScreen("runlist");
  };
  const finishFieldRun = () => {
    if (!fieldRun) return;
    const left = runStops.filter(x => !runStopDone(x)).length;
    if (!confirm(left
      ? `Finish run ${fieldRun.runId}?\n\n${left} ${left === 1 ? "stop hasn't" : "stops haven't"} been delivered. They'll stay undelivered on the run.`
      : `Finish run ${fieldRun.runId}? Every stop is delivered.`)) return;
    const id = fieldRun.runId;
    writeRunAck(id, "no");
    api.post("finishRun", { runId: id, user: currentUser?.name || "" })
      .then(r => showToast(r && r.skipped ? `Left run ${id} \u2014 it's still ${r.status} in the Runs app, so it wasn't closed`
        : r && r.success ? `Run ${id} finished` : "Couldn't close the run in the sheet \u2014 " + ((r && r.error) || "try again")))
      .catch(() => showToast("No signal \u2014 run not closed in the sheet"));
    setFieldRun(null); setOnRun(false); setRunStopCtx(null); setRunActiveSeq(null); setCust(null);
    setScreen("home");
  };

  // Line clean / full clean / gas, worked out the same way the Maintenance and
  // Gas screens do, so the slip can't disagree with them.
  const runMaintPrompts = custId => {
    const out = [];
    const eq = savedEquipment[custId];
    if (eq && eq.tower) {
      const all = maintLog[custId] || [];
      const latest = pred => all.reduce((mx, m) => pred(m) && (m.ts || 0) > mx ? (m.ts || 0) : mx, 0);
      const days = ts => ts ? Math.floor((Date.now() - ts) / 86400000) : null;
      const dLC = days(latest(m => m.type === "Line Clean" || m.type === "Full System Clean"));
      const dFC = days(latest(m => m.type === "Full System Clean"));
      const lcDue = lcDueDaysFor(eq);
      if (lcDue === null) { /* NA for this venue \u2014 no line clean prompt */ }
      else if (dLC === null) out.push({ level: "red", text: "Line clean \u2014 no record on file" });
      else if (dLC >= lcDue) out.push({ level: lcStatusOf(dLC, lcDue).level === "overdue" ? "red" : "amber", text: `Line clean today \u2014 ${lcStatusOf(dLC, lcDue).label} (aim every ${lcDue} days)` });
      if (dFC === null) out.push({ level: "amber", text: "Full system clean \u2014 no record on file" });
      else if (dFC > 270) out.push({ level: "red", text: `Full system clean overdue \u2014 ${dFC} days ago` });
      else if (dFC > 240) out.push({ level: "amber", text: `Full system clean due soon \u2014 ${dFC} days ago` });
    }
    const kpa = getLatestGasKpa(custId);
    if (kpa !== null && kpa !== -1 && kpa <= 4000) out.push({ level: kpa <= 0 ? "red" : "amber", text: `Gas ${kpa <= 0 ? "empty" : "low"} \u2014 last reading ${getGasShort(kpa)} kPa. Check the cylinder.` });
    return out;
  };

  // First check once the app has loaded for a run-mode user; then again every
  // time the app comes back to the front (the driver starting the run in the
  // Runs app and switching over is exactly that moment).
  // Fired as soon as the driver is known — in parallel with the customer load,
  // not after it — so the question can be on screen before the list is.
  useEffect(() => {
    if (!currentUser || runFirstCheckRef.current) return;
    if (!runModeEnabledFor(currentUser)) return;
    runFirstCheckRef.current = true;
    checkMyRun({ force: true, first: true });
  }, [currentUser]);
  // While the app is open with no run found yet, look again every 45s — covers
  // Field Ops left open on the dash while the run is started on another device,
  // where no focus/visibility event ever fires.
  useEffect(() => {
    if (!currentUser || !runModeEnabledFor(currentUser)) return;
    const t = setInterval(() => {
      if (fieldRunRef.current) return;
      if (document.visibilityState && document.visibilityState !== "visible") return;
      checkMyRun({});
    }, 45000);
    return () => clearInterval(t);
  }, [currentUser]);
  useEffect(() => {
    if (!currentUser || !runModeEnabledFor(currentUser)) return;
    const f = () => { if (document.visibilityState === "visible") checkMyRun({}); };
    document.addEventListener("visibilitychange", f);
    window.addEventListener("focus", f);
    return () => { document.removeEventListener("visibilitychange", f); window.removeEventListener("focus", f); };
  }, [currentUser]);
  useEffect(() => { if (screen === "runlist") checkMyRun({}); }, [screen]);

  // ============================================================
  // RUN MODE — screens
  // ============================================================
  const rce = React.createElement;
  const runSecLabel = t => rce("p", { style: { fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: C.textTer, margin: "0 0 8px" } }, t);
  const runDot = p => rce("span", { style: { width: 9, height: 9, borderRadius: 5, background: PRODUCT_COLOR[p] || PRODUCT_COLOR[runProdBase(p)] || C.textTer, display: "inline-block", flexShrink: 0 } });

  // "Are you on run BG-240926?" — only over the home screen, so it never lands
  // on top of a delivery that's half entered. A cover screen that takes over
  // the whole app (not a sheet or a banner) so the driver answers it first.
  // (The Sept 2026 TEST mode — loading any run by ID, saving nothing — has been
  // removed now run mode is live for all drivers. loadMyRun in Code.gs still
  // accepts a runId, but nothing in the app sends one.)
  const renderRunPrompt = () => {
    const asking = !!(runAsk && fieldRun && !onRun && screen === "home");
    if (!asking) return null;
    const n = runStops.length, kegs = runStops.reduce((a, x) => a + runKegCount(x), 0);
    const btn = (label, onClick, primary, extra) => rce("button", { onClick: onClick, style: Object.assign({ width: "100%", padding: primary ? "17px" : "14px", fontSize: primary ? 17 : 15, fontWeight: primary ? 700 : 600,
      background: primary ? C.green : C.surface, color: primary ? "#fff" : C.textSec, border: primary ? "none" : `1.5px solid ${C.border}`, borderRadius: 14, cursor: "pointer", fontFamily: font }, extra || {}) }, label);
    return rce("div", { style: { position: "fixed", inset: 0, zIndex: 300, background: C.bg, overflowY: "auto", fontFamily: font } },
      rce("div", { style: { maxWidth: 440, margin: "0 auto", minHeight: "100%", boxSizing: "border-box", padding: "56px 24px 32px", display: "flex", flexDirection: "column" } },
        rce("img", { src: LOGO_URL, alt: "Filament Coffee", style: { width: 150, height: "auto", display: "block", margin: "0 auto 40px" } }),
        rce("p", { style: { fontSize: 12, fontWeight: 700, letterSpacing: 1.3, textTransform: "uppercase", color: C.green, margin: "0 0 12px", textAlign: "center" } },
          "Run on the road" + (fieldRun.actualStart ? " \u00B7 left " + fieldRun.actualStart : "")),
        rce("h1", { style: { fontSize: 30, fontWeight: 700, color: C.text, margin: "0 0 10px", lineHeight: 1.2, textAlign: "center" } },
          "Are you on run", rce("br"), rce("span", { style: { fontFamily: "ui-monospace,Menlo,monospace" } }, fieldRun.runId), "?"),
        rce("p", { style: { fontSize: 15, color: C.textSec, margin: "0 0 30px", textAlign: "center" } },
          `${runDayLabel(fieldRun.date)}${fieldRun.slot ? " \u00B7 " + fieldRun.slot : ""} \u00B7 ${n} ${n === 1 ? "stop" : "stops"} \u00B7 ${kegs} kegs`),
        btn("Yes \u2014 show my stops", () => answerRunPrompt(true), true),
        btn("No", () => answerRunPrompt(false), false, { marginTop: 10 })));
  };

  // Home screen strip: green while on the run, a quiet way back in if they said no.
  const renderRunFind = () => {
    if (!runFind) return null;
    const f = runFind, found = f.found;
    const close = () => setRunFind(null);
    const onRoad = found && String(found.status || "").toLowerCase() === "on the road";
    const fn = found ? (found.stops || []).length : 0;
    const fk = found ? (found.stops || []).reduce((a, x) => a + runKegCount(x), 0) : 0;
    const btn = (label, onClick, primary, extra) => rce("button", { onClick: onClick, disabled: f.busy, style: Object.assign({ width: "100%", padding: primary ? "17px" : "14px", fontSize: primary ? 17 : 15, fontWeight: primary ? 700 : 600,
      background: primary ? C.green : C.surface, color: primary ? "#fff" : C.textSec, border: primary ? "none" : `1.5px solid ${C.border}`, borderRadius: 14, cursor: "pointer", fontFamily: font, opacity: f.busy ? .6 : 1 }, extra || {}) }, label);
    return rce("div", { style: { position: "fixed", inset: 0, zIndex: 300, background: C.bg, overflowY: "auto", fontFamily: font } },
      rce("div", { style: { maxWidth: 440, margin: "0 auto", minHeight: "100%", boxSizing: "border-box", padding: "56px 24px 32px", display: "flex", flexDirection: "column" } },
        rce("img", { src: LOGO_URL, alt: "Filament Coffee", style: { width: 150, height: "auto", display: "block", margin: "0 auto 40px" } }),
        rce("h1", { style: { fontSize: 28, fontWeight: 700, color: C.text, margin: "0 0 10px", lineHeight: 1.2, textAlign: "center" } }, "Find your run"),
        f.phase === "checking"
          ? rce("p", { style: { fontSize: 15, color: C.textSec, margin: "0 0 28px", textAlign: "center" } }, "Checking for a run on the road\u2026")
          : rce("p", { style: { fontSize: 15, color: C.textSec, margin: "0 0 28px", textAlign: "center", lineHeight: 1.45 } }, "No run is showing as on the road for you. Check the run ID below."),
        f.phase === "pick" ? rce(React.Fragment, null,
          rce("label", { style: { display: "block", fontSize: 12, fontWeight: 600, color: C.textTer, letterSpacing: .5, textTransform: "uppercase", marginBottom: 6 } }, "Run ID"),
          rce("div", { style: { display: "flex", gap: 8 } },
            rce("input", { type: "text", value: f.id || "", autoCapitalize: "characters", autoCorrect: "off", spellCheck: false,
              onChange: e => { const v = e.target.value.toUpperCase(); setRunFind(x => x && ({ ...x, id: v, error: "", found: null })); },
              onKeyDown: e => { if (e.key === "Enter") runFindById(f.id); },
              style: { flex: 1, minWidth: 0, padding: "14px 16px", fontSize: 18, fontFamily: "ui-monospace,Menlo,monospace", letterSpacing: 1, background: C.surface,
                border: `1.5px solid ${f.error ? C.red : C.border}`, borderRadius: 12, color: C.text, boxSizing: "border-box" } }),
            rce("button", { onClick: () => runFindById(f.id), disabled: f.busy, style: { padding: "0 18px", fontSize: 15, fontWeight: 700, background: C.surface, color: C.green,
              border: `1.5px solid ${C.green}`, borderRadius: 12, cursor: "pointer", fontFamily: font, opacity: f.busy ? .6 : 1, flexShrink: 0 } }, f.busy ? "\u2026" : "Look up")),
          rce("p", { style: { fontSize: 12, color: C.textTer, margin: "6px 0 0" } }, "Second run today? Add B to the end."),
          f.error ? rce("p", { style: { fontSize: 13, color: C.red, margin: "8px 0 0", fontWeight: 500 } }, f.error) : null,
          found ? rce("div", { style: { marginTop: 18, padding: "16px", background: C.surface, border: `2px solid ${C.green}`, borderRadius: 14 } },
            rce("div", { style: { fontSize: 20, fontWeight: 700, fontFamily: "ui-monospace,Menlo,monospace", color: C.text } }, found.runId),
            rce("div", { style: { fontSize: 14, color: C.textSec, marginTop: 4 } },
              [runDayLabel(found.date), found.slot, found.driver, `${fn} ${fn === 1 ? "stop" : "stops"} \u00B7 ${fk} kegs`].filter(Boolean).join(" \u00B7 ")),
            onRoad ? null : rce("div", { style: { fontSize: 12.5, color: C.accent, marginTop: 8, lineHeight: 1.4 } },
              `Shows as ${found.status || "Planned"} in the Runs app. Stops will still tick off as you deliver; start it in the Runs app if you want its times recorded.`),
            btn("Yes \u2014 this is my run", () => enterFoundRun(found, true), true, { marginTop: 14 })) : null) : null,
        rce("div", { style: { flex: 1, minHeight: 30 } }),
        btn("Cancel", close, false)));
  };

  const renderRunHomeBanner = () => {
    if (!runModeEnabledFor(currentUser)) return null;
    if (!fieldRun) {
      return rce("button", { onClick: openRunFind, style: { width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 14, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 12, cursor: "pointer", fontFamily: font } },
        rce("span", { style: { flex: 1, fontSize: 13, color: C.textSec } }, "No run showing"),
        rce("span", { style: { fontSize: 13, fontWeight: 600, color: C.green } }, "I'm on a run \u203A"));
    }
    const done = runStops.filter(runStopDone).length, next = runNextStop();
    if (onRun) {
      return rce("button", { onClick: () => setScreen("runlist"), style: { width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", marginBottom: 14, background: C.greenSoft, border: `2px solid ${C.green}`, borderRadius: 14, cursor: "pointer", fontFamily: font } },
        rce("div", { style: { flex: 1, minWidth: 0 } },
          rce("div", { style: { fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: C.green } }, "On run \u00B7 " + fieldRun.runId),
          rce("div", { style: { fontSize: 15, fontWeight: 600, color: C.text, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }, next ? "Next: " + next.name : "All stops delivered"),
          rce("div", { style: { fontSize: 12, color: C.textSec, marginTop: 2 } }, `${done} of ${runStops.length} delivered`)),
        rce("span", { style: { fontSize: 14, fontWeight: 700, color: C.green, whiteSpace: "nowrap" } }, "Stops \u203A"));
    }
    return rce("button", { onClick: enterRunMode, style: { width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 14, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 12, cursor: "pointer", fontFamily: font } },
      rce("span", { style: { flex: 1, fontSize: 13, color: C.textSec } }, "Run ", rce("b", { style: { fontFamily: "ui-monospace,Menlo,monospace", color: C.text } }, fieldRun.runId), " is on the road"),
      rce("span", { style: { fontSize: 13, fontWeight: 600, color: C.accent } }, "Open run \u203A"));
  };

  const renderRunList = () => {
    if (!fieldRun) {
      return rce(React.Fragment, null,
        rce(BackBtn, { label: "Customers", onClick: () => setScreen("home") }),
        rce("p", { style: { fontSize: 14, color: C.textSec, marginTop: 20 } }, "No run on the road for you right now."));
    }
    const done = runStops.filter(runStopDone).length, total = runStops.length, next = runNextStop();
    const allDone = total > 0 && done === total;
    return rce(React.Fragment, null,
      rce(BackBtn, { label: "Customers", onClick: () => { setRunStopCtx(null); setCust(null); setScreen("home"); } }),
      rce("div", { style: { display: "flex", alignItems: "flex-start", gap: 10 } },
        rce("div", { style: { flex: 1, minWidth: 0 } },
          rce("p", { style: { fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: C.green, margin: "0 0 4px" } },
            ["On the road", runDayLabel(fieldRun.date), fieldRun.slot, fieldRun.actualStart ? "left " + fieldRun.actualStart : ""].filter(Boolean).join(" \u00B7 ")),
          rce("h2", { style: { fontSize: 24, fontWeight: 700, color: C.text, margin: "0 0 4px", fontFamily: font } }, "Run ", rce("span", { style: { fontFamily: "ui-monospace,Menlo,monospace" } }, fieldRun.runId)),
          rce("p", { style: { fontSize: 13, color: C.textSec, margin: 0 } }, `${done} of ${total} delivered`)),
        rce("button", { onClick: () => checkMyRun({ force: true, manual: true }), disabled: runChecking, title: "Refresh", style: { width: 38, height: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.textSec, fontSize: 18, cursor: "pointer", flexShrink: 0, opacity: runChecking ? 0.5 : 1 } }, "\u21BB")),
      rce("div", { style: { height: 6, background: C.borderLight, borderRadius: 3, margin: "12px 0 16px", overflow: "hidden" } },
        rce("div", { style: { height: "100%", width: (total ? done / total * 100 : 0) + "%", background: C.green, transition: "width .3s" } })),
      rce("div", { style: { display: "flex", flexDirection: "column", gap: 8 } },
        runStops.map((st, i) => {
          const isDone = runStopDone(st), isNext = !!(next && next.seq === st.seq);
          const kegs = runKegCount(st), extras = runExtraLines(st.lines).length;
          const cust = runFindCustomer(st);
          const sub = isDone
            ? "Delivered" + (st.deliveredAt ? " " + st.deliveredAt : "")
            : [cust ? cust.suburb : (st.customerId ? "" : "Custom stop"), st.eta ? "ETA " + st.eta : "", kegs + (kegs === 1 ? " keg" : " kegs"), extras ? extras + (extras === 1 ? " extra" : " extras") : ""].filter(Boolean).join(" \u00B7 ");
          return rce("button", { key: st.seq + "-" + i, onClick: () => openRunSlip(st), style: {
              width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12, padding: "14px 14px", cursor: "pointer", fontFamily: font,
              background: isDone ? "#EFEEEA" : isNext ? C.greenSoft : C.surface,
              border: isNext ? `2px solid ${C.green}` : `1px solid ${isDone ? C.borderLight : C.border}`, borderRadius: 14 } },
            rce("span", { style: { width: 32, height: 32, borderRadius: 16, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700,
                background: isDone ? "#D6D3CC" : isNext ? C.green : C.surfaceAlt, color: isDone ? "#8C877E" : isNext ? "#fff" : C.textSec } }, isDone ? "\u2713" : String(i + 1)),
            rce("span", { style: { flex: 1, minWidth: 0 } },
              rce("span", { style: { display: "block", fontSize: 15, fontWeight: 600, color: isDone ? "#8C877E" : C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }, st.name || "Stop"),
              rce("span", { style: { display: "block", fontSize: 12, color: isDone ? "#A9A49B" : C.textSec, marginTop: 2 } }, sub),
              (!isDone && st.orderNo) ? rce("span", { style: { display: "block", fontSize: 11, fontFamily: "ui-monospace,Menlo,monospace", color: C.textTer, marginTop: 2 } }, st.orderNo) : null),
            isNext ? rce("span", { style: { fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#fff", background: C.green, borderRadius: 6, padding: "4px 7px", flexShrink: 0 } }, "NEXT")
              : (!isDone ? rce("span", { style: { fontSize: 18, color: C.textTer, flexShrink: 0 } }, "\u203A") : null));
        })),
      !total ? rce("p", { style: { fontSize: 14, color: C.textSec, marginTop: 10 } }, "This run has no stops recorded.") : null,
      allDone ? rce("div", { style: { marginTop: 18, padding: "16px", background: C.greenSoft, border: `1px solid ${C.greenBorder}`, borderRadius: 14, textAlign: "center" } },
        rce("p", { style: { fontSize: 16, fontWeight: 700, color: C.green, margin: "0 0 4px" } }, "Every stop delivered"),
        rce("p", { style: { fontSize: 13, color: C.textSec, margin: 0 } }, "Finish the run to close it off in the Runs app.")) : null,
      rce("button", { onClick: finishFieldRun, style: { width: "100%", marginTop: 16, padding: "14px", fontSize: 15, fontWeight: 700, borderRadius: 12, cursor: "pointer", fontFamily: font,
          background: allDone ? C.green : C.surface, color: allDone ? "#fff" : C.textSec, border: allDone ? "none" : `1.5px solid ${C.border}` } }, "Finish run"),
      rce("button", { onClick: leaveRunMode, style: { width: "100%", marginTop: 8, padding: "10px", fontSize: 13, fontWeight: 500, background: "none", border: "none", color: C.textTer, cursor: "pointer", fontFamily: font, textDecoration: "underline" } }, "Leave run mode (run stays on the road)"));
  };

  // The delivery slip: everything about this stop in one place before the
  // driver walks in with the kegs.
  const renderRunSlip = () => {
    const st = runCtxStop;
    if (!fieldRun || !st) {
      return rce(React.Fragment, null,
        rce(BackBtn, { label: "Run", onClick: () => setScreen("runlist") }),
        rce("p", { style: { fontSize: 14, color: C.textSec, marginTop: 20 } }, "That stop is no longer on the run."));
    }
    const cust = runFindCustomer(st);
    const pos = runStops.findIndex(x => x.seq === st.seq) + 1;
    const isDone = runStopDone(st);
    const kegs = runKegTally(st.lines), kegTotal = kegs.reduce((a, r) => a + r.qty, 0);
    const extras = runExtraLines(st.lines);
    const cards = runSignageItems(st.tapCards), cor = runSignageItems(st.corflute);
    const omos = runOmoList(st.orderNo);
    const sticky = cust && cust.activeNote && !cust.noteCleared ? cust.activeNote : "";
    const actions = cust ? actionItems.filter(a => String(a.customerId) === String(cust.id) && a.status === "Open") : [];
    const maint = cust ? runMaintPrompts(cust.id) : [];
    const card = (children, extra) => rce.apply(null, ["div", { style: Object.assign({ padding: "14px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, marginBottom: 10 }, extra || {}) }].concat(children));
    const note = (icon, title, text, tone, key) => rce("div", { key: key, style: { padding: "10px 12px", borderRadius: 10, marginTop: 8,
        background: tone === "red" ? C.redSoft : tone === "amber" ? "#FFF8E1" : C.surfaceAlt,
        border: `1px solid ${tone === "red" ? "#F0B0A0" : tone === "amber" ? "#FFE082" : C.borderLight}` } },
      rce("div", { style: { fontSize: 12, fontWeight: 700, color: tone === "red" ? C.red : tone === "amber" ? "#B45309" : C.textSec } }, icon + " " + title),
      text ? rce("div", { style: { fontSize: 13, color: C.text, marginTop: 3, lineHeight: 1.45, whiteSpace: "pre-wrap" } }, text) : null);
    const noteBlocks = [];
    if (st.note) noteBlocks.push(note("\uD83D\uDDD2\uFE0F", "Run note", st.note, "", "rn"));
    if (cust && cust.deliveryInstructions) noteBlocks.push(note("\uD83D\uDCCB", "Delivery instructions", cust.deliveryInstructions, "amber", "di"));
    if (sticky) noteBlocks.push(note("\uD83D\uDCCC", "Sticky note", sticky, "amber", "sn"));
    actions.forEach((a, i) => noteBlocks.push(note("\u26A1", "Action item" + (a.assignedTo ? " \u00B7 " + a.assignedTo : "") + (a.dueDate ? " \u00B7 due " + a.dueDate : ""), a.description, "red", "ai" + i)));
    maint.forEach((m, i) => noteBlocks.push(note("\uD83D\uDD27", m.text, "", m.level, "mt" + i)));

    return rce(React.Fragment, null,
      rce(BackBtn, { label: "Run " + fieldRun.runId, onClick: () => { setRunStopCtx(null); setScreen("runlist"); } }),
      // Slip header
      rce("div", { style: { padding: "16px 16px 14px", background: C.surface, border: `1px solid ${C.border}`, borderTop: `4px solid ${isDone ? "#B8B3AA" : C.green}`, borderRadius: 14, marginBottom: 10 } },
        rce("div", { style: { display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: C.textTer } },
          rce("span", null, "Delivery slip"), rce("span", null, `Stop ${pos} of ${runStops.length}`)),
        rce("h2", { style: { fontSize: 22, fontWeight: 700, color: C.text, margin: "8px 0 2px", fontFamily: font } }, st.name || (cust && cust.name) || "Stop"),
        rce("p", { style: { fontSize: 13, color: C.textSec, margin: 0 } }, cust ? (cust.address || cust.suburb || "") : "Custom stop \u2014 not a customer"),
        rce("div", { style: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 } },
          st.eta ? rce("span", { style: { fontSize: 12, fontWeight: 600, background: C.surfaceAlt, borderRadius: 8, padding: "5px 9px", color: C.textSec } }, "ETA " + st.eta) : null,
          omos.map(o => rce("span", { key: o, style: { fontSize: 12, fontWeight: 700, fontFamily: "ui-monospace,Menlo,monospace", background: C.accentSoft, border: `1px solid ${C.accentBorder}`, borderRadius: 8, padding: "5px 9px", color: C.accent } }, o)),
          isDone ? rce("span", { style: { fontSize: 12, fontWeight: 700, background: "#EFEEEA", borderRadius: 8, padding: "5px 9px", color: "#8C877E" } }, "\u2713 Delivered" + (st.deliveredAt ? " " + st.deliveredAt : "")) : null)),
      // Order
      card([
        runSecLabel(`Order \u00B7 ${kegTotal} ${kegTotal === 1 ? "keg" : "kegs"}`),
        kegs.length ? kegs.map(r => rce("div", { key: r.key, style: { display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.borderLight}` } },
            runDot(r.product),
            rce("span", { style: { flex: 1, fontSize: 15, fontWeight: 600, color: C.text } }, r.product),
            rce("span", { style: { fontSize: 13, color: C.textSec, width: 44, textAlign: "right" } }, r.size),
            rce("span", { style: { fontSize: 17, fontWeight: 700, color: C.text, width: 40, textAlign: "right", fontFamily: "ui-monospace,Menlo,monospace" } }, "\u00D7" + r.qty)))
          : rce("p", { key: "nk", style: { fontSize: 13, color: C.textSec, margin: 0 } }, st.kegCount ? `${st.kegCount} kegs (no product breakdown saved on this run)` : "No kegs on this stop."),
        extras.length ? rce("div", { key: "ex", style: { marginTop: 12 } },
          runSecLabel("Extras"),
          extras.map((x, i) => rce("div", { key: i, style: { display: "flex", gap: 10, padding: "6px 0", fontSize: 14, color: C.text } },
            rce("span", { style: { flex: 1 } }, x.product),
            rce("span", { style: { fontWeight: 700, fontFamily: "ui-monospace,Menlo,monospace" } }, "\u00D7" + runQtyLabel(x.qty))))) : null
      ]),
      // Signage to take in
      (cards.length || cor.length) ? card([
        runSecLabel("Take in with the delivery"),
        cards.map((x, i) => rce("div", { key: "c" + i, style: { display: "flex", gap: 10, padding: "6px 0", fontSize: 14 } },
          rce("span", { style: { flex: 1, color: C.text } }, "Tap card \u00B7 " + runProdBase(x.product), x.reason ? rce("span", { style: { display: "block", fontSize: 11, color: C.textTer } }, x.reason) : null),
          rce("span", { style: { fontWeight: 700 } }, "\u00D7" + x.qty))),
        cor.map((x, i) => rce("div", { key: "f" + i, style: { display: "flex", gap: 10, padding: "6px 0", fontSize: 14 } },
          rce("span", { style: { flex: 1, color: C.text } }, "Corflute \u00B7 " + runProdBase(x.product), x.reason ? rce("span", { style: { display: "block", fontSize: 11, color: C.textTer } }, x.reason) : null),
          rce("span", { style: { fontWeight: 700 } }, "\u00D7" + x.qty)))
      ]) : null,
      // Notes, reminders, maintenance
      card([
        runSecLabel("Notes & reminders"),
        noteBlocks.length ? noteBlocks : rce("p", { key: "none", style: { fontSize: 13, color: C.textSec, margin: 0 } }, "Nothing flagged for this stop.")
      ]),
      // Actions — sticky so Start delivery is always a thumb away
      rce("div", { style: { position: "sticky", bottom: 0, background: C.bg, padding: "10px 0 14px", marginTop: 6 } },
        cust ? rce("button", { onClick: () => openDelivery(cust, st), style: { width: "100%", padding: "16px", fontSize: 17, fontWeight: 700, background: isDone ? C.surface : C.green, color: isDone ? C.green : "#fff", border: isDone ? `2px solid ${C.green}` : "none", borderRadius: 14, cursor: "pointer", fontFamily: font } },
            isDone ? "Log another delivery" : "Start delivery \u2192")
          : (!isDone ? rce("button", { onClick: () => markRunStopDoneNoDelivery(st), style: { width: "100%", padding: "16px", fontSize: 17, fontWeight: 700, background: C.green, color: "#fff", border: "none", borderRadius: 14, cursor: "pointer", fontFamily: font } }, "Mark stop done") : null),
        cust ? rce("button", { onClick: () => { setCust(cust); setScreen("customer"); }, style: { width: "100%", marginTop: 8, padding: "12px", fontSize: 14, fontWeight: 600, background: C.surface, color: C.textSec, border: `1.5px solid ${C.border}`, borderRadius: 12, cursor: "pointer", fontFamily: font } }, "Customer details") : null));
  };

  // Inside the Kegs section of the delivery screen: what this stop's order
  // says to drop, ticking up as kegs are scanned. Information only — nothing
  // is prefilled into the keg list.
  const renderRunKegBanner = () => {
    if (!runCtxStop || !fieldRun) return null;
    const tally = runKegTally(runCtxStop.lines), extras = runExtraLines(runCtxStop.lines);
    if (!tally.length && !extras.length && !runCtxStop.kegCount) return null;
    const got = {};
    delivered.forEach(k => { const key = runProdKey(k.product) + "|" + runSizeKey(k.size); got[key] = (got[key] || 0) + 1; });
    const want = tally.reduce((a, r) => a + r.qty, 0) || (+runCtxStop.kegCount || 0);
    const have = tally.length ? tally.reduce((a, r) => a + Math.min(got[r.key] || 0, r.qty), 0) : delivered.length;
    const complete = want > 0 && have >= want;
    return rce("div", { style: { padding: "12px 14px", marginBottom: 12, background: complete ? C.greenSoft : "#F3F8F4", border: `1.5px solid ${complete ? C.green : C.greenBorder}`, borderRadius: 12 } },
      rce("div", { style: { display: "flex", alignItems: "baseline", gap: 8, marginBottom: tally.length ? 6 : 0 } },
        rce("span", { style: { flex: 1, fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: C.green } }, "To deliver \u00B7 run " + fieldRun.runId),
        rce("span", { style: { fontSize: 13, fontWeight: 700, color: complete ? C.green : C.textSec } }, `${have} / ${want}` + (complete ? " \u2713" : ""))),
      tally.map(r => {
        const g = got[r.key] || 0, ok = g >= r.qty;
        return rce("div", { key: r.key, style: { display: "flex", alignItems: "center", gap: 9, padding: "5px 0" } },
          runDot(r.product),
          rce("span", { style: { flex: 1, fontSize: 14, fontWeight: 600, color: C.text } }, r.product + " " + r.size),
          rce("span", { style: { fontSize: 14, fontWeight: 700, fontFamily: "ui-monospace,Menlo,monospace", color: ok ? C.green : C.text } }, (ok ? "\u2713 " : "") + g + "/" + r.qty));
      }),
      (!tally.length && runCtxStop.kegCount) ? rce("div", { style: { fontSize: 13, color: C.textSec } }, runCtxStop.kegCount + " kegs on this stop") : null,
      extras.length ? rce("div", { style: { fontSize: 12, color: C.textSec, marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${C.greenBorder}` } },
        "Also on the order: " + extras.map(x => x.product + " \u00D7" + runQtyLabel(x.qty)).join(" \u00B7 ")) : null);
  };

  // Tap cards and corflute, reachable from the delivery screen. Not required
  // to complete a delivery — just one tap away instead of backing out to the
  // customer screen. On a run stop, shows what the run says to take in.
  // Days since the last line clean (a Full System Clean counts), or null.
  const daysSinceLineClean = custId => {
    const all = maintLog[custId] || [];
    const ts = all.reduce((mx, m) => (m.type === "Line Clean" || m.type === "Full System Clean") && (m.ts || 0) > mx ? (m.ts || 0) : mx, 0);
    return ts ? Math.floor((Date.now() - ts) / 86400000) : null;
  };

  // Green prompt at the top of the delivery screen when a line clean is due.
  // Done logs a Line Clean; Unable needs a typed reason and logs a
  // "Line Clean - Unable" row, which does NOT reset the timer. Not required to
  // complete the delivery.
  const logLineCleanFromDelivery = (outcome, reason) => {
    const useDate = backdateDate ? new Date(backdateDate + "T00:00:00") : new Date();
    const n = new Date();
    const record = {
      date: useDate.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }),
      time: ("0" + n.getHours()).slice(-2) + ":" + ("0" + n.getMinutes()).slice(-2),
      type: outcome === "done" ? "Line Clean" : LC_UNABLE_TYPE,
      ts: backdateDate ? useDate.getTime() : n.getTime(),
      notes: outcome === "done" ? "Logged from delivery" : reason
    };
    setMaintLog(prev => ({ ...prev, [customer.id]: [record, ...(prev[customer.id] || [])] }));
    addLog("maint", record);
    syncToSheet("logMaintenance", { data: { customerId: customer.id, customerName: customer.name, category: "Maintenance",
      type: record.type, notes: record.notes, user: currentUser?.name || "Unknown", date: record.date, time: record.time } });
    scrollSafe(() => setLcAction({ outcome, record }));
    showToast(outcome === "done" ? "Line clean logged" : "Line clean not done \u2014 reason saved");
  };
  const undoLineCleanFromDelivery = () => {
    if (!lcAction || !lcAction.record) return;
    const rec = lcAction.record;
    setMaintLog(prev => ({ ...prev, [customer.id]: (prev[customer.id] || []).filter(m => m !== rec) }));
    // No time sent: deleteMaint then takes the newest matching row from the bottom.
    syncToSheet("deleteMaint", { data: { customerId: customer.id, date: rec.date, time: "", type: rec.type, category: "Maintenance" } });
    scrollSafe(() => setLcAction(null));
    showToast("Undone");
  };
  // Oct 2026: the line clean was being missed (done, but Done never tapped,
  // and the delivery still completed). Now, whenever the prompt is showing it
  // is also a required checklist item — Done or Unable (with a reason) clears
  // it, Undo puts it back. "required" stays true after Done (the timer has
  // reset by then) because lcAction holds the outcome for this delivery.
  const lineCleanState = () => {
    if (!customer) return { required: false, ok: true };
    const eq = savedEquipment[customer.id];
    if (!eq || !eq.tower || lcOffFor(eq)) return { required: false, ok: true };
    const d = daysSinceLineClean(customer.id);
    const logged = !!(lcAction && typeof lcAction === "object");
    const due = d === null || d >= lcDueDaysFor(eq);
    return { required: logged || due, ok: logged, d: d, outcome: logged ? lcAction.outcome : null };
  };
  const scrollToLineClean = () => {
    const el = document.getElementById("lc-prompt");
    if (!el) return;
    const y = el.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0) - 70;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };
  // Solid green strip at the top of the delivery screen. The full prompt
  // (Done / Unable / Undo) sits under Corflute; tapping this jumps to it.
  const renderLineCleanTopBanner = () => {
    const s = lineCleanState();
    if (!s.required) return null;
    if (s.ok) {
      const ok = s.outcome === "done";
      return rce("button", { onClick: scrollToLineClean, style: { width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 14,
          background: ok ? C.greenSoft : C.surfaceAlt, border: `1.5px solid ${ok ? C.greenBorder : C.border}`, borderRadius: 12, cursor: "pointer", fontFamily: font } },
        rce("span", { style: { flex: 1, fontSize: 13, fontWeight: 700, color: ok ? C.green : C.textSec, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } },
          ok ? "\u2713 Line clean logged" : "Line clean not done \u2014 " + ((lcAction.record && lcAction.record.notes) || "")));
    }
    const sub = s.d === null ? "No line clean on record" : ("Last done " + lcStatusOf(s.d, lcDueDaysFor(savedEquipment[customer.id])).label);
    return rce("button", { onClick: scrollToLineClean, style: { width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", marginBottom: 14,
        background: C.green, border: "none", borderRadius: 12, cursor: "pointer", fontFamily: font, boxShadow: "0 2px 8px rgba(45,122,79,.25)" } },
      rce("div", { style: { flex: 1, minWidth: 0 } },
        rce("div", { style: { fontSize: 13, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "#fff" } }, "Line clean due today"),
        rce("div", { style: { fontSize: 12, color: "rgba(255,255,255,.88)", marginTop: 2 } }, sub + " \u00B7 Done or Unable needed to complete")),
      rce("span", { style: { fontSize: 13, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" } }, "Log \u2193"));
  };
  const renderLineCleanBanner = () => {
    if (!customer) return null;
    const eq = savedEquipment[customer.id];
    if (!eq || !eq.tower) return null;               // nothing of ours to clean
    if (lcOffFor(eq)) return null;                   // NA in Equipment col AD
    const d = daysSinceLineClean(customer.id);
    const done = lcAction && typeof lcAction === "object";
    const lcDue = lcDueDaysFor(eq);
    if (!done && d !== null && d < lcDue) return null;
    const box = { padding: "12px 14px", marginBottom: 14, background: C.greenSoft, border: `1.5px solid ${C.green}`, borderRadius: 12 };
    const btn = (label, onClick, primary) => rce("button", { onClick: onClick, style: { flex: 1, padding: "11px", fontSize: 14, fontWeight: 700,
      background: primary ? C.green : C.surface, color: primary ? "#fff" : C.green, border: primary ? "none" : `1.5px solid ${C.green}`,
      borderRadius: 10, cursor: "pointer", fontFamily: font } }, label);
    if (done) {
      const ok = lcAction.outcome === "done";
      return rce("div", { style: Object.assign({}, box, { display: "flex", alignItems: "center", gap: 10, background: ok ? C.greenSoft : C.surfaceAlt, border: `1.5px solid ${ok ? C.greenBorder : C.border}` }) },
        rce("span", { style: { flex: 1, fontSize: 13, fontWeight: 600, color: ok ? C.green : C.textSec } },
          ok ? "\u2713 Line clean logged" : "Line clean not done \u2014 " + lcAction.record.notes),
        rce("button", { onClick: undoLineCleanFromDelivery, style: { fontSize: 12, fontWeight: 600, color: C.textTer, background: "none", border: "none", cursor: "pointer", fontFamily: font, textDecoration: "underline" } }, "Undo"));
    }
    const sub = d === null ? "No line clean on record" : ("Last done " + lcStatusOf(d, lcDue).label);
    return rce("div", { style: box },
      rce("div", { style: { fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: C.green } }, "Line clean today"),
      rce("div", { style: { fontSize: 13, color: C.text, margin: "3px 0 10px" } }, sub + ` \u00B7 aim every ${lcDue} days`),
      lcAction === "unable"
        ? rce("div", null,
            rce("textarea", { ref: lcReasonRef, rows: 2, placeholder: "Why couldn't it be done?",
              style: { width: "100%", padding: "10px 12px", fontSize: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box", marginBottom: 8 } }),
            rce("div", { style: { display: "flex", gap: 8 } },
              btn("Cancel", () => scrollSafe(() => setLcAction(null)), false),
              btn("Save reason", () => {
                const r = lcReasonRef.current ? lcReasonRef.current.value.trim() : "";
                if (!r) { showToast("Enter a reason"); return; }
                logLineCleanFromDelivery("unable", r);
              }, true)))
        : rce("div", { style: { display: "flex", gap: 8 } },
            btn("Done", () => logLineCleanFromDelivery("done"), true),
            btn("Unable", () => scrollSafe(() => setLcAction("unable")), false)));
  };

  const renderSignageBanners = () => {
    if (!customer) return null;
    const cid = customer.id;
    const short = p => PRODUCT_SHORT[p] || PRODUCT_SHORT[runProdBase(p)] || runProdBase(p);
    const sess = sessionLog[cid] || {};
    const tc = savedTapCards[cid] || {};
    const cf = savedCorflute[cid] || {};
    const single = String(cf.displayMode || "").indexOf("single") === 0;
    const cards = (tc.cards || []).filter(Boolean);
    const faces = (single ? [cf.sideA, cf.frame2A] : [cf.sideA, cf.sideB, cf.frame2A, cf.frame2B]).filter(Boolean);
    const takeCards = runCtxStop ? runSignageItems(runCtxStop.tapCards) : [];
    const takeCor = runCtxStop ? runSignageItems(runCtxStop.corflute) : [];
    const banner = (key, label, current, take, done, onOpen) => rce("button", {
      key: key, onClick: onOpen,
      style: { width: "100%", textAlign: "left", padding: "14px 16px", marginBottom: 10, background: C.surface,
        border: `1.5px solid ${take.length && !done ? C.accentBorder : C.border}`, borderRadius: 12, cursor: "pointer",
        fontFamily: font, display: "flex", alignItems: "center", gap: 12 } },
      rce("div", { style: { flex: 1, minWidth: 0 } },
        rce("div", { style: { fontSize: 14, fontWeight: 700, letterSpacing: 1, color: C.text } }, label),
        rce("div", { style: { fontSize: 12, color: C.textSec, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } },
          current.length ? current.map(short).join(" \u00B7 ") : "Nothing recorded"),
        take.length ? rce("div", { style: { fontSize: 12, fontWeight: 600, color: C.accent, marginTop: 3 } },
          "Take in: " + take.map(x => short(x.product) + " \u00D7" + x.qty).join(" \u00B7 ")) : null),
      done ? rce("span", { style: { fontSize: 12, fontWeight: 700, color: C.green, whiteSpace: "nowrap" } }, "\u2713 Updated") : null,
      rce("span", { style: { fontSize: 18, color: C.textTer, flexShrink: 0 } }, "\u203A"));
    return rce(React.Fragment, null,
      banner("tc", "TAP CARDS", cards, takeCards, !!(sess.tapcard && sess.tapcard.length), () => openTapCards("delivery")),
      banner("cf", "CORFLUTE", faces, takeCor, !!(sess.corflute && sess.corflute.length), () => openCorflute("delivery")));
  };

  const openCorflute = (from) => {
    const saved = savedCorflute[customer.id];
    if (saved) {
      setCorSideA(saved.sideA || "");
      setCorSideB(saved.sideB || "");
      setCor2SideA(saved.frame2A || "");
      setCor2SideB(saved.frame2B || "");
      setCorBackups(saved.backups || (saved.backupA || saved.backupB ? [saved.backupA, saved.backupB].filter(Boolean) : []));
      setCorDisplayMode(saved.displayMode || "");
    } else {
      setCorSideA("");
      setCorSideB("");
      setCor2SideA("");
      setCor2SideB("");
      setCorBackups([]);
      setCorDisplayMode("");
    }
    setCorSelecting(null);
    setCorBackupAdding(false);
    openSignage("corflute", from);
  };
  // from === "delivery" when tapped from the banner on the delivery screen.
  // The customer-screen button passes a click event, which is ignored.
  const openSignage = (scr, from) => {
    if (from === "delivery") {
      _delivReturnY.current = window.pageYOffset || document.documentElement.scrollTop || 0;
      setSignReturn("delivery");
    } else {
      setSignReturn(null);
    }
    setScreen(scr);
  };
  const leaveSignage = () => {
    if (signReturn === "delivery") { setSignReturn(null); setScreen("delivery"); return; }
    setScreen("customer");
  };
  const openTapCards = (from) => {
    const saved = savedTapCards[customer.id];
    if (saved) {
      setTapCards(saved.cards || []);
      // Backup cards are {product, onDisplay}. Older saves were plain strings.
      setTapBackupCards((saved.backup || []).map(b =>
        b && typeof b === "object" ? { product: b.product || "", onDisplay: b.onDisplay === true }
                                   : { product: b || "", onDisplay: false }));
    } else {
      // Read active tap count from equipment
      const tapCount = getActiveTapCount(customer.id);
      const ex = customer ? liveKegs[customer.id] || {
        onTap: []
      } : {
        onTap: []
      };
      const cards = Array(tapCount).fill("").map((_, i) => ex.onTap[i]?.product || "");
      setTapCards(cards);
      setTapBackupCards([]);
    }
    setTapSelecting(null);
    setTapBackupSelecting(null);
    openSignage("tapcard", from);
  };
  const GAS_LINE_COMPONENTS = {
    "Single": ["6×9.5mm gas line", "9mm gas non-return valve", "1× Grey gas disconnect"],
    "Double": ["6×9.5mm gas line", "Gas line splitter or steel T-piece", "9mm gas non-return valve", "2× Grey gas disconnect"],
    "Triple": ["6×9.5mm gas line", "2× Gas line splitter or steel T-piece", "9mm gas non-return valve", "3× Grey gas disconnect"]
  };
  const TOWER_TAP_COUNT = {
    "Single tap tower": 1,
    "Twin tap tower": 2,
    "Triple tap tower": 3
  };
  const openEquipment = () => {
    const saved = savedEquipment[customer.id];
    if (saved) {
      setEquipFridge(saved.fridge || "");
      setEquipTower(saved.tower || "");
      setEquipTaps(saved.taps || []);
      setEquipGasLine(saved.gasLine || "");
      setEquipRegulator(saved.regulator || "");
      setEquipNotes(saved.notes || "");
      setEquipAframes(saved.aframes || 0);
      setFormVal(prev => ({
        ...prev,
        fridgeOwner: saved.fridgeOwner || "ours",
        backupFridge: saved.backupFridge || "None",
        backupFridgeOwner: saved.backupFridgeOwner || "ours",
        towerOwner: saved.towerOwner || "ours",
        towerStyle: saved.towerStyle || "",
        liquidConnection: saved.liquidConnection || "",
        liquidStyle: saved.liquidStyle || "",
        gasHoseStyle: saved.gasHoseStyle || "",
        gasSplitter: saved.gasSplitter || "",
        regOwner: saved.regOwner || "ours",
        cylOwner: saved.cylOwner || "theirs",
        aframeOwner: saved.aframeOwner || "ours"
      }));
    } else {
      setEquipFridge("");
      setEquipTower("");
      setEquipTaps([]);
      setEquipGasLine("");
      setEquipRegulator("");
      setEquipNotes("");
      setEquipAframes(0);
    }
    setEquipSelecting(null);
    setScreen("equipment");
  };
  const filtered = search.length > 0 ? activeCustomers.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.suburb.toLowerCase().includes(search.toLowerCase())) : [];
  const custSession = customer ? sessionLog[customer.id] || {} : {};
  return /*#__PURE__*/React.createElement("div", {
    style: {
      minHeight: "100vh",
      background: C.bg,
      color: C.text,
      fontFamily: font
    }
  }, /*#__PURE__*/React.createElement("meta", {
    name: "viewport",
    content: "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
  }), /*#__PURE__*/React.createElement("link", {
    href: "https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@400;500;600;700&family=Newsreader:ital,wght@0,400;0,600;1,400&display=swap",
    rel: "stylesheet"
  }), !currentUser && /*#__PURE__*/React.createElement("div", {
    style: {
      maxWidth: 400,
      margin: "0 auto",
      padding: "80px 24px",
      textAlign: "center"
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: LOGO_URL,
    alt: "Filament Coffee",
    style: {
      width: 200,
      height: "auto",
      margin: "0 auto 12px",
      display: "block"
    }
  }), /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 13,
      color: C.textTer,
      margin: "0 0 40px",
      letterSpacing: 1.5,
      textTransform: "uppercase",
      fontWeight: 600
    }
  }, "Field Operations"), /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: "left"
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: "block",
      fontSize: 12,
      fontWeight: 600,
      color: C.textTer,
      letterSpacing: .5,
      textTransform: "uppercase",
      marginBottom: 6
    }
  }, "Username"), /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: loginUsername,
    onChange: e => {
      setLoginUsername(e.target.value);
      setLoginError("");
    },
    autoComplete: "username",
    autoCapitalize: "none",
    autoCorrect: "off",
    placeholder: "Enter username",
    style: {
      width: "100%",
      padding: "14px 16px",
      fontSize: 16,
      background: C.surface,
      border: `1.5px solid ${loginError ? C.red : C.border}`,
      borderRadius: 12,
      color: C.text,
      fontFamily: font,
      boxSizing: "border-box",
      marginBottom: 12
    }
  }), /*#__PURE__*/React.createElement("label", {
    style: {
      display: "block",
      fontSize: 12,
      fontWeight: 600,
      color: C.textTer,
      letterSpacing: .5,
      textTransform: "uppercase",
      marginBottom: 6
    }
  }, "Password"), /*#__PURE__*/React.createElement("input", {
    type: "password",
    value: loginPassword,
    onChange: e => {
      setLoginPassword(e.target.value);
      setLoginError("");
    },
    autoComplete: "current-password",
    placeholder: "Enter password",
    onKeyDown: e => e.key === "Enter" && handleLogin(),
    style: {
      width: "100%",
      padding: "14px 16px",
      fontSize: 16,
      background: C.surface,
      border: `1.5px solid ${loginError ? C.red : C.border}`,
      borderRadius: 12,
      color: C.text,
      fontFamily: font,
      boxSizing: "border-box",
      marginBottom: loginError ? 8 : 20
    }
  }), loginError && /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 13,
      color: C.red,
      margin: "0 0 12px",
      fontWeight: 500
    }
  }, loginError), /*#__PURE__*/React.createElement("button", {
    onClick: handleLogin,
    style: {
      width: "100%",
      padding: "14px",
      fontSize: 16,
      fontWeight: 700,
      background: C.accent,
      color: "#fff",
      border: "none",
      borderRadius: 12,
      cursor: "pointer",
      fontFamily: font
    }
  }, "Sign In"))), currentUser && /*#__PURE__*/React.createElement("div", {
    style: {
      maxWidth: 520,
      margin: "0 auto",
      padding: TRAINING_MODE ? "54px 20px 80px" : "24px 20px 80px",
      position: "relative"
    }
  }, syncing && /*#__PURE__*/React.createElement("div", {
    style: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `linear-gradient(90deg, ${C.accent}, ${C.green}, ${C.accent})`,
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s ease-in-out infinite",
      zIndex: 200
    }
  }), /*#__PURE__*/React.createElement("style", null, `@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`), renderRunPrompt(), renderRunFind(), loading && /*#__PURE__*/React.createElement("div", {
    style: {
      paddingTop: 120,
      textAlign: "center"
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: LOGO_URL,
    alt: "Filament Coffee",
    style: {
      width: 160,
      height: "auto",
      margin: "0 auto 16px",
      display: "block",
      opacity: 0.6
    }
  }), /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 14,
      color: C.textTer,
      fontWeight: 500
    }
  }, loadError ? "Couldn't load from the spreadsheet." : "Loading data from spreadsheet..."), loadError && /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 13,
      color: C.red,
      marginTop: 12
    }
  }, "Error: ", loadError, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("button", {
    onClick: retryLoad,
    style: {
      marginTop: 8,
      padding: "8px 16px",
      background: C.accent,
      color: "#fff",
      border: "none",
      borderRadius: 8,
      cursor: "pointer",
      fontFamily: font,
      fontSize: 13,
      fontWeight: 600
    }
  }, "Retry"))), !loading && /*#__PURE__*/React.createElement(React.Fragment, null, TRAINING_MODE && /*#__PURE__*/React.createElement("div", {
    style: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 150, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
      background: "#B45309", color: "#fff", fontSize: 12, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", fontFamily: font }
  }, "\uD83C\uDF93 Training Mode \u00B7 nothing is saved"), staleBanner, /*#__PURE__*/React.createElement("div", {
    ref: toastRef,
    style: {
      position: "fixed",
      top: 24,
      left: "50%",
      transform: "translateX(-50%)",
      padding: "10px 24px",
      background: C.text,
      color: C.bg,
      borderRadius: 20,
      fontSize: 13,
      fontWeight: 500,
      zIndex: 100,
      boxShadow: "0 8px 30px rgba(0,0,0,.15)",
      visibility: "hidden",
      pointerEvents: "none",
      opacity: 0,
      transition: "opacity .3s ease"
    }
  }), showBackToTop && /*#__PURE__*/React.createElement("button", {
    onClick: () => { window.scrollTo({ top: 0, behavior: "smooth" }); },
    style: {
      position: "fixed", bottom: 24, right: 20, width: 44, height: 44, borderRadius: 22,
      background: C.text, color: C.bg, border: "none", fontSize: 18, fontWeight: 700,
      cursor: "pointer", zIndex: 90, boxShadow: "0 4px 12px rgba(0,0,0,.2)",
      display: "flex", alignItems: "center", justifyContent: "center"
    }
  }, "\u2191"), screen === "runlist" && renderRunList(), screen === "runslip" && renderRunSlip(), screen === "home" && (() => {
    const sorted = [...activeCustomers].sort((a, b) => a.name.localeCompare(b.name));
    const archivedSorted = [...archivedCustomers].sort((a, b) => a.name.localeCompare(b.name));
    const displayList = search.length > 0 ? sorted.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.suburb.toLowerCase().includes(search.toLowerCase())).concat(archivedSorted.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.suburb.toLowerCase().includes(search.toLowerCase())).map(c => ({...c, _archived: true}))) : sorted;
    return /*#__PURE__*/React.createElement("div", {
      style: {
        paddingTop: 40
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        textAlign: "center",
        marginBottom: 28
      }
    }, /*#__PURE__*/React.createElement("img", {
      src: LOGO_URL,
      alt: "Filament Coffee",
      style: {
        width: 180,
        height: "auto",
        margin: "0 auto 8px",
        display: "block"
      }
    }), /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        color: C.textTer,
        margin: "0 0 12px",
        letterSpacing: 1.5,
        textTransform: "uppercase",
        fontWeight: 600
      }
    }, "Field Operations"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 12
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        color: C.textSec,
        fontWeight: 500
      }
    }, currentUser.name), /*#__PURE__*/React.createElement("span", {
      style: {
        color: C.borderLight
      }
    }, "\xB7"), /*#__PURE__*/React.createElement("button", {
      onClick: handleLogout,
      style: {
        background: "none",
        border: "none",
        color: C.red,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: font,
        padding: 0
      }
    }, "Sign Out"))), renderRunHomeBanner(), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8,
        marginBottom: 4
      }
    }, /*#__PURE__*/React.createElement("div", { style: { flex: 1, position: "relative" } },
    /*#__PURE__*/React.createElement("input", {
      type: "text",
      value: search,
      onChange: e => setSearch(e.target.value),
      placeholder: "Search customers...",
      style: {
        width: "100%",
        padding: "14px 40px 14px 18px",
        fontSize: 16,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        color: C.text,
        fontFamily: font,
        boxSizing: "border-box",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)"
      }
    }), search.length > 0 && /*#__PURE__*/React.createElement("button", {
      onClick: () => setSearch(""),
      style: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: C.surfaceAlt, border: "none", borderRadius: 12, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.textTer, padding: 0 }
    }, "\xD7")), /*#__PURE__*/React.createElement("button", {
      onClick: () => {
        if (screen === "home") { _homeScrollY.current = window.pageYOffset || document.documentElement.scrollTop || 0; }
        setKegSearch("");
        setKegSearchResults([]);
        setScreen("kegsearch");
      },
      style: {
        padding: "14px 16px",
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 6,
        flexShrink: 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: 20,
        height: 20
      }
    }, icons.keg(C.accent)), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.accent
      }
    }, "Find Keg"))), (() => {
      const gasCusts = activeCustomers.filter(c => savedEquipment[c.id]?.cylOwner === "ours");
      return /*#__PURE__*/React.createElement("button", {
        onClick: () => setScreen("gassummary"),
        style: {
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          background: "#FFF0ED",
          border: "1px solid #F0B0A0",
          borderRadius: 10,
          cursor: "pointer",
          marginTop: 4,
          marginBottom: 4,
          width: "100%"
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          width: 20,
          height: 20,
          flexShrink: 0
        }
      }, icons.gas("#1A3D7C")), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 13,
          fontWeight: 600,
          color: "#CC0000"
        }
      }, gasCusts.length, " gas cylinder", gasCusts.length !== 1 ? "s" : "", " managed"), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 13,
          color: C.textTer,
          marginLeft: "auto"
        }
      }, "View \u203A"));
    })(), search.length === 0 && Object.keys(sessionLog).length > 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 16,
        marginBottom: 8
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        letterSpacing: .5,
        textTransform: "uppercase",
        marginBottom: 8
      }
    }, "Visited today"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        overflowX: "auto",
        flexWrap: "nowrap",
        marginBottom: 8,
        paddingBottom: 4,
        WebkitOverflowScrolling: "touch"
      }
    }, activeCustomers.filter(c => sessionLog[c.id]).reverse().map(c => /*#__PURE__*/React.createElement("button", {
      key: c.id,
      onClick: () => openCustomer(c),
      style: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        background: C.greenSoft,
        border: `1px solid ${C.greenBorder}`,
        borderRadius: 20,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 500,
        color: C.green,
        fontFamily: font,
        whiteSpace: "nowrap",
        flexShrink: 0
      }
    }, "\u2713 ", c.name)))), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 12
      }
    }, search.length === 0 && /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        letterSpacing: .5,
        textTransform: "uppercase",
        marginBottom: 8
      }
    }, "All customers"), displayList.map(c => {
      const v = sessionLog[c.id];
      const kegData = liveKegs[c.id];
      const totalKegs = kegData ? (kegData.onTap?.length || 0) + (kegData.backup?.length || 0) : 0;
      return /*#__PURE__*/React.createElement("button", {
        key: c.id,
        onClick: () => openCustomer(c),
        style: {
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 14px",
          background: C.surface,
          border: `1px solid ${v ? C.greenBorder : C.borderLight}`,
          borderRadius: 12,
          marginBottom: 5,
          cursor: "pointer",
          textAlign: "left",
          opacity: c._archived ? 0.5 : 1
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          width: 34,
          height: 34,
          borderRadius: 10,
          background: v ? C.greenSoft : C.surfaceAlt,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 600,
          color: v ? C.green : C.textTer,
          flexShrink: 0
        }
      }, v ? "✓" : c.name[0]), /*#__PURE__*/React.createElement("div", {
        style: {
          flex: 1
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 14,
          fontWeight: 600,
          color: C.text
        }
      }, c.name, c.activeNote && !c.noteCleared && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 600, color: "#F57F17", background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 10, padding: "1px 7px", marginLeft: 6 } }, "\uD83D\uDCCB"), actionItems.some(a => a.customerId === c.id && a.status === "Open") && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 600, color: "#C43E2A", background: "#FFF0ED", border: "1px solid #F0B0A0", borderRadius: 10, padding: "1px 7px", marginLeft: 4 } }, "\u26A1")), /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: C.textTer } }, c.suburb, c._archived ? " \xB7 Archived" : " \xB7 " + getActiveTapCount(c.id) + " tap" + (getActiveTapCount(c.id) > 1 ? "s" : "") + (totalKegs > 0 ? " \xB7 " + totalKegs + " kegs" : ""))), !c._archived && (() => { const _kpa = getLatestGasKpa(c.id); const _isOurs = savedEquipment[c.id]?.cylOwner === "ours"; const _hasReading = _kpa != null; const _isLow = _hasReading && _kpa !== -1 && getGasStatus(_kpa).warn; const _act = getLatestGasAction(c.id); const _actShort = _act ? (_act.notes.startsWith("Notified") || _act.notes.startsWith("Contacted") ? "notified" : _act.notes.startsWith("Cylinder") ? "ordered" : "actioned") : null; if(_isOurs) return /*#__PURE__*/React.createElement("span", { style: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginLeft: "auto" } }, _actShort && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 600, color: "#2E7D32" } }, "\u2713 " + _actShort), /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 700, color: getGasColor(_kpa) } }, "\u26FD ", getGasShort(_kpa), _hasReading ? " \xB7 " + getGasStatus(_kpa).label : "")); if(_hasReading) return /*#__PURE__*/React.createElement("span", { style: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginLeft: "auto" } }, _actShort && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 600, color: "#2E7D32" } }, "\u2713 " + _actShort), /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: "#9E9E9E" } }, getGasShort(_kpa), _isLow ? " !" : "")); return null; })(), /*#__PURE__*/React.createElement("span", {
        style: {
          color: C.textTer,
          fontSize: 16
        }
      }, "\u203A"));
    }), displayList.length === 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        padding: 20,
        textAlign: "center",
        color: C.textTer,
        fontSize: 14
      }
    }, "No customers found")), archivedCustomers.length > 0 && /*#__PURE__*/React.createElement("div", {
      style: { marginTop: 20 }
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => setShowArchived(!showArchived),
      style: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        background: "none",
        border: `1px solid ${C.borderLight}`,
        borderRadius: 10,
        cursor: "pointer",
        fontFamily: font
      }
    }, /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, letterSpacing: 0.5, textTransform: "uppercase" } }, "Archived (", archivedCustomers.length, ")"), /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textTer } }, showArchived ? "\u25B2" : "\u25BC")),
    showArchived && /*#__PURE__*/React.createElement("div", { style: { marginTop: 8 } },
      [...archivedCustomers].sort((a, b) => a.name.localeCompare(b.name)).map(c => /*#__PURE__*/React.createElement("button", {
        key: "arc-" + c.id,
        onClick: () => openCustomer(c),
        style: {
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 14px",
          background: C.surface,
          border: `1px solid ${C.borderLight}`,
          borderRadius: 12,
          marginBottom: 5,
          cursor: "pointer",
          textAlign: "left",
          opacity: 0.65
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: { width: 34, height: 34, borderRadius: 10, background: C.surfaceAlt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: C.textTer, flexShrink: 0 }
      }, c.name[0]), /*#__PURE__*/React.createElement("div", { style: { flex: 1 } },
        /*#__PURE__*/React.createElement("div", { style: { fontSize: 14, fontWeight: 600, color: C.textSec } }, c.name),
        /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: C.textTer } }, c.suburb, " \xB7 Archived")),
      /*#__PURE__*/React.createElement("span", { style: { color: C.textTer, fontSize: 16 } }, "\u203A"))))),
    /*#__PURE__*/React.createElement("button", {
      onClick: async () => {
        if (syncingCustomers) return;
        setSyncingCustomers(true);
        try {
          if (TRAINING_MODE) { showToast("Customer sync is off in training"); setSyncingCustomers(false); return; }
          const res = await fetchJson(`${API_URL}?action=syncCustomers`, null, 0);
          if (res.error) { alert("Sync error: " + res.error); }
          else {
            const l = res.log || {};
            const parts = [];
            if (l.added?.length) parts.push(l.added.length + " added");
            if (l.flagged?.length) parts.push(l.flagged.length + " flagged");
            if (l.unflagged?.length) parts.push(l.unflagged.length + " restored");
            if (l.updated?.length) parts.push(l.updated.length + " updated");
            alert(parts.length > 0 ? "Sync complete: " + parts.join(", ") : "Sync complete — no changes");
            const data = await api.load();
            if (data.customers) setRemoteCustomers(data.customers.map(c => ({ ...c, syncStatus: c.syncStatus || '' })));
          }
        } catch (err) { alert("Sync failed: " + err.message); }
        setSyncingCustomers(false);
      },
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 14px",
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        cursor: "pointer",
        marginTop: 16,
        marginBottom: 4,
        width: "100%",
        opacity: syncingCustomers ? 0.6 : 1
      }
    }, /*#__PURE__*/React.createElement("span", { style: { fontSize: 16 } }, "\uD83D\uDD04"), /*#__PURE__*/React.createElement("span", {
      style: { fontSize: 13, fontWeight: 600, color: C.accent }
    }, syncingCustomers ? "Syncing..." : "Sync Customers"), /*#__PURE__*/React.createElement("span", {
      style: { fontSize: 13, color: C.textTer, marginLeft: "auto" }
    }, activeCustomers.length + " customers")),
    /*#__PURE__*/React.createElement("p", {
      style: {
        textAlign: "center",
        marginTop: 24,
        fontSize: 12,
        color: C.textTer
      }
    }, new Date().toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })));
  })(), screen === "kegsearch" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
    label: "Home",
    onClick: () => {
      setScreen("home");
      setKegSearch("");
      setKegSearchResults([]);
    }
  }), /*#__PURE__*/React.createElement("h2", {
    style: {
      fontFamily: font,
      fontSize: 22,
      fontWeight: 600,
      color: C.text,
      margin: "0 0 4px"
    }
  }, "Find a Keg"), /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 13,
      color: C.textTer,
      margin: "0 0 16px"
    }
  }, "Search by keg ID to find its location"), /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: kegSearch,
    onChange: e => searchKegs(e.target.value),
    placeholder: "Enter keg ID e.g. KEG-18-001",
    autoFocus: true,
    autoCapitalize: "characters",
    style: {
      width: "100%",
      padding: "14px 18px",
      fontSize: 16,
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      color: C.text,
      fontFamily: font,
      boxSizing: "border-box",
      marginBottom: 16,
      boxShadow: "0 1px 3px rgba(0,0,0,.04)"
    }
  }), kegSearch.trim() && kegSearchResults.length === 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      padding: 24,
      textAlign: "center",
      color: C.textTer,
      fontSize: 14
    }
  }, "No kegs found matching \"", kegSearch.trim().toUpperCase(), "\""), kegSearchResults.map((r, i) => /*#__PURE__*/React.createElement("button", {
    key: i,
    onClick: () => {
      openCustomer(r.customer);
      setKegSearch("");
      setKegSearchResults([]);
    },
    style: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 14px",
      background: C.surface,
      border: `1px solid ${r.location === "On Tap" ? C.greenBorder : C.borderLight}`,
      borderRadius: 12,
      marginBottom: 6,
      cursor: "pointer",
      textAlign: "left"
    }
  }, /*#__PURE__*/React.createElement(KegVisual, {
    size: r.size,
    product: r.product,
    small: true,
    level: r.level || "Full"
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 14,
      fontWeight: 600,
      color: C.text
    }
  }, r.kegId), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      color: C.textSec
    }
  }, r.product, " \xB7 ", r.size), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      color: r.location === "On Tap" ? C.green : C.blue,
      fontWeight: 600,
      marginTop: 2
    }
  }, r.location, " at ", r.customer.name), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: C.textTer
    }
  }, r.customer.suburb)), /*#__PURE__*/React.createElement("span", {
    style: {
      color: C.textTer,
      fontSize: 16
    }
  }, "\u203A"))), !kegSearch.trim() && /*#__PURE__*/React.createElement("div", {
    style: {
      padding: 24,
      textAlign: "center",
      color: C.textTer,
      fontSize: 13
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 48,
      height: 48,
      margin: "0 auto 12px"
    }
  }, icons.keg(C.textTer)), "Type a keg ID to search all customer locations")), screen === "gassummary" && (() => {
    const gasCusts = activeCustomers.filter(c => {
      const owner = savedEquipment[c.id]?.cylOwner || "theirs";
      if(gasFilter === "ours") return owner === "ours";
      if(gasFilter === "theirs") return owner !== "ours";
      return true;
    }).map(c => {
      const logs = gasLog[c.id] || [];
      const lastLog = logs.length > 0 ? logs[0] : null;   // newest first
      const kpa = getLatestGasKpa(c.id);
      const isOurs = savedEquipment[c.id]?.cylOwner === "ours";
      return { ...c, lastLog, logCount: logs.length, kpa, isOurs };
    }).sort((a, b) => {
      const aVal = a.kpa == null ? 99999 : (a.kpa === -1 ? 99998 : a.kpa);
      const bVal = b.kpa == null ? 99999 : (b.kpa === -1 ? 99998 : b.kpa);
      if (aVal !== bVal) return aVal - bVal;
      return a.name.localeCompare(b.name);
    });
    const _fLabels = { ours: "Managed by Filament", theirs: "Customer manages own gas", all: "All customers" };
    const _openCust = (c) => { openCustomer(c); setGasFromSummary(true); setFormVal({}); setTimeout(() => setScreen("gas"), 50); };
    const _tapCust = (c) => { const act = getLatestGasAction(c.id); if(act) { setGasViewAction({ cust: c, action: act }); } else { _openCust(c); } };
    const _todayISO = new Date().toISOString().slice(0,10);
    const _logAction = (cust, type, detail, dateISO) => {
      const useDate = dateISO && dateISO !== _todayISO ? new Date(dateISO + 'T00:00:00') : new Date();
      const dateStr = useDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      const timeStr = new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
      const fullNote = detail ? type + " \u2014 " + detail : type;
      const record = { date: dateStr, time: timeStr, reason: 'Status update', notes: fullNote, user: currentUser && currentUser.name || 'Unknown', ts: useDate.getTime() };
      setGasLog(prev => { const n = {}; for(var k in prev) n[k]=prev[k]; n[cust.id] = [record].concat(prev[cust.id] || []); return n; });
      syncToSheet('logGas', { data: { customerId: cust.id, customerName: cust.name, reason: 'Status update', notes: fullNote, cylOwner: savedEquipment[cust.id]?.cylOwner || '', user: currentUser && currentUser.name || 'Unknown', date: dateStr, time: timeStr } });
      showToast("\u2713 " + fullNote);
      setGasActionCust(null); setGasActionNote(""); setGasActionType("Notified customer"); setGasActionDate("");
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null,
      /*#__PURE__*/React.createElement(BackBtn, { label: "Home", onClick: () => setScreen("home") }),
      /*#__PURE__*/React.createElement("div", { style: { position: "sticky", top: TRAINING_MODE ? 74 : 44, zIndex: 15, background: C.bg, paddingBottom: 12 } },
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 12, marginBottom: 8 } },
          /*#__PURE__*/React.createElement("h2", { style: { fontFamily: font, fontSize: 22, fontWeight: 600, color: C.text, margin: 0 } }, "Gas Cylinders"),
          /*#__PURE__*/React.createElement("span", { style: { fontSize: 18, fontWeight: 700, color: "#CC0000", background: "#FFF0ED", border: "1.5px solid #F0B0A0", borderRadius: 10, padding: "4px 12px", lineHeight: 1 } }, gasCusts.length)
        ),
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 0, borderRadius: 10, overflow: "hidden", border: "1.5px solid " + C.border, marginBottom: 4 } },
          ["ours", "theirs", "all"].map(f => /*#__PURE__*/React.createElement("button", { key: f, onClick: () => setGasFilter(f), style: { flex: 1, padding: "8px 0", fontSize: 12, fontWeight: gasFilter === f ? 700 : 500, fontFamily: font, color: gasFilter === f ? "#fff" : C.textSec, background: gasFilter === f ? C.accent : C.surface, border: "none", borderRight: f !== "all" ? "1px solid " + C.border : "none", cursor: "pointer" } }, f === "ours" ? "Ours" : f === "theirs" ? "Theirs" : "All"))
        ),
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.textTer, margin: 0 } }, _fLabels[gasFilter])
      ),
      gasCusts.map(c => {
        const _action = getLatestGasAction(c.id);
        const _isLow = c.kpa != null && c.kpa !== -1 && getGasStatus(c.kpa).warn;
        return /*#__PURE__*/React.createElement("div", { key: c.id, onClick: () => _tapCust(c), style: { width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: C.surface, border: _isLow && !_action ? "1.5px solid #FFCC80" : _action ? "1.5px solid #A5D6A7" : "1px solid " + C.borderLight, borderRadius: 12, marginBottom: 6, textAlign: "left", cursor: "pointer" } },
          /*#__PURE__*/React.createElement("div", { style: { width: 32, height: 32, flexShrink: 0 } },
            icons.gas(c.lastLog?.reason === "Gas lost" ? C.red : c.isOurs ? "#E67E22" : "#9E9E9E")
          ),
          /*#__PURE__*/React.createElement("div", { style: { flex: 1 } },
            /*#__PURE__*/React.createElement("div", { style: { fontSize: 14, fontWeight: 600, color: C.text } },
              c.name,
              !c.isOurs && /*#__PURE__*/React.createElement("span", { style: { fontSize: 10, fontWeight: 500, color: "#9E9E9E", marginLeft: 6 } }, "self")
            ),
            /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: C.textTer } }, c.suburb),
            (c.bocAccount || c.paymentType) && /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginTop: 2, flexWrap: "wrap" } },
              c.bocAccount && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textSec, fontWeight: 500 } }, "BOC ", c.bocAccount),
              /*#__PURE__*/React.createElement("span", { style: { fontSize: 10, fontWeight: 700, letterSpacing: .3, textTransform: "uppercase", color: (c.paymentType === "credit") ? C.green : (c.paymentType === "unknown") ? C.textSec : C.accent, background: (c.paymentType === "credit") ? C.greenSoft : (c.paymentType === "unknown") ? C.surfaceAlt : C.accentSoft, border: "1px solid " + ((c.paymentType === "credit") ? C.greenBorder : (c.paymentType === "unknown") ? C.border : C.accentBorder), borderRadius: 6, padding: "1px 7px" } }, (c.paymentType === "credit") ? "Credit" : (c.paymentType === "unknown") ? "Unknown" : "Cash")
            ),
            c.lastLog ? /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: c.lastLog.reason === "Gas lost" ? C.red : C.textSec, marginTop: 2 } },
              "Last: ", c.lastLog.date, " \xB7 ", c.lastLog.reason
            ) : /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: C.accent, fontWeight: 500, marginTop: 2 } }, "No records yet")
          ),
          /*#__PURE__*/React.createElement("div", { style: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0, marginLeft: "auto" } },
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 700, color: c.isOurs ? getGasColor(c.kpa) : "#9E9E9E" } },
              getGasShort(c.kpa), c.kpa != null ? " \xB7 " + getGasStatus(c.kpa).label : ""
            ),
            _isLow && !_action && /*#__PURE__*/React.createElement("button", { onClick: (e) => { e.stopPropagation(); setGasActionCust(c); setGasActionNote(""); setGasActionType("Notified customer"); setGasActionDate(_todayISO); }, style: { fontSize: 11, fontWeight: 600, color: "#E65100", background: "#FFF3E0", border: "1px solid #FFCC80", borderRadius: 8, padding: "3px 10px", cursor: "pointer", fontFamily: font, whiteSpace: "nowrap" } }, "Action"),
            _action && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 600, color: "#2E7D32", background: "#E8F5E9", border: "1px solid #A5D6A7", borderRadius: 8, padding: "3px 10px", whiteSpace: "nowrap" } }, "\u2713 ", _action.notes.startsWith("Notified") || _action.notes.startsWith("Contacted") ? "Notified" : _action.notes.startsWith("Cylinder") ? "Ordered" : "Actioned")
          ),
          /*#__PURE__*/React.createElement("span", { style: { color: C.textTer, fontSize: 16 } }, "\u203A")
        );
      }),
      gasActionCust && /*#__PURE__*/React.createElement("div", { style: { position: "fixed", inset: 0, zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center" } },
        /*#__PURE__*/React.createElement("div", { onClick: () => { setGasActionCust(null); setGasActionNote(""); setGasActionType("Notified customer"); setGasActionDate(""); }, style: { position: "absolute", inset: 0, background: "rgba(0,0,0,.4)" } }),
        /*#__PURE__*/React.createElement("div", { style: { position: "relative", width: "100%", maxWidth: 420, background: C.surface, borderRadius: "16px 16px 0 0", padding: "20px 16px 28px", boxShadow: "0 -4px 24px rgba(0,0,0,.15)" } },
          /*#__PURE__*/React.createElement("div", { style: { fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 } }, gasActionCust.name),
          /*#__PURE__*/React.createElement("div", { style: { fontSize: 13, color: C.textTer, marginBottom: 14 } }, "Log a follow-up action"),
          /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 0, borderRadius: 10, overflow: "hidden", border: "1.5px solid " + C.border, marginBottom: 12 } },
            ["Notified customer", "Cylinder ordered"].map(t =>
              /*#__PURE__*/React.createElement("button", { key: t, onClick: () => setGasActionType(t), style: { flex: 1, padding: "9px 4px", fontSize: 13, fontWeight: gasActionType === t ? 700 : 500, fontFamily: font, color: gasActionType === t ? "#fff" : C.textSec, background: gasActionType === t ? C.accent : C.surface, border: "none", borderRight: t === "Notified customer" ? "1px solid " + C.border : "none", cursor: "pointer" } }, t === "Notified customer" ? "Notified" : "Ordered")
            )
          ),
          /*#__PURE__*/React.createElement("input", { type: "text", placeholder: "Detail \u2014 e.g. spoke to Paul, text Paul\u2026", value: gasActionNote, onChange: e => setGasActionNote(e.target.value), style: { width: "100%", padding: "10px 12px", fontSize: 14, fontFamily: font, border: "1.5px solid " + C.border, borderRadius: 10, background: C.bg, color: C.text, boxSizing: "border-box", marginBottom: 10 } }),
          /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 14 } },
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, color: C.textSec, fontWeight: 500 } }, "Date:"),
            /*#__PURE__*/React.createElement("input", { type: "date", value: gasActionDate || _todayISO, max: _todayISO, onChange: e => { if(e.target.value) setGasActionDate(e.target.value); }, style: { fontSize: 13, fontWeight: 600, color: C.text, padding: "6px 10px", background: C.bg, border: "1.5px solid " + C.border, borderRadius: 8, fontFamily: font, WebkitAppearance: "none" } }),
            (gasActionDate && gasActionDate !== _todayISO) && /*#__PURE__*/React.createElement("button", { onClick: () => setGasActionDate(_todayISO), style: { fontSize: 12, fontWeight: 600, color: C.textTer, background: "none", border: "none", cursor: "pointer", fontFamily: font, textDecoration: "underline" } }, "Today")
          ),
          /*#__PURE__*/React.createElement("button", { onClick: () => _logAction(gasActionCust, gasActionType, gasActionNote.trim(), gasActionDate || _todayISO), style: { width: "100%", padding: "12px", fontSize: 14, fontWeight: 700, fontFamily: font, color: "#fff", background: C.accent, border: "none", borderRadius: 10, cursor: "pointer", marginBottom: 6 } }, "Save"),
          /*#__PURE__*/React.createElement("button", { onClick: () => { setGasActionCust(null); setGasActionNote(""); setGasActionType("Notified customer"); setGasActionDate(""); }, style: { width: "100%", padding: "11px", fontSize: 14, fontWeight: 600, fontFamily: font, color: C.textTer, background: "none", border: "none", cursor: "pointer" } }, "Cancel")
        )
      ),
      gasViewAction && /*#__PURE__*/React.createElement("div", { style: { position: "fixed", inset: 0, zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 } },
        /*#__PURE__*/React.createElement("div", { onClick: () => setGasViewAction(null), style: { position: "absolute", inset: 0, background: "rgba(0,0,0,.4)" } }),
        /*#__PURE__*/React.createElement("div", { style: { position: "relative", width: "100%", maxWidth: 360, background: C.surface, borderRadius: 16, padding: "20px 16px", boxShadow: "0 4px 24px rgba(0,0,0,.15)" } },
          /*#__PURE__*/React.createElement("div", { style: { fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 2 } }, gasViewAction.cust.name),
          /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: C.textTer, marginBottom: 12 } }, gasViewAction.cust.suburb),
          /*#__PURE__*/React.createElement("div", { style: { padding: "12px 14px", background: "#E8F5E9", border: "1.5px solid #A5D6A7", borderRadius: 10, marginBottom: 12 } },
            /*#__PURE__*/React.createElement("div", { style: { fontSize: 14, fontWeight: 700, color: "#2E7D32", marginBottom: 4 } }, "\u2713 ", gasViewAction.action.notes),
            /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: "#558B2F" } }, gasViewAction.action.date, " at ", gasViewAction.action.time, gasViewAction.action.user ? " \xB7 " + gasViewAction.action.user : "")
          ),
          /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8 } },
            /*#__PURE__*/React.createElement("button", { onClick: () => { const c = gasViewAction.cust; setGasViewAction(null); _openCust(c); }, style: { flex: 1, padding: "11px", fontSize: 13, fontWeight: 600, fontFamily: font, color: C.textSec, background: C.bg, border: "1.5px solid " + C.border, borderRadius: 10, cursor: "pointer" } }, "Go to Gas"),
            /*#__PURE__*/React.createElement("button", { onClick: () => setGasViewAction(null), style: { flex: 1, padding: "11px", fontSize: 13, fontWeight: 700, fontFamily: font, color: "#fff", background: C.accent, border: "none", borderRadius: 10, cursor: "pointer" } }, "OK")
          )
        )
      )
    );
  })(), screen === "customer" && customer && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
    label: runCtxStop ? "Delivery slip" : "Home",
    onClick: () => {
      if (runCtxStop) { setScreen("runslip"); return; }
      setScreen("home");
      setCust(null);
      setSearch("");
    }
  }), /*#__PURE__*/React.createElement("h2", {
    style: {
      fontFamily: font,
      fontSize: 24,
      fontWeight: 600,
      color: C.text,
      margin: "0 0 4px"
    }
  }, customer.name), (() => {
    const taps = getActiveTapCount(customer.id);
    return /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        color: C.textSec,
        margin: "0 0 16px"
      }
    }, customer.suburb, " \xB7 ", taps, " tap", taps > 1 ? "s" : "");
  })(), (() => {
    // Archived customer banner with restore
    if (customer.active !== 'No') return null;
    return /*#__PURE__*/React.createElement("div", { style: { marginBottom: 10, padding: "12px 14px", background: "#F5F5F5", border: "1px solid #E0E0E0", borderRadius: 10 } },
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 } },
        /*#__PURE__*/React.createElement("span", { style: { fontSize: 14 } }, "\uD83D\uDDD3\uFE0F"),
        /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: "#616161" } }, "Archived customer")),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: "0 0 10px", lineHeight: 1.4 } }, "This customer has been archived. Their data is still accessible. Tap Restore to make them active again."),
      /*#__PURE__*/React.createElement("button", {
        onClick: () => {
          if (confirm("Restore " + customer.name + " as an active customer?")) {
            syncToSheet("restoreCustomer", { data: { customerId: customer.id } });
            setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, active: 'Yes' } : c) : prev);
            setCust(prev => prev && prev.id === customer.id ? { ...prev, active: 'Yes' } : prev);
          }
        },
        style: { padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#fff", background: "#2D7A4F", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: font }
      }, "Restore Customer"));
  })(), (() => {
    const lastDel = liveKegs[customer.id]?.lastDelivery;
    if (!lastDel) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const delDate = new Date(lastDel); delDate.setHours(0,0,0,0);
    const diff = Math.round((today - delDate) / 86400000);
    let label, color, bg, border;
    if (diff <= 0)       { label = "Last delivery: Today"; }
    else if (diff === 1) { label = "Last delivery: 1 day ago"; }
    else                 { label = `Last delivery: ${diff} days ago`; }
    if (diff <= 7)       { color = "#2D7A4F"; bg = "#EEFBF3"; border = "#B8E4CA"; }
    else if (diff <= 21) { color = "#C17817"; bg = "#FFF5E6"; border = "#F0D4A0"; }
    else                 { color = "#C43E2A"; bg = "#FFF0ED"; border = "#F0C4BB"; }
    return /*#__PURE__*/React.createElement("div", {
      style: { marginBottom: 10, cursor: "pointer" },
      onClick: () => { setCrmTab("deliveries"); setFormVal({}); setScreen("notes"); }
    }, /*#__PURE__*/React.createElement("span", {
      style: { fontSize: 12, fontWeight: 700, color, background: bg, border: `1px solid ${border}`, borderRadius: 20, padding: "4px 12px", letterSpacing: 0.3 }
    }, label, " \u203A"));
  })(), (() => {
    // Active note banner
    const hasNote = customer.activeNote && !customer.noteCleared;
    const custActions = actionItems.filter(a => a.customerId === customer.id && a.status === "Open");
    if (!hasNote && custActions.length === 0) return null;
    return /*#__PURE__*/React.createElement("div", { style: { marginBottom: 10, display: "flex", flexDirection: "column", gap: 8 } },
    hasNote && /*#__PURE__*/React.createElement("div", {
      onClick: () => { setCrmTab("notes"); setFormVal({}); setScreen("notes"); },
      style: { padding: "10px 14px", background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 10, cursor: "pointer" }
    }, /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 } },
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 14 } }, "\uD83D\uDCCB"),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: "#F57F17" } }, "Sticky note"),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer, marginLeft: "auto" } }, "View \u203A")),
    /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.text, margin: 0, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" } }, customer.activeNote)),
    custActions.map((a, ai) => /*#__PURE__*/React.createElement("div", { key: "ai-" + ai, onClick: () => { setCrmTab("notes"); setFormVal({}); setScreen("notes"); }, style: { padding: "10px 14px", background: "#FFF0ED", border: "1px solid #F0B0A0", borderRadius: 10, cursor: "pointer" } },
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 2 } },
        /*#__PURE__*/React.createElement("span", { style: { fontSize: 14 } }, "\u26A1"),
        /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: "#C43E2A" } }, "Action item"),
        /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer, marginLeft: "auto" } }, a.assignedTo, " \xB7 View \u203A")),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.text, margin: 0, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" } }, a.description))));
  })(), (() => {
    // "Not in sync" banner — shown for customers missing from the intermediary sync sheet
    if (customer.syncStatus !== 'Not in sync') return null;
    if (customer.active === 'No') return null;
    return /*#__PURE__*/React.createElement("div", { style: { marginBottom: 10, padding: "12px 14px", background: "#FFF3E0", border: "1px solid #FFB74D", borderRadius: 10 } },
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 } },
        /*#__PURE__*/React.createElement("span", { style: { fontSize: 14 } }, "\u26A0\uFE0F"),
        /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: "#E65100" } }, "Not in customer sync")),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: "0 0 10px", lineHeight: 1.4 } }, "This customer does not appear in the synced customer list. They may be an inactive customer."),
      /*#__PURE__*/React.createElement("button", {
        onClick: () => {
          if (confirm("Archive " + customer.name + "?\n\nTheir data will still be available from the Archived section and they can be restored at any time.")) {
            syncToSheet("archiveCustomer", { data: { customerId: customer.id } });
            setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, active: 'No' } : c) : prev);
            setCust(null);
            setScreen("home");
          }
        },
        style: { padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#fff", background: "#E65100", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: font }
      }, "Archive Customer"));
  })(), (() => {
    const onTapKegs = liveKegs[customer.id]?.onTap || [];
    const backupKegs = liveKegs[customer.id]?.backup || [];
    const total = onTapKegs.length + backupKegs.length;
    return /*#__PURE__*/React.createElement("button", {
      onClick: openAdjust,
      style: {
        width: "100%",
        padding: "14px 16px",
        background: C.surface,
        borderRadius: 14,
        border: `1px solid ${C.border}`,
        marginBottom: 20,
        cursor: "pointer",
        textAlign: "left"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: total > 0 ? 10 : 0
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: 0
      }
    }, "Kegs on hand"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 18,
        fontWeight: 700,
        color: C.text
      }
    }, total), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: C.blue,
        fontWeight: 600
      }
    }, "Adjust \u203A"))), total === 0 && /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        color: C.textTer,
        margin: "8px 0 0"
      }
    }, "No kegs recorded yet \u2014 tap to set up"), total > 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8
      }
    }, onTapKegs.length > 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: "10px",
        background: C.greenSoft,
        borderRadius: 10,
        border: `1px solid ${C.greenBorder}`
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.green,
        margin: "0 0 8px"
      }
    }, "On Tap (", onTapKegs.length, ")"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8,
        justifyContent: "center"
      }
    }, onTapKegs.map((k, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2
      }
    }, /*#__PURE__*/React.createElement(KegVisual, {
      size: k.size,
      product: k.product,
      level: k.level,
      small: true
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: PRODUCT_COLOR[k.product] || C.text
      }
    }, PRODUCT_SHORT[k.product]), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        color: levelColor(k.level),
        fontWeight: 600
      }
    }, k.level))))), backupKegs.length > 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "10px",
        background: C.surfaceAlt,
        borderRadius: 10,
        border: `1px solid ${C.borderLight}`
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.textTer,
        margin: "0 0 8px"
      }
    }, "Backup (", backupKegs.length, ")"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        justifyContent: "center"
      }
    }, backupKegs.slice(0, 4).map((k, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2
      }
    }, /*#__PURE__*/React.createElement(KegVisual, {
      size: k.size,
      product: k.product,
      small: true,
      level: "Full"
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: PRODUCT_COLOR[k.product] || C.textSec
      }
    }, PRODUCT_SHORT[k.product]))), backupKegs.length > 4 && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        minWidth: 36
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 16,
        fontWeight: 700,
        color: C.textSec,
        background: C.surfaceAlt,
        borderRadius: 20,
        padding: "2px 8px",
        border: `1px solid ${C.borderLight}`
      }
    }, "+", backupKegs.length - 4))))));
  })(), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      flexDirection: "column",
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: openDelivery,
    style: {
      width: "100%",
      padding: "18px 20px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      background: C.surface,
      border: `1.5px solid ${C.accentBorder}`,
      borderRadius: 16,
      cursor: "pointer",
      textAlign: "left",
      boxShadow: "0 2px 8px rgba(193,120,23,.06)"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 48,
      height: 48,
      flexShrink: 0
    }
  }, icons.keg(C.accent)), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 16,
      fontWeight: 700,
      color: C.accent
    }
  }, "Delivery"), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 13,
      color: C.textTer,
      marginTop: 2
    }
  }, "Kegs delivered & collected"))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: openTapCards,
    style: {
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      background: C.surface,
      border: `1px solid ${C.borderLight}`,
      borderRadius: 14,
      cursor: "pointer"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 36,
      height: 36
    }
  }, icons.tap(C.blue)), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.blue
    }
  }, "Tap Card")), /*#__PURE__*/React.createElement("button", {
    onClick: openCorflute,
    style: {
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      background: C.surface,
      border: `1px solid ${C.borderLight}`,
      borderRadius: 14,
      cursor: "pointer"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 36,
      height: 36
    }
  }, icons.aframe(C.green)), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.green
    }
  }, "Corflute"))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: openEquipment,
    style: {
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      background: C.surface,
      border: `1px solid ${C.borderLight}`,
      borderRadius: 14,
      cursor: "pointer"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 36,
      height: 36
    }
  }, icons.fridge(C.purple)), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.purple
    }
  }, "Equipment")), /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      setFormVal({});
      setScreen("maint");
    },
    style: {
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      background: C.surface,
      border: `1px solid ${C.borderLight}`,
      borderRadius: 14,
      cursor: "pointer"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 36,
      height: 36
    }
  }, icons.spanner(C.cyan)), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.cyan
    }
  }, "Maintenance"))), /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      setFormVal({});
      setScreen("gas");
    },
    style: {
      width: "100%",
      padding: "14px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      background: C.surface,
      border: `1px solid ${C.borderLight}`,
      borderRadius: 12,
      cursor: "pointer",
      textAlign: "left"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 28,
      height: 28,
      flexShrink: 0
    }
  }, icons.gas("#E67E22")), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 13,
      fontWeight: 600,
      color: "#E67E22"
    }
  }, "Gas Cylinders"), (() => { const _kpa = getLatestGasKpa(customer.id); if(_kpa != null && _kpa !== -1) return /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: getGasColor(_kpa), fontWeight: 600, marginTop: 1 } }, _kpa.toLocaleString() + " kPa \xB7 " + getGasStatus(_kpa).label); if(_kpa === -1) return /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: "#9E9E9E", fontWeight: 600, marginTop: 1 } }, "Can't read"); if(savedEquipment[customer.id]?.cylOwner === "ours") return /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: C.textTer, marginTop: 1 } }, "No level recorded"); return null; })(), (gasLog[customer.id]?.length || 0) > 0 && /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 12,
      color: C.textTer,
      marginLeft: "auto"
    }
  }, gasLog[customer.id].length, " record", gasLog[customer.id].length > 1 ? "s" : "")), /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      setFormVal({});
      setCrmTab("notes");
      setScreen("notes");
    },
    style: {
      width: "100%",
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      background: C.surface,
      border: `1px solid ${C.borderLight}`,
      borderRadius: 12,
      cursor: "pointer",
      textAlign: "left"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 24,
      height: 24,
      flexShrink: 0
    }
  }, icons.notes(C.slate)), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 13,
      fontWeight: 500,
      color: C.textSec
    }
  }, "Notes & CRM")))), screen === "delivery" && customer && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
    label: runCtxStop ? "Delivery slip" : customer.name,
    onClick: () => setScreen(runCtxStop ? "runslip" : "customer")
  }), /*#__PURE__*/React.createElement("h2", {
    style: {
      fontFamily: font,
      fontSize: 24,
      fontWeight: 700,
      color: C.text,
      margin: "0 0 4px"
    }
  }, customer.name), /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 13,
      color: backdateDate ? "#B45309" : C.accent,
      fontWeight: 600,
      margin: "0 0 4px"
    }
  }, backdateDate ? "Delivery (Backdated)" : "Delivery", (runCtxStop && fieldRun) ? " \u00B7 Run " + fieldRun.runId + " \u00B7 stop " + (runStops.findIndex(x => x.seq === runCtxStop.seq) + 1) + " of " + runStops.length : ""),
  // ========== SCAN MODAL ==========
  scanModalMode && /*#__PURE__*/React.createElement("div", {
    style: { position: "fixed", inset: 0, zIndex: 9999, background: "#0F0F10", display: "flex", flexDirection: "column", fontFamily: font }
  },
    // Header
    /*#__PURE__*/React.createElement("div", { style: { padding: "14px 16px", background: "#0F0F10", borderBottom: "1px solid #2A2A2C", display: "flex", alignItems: "center", gap: 12 } },
      // Mode toggle pill
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", flex: 1, borderRadius: 10, overflow: "hidden", border: "1.5px solid #444" } },
        /*#__PURE__*/React.createElement("button", { onClick: () => setScanModalMode("deliver"), style: { flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 800, letterSpacing: 1, border: "none", cursor: "pointer", fontFamily: font, background: scanModalMode === "deliver" ? "#2D7A4F" : "transparent", color: scanModalMode === "deliver" ? "#fff" : "#999", transition: "background .15s, color .15s" } }, "DELIVER"),
        /*#__PURE__*/React.createElement("button", { onClick: () => setScanModalMode("collect"), style: { flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 800, letterSpacing: 1, border: "none", borderLeft: "1px solid #444", cursor: "pointer", fontFamily: font, background: scanModalMode === "collect" ? "#C17817" : "transparent", color: scanModalMode === "collect" ? "#fff" : "#999", transition: "background .15s, color .15s" } }, "COLLECT")),
      // Close button
      /*#__PURE__*/React.createElement("button", { onClick: () => setScanModalMode(""), style: { width: 40, height: 40, padding: 0, fontSize: 22, fontWeight: 400, border: "none", background: "transparent", color: "#fff", cursor: "pointer", fontFamily: font } }, "\u00D7")
    ),
    // Scanner mount area (the html5-qrcode UI lives here -- injected by useEffect)
    /*#__PURE__*/React.createElement("div", { ref: scannerMountRef, style: { flex: 1, padding: "12px 14px", overflowY: "auto", background: "#0F0F10" } }),
    // Done button
    /*#__PURE__*/React.createElement("div", { style: { padding: "12px 14px 16px", background: "#0F0F10", borderTop: "1px solid #2A2A2C" } },
      /*#__PURE__*/React.createElement("button", { onClick: () => setScanModalMode(""), style: { width: "100%", padding: "14px", fontSize: 15, fontWeight: 700, background: "#fff", color: "#0F0F10", border: "none", borderRadius: 12, cursor: "pointer", fontFamily: font } }, "Done")
    )
  ),
  // --- Backdate toggle ---
  (() => {
    if (!backdateDate) {
      return /*#__PURE__*/React.createElement("button", { onClick: () => scrollSafe(() => {
        const _y = new Date(); _y.setDate(_y.getDate() - 1);
        setBackdateDate(_y.toLocaleDateString('en-CA'));
      }), style: { fontSize: 11, fontWeight: 500, color: C.textTer, background: "none", border: "none", cursor: "pointer", fontFamily: font, padding: "0 0 14px", textDecoration: "underline", textDecorationStyle: "dotted" } }, "Backdate this delivery");
    }
    const _bdDisp = new Date(backdateDate + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    return /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 10, marginBottom: 14 } },
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: "#B45309" } }, "\uD83D\uDCC5 " + _bdDisp),
      /*#__PURE__*/React.createElement("input", { type: "date", value: backdateDate, max: new Date().toLocaleDateString('en-CA'), onChange: function(e) { if(e.target.value) scrollSafe(() => setBackdateDate(e.target.value)); }, style: { fontSize: 12, fontWeight: 600, color: "#B45309", padding: "4px 8px", background: "#fff", border: "1px solid #FFE082", borderRadius: 6, fontFamily: font, WebkitAppearance: "none", marginLeft: "auto" } }),
      /*#__PURE__*/React.createElement("button", { onClick: () => scrollSafe(() => setBackdateDate("")), style: { fontSize: 11, fontWeight: 600, color: C.textTer, background: "none", border: "none", cursor: "pointer", fontFamily: font, textDecoration: "underline" } }, "Cancel"));
  })(),
  // --- Line clean strip (green, only when due) — full prompt is under Corflute ---
  renderLineCleanTopBanner(),
  // --- Delivery Instructions section ---
  (() => {
    const instr = customer.deliveryInstructions || "";
    if (!editingDelivInstr && !instr) {
      return /*#__PURE__*/React.createElement("button", { onClick: () => scrollSafe(() => setEditingDelivInstr(true)), style: { width: "100%", padding: "10px 14px", fontSize: 13, fontWeight: 600, background: C.surfaceAlt, color: C.textSec, border: `1px dashed ${C.border}`, borderRadius: 10, cursor: "pointer", fontFamily: font, marginBottom: 16, textAlign: "left" } }, "\uD83D\uDCCB Add delivery instructions");
    }
    if (editingDelivInstr) {
      return /*#__PURE__*/React.createElement("div", { style: { padding: "12px 14px", background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 12, marginBottom: 16 } },
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: "#F57F17", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: 0.5 } }, "Delivery instructions"),
        /*#__PURE__*/React.createElement("textarea", { ref: delivInstrRef, defaultValue: instr, placeholder: "e.g. Gate code 2406, leave kegs at back door, call on arrival...", rows: 3, style: { width: "100%", padding: "10px 12px", fontSize: 14, background: "#fff", border: "1px solid #FFE082", borderRadius: 8, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box" } }),
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8, marginTop: 8 } },
          /*#__PURE__*/React.createElement("button", { onClick: () => {
            const val = delivInstrRef.current ? delivInstrRef.current.value.trim() : "";
            scrollSafe(() => {
              setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, deliveryInstructions: val } : c) : prev);
              setCust(prev => prev && prev.id === customer.id ? { ...prev, deliveryInstructions: val } : prev);
              setEditingDelivInstr(false);
              showToast(val ? "Instructions saved" : "Instructions cleared");
            });
            api.post("saveDeliveryInstructions", { data: { customerId: customer.id, deliveryInstructions: val } });
          }, style: { flex: 1, padding: "10px", fontSize: 14, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Save"),
          instr ? /*#__PURE__*/React.createElement("button", { onClick: () => {
            scrollSafe(() => {
              setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, deliveryInstructions: "" } : c) : prev);
              setCust(prev => prev && prev.id === customer.id ? { ...prev, deliveryInstructions: "" } : prev);
              setEditingDelivInstr(false);
              showToast("Instructions deleted");
            });
            api.post("saveDeliveryInstructions", { data: { customerId: customer.id, deliveryInstructions: "" } });
          }, style: { padding: "10px 16px", fontSize: 14, fontWeight: 600, background: C.redSoft || "#FFF0ED", color: C.red, border: "1px solid " + (C.red + "33"), borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Delete") : null,
          /*#__PURE__*/React.createElement("button", { onClick: () => scrollSafe(() => setEditingDelivInstr(false)), style: { padding: "10px 16px", fontSize: 14, fontWeight: 600, background: C.surface, color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Close")));
    }
    return /*#__PURE__*/React.createElement("div", { onClick: () => scrollSafe(() => setEditingDelivInstr(true)), style: { display: "flex", alignItems: "center", padding: "10px 14px", background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 10, marginBottom: 16, cursor: "pointer" } },
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: "#F57F17" } }, "\uD83D\uDCCB Delivery instructions"),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: "#F57F17", marginLeft: "auto", fontWeight: 500 } }, "Tap to show \u203A"));
  })(),
  (() => { const _omoOk = /^OMO\d{4}$/.test(orderRef); return /*#__PURE__*/React.createElement("div", {
    style: { display: "flex", alignItems: "center", gap: 0, marginBottom: 16 }
  }, /*#__PURE__*/React.createElement("div", {
    style: { padding: "10px 12px", fontSize: 16, fontWeight: 700, background: _omoOk ? C.greenSoft : C.surfaceAlt, border: `1.5px solid ${_omoOk ? C.green : C.border}`, borderTopLeftRadius: 10, borderBottomLeftRadius: 10, color: _omoOk ? C.green : C.text, fontFamily: font, borderRight: "none" }
  }, _omoOk ? "\u2713 OMO" : "OMO"), /*#__PURE__*/React.createElement("input", {
    key: "omo-fixed",
    type: "tel",
    inputMode: "numeric",
    pattern: "[0-9]*",
    maxLength: 4,
    defaultValue: orderRef.replace(/^OMO/i, ""),
    onInput: e => { e.target.value = e.target.value.replace(/\D/g, "").slice(0, 4); },
    onBlur: e => { var _v = e.target.value.replace(/\D/g, "").slice(0, 4); scrollSafe(() => setOrderRef("OMO" + _v)); },
    placeholder: "1234",
    style: {
      flex: 1,
      padding: "10px 14px",
      fontSize: 16,
      background: _omoOk ? C.greenSoft : C.surface,
      border: `1.5px solid ${_omoOk ? C.green : C.border}`,
      borderTopRightRadius: 10,
      borderBottomRightRadius: 10,
      color: C.text,
      fontFamily: font,
      boxSizing: "border-box"
    }
  })); })(),
  /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setDelivSection(delivSection === "kegs" ? "" : "kegs")), style: { width: "100%", padding: "14px 16px", marginBottom: 10, background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 12, cursor: "pointer", fontFamily: font, display: "flex", alignItems: "center", justifyContent: "space-between" } },
    /*#__PURE__*/React.createElement("span", { style: { fontSize: 14, fontWeight: 700, letterSpacing: 1, color: delivSection === "kegs" ? C.accent : C.text } }, "KEGS"),
    /*#__PURE__*/React.createElement("span", { style: { display: "flex", alignItems: "center", gap: 8 } },
      (delivered.length > 0 || collected.length > 0) && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 700, color: C.textSec } }, (delivered.length > 0 ? delivered.length + " drop" : "") + (delivered.length > 0 && collected.length > 0 ? " \u00B7 " : "") + (collected.length > 0 ? collected.length + " collect" : "")),
      confirmed && /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.green, fontWeight: 700 } }, "\u2713"),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 14, color: C.textTer, transform: delivSection === "kegs" ? "rotate(180deg)" : "none", transition: "transform .2s" } }, "\u25BC"))),
  /*#__PURE__*/React.createElement("div", { style: { display: delivSection === "kegs" ? "block" : "none", marginBottom: 16 } },
    renderRunKegBanner(),
    /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8, marginBottom: 12 } },
      /*#__PURE__*/React.createElement("button", { onClick: () => { setDelivSection("kegs"); setScanModalMode("deliver"); }, style: { flex: 1, padding: "14px 8px", fontSize: 14, fontWeight: 800, letterSpacing: 1, background: "#EEFBF3", color: "#2D7A4F", border: "2px solid #2D7A4F", borderRadius: 12, cursor: "pointer", fontFamily: font } }, "\uD83D\uDCF7 SCAN \u2192 DELIVER"),
      /*#__PURE__*/React.createElement("button", { onClick: () => { setDelivSection("kegs"); setScanModalMode("collect"); }, style: { flex: 1, padding: "14px 8px", fontSize: 14, fontWeight: 800, letterSpacing: 1, background: "#FFF5E6", color: "#C17817", border: "2px solid #C17817", borderRadius: 12, cursor: "pointer", fontFamily: font } }, "\uD83D\uDCF7 SCAN \u2192 COLLECT")),
  /*#__PURE__*/React.createElement("div", {
    style: { padding: "16px", background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 14, marginBottom: 16 }
  },
  /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 24
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.green,
      letterSpacing: .5,
      textTransform: "uppercase",
      margin: "0 0 10px"
    }
  }, "Kegs Delivered"), delivered.map((k, i) => /*#__PURE__*/React.createElement(KegCell, {
    key: i,
    keg: k,
    color: "green",
    onRemove: () => scrollSafe(() => {
      setDelivered(p => p.filter((_, j) => j !== i));
      setOnHandBackup(p => {
        const x = p.findIndex(x => x.kegId === k.kegId);
        return x >= 0 ? p.filter((_, j) => j !== x) : p;
      });
    })
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 6,
      marginTop: 6,
      alignItems: "center"
    }
  },
  /* KEG- prefix label */
  /*#__PURE__*/React.createElement("span", {
    style: { fontSize: 15, fontWeight: 700, color: C.textSec, letterSpacing: 0.3, whiteSpace: "nowrap" }
  }, "KEG-"),
  /* 09/18 toggle */
  /*#__PURE__*/React.createElement("div", {
    style: { display: "flex", borderRadius: 8, overflow: "hidden", border: `1.5px solid ${C.greenBorder}`, flexShrink: 0 }
  },
    /*#__PURE__*/React.createElement("button", {
      onClick: () => { scrollSafe(() => setScanSize("18L")); },
      style: { padding: "8px 10px", fontSize: 14, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: font,
        background: scanSize === "18L" ? C.accent : C.surface,
        color: scanSize === "18L" ? "#fff" : C.textSec,
        transition: "background .15s, color .15s" }
    }, "18"),
    /*#__PURE__*/React.createElement("button", {
      onClick: () => { scrollSafe(() => setScanSize("9L")); },
      style: { padding: "8px 10px", fontSize: 14, fontWeight: 700, border: "none", borderLeft: `1px solid ${C.border}`, cursor: "pointer", fontFamily: font,
        background: scanSize === "9L" ? C.accent : C.surface,
        color: scanSize === "9L" ? "#fff" : C.textSec,
        transition: "background .15s, color .15s" }
    }, "09")
  ),
  /* Dash separator */
  /*#__PURE__*/React.createElement("span", {
    style: { fontSize: 15, fontWeight: 700, color: C.textSec }
  }, "-"),
  /* 3-digit number input */
  /*#__PURE__*/React.createElement("input", {
    ref: scanInputRef,
    type: "tel",
    inputMode: "numeric",
    pattern: "[0-9]*",
    maxLength: 3,
    defaultValue: scanInput || "",
    onBlur: e => { var _v = e.target.value; scrollSafe(() => setScanInput(_v)); },
    onInput: e => { var _y = window.pageYOffset || document.documentElement.scrollTop || 0; setScanInput(e.target.value); requestAnimationFrame(function() { window.scrollTo(0, _y); }); },
    onKeyDown: e => e.key === "Enter" && addDelivered(),
    placeholder: "001",
    style: {
      width: 58,
      padding: "10px 8px",
      fontSize: 17,
      fontWeight: 700,
      textAlign: "center",
      letterSpacing: 2,
      background: C.surface,
      border: `1px solid ${C.greenBorder}`,
      borderRadius: 10,
      color: C.text,
      fontFamily: font,
      boxSizing: "border-box"
    }
  }),
  /* product colour dot - fills when the SKU is recognised */
  (() => {
    const _d = scanInputRef.current ? scanInputRef.current.value : scanInput;
    const _fid = _d ? ("KEG-" + (scanSize === "9L" ? "09" : "18") + "-" + String(_d).padStart(3, "0")).toUpperCase() : "";
    const _p = scanProduct || (_fid ? (kegRegister.find(r => r.kegId === _fid) || {}).product || "" : "");
    return /*#__PURE__*/React.createElement("span", { style: { width: 14, height: 14, borderRadius: "50%", flexShrink: 0, background: _p ? (PRODUCT_COLOR[_p] || C.textTer) : "transparent" } });
  })()
  ), (() => {
    const _d = scanInputRef.current ? scanInputRef.current.value : scanInput;
    if (!_d) return null;
    const _fid = ("KEG-" + (scanSize === "9L" ? "09" : "18") + "-" + String(_d).padStart(3, "0")).toUpperCase();
    const _hasReg = kegRegister && kegRegister.length > 0;
    const _entry = _hasReg ? kegRegister.find(r => r.kegId === _fid) : null;
    const _status = _entry ? String(_entry.status || "").trim() : "";
    const _inFridge = _status.toLowerCase() === "in fridge";
    let _msg = "", _col = C.green, _bg = C.greenSoft, _bd = C.greenBorder, _icon = "\u2713 ";
    if (_hasReg) {
      if (!_entry) { _msg = "Not in register"; _col = C.red; _bg = C.redSoft; _bd = "#F0B0A0"; _icon = "\u26A0 "; }
      else if (_inFridge) { _msg = "In fridge stock"; }
      else { _msg = "Not in fridge stock \u00B7 " + (_status || "unknown"); _col = C.accent; _bg = C.accentSoft; _bd = C.accentBorder; _icon = "\u26A0 "; }
    }
    return /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", margin: "7px 0 0 2px" } }, /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer } }, "\u2192 " + _fid), _msg ? /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 700, color: _col, background: _bg, border: "1px solid " + _bd, borderRadius: 20, padding: "1px 8px" } }, _icon + _msg) : null);
  })(), /*#__PURE__*/React.createElement("div", {
    style: { display: "flex", gap: 8, marginTop: 10, alignItems: "center" }
  }, /*#__PURE__*/React.createElement("div", {
    style: { flex: 1 }
  }, /*#__PURE__*/React.createElement(Select, {
    value: scanProduct || (() => {
      const digits = scanInputRef.current ? scanInputRef.current.value : scanInput;
      if (!digits) return "";
      const fullId = "KEG-" + (scanSize === "9L" ? "09" : "18") + "-" + String(digits).padStart(3, "0");
      const reg = kegRegister.find(r => r.kegId === fullId);
      return reg ? reg.product : "";
    })(),
    onChange: setScanProduct,
    options: PRODUCTS,
    placeholder: "Product"
  })), (() => {
    const _digits = scanInputRef.current ? scanInputRef.current.value : scanInput;
    const _fullId = _digits ? ("KEG-" + (scanSize === "9L" ? "09" : "18") + "-" + String(_digits).padStart(3, "0")).toUpperCase() : "";
    const _hasReg = kegRegister && kegRegister.length > 0;
    const _entry = _hasReg && _fullId ? kegRegister.find(r => r.kegId === _fullId) : null;
    const _regProduct = (_entry || {}).product || "";
    const _hasProduct = !!(scanProduct || _regProduct);
    const _en = !!(_digits && _hasProduct);
    const _override = !!(_hasReg && _fullId && (!_entry || String((_entry || {}).status || "").trim().toLowerCase() !== "in fridge"));
    const _btnCol = !_en ? C.textTer : (_override ? C.accent : C.green);
    const _btnBg = !_en ? C.surfaceAlt : (_override ? C.accentSoft : C.greenSoft);
    const _btnBd = !_en ? C.borderLight : (_override ? C.accent : C.green);
    return /*#__PURE__*/React.createElement("button", { onClick: () => scrollSafe(addDelivered), disabled: !_en, style: { padding: "11px 18px", background: _btnBg, color: _btnCol, border: "1.5px solid " + _btnBd, borderRadius: 10, cursor: _en ? "pointer" : "not-allowed", fontSize: 14, fontWeight: 600, fontFamily: font, whiteSpace: "nowrap" } }, _en && _override ? "Drop Keg anyway" : "Drop Keg");
  })())
  ), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 24
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.accent,
      letterSpacing: .5,
      textTransform: "uppercase",
      margin: "0 0 10px"
    }
  }, "Kegs Collected"), collected.map((k, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    style: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      background: C.accentSoft,
      border: `1px solid ${C.accentBorder}`,
      borderRadius: 12,
      marginBottom: 6
    }
  }, /*#__PURE__*/React.createElement(KegVisual, {
    size: k.size || "18L",
    product: k.product || "",
    small: true,
    ghost: true
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 13,
      fontWeight: 600,
      color: C.text
    }
  }, k.kegId), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      color: C.textTer
    }
  }, k.product ? `${k.size} · ${k.product}` : "Empty")), /*#__PURE__*/React.createElement("button", {
    onClick: () => scrollSafe(() => setCollected(p => p.filter((_, j) => j !== i))),
    style: {
      background: "none",
      border: "none",
      color: C.textTer,
      cursor: "pointer",
      fontSize: 18
    }
  }, "\xD7"))), /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "flex-start", gap: 9, padding: "11px 13px", background: C.accentSoft, border: "1px solid " + C.accentBorder, borderRadius: 11, fontSize: 13, fontWeight: 500, color: C.accent, lineHeight: 1.45, marginTop: 4 } }, "Scan a keg above, or tap Collect on a keg in Kegs on Hand below.")
  )),
  /*#__PURE__*/React.createElement("div", {
    style: {
      padding: "16px",
      background: C.surface,
      border: `1.5px solid ${C.border}`,
      borderRadius: 14,
      marginBottom: 16,
      boxShadow: "0 2px 8px rgba(0,0,0,.04)"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: 14
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: font,
      fontSize: 16,
      fontWeight: 600,
      color: C.text,
      margin: 0
    }
  }, "Kegs on Hand"), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 12,
      color: C.textTer
    }
  }, onHandTap.length, " tap \xB7 ", onHandBackup.length, " backup")), onHandTap.length > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 14
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.green,
      letterSpacing: .5,
      textTransform: "uppercase",
      margin: "0 0 10px"
    }
  }, "On Tap"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: `repeat(${Math.min(onHandTap.length, 3)}, 1fr)`,
      gap: 6
    }
  }, onHandTap.map((k, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    style: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 3,
      padding: "8px 4px 6px",
      background: C.greenSoft,
      borderRadius: 10,
      border: `1px solid ${C.greenBorder}`
    }
  }, /*#__PURE__*/React.createElement(KegVisual, {
    size: k.size,
    product: k.product,
    level: k.level || "Medium",
    small: true
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: PRODUCT_COLOR[k.product] || C.text,
      textAlign: "center"
    }
  }, PRODUCT_SHORT[k.product] || k.product), k.kegId ? /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: C.textTer,
      fontFamily: "monospace",
      letterSpacing: .3,
      textAlign: "center"
    }
  }, k.kegId) : null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      color: C.textTer,
      textAlign: "center"
    }
  }, k.size), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 2
    }
  }, TAP_LEVELS.map(lv => /*#__PURE__*/React.createElement("button", {
    key: lv,
    onClick: () => scrollSafe(() => setOnHandTap(p => p.map((x, j) => j === i ? {
      ...x,
      level: lv
    } : x))),
    style: {
      padding: "2px 5px",
      fontSize: 12,
      fontWeight: 600,
      background: k.level === lv ? levelColor(lv) : "transparent",
      color: k.level === lv ? "#fff" : C.textTer,
      border: `1px solid ${k.level === lv ? levelColor(lv) : C.borderLight}`,
      borderRadius: 4,
      cursor: "pointer",
      fontFamily: font
    }
  }, lv[0]))), /*#__PURE__*/React.createElement("button", {
    onClick: () => collectFromTap(i),
    style: {
      width: "100%",
      marginTop: 2,
      padding: "4px 0",
      fontSize: 12,
      fontWeight: 600,
      background: C.redSoft,
      border: `1px solid ${C.red}33`,
      borderRadius: 6,
      color: C.red,
      cursor: "pointer",
      fontFamily: font
    }
  }, "\xD7 Collect"), /*#__PURE__*/React.createElement("button", {
    onClick: () => untapToFridge(i),
    style: {
      width: "100%",
      marginTop: 2,
      padding: "4px 0",
      fontSize: 12,
      fontWeight: 600,
      background: C.blueSoft,
      border: `1px solid ${C.blueBorder}`,
      borderRadius: 6,
      color: C.blue,
      cursor: "pointer",
      fontFamily: font
    }
  }, "\u21A9 Untap"))))), (() => {
    const fridgeKegs = onHandBackup.map((k, i) => ({...k, _idx: i})).filter(k => k.location === "fridge");
    const bfKegs = onHandBackup.map((k, i) => ({...k, _idx: i})).filter(k => k.location !== "fridge");
    const renderDelBackup = (sectionKey, title, color, kegs, moveLabel, moveLoc) => {
      if (kegs.length === 0) return null;
      const expanded = expandedBackup[sectionKey];
      const visible = expanded ? kegs : kegs.slice(0, 4);
      const hasMore = kegs.length > 4;
      return /*#__PURE__*/React.createElement("div", { style: { marginBottom: 8 } },
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, margin: "0 0 8px" } },
          /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color, letterSpacing: .5, textTransform: "uppercase", margin: 0 } }, title),
          hasMore && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 700, color: "#fff", background: color, borderRadius: 10, padding: "1px 7px" } }, kegs.length)
        ),
        /*#__PURE__*/React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 } },
          visible.map(k => /*#__PURE__*/React.createElement("div", { key: k._idx, style: { display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", background: C.surfaceAlt, borderRadius: 10, border: `1px solid ${C.borderLight}` } },
            /*#__PURE__*/React.createElement(KegVisual, { size: k.size, product: k.product, small: true, level: "Full" }),
            /*#__PURE__*/React.createElement("div", { style: { flex: 1, minWidth: 0 } },
              /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, fontWeight: 600, color: PRODUCT_COLOR[k.product] || C.textSec } }, PRODUCT_SHORT[k.product] || k.product),
              /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: C.textTer } }, k.size, " \xB7 ", k.kegId),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" } },
                /*#__PURE__*/React.createElement("button", { onClick: () => promoteToTap(k._idx), style: { fontSize: 11, fontWeight: 600, padding: "2px 6px", background: C.greenSoft, border: `1px solid ${C.greenBorder}`, borderRadius: 4, color: C.green, cursor: "pointer", fontFamily: font } }, "\u2192 Tap"),
                /*#__PURE__*/React.createElement("button", { onClick: () => scrollSafe(() => { setOnHandBackup(p => p.map((x, j) => j === k._idx ? {...x, location: moveLoc} : x)); showToast("Moved"); }), style: { fontSize: 11, fontWeight: 600, padding: "2px 6px", background: C.blueSoft, border: `1px solid ${C.blueBorder}`, borderRadius: 4, color: C.blue, cursor: "pointer", fontFamily: font } }, moveLabel),
                /*#__PURE__*/React.createElement("button", { onClick: () => collectFromBackup(k._idx), style: { fontSize: 11, fontWeight: 600, padding: "2px 6px", background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 4, color: C.red, cursor: "pointer", fontFamily: font } }, "\xD7 Collect"),
                /*#__PURE__*/React.createElement("button", { onClick: () => scrollSafe(() => { setOnHandBackup(p => p.filter((_, j) => j !== k._idx)); showToast(`${PRODUCT_SHORT[k.product] || k.product} removed`); }), style: { fontSize: 11, fontWeight: 600, padding: "2px 6px", background: C.surfaceAlt, border: `1px solid ${C.borderLight}`, borderRadius: 4, color: C.textTer, cursor: "pointer", fontFamily: font } }, "\u21A9 Remove")
              )
            )
          ))
        ),
        hasMore && /*#__PURE__*/React.createElement("button", { onClick: () => { setExpandedBackup(p => ({...p, [sectionKey]: !p[sectionKey]})); }, style: { width: "100%", marginTop: 6, padding: "6px", fontSize: 12, fontWeight: 600, background: "transparent", border: `1px dashed ${C.borderLight}`, borderRadius: 8, color, cursor: "pointer", fontFamily: font } }, expanded ? "Show less" : "Show all " + kegs.length + " kegs")
      );
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null,
      renderDelBackup("del_fridge", "In Fridge Backups", C.blue, fridgeKegs, "\u2192 Backup", "backup"),
      renderDelBackup("del_backup", "Backup Fridge / Cool Room", C.purple, bfKegs, "\u2192 Fridge", "fridge")
    );
  })(), (() => {
    const unsetLevels = onHandTap.filter(k => !k.level).length;
    const canConfirm = unsetLevels === 0;
    return /*#__PURE__*/React.createElement("button", {
      onClick: () => canConfirm && scrollSafe(() => setConfirmed(!confirmed)),
      style: {
        width: "100%",
        marginTop: 14,
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: confirmed ? C.greenSoft : !canConfirm ? `${C.red}08` : `${C.accent}08`,
        border: `2px solid ${confirmed ? C.green : !canConfirm ? C.red : C.accent}`,
        borderRadius: 12,
        cursor: canConfirm ? "pointer" : "not-allowed",

        opacity: canConfirm ? 1 : .7
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: 28,
        height: 28,
        borderRadius: 8,
        flexShrink: 0,
        border: `2.5px solid ${confirmed ? C.green : !canConfirm ? C.red : C.accent}`,
        background: confirmed ? C.green : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }
    }, confirmed && /*#__PURE__*/React.createElement("span", {
      style: {
        color: "#fff",
        fontSize: 16,
        fontWeight: 700
      }
    }, "\u2713")), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        textAlign: "left"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 14,
        fontWeight: 700,
        color: confirmed ? C.green : !canConfirm ? C.red : C.accent
      }
    }, confirmed ? "Kegs on hand confirmed \u2713" : !canConfirm ? `Set levels for ${unsetLevels} keg${unsetLevels > 1 ? "s" : ""} on tap` : "Confirm kegs on hand"), !confirmed && canConfirm && /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 12,
        color: C.textTer,
        marginTop: 2
      }
    }, "Required before completing delivery")));
  })())),
  /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setDelivSection(delivSection === "gas" ? "" : "gas")), style: { width: "100%", padding: "14px 16px", marginBottom: 10, background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 12, cursor: "pointer", fontFamily: font, display: "flex", alignItems: "center", justifyContent: "space-between" } },
    /*#__PURE__*/React.createElement("span", { style: { fontSize: 14, fontWeight: 700, letterSpacing: 1, color: delivSection === "gas" ? C.accent : C.text } }, "GAS"),
    /*#__PURE__*/React.createElement("span", { style: { display: "flex", alignItems: "center", gap: 8 } },
      (() => { var _lk = getLatestGasKpa(customer.id); var _sess = !!formVal.gasConfirmedThisSession; if (_lk == null) return null; var _label = _lk === -1 ? "N/R" : _lk.toLocaleString() + " kPa"; return /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 700, color: _sess ? C.green : C.textSec } }, (_sess ? "\u2713 " : "") + _label); })(),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 14, color: C.textTer, transform: delivSection === "gas" ? "rotate(180deg)" : "none", transition: "transform .2s" } }, "\u25BC"))),
  /*#__PURE__*/React.createElement("div", { style: { display: delivSection === "gas" ? "block" : "none", marginBottom: 16 } },
    (savedEquipment[customer.id]?.cylOwner !== "ours") && /*#__PURE__*/React.createElement("div", { style: { marginBottom: 10, padding: "10px 14px", background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 10, fontSize: 12, color: "#F57F17", fontWeight: 500 } }, "\u26FD Record gas level \u2014 notify customer if they need to order from BOC."),
    /*#__PURE__*/React.createElement(GasGaugeCard, { custId: customer.id, gasLog: gasLog, setGasLog: setGasLog, syncToSheet: syncToSheet, customer: customer, equip: savedEquipment[customer.id], currentUser: currentUser, showToast: showToast, formVal: formVal, setFormVal: setFormVal })
  ),
  renderSignageBanners(),
  // --- Line clean prompt (green, only when due) — Done / Unable / Undo ---
  rce("div", { id: "lc-prompt" }, renderLineCleanBanner()),
  /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setDelivSection(delivSection === "notes" ? "" : "notes")), style: { width: "100%", padding: "14px 16px", marginBottom: 10, background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 12, cursor: "pointer", fontFamily: font, display: "flex", alignItems: "center", justifyContent: "space-between" } },
    /*#__PURE__*/React.createElement("span", { style: { fontSize: 14, fontWeight: 700, letterSpacing: 1, color: delivSection === "notes" ? C.accent : C.text } }, "NOTES"),
    /*#__PURE__*/React.createElement("span", { style: { display: "flex", alignItems: "center", gap: 8 } },
      ((noteSaved && deliveryNote) || actionDesc.trim()) && /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.green, fontWeight: 700 } }, "\u2713"),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 14, color: C.textTer, transform: delivSection === "notes" ? "rotate(180deg)" : "none", transition: "transform .2s" } }, "\u25BC"))),
  /*#__PURE__*/React.createElement("div", { style: { display: delivSection === "notes" ? "block" : "none", marginBottom: 16 } },
 /*#__PURE__*/React.createElement("div", { style: { marginTop: 12, padding: "12px 14px", border: `1.5px solid ${C.accent}`, borderRadius: 12, background: C.surface } },
  /*#__PURE__*/React.createElement("div", { style: { display: "flex", background: C.surfaceAlt, borderRadius: 8, padding: 3, marginBottom: 10 } },
    /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setNoteMode("note")), style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: noteMode === "note" ? 600 : 400, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: font, background: noteMode === "note" ? "#fff" : "transparent", color: noteMode === "note" ? C.text : C.textSec, boxShadow: noteMode === "note" ? "0 1px 2px rgba(0,0,0,0.06)" : "none" } }, "Note only"),
    /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setNoteMode("action")), style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: noteMode === "action" ? 600 : 400, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: font, background: noteMode === "action" ? "#fff" : "transparent", color: noteMode === "action" ? C.text : C.textSec, boxShadow: noteMode === "action" ? "0 1px 2px rgba(0,0,0,0.06)" : "none" } }, "Action item")),
  noteMode === "note" ? /*#__PURE__*/React.createElement(React.Fragment, null,
    !noteSaved ? /*#__PURE__*/React.createElement(React.Fragment, null,
      /*#__PURE__*/React.createElement("textarea", { ref: deliveryNoteRef, defaultValue: deliveryNote, placeholder: "Delivery note (optional)", rows: 2, onBlur: e => { var _v = e.target.value; scrollSafe(() => setDeliveryNote(_v)); }, style: { width: "100%", padding: "10px 12px", fontSize: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box" } }),
      _crmTagButtons(),
      /*#__PURE__*/React.createElement("button", { onClick: () => { const val = deliveryNoteRef.current ? deliveryNoteRef.current.value.trim() : ""; if (!val) { showToast("Enter a note"); return; } _sendCrmOnSave(val); if (stickyTagged) { setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, activeNote: val, noteCleared: false } : c) : prev); setCust(prev => prev && prev.id === customer.id ? { ...prev, activeNote: val, noteCleared: false } : prev); } _keepScroll(() => { setDeliveryNote(val); setNoteSaved(true); }); showToast(stickyTagged ? "Note saved + sticky" : "Note saved"); }, style: { width: "100%", padding: "10px", fontSize: 14, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: font, marginTop: 4 } }, "Save note")
    ) : /*#__PURE__*/React.createElement("div", { style: { padding: "10px 12px", background: C.greenSoft, border: `1px solid ${C.greenBorder}`, borderRadius: 10 } },
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, fontWeight: 600, color: C.green, margin: "0 0 4px" } }, "\u2705 Note saved"),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.text, margin: "0 0 8px" } }, deliveryNote),
      stickyTagged && /*#__PURE__*/React.createElement("p", { style: { fontSize: 11, color: "#F57F17", margin: "0 0 4px", fontWeight: 600 } }, "\uD83D\uDCCC Sticky note — shows on customer screen until cleared"),
      crmTagged && /*#__PURE__*/React.createElement("p", { style: { fontSize: 11, color: "#2563A8", margin: "0 0 4px", fontWeight: 600 } }, "\uD83D\uDCCB Logged to CRM"),
      !stickyTagged && !crmTagged && /*#__PURE__*/React.createElement("p", { style: { fontSize: 11, color: C.textTer, margin: "0 0 8px" } }, "Delivery note only"),
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8 } },
        /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setNoteSaved(false)), style: { flex: 1, padding: "6px", fontSize: 12, fontWeight: 600, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer", fontFamily: font, color: C.accent } }, "Edit"),
        /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setDeliveryNote(""); setNoteSaved(false); if (deliveryNoteRef.current) deliveryNoteRef.current.value = ""; }), style: { flex: 1, padding: "6px", fontSize: 12, fontWeight: 600, background: C.redSoft || "#FFF0ED", border: `1px solid ${C.red}33`, borderRadius: 8, cursor: "pointer", fontFamily: font, color: C.red } }, "Delete")))
  ) : /*#__PURE__*/React.createElement(React.Fragment, null,
    !actionDesc.trim() ? /*#__PURE__*/React.createElement(React.Fragment, null,
      /*#__PURE__*/React.createElement("textarea", { ref: actionDescRef, defaultValue: actionDesc, placeholder: "What needs to be done?", rows: 2, style: { width: "100%", padding: "10px 12px", fontSize: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box" } }),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: "8px 0 4px", fontWeight: 500 } }, "Assign to"),
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", background: C.surfaceAlt, borderRadius: 8, padding: 3, marginBottom: 8 } },
        /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setActionAssignee(currentUser?.name || "")), style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: (actionAssignee === (currentUser?.name || "")) ? 600 : 400, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: font, background: (actionAssignee === (currentUser?.name || "")) ? "#fff" : "transparent", color: (actionAssignee === (currentUser?.name || "")) ? C.text : C.textSec, boxShadow: (actionAssignee === (currentUser?.name || "")) ? "0 1px 2px rgba(0,0,0,0.06)" : "none" } }, (currentUser?.name || "Me"), (actionAssignee === (currentUser?.name || "")) ? " \u2713" : ""),
        /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setActionAssignee(currentUser?.name === "Brendan" ? "Aaron" : "Brendan")), style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: (actionAssignee !== (currentUser?.name || "")) ? 600 : 400, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: font, background: (actionAssignee !== (currentUser?.name || "")) ? "#fff" : "transparent", color: (actionAssignee !== (currentUser?.name || "")) ? C.text : C.textSec, boxShadow: (actionAssignee !== (currentUser?.name || "")) ? "0 1px 2px rgba(0,0,0,0.06)" : "none" } }, currentUser?.name === "Brendan" ? "Aaron" : "Brendan", (actionAssignee !== (currentUser?.name || "")) ? " \u2713" : "")),
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8, alignItems: "center", marginBottom: 6 } },
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: 0, fontWeight: 500 } }, "Due date"),
        actionDueDate ? /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: C.accent } }, new Date(actionDueDate + "T00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" })) : null,
        /*#__PURE__*/React.createElement("input", { type: "date", value: actionDueDate, onChange: e => setActionDueDate(e.target.value), style: { fontSize: 12, padding: "4px 8px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, color: C.text, fontFamily: font } })),
      /*#__PURE__*/React.createElement("button", { onClick: () => { const val = actionDescRef.current ? actionDescRef.current.value.trim() : ""; if (!val) { showToast("Enter a description"); return; } _keepScroll(() => setActionDesc(val)); showToast("Action item saved"); }, style: { width: "100%", padding: "10px", fontSize: 14, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: font, marginTop: 4 } }, "Save action item")
    ) : /*#__PURE__*/React.createElement("div", { style: { padding: "10px 12px", background: C.greenSoft, border: `1px solid ${C.greenBorder}`, borderRadius: 10 } },
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, fontWeight: 600, color: C.green, margin: "0 0 4px" } }, "\u2705 Action item saved"),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.text, margin: "0 0 2px" } }, actionDesc),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: "0 0 8px" } }, "Assigned to: ", actionAssignee, actionDueDate ? " \xB7 Due: " + new Date(actionDueDate + "T00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : ""),
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8 } },
        /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setActionDesc("")), style: { flex: 1, padding: "6px", fontSize: 12, fontWeight: 600, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer", fontFamily: font, color: C.accent } }, "Edit"),
        /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setActionDesc(""); setActionDueDate(""); setActionAssignee(currentUser?.name || ""); if (actionDescRef.current) actionDescRef.current.value = ""; }), style: { flex: 1, padding: "6px", fontSize: 12, fontWeight: 600, background: C.redSoft || "#FFF0ED", border: `1px solid ${C.red}33`, borderRadius: 8, cursor: "pointer", fontFamily: font, color: C.red } }, "Delete")))
  ))), (() => {
    const omoOk = /^OMO\d{4}$/.test(orderRef);
    const kegsOk = confirmed;
    const gasOk = !!formVal.gasConfirmedThisSession;
    const kegsAdded = delivered.length > 0;
    const _lc = lineCleanState();
    const lcOk = !_lc.required || _lc.ok;
    const allOk = omoOk && kegsOk && gasOk && kegsAdded && lcOk;
    const tick = ok => ok ? "\u2705" : "\u2B1C";
    // Sticky note, shown last thing before the driver completes the stop (Oct 2026).
    const _sticky = customer.activeNote && !customer.noteCleared ? customer.activeNote : "";
    const _stickyCard = _sticky ? /*#__PURE__*/React.createElement("div", {
      style: { marginTop: 12, padding: "12px 14px", background: "#FFF8E1", border: "1.5px solid #FFE082", borderRadius: 12 }
    }, /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 } },
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 14 } }, "\uD83D\uDCCC"),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 700, color: "#F57F17" } }, "Sticky note"),
      /*#__PURE__*/React.createElement("button", { onClick: () => {
        if (!confirm("Clear this sticky note?")) return;
        syncToSheet("clearCustomerNote", { data: { customerId: customer.id } });
        scrollSafe(() => {
          setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, activeNote: "", noteCleared: true } : c) : prev);
          setCust(prev => prev && prev.id === customer.id ? { ...prev, activeNote: "", noteCleared: true } : prev);
        });
        showToast("Sticky note cleared");
      }, style: { marginLeft: "auto", padding: "4px 10px", fontSize: 12, fontWeight: 600, background: "#fff", border: "1px solid #FFE082", borderRadius: 8, cursor: "pointer", fontFamily: font, color: "#B45309" } }, "Clear")),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 14, color: C.text, margin: 0, lineHeight: 1.5, whiteSpace: "pre-wrap" } }, _sticky)) : null;
    // Open action items sit with the sticky note. Information only — they never
    // block Complete Delivery.
    const _openActions = actionItems.filter(a => a.customerId === customer.id && a.status === "Open");
    const _actionCards = _openActions.map((a, ai) => /*#__PURE__*/React.createElement("div", {
      key: "dai-" + ai,
      style: { marginTop: 10, padding: "12px 14px", background: "#FFF0ED", border: "1.5px solid #F0B0A0", borderRadius: 12 }
    }, /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 } },
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 14 } }, "\u26A1"),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 700, color: "#C43E2A" } }, "Action item"),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer, marginLeft: "auto" } }, a.assignedTo || "", a.dueDate ? " \u00B7 due " + a.dueDate : "")),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 14, color: C.text, margin: "0 0 8px", lineHeight: 1.5, whiteSpace: "pre-wrap" } }, a.description),
      /*#__PURE__*/React.createElement("button", { onClick: () => {
        syncToSheet("completeActionItem", { data: { customerId: customer.id, description: a.description, completedBy: currentUser?.name || "" } });
        scrollSafe(() => setActionItems(prev => prev.map(item => item.customerId === customer.id && item.description === a.description && item.status === "Open" ? { ...item, status: "Complete", completedBy: currentUser?.name || "" } : item)));
        showToast("Action item completed");
      }, style: { padding: "6px 14px", fontSize: 12, fontWeight: 600, background: "#EEFBF3", border: "1px solid #B8E4CA", borderRadius: 8, cursor: "pointer", fontFamily: font, color: "#2D7A4F" } }, "\u2713 Mark complete")));
    return /*#__PURE__*/React.createElement(React.Fragment, null, _stickyCard, _actionCards, /*#__PURE__*/React.createElement("div", {
      style: { marginTop: 12, padding: "12px 14px", background: C.surface, border: `1.5px solid ${allOk ? C.greenBorder : C.border}`, borderRadius: 12 }
    }, /*#__PURE__*/React.createElement("div", { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 } }, "To complete delivery:"), /*#__PURE__*/React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 6 } },
      /*#__PURE__*/React.createElement("div", { style: { fontSize: 13, color: kegsAdded ? C.green : C.textSec, fontWeight: kegsAdded ? 600 : 400 } }, tick(kegsAdded), " Dropped kegs"),
      /*#__PURE__*/React.createElement("div", { style: { fontSize: 13, color: omoOk ? C.green : C.textSec, fontWeight: omoOk ? 600 : 400 } }, tick(omoOk), " Record OMO number"),
      /*#__PURE__*/React.createElement("div", { style: { fontSize: 13, color: kegsOk ? C.green : C.textSec, fontWeight: kegsOk ? 600 : 400 } }, tick(kegsOk), " Confirm Kegs on Hand"),
      /*#__PURE__*/React.createElement("div", { style: { fontSize: 13, color: gasOk ? C.green : C.textSec, fontWeight: gasOk ? 600 : 400 } }, tick(gasOk), " Record Gas Level"),
      _lc.required && /*#__PURE__*/React.createElement("div", { onClick: scrollToLineClean, style: { fontSize: 13, color: lcOk ? C.green : C.textSec, fontWeight: lcOk ? 600 : 400, cursor: "pointer" } }, tick(lcOk), " Line clean \u2014 Done or Unable")
    )), /*#__PURE__*/React.createElement("button", {
      onClick: allOk ? submitDelivery : undefined,
      disabled: !allOk,
      style: {
        width: "100%",
        padding: "14px",
        fontSize: 15,
        fontWeight: 700,
        boxShadow: "0 2px 8px rgba(0,0,0,.08)",
        background: allOk ? C.green : C.surfaceAlt,
        color: allOk ? "#fff" : C.textTer,
        border: "none",
        borderRadius: 12,
        cursor: allOk ? "pointer" : "not-allowed",
        fontFamily: font,
        opacity: allOk ? 1 : .6,
        marginTop: 10
      }
    }, "Complete Delivery"));
  })()), screen === "adjust" && customer && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
    label: customer.name,
    onClick: () => setScreen("customer")
  }), /*#__PURE__*/React.createElement("h2", {
    style: {
      fontFamily: font,
      fontSize: 22,
      fontWeight: 600,
      color: C.text,
      margin: "0 0 4px"
    }
  }, "Adjust Kegs"), /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 13,
      color: C.textTer,
      margin: "0 0 20px"
    }
  }, "Update levels, collect or move kegs"), onHandTap.length > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 16
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.green,
      letterSpacing: .5,
      textTransform: "uppercase",
      margin: "0 0 10px"
    }
  }, "On Tap"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: `repeat(${Math.min(onHandTap.length, 3)}, 1fr)`,
      gap: 6
    }
  }, onHandTap.map((k, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    style: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 3,
      padding: "8px 4px 6px",
      background: C.greenSoft,
      borderRadius: 10,
      border: `1px solid ${C.greenBorder}`
    }
  }, /*#__PURE__*/React.createElement(KegVisual, {
    size: k.size,
    product: k.product,
    level: k.level || "Medium",
    small: true
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: PRODUCT_COLOR[k.product] || C.text
    }
  }, PRODUCT_SHORT[k.product] || k.product), k.kegId ? /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: C.textTer,
      fontFamily: "monospace",
      letterSpacing: 0.3
    }
  }, k.kegId) : null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      color: C.textTer
    }
  }, k.size), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 2
    }
  }, TAP_LEVELS.map(lv => /*#__PURE__*/React.createElement("button", {
    key: lv,
    onClick: () => scrollSafe(() => setOnHandTap(p => p.map((x, j) => j === i ? {
      ...x,
      level: lv
    } : x))),
    style: {
      padding: "2px 5px",
      fontSize: 12,
      fontWeight: 600,
      background: k.level === lv ? levelColor(lv) : "transparent",
      color: k.level === lv ? "#fff" : C.textTer,
      border: `1px solid ${k.level === lv ? levelColor(lv) : C.borderLight}`,
      borderRadius: 4,
      cursor: "pointer",
      fontFamily: font
    }
  }, lv[0]))), /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      setCollected(prev => [...prev, {
        kegId: k.kegId,
        product: k.product,
        size: k.size
      }]);
      setOnHandTap(p => p.filter((_, j) => j !== i));
      showToast(`${PRODUCT_SHORT[k.product] || k.product} collected`);
    },
    style: {
      width: "100%",
      marginTop: 2,
      padding: "4px 0",
      fontSize: 12,
      fontWeight: 600,
      background: C.redSoft,
      border: `1px solid ${C.red}33`,
      borderRadius: 6,
      color: C.red,
      cursor: "pointer",
      fontFamily: font
    }
  }, "\xD7 Collect"), /*#__PURE__*/React.createElement("button", {
    onClick: () => untapToFridge(i),
    style: {
      width: "100%",
      marginTop: 2,
      padding: "4px 0",
      fontSize: 12,
      fontWeight: 600,
      background: C.blueSoft,
      border: `1px solid ${C.blueBorder}`,
      borderRadius: 6,
      color: C.blue,
      cursor: "pointer",
      fontFamily: font
    }
  }, "\u21A9 Untap"))))), (() => {
    const fridgeKegs = onHandBackup.map((k, i) => ({...k, _idx: i})).filter(k => k.location === "fridge");
    const bfKegs = onHandBackup.map((k, i) => ({...k, _idx: i})).filter(k => k.location !== "fridge");
    const MAX_SHOW = 4;
    const renderBackupSection = (sectionKey, title, color, kegs, moveLabel, moveLoc) => {
      if (kegs.length === 0) return null;
      const expanded = expandedBackup[sectionKey];
      const visible = expanded ? kegs : kegs.slice(0, MAX_SHOW);
      const hasMore = kegs.length > MAX_SHOW;
      return /*#__PURE__*/React.createElement("div", { style: { marginBottom: 12 } },
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, margin: "0 0 8px" } },
          /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color, letterSpacing: .5, textTransform: "uppercase", margin: 0 } }, title),
          hasMore && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 700, color: "#fff", background: color, borderRadius: 10, padding: "1px 7px", minWidth: 18, textAlign: "center" } }, kegs.length)
        ),
        /*#__PURE__*/React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 } },
          visible.map(k => /*#__PURE__*/React.createElement("div", { key: k._idx, style: { display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", background: C.surfaceAlt, borderRadius: 10, border: `1px solid ${C.borderLight}` } },
            /*#__PURE__*/React.createElement(KegVisual, { size: k.size, product: k.product, small: true, level: "Full" }),
            /*#__PURE__*/React.createElement("div", { style: { flex: 1, minWidth: 0 } },
              /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, fontWeight: 600, color: PRODUCT_COLOR[k.product] || C.textSec } }, PRODUCT_SHORT[k.product] || k.product),
              k.kegId ? /*#__PURE__*/React.createElement("div", { style: { fontSize: 10, color: C.textTer, fontFamily: "monospace", letterSpacing: 0.3 } }, k.kegId) : null,
              /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: C.textTer } }, k.size),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" } },
                /*#__PURE__*/React.createElement("button", { onClick: () => promoteToTap(k._idx), style: { fontSize: 11, fontWeight: 600, padding: "2px 6px", background: C.greenSoft, border: `1px solid ${C.greenBorder}`, borderRadius: 4, color: C.green, cursor: "pointer", fontFamily: font } }, "\u2192 Tap"),
                moveLabel && /*#__PURE__*/React.createElement("button", { onClick: () => scrollSafe(() => { setOnHandBackup(p => p.map((x, j) => j === k._idx ? {...x, location: moveLoc} : x)); showToast("Moved"); }), style: { fontSize: 11, fontWeight: 600, padding: "2px 6px", background: C.blueSoft, border: `1px solid ${C.blueBorder}`, borderRadius: 4, color: C.blue, cursor: "pointer", fontFamily: font } }, moveLabel),
                /*#__PURE__*/React.createElement("button", { onClick: () => { setCollected(prev => [...prev, { kegId: k.kegId, product: k.product, size: k.size }]); setOnHandBackup(p => p.filter((_, j) => j !== k._idx)); showToast(`${PRODUCT_SHORT[k.product] || k.product} collected`); }, style: { fontSize: 11, fontWeight: 600, padding: "2px 6px", background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 4, color: C.red, cursor: "pointer", fontFamily: font } }, "\xD7 Collect")
              )
            )
          ))
        ),
        hasMore && /*#__PURE__*/React.createElement("button", { onClick: () => { setExpandedBackup(p => ({...p, [sectionKey]: !p[sectionKey]})); }, style: { width: "100%", marginTop: 6, padding: "6px", fontSize: 12, fontWeight: 600, background: "transparent", border: `1px dashed ${C.borderLight}`, borderRadius: 8, color, cursor: "pointer", fontFamily: font } }, expanded ? "Show less" : "Show all " + kegs.length + " kegs")
      );
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null,
      renderBackupSection("fridge", "In Fridge Backups", C.blue, fridgeKegs, "\u2192 Backup", "backup"),
      renderBackupSection("backup", "Backup Fridge / Cool Room", C.purple, bfKegs, "\u2192 Fridge", "fridge")
    );
  })(), collected.length > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 16
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.accent,
      letterSpacing: .5,
      textTransform: "uppercase",
      margin: "0 0 8px"
    }
  }, "Collected"), collected.map((k, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    style: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 12px",
      background: C.accentSoft,
      border: `1px solid ${C.accentBorder}`,
      borderRadius: 10,
      marginBottom: 4
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 13,
      fontWeight: 600,
      color: C.text
    }
  }, k.kegId), k.product && /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 12,
      color: C.textTer
    }
  }, " \xB7 ", PRODUCT_SHORT[k.product], " \xB7 ", k.size))))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 16
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: C.purple,
      letterSpacing: .5,
      textTransform: "uppercase",
      margin: "0 0 8px"
    }
  }, "Add Kegs"), /*#__PURE__*/React.createElement("div", {
    style: { display: "flex", gap: 6, marginBottom: 6 }
  }, /*#__PURE__*/React.createElement("input", {
    ref: scanInputRef,
    type: "text", defaultValue: scanInput, onBlur: e => { var _v = e.target.value; scrollSafe(() => setScanInput(_v)); }, placeholder: "Keg ID",
    style: { width: 80, padding: "10px 10px", fontSize: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: font, boxSizing: "border-box" }
  }), /*#__PURE__*/React.createElement("div", { style: { flex: 1 } },
    /*#__PURE__*/React.createElement(Select, { value: scanProduct, onChange: setScanProduct, options: PRODUCTS, placeholder: "Product" })
  ), /*#__PURE__*/React.createElement("div", { style: { width: 60 } },
    /*#__PURE__*/React.createElement(Select, { value: scanSize, onChange: setScanSize, options: KEG_SIZES, placeholder: "Size" })
  )), /*#__PURE__*/React.createElement("div", {
    style: { display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }
  }, /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: C.textSec } }, "Qty"),
  /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}` } },
    /*#__PURE__*/React.createElement("button", { onClick: () => { if (scanQtyRef.current > 1) scanQtyRef.current--; if (scanQtyDisplayRef.current) scanQtyDisplayRef.current.textContent = scanQtyRef.current; }, style: { width: 36, height: 36, fontSize: 18, fontWeight: 700, background: C.surface, border: "none", color: C.text, cursor: "pointer", fontFamily: font } }, "\u2212"),
    /*#__PURE__*/React.createElement("div", { ref: scanQtyDisplayRef, style: { width: 40, height: 36, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: C.text, background: C.surfaceAlt } }, scanQtyRef.current),
    /*#__PURE__*/React.createElement("button", { onClick: () => { scanQtyRef.current++; if (scanQtyDisplayRef.current) scanQtyDisplayRef.current.textContent = scanQtyRef.current; }, style: { width: 36, height: 36, fontSize: 18, fontWeight: 700, background: C.surface, border: "none", color: C.text, cursor: "pointer", fontFamily: font } }, "+")
  ), scanQty > 1 && scanInput.trim() && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer, flex: 1 } }, scanInput.trim().toUpperCase(), " \u2192 ", incrementKegId(scanInput.trim().toUpperCase(), scanQty - 1))
  ), /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 6 } },
  /*#__PURE__*/React.createElement("button", {
    onClick: () => { const b = (scanInputRef.current ? scanInputRef.current.value : scanInput).trim().toUpperCase(); if (!b || !scanProduct) return; const nk = []; const _qty = scanQtyRef.current || 1;
    for (let i = 0; i < _qty; i++) { nk.push({ kegId: scanQty === 1 ? b : incrementKegId(b, i), product: scanProduct, size: scanSize, level: "Full" }); } setOnHandTap(p => [...p, ...nk]); const nxt = incrementKegId(b, _aq); setScanInput(nxt); if (scanInputRef.current) scanInputRef.current.value = nxt; scanQtyRef.current = 1; setScanQty(1); if (scanQtyDisplayRef.current) scanQtyDisplayRef.current.textContent = "1"; showToast(_aq + "\xD7 added on tap"); },
    disabled: !scanInput.trim() || !scanProduct,
    style: { flex: 1, padding: "10px", fontSize: 12, fontWeight: 600, background: scanInput.trim() && scanProduct ? C.greenSoft : C.surfaceAlt, color: scanInput.trim() && scanProduct ? C.green : C.textTer, border: `1.5px solid ${scanInput.trim() && scanProduct ? C.greenBorder : C.borderLight}`, borderRadius: 10, cursor: "pointer", fontFamily: font }
  }, "+ Tap"),
  /*#__PURE__*/React.createElement("button", {
    onClick: () => { const b = (scanInputRef.current ? scanInputRef.current.value : scanInput).trim().toUpperCase(); if (!b || !scanProduct) return; const nk = []; const _qty = scanQtyRef.current || 1;
    for (let i = 0; i < _qty; i++) { nk.push({ kegId: scanQty === 1 ? b : incrementKegId(b, i), product: scanProduct, size: scanSize, location: "backup" }); } setOnHandBackup(p => [...p, ...nk]); const nxt = incrementKegId(b, _aq); setScanInput(nxt); if (scanInputRef.current) scanInputRef.current.value = nxt; scanQtyRef.current = 1; setScanQty(1); if (scanQtyDisplayRef.current) scanQtyDisplayRef.current.textContent = "1"; showToast(_aq + "\xD7 added to backup"); },
    disabled: !scanInput.trim() || !scanProduct,
    style: { flex: 1, padding: "10px", fontSize: 12, fontWeight: 600, background: scanInput.trim() && scanProduct ? `${C.purple}10` : C.surfaceAlt, color: scanInput.trim() && scanProduct ? C.purple : C.textTer, border: `1.5px solid ${scanInput.trim() && scanProduct ? C.purple : C.borderLight}`, borderRadius: 10, cursor: "pointer", fontFamily: font }
  }, "+ Backup"))), (() => {
    const _n = s => String(s || "").toUpperCase().trim();
    const _tCol = C.cyan;
    const _digits = (transferDigits || "").trim().replace(/\D/g, "");
    const _fullId = _digits ? ("KEG-" + (transferSize === "9L" ? "09" : "18") + "-" + _digits.padStart(3, "0")).toUpperCase() : "";
    const _reg = _fullId ? kegRegister.find(r => _n(r.kegId) === _fullId) : null;
    const _regStatus = _reg ? String(_reg.status || "").trim() : "";
    const _regLoc = _reg ? String(_reg.location || "").trim() : "";
    const _isHere = _regLoc.toLowerCase() === String(customer.name || "").trim().toLowerCase();
    const _canIn = !!(_reg && _regStatus === "At Customer" && _regLoc && !_isHere);
    const _reasonOk = (transferReason || "").trim().length > 0;

    // Transfer-out candidates: everything currently on hand here
    const _outKegs = [].concat(
      onHandTap.map(k => Object.assign({}, k, { _where: "tap" })),
      onHandBackup.map(k => Object.assign({}, k, { _where: "backup" }))
    ).filter(k => k.kegId);
    const _outSel = _outKegs.find(k => _n(k.kegId) === _n(transferOutKeg));
    const _destCust = activeCustomers.find(c => String(c.id) === String(transferDest));
    const _canOut = !!(_outSel && _destCust);

    // Panel describing what the register knows about the typed keg
    const _inPanel = () => {
      if (!_digits) return null;
      let msg, col, bg, bd;
      if (!_reg) { msg = "\u26A0 " + _fullId + " is not in the keg register"; col = C.red; bg = C.redSoft; bd = "#F0B0A0"; }
      else if (_isHere) { msg = "\u2713 " + _fullId + " is already logged here \u2014 nothing to transfer"; col = C.green; bg = C.greenSoft; bd = C.greenBorder; }
      else if (_regStatus !== "At Customer") { msg = "\u26A0 Register says \u201C" + (_regStatus || "blank") + "\u201D \u2014 that\u2019s a delivery, not a transfer"; col = C.accent; bg = C.accentSoft; bd = C.accentBorder; }
      else if (!_regLoc) { msg = "\u26A0 No source customer recorded against this keg"; col = C.red; bg = C.redSoft; bd = "#F0B0A0"; }
      else { msg = null; }
      if (msg) return /*#__PURE__*/React.createElement("div", { style: { marginTop: 8, padding: "9px 12px", background: bg, border: `1px solid ${bd}`, borderRadius: 10, fontSize: 12, fontWeight: 600, color: col } }, msg);
      return /*#__PURE__*/React.createElement("div", { style: { marginTop: 8, padding: "10px 12px", background: `${_tCol}0A`, border: `1.5px solid ${_tCol}`, borderRadius: 10, display: "flex", alignItems: "center", gap: 10 } },
        /*#__PURE__*/React.createElement(KegVisual, { size: _reg.size || transferSize, product: _reg.product, small: true, level: "Full" }),
        /*#__PURE__*/React.createElement("div", { style: { flex: 1, minWidth: 0 } },
          /*#__PURE__*/React.createElement("div", { style: { fontSize: 13, fontWeight: 700, color: C.text } }, _reg.kegId),
          /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: PRODUCT_COLOR[_reg.product] || C.textSec, fontWeight: 600 } }, (PRODUCT_SHORT[_reg.product] || _reg.product || "\u2014"), " \xB7 ", _reg.size || transferSize),
          /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, color: _tCol, fontWeight: 600, marginTop: 2 } }, "Currently at: ", _regLoc),
          _reg.lastMovement ? /*#__PURE__*/React.createElement("div", { style: { fontSize: 11, color: C.textTer } }, "Since ", _reg.lastMovement) : null));
    };

    return /*#__PURE__*/React.createElement("div", { style: { marginBottom: 16 } },
      /*#__PURE__*/React.createElement("button", {
        onClick: () => _keepScroll(() => setTransferOpen(!transferOpen)),
        style: { width: "100%", padding: "12px 14px", background: transferOpen ? `${_tCol}0A` : C.surface, border: `1.5px solid ${transferOpen ? _tCol : C.border}`, borderRadius: 12, cursor: "pointer", fontFamily: font, display: "flex", alignItems: "center", justifyContent: "space-between" }
      },
        /*#__PURE__*/React.createElement("span", { style: { display: "flex", alignItems: "center", gap: 8 } },
          /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, letterSpacing: .5, textTransform: "uppercase", color: transferOpen ? _tCol : C.textSec } }, "Transfer keg"),
          /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer } }, "to / from another customer")),
        /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textTer, transform: transferOpen ? "rotate(180deg)" : "none", transition: "transform .2s" } }, "\u25BC")),

      transferOpen && /*#__PURE__*/React.createElement("div", { style: { marginTop: 8, padding: "14px", background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 12 } },
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 0, borderRadius: 10, overflow: "hidden", border: `1.5px solid ${C.border}`, marginBottom: 12 } },
          [["in", "Keg is here \u2014 shouldn\u2019t be"], ["out", "Keg\u2019s gone \u2014 should be here"]].map(m =>
            /*#__PURE__*/React.createElement("button", {
              key: m[0],
              onClick: () => _keepScroll(() => { setTransferMode(m[0]); setTransferReason(""); }),
              style: { flex: 1, padding: "9px 4px", fontSize: 11, fontWeight: transferMode === m[0] ? 700 : 500, fontFamily: font, color: transferMode === m[0] ? "#fff" : C.textSec, background: transferMode === m[0] ? _tCol : C.surface, border: "none", borderRight: m[0] === "in" ? `1px solid ${C.border}` : "none", cursor: "pointer", lineHeight: 1.3 }
            }, m[1]))),

        // ── TRANSFER IN ──
        transferMode === "in" && /*#__PURE__*/React.createElement(React.Fragment, null,
          /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textTer, margin: "0 0 8px", lineHeight: 1.45 } }, "Scan or key the keg. The register knows where it came from \u2014 both customers are corrected in one go."),
          /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 6, alignItems: "center" } },
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 15, fontWeight: 700, color: C.textSec, letterSpacing: .3, whiteSpace: "nowrap" } }, "KEG-"),
            /*#__PURE__*/React.createElement("div", { style: { display: "flex", borderRadius: 8, overflow: "hidden", border: `1.5px solid ${C.border}`, flexShrink: 0 } },
              ["18L", "9L"].map(sz => /*#__PURE__*/React.createElement("button", {
                key: sz,
                onClick: () => _keepScroll(() => setTransferSize(sz)),
                style: { padding: "8px 10px", fontSize: 14, fontWeight: 700, border: "none", borderLeft: sz === "9L" ? `1px solid ${C.border}` : "none", cursor: "pointer", fontFamily: font, background: transferSize === sz ? _tCol : C.surface, color: transferSize === sz ? "#fff" : C.textSec }
              }, sz === "18L" ? "18" : "09"))),
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 15, fontWeight: 700, color: C.textSec } }, "-"),
            /*#__PURE__*/React.createElement("input", {
              ref: transferInputRef,
              type: "tel", inputMode: "numeric", pattern: "[0-9]*", maxLength: 3,
              defaultValue: transferDigits || "",
              onInput: e => { var _y = window.pageYOffset || document.documentElement.scrollTop || 0; e.target.value = e.target.value.replace(/\D/g, "").slice(0, 3); setTransferDigits(e.target.value); requestAnimationFrame(function() { window.scrollTo(0, _y); }); },
              placeholder: "001",
              style: { width: 62, padding: "10px 8px", fontSize: 17, fontWeight: 700, textAlign: "center", letterSpacing: 2, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: font, boxSizing: "border-box" }
            })),
          _inPanel(),
          _canIn && /*#__PURE__*/React.createElement("div", { style: { marginTop: 10 } },
            /*#__PURE__*/React.createElement(TextArea, { value: transferReason, onChange: setTransferReason, placeholder: "Why did it move? (required)", rows: 2 }),
            /*#__PURE__*/React.createElement("button", {
              onClick: () => { if (_reasonOk && !transferBusy) doTransfer({ kegId: _reg.kegId, toCustomerId: customer.id, toCustomerName: customer.name, product: _reg.product, size: _reg.size || transferSize, reason: transferReason.trim() }); },
              disabled: !_reasonOk || transferBusy,
              style: { width: "100%", marginTop: 8, padding: "13px", fontSize: 14, fontWeight: 700, fontFamily: font, background: (_reasonOk && !transferBusy) ? _tCol : C.surfaceAlt, color: (_reasonOk && !transferBusy) ? "#fff" : C.textTer, border: "none", borderRadius: 10, cursor: (_reasonOk && !transferBusy) ? "pointer" : "not-allowed" }
            }, transferBusy ? "Transferring\u2026" : "Transfer in from " + _regLoc))),

        // ── TRANSFER OUT ──
        transferMode === "out" && /*#__PURE__*/React.createElement(React.Fragment, null,
          /*#__PURE__*/React.createElement("div", { style: { padding: "9px 12px", background: C.accentSoft, border: `1px solid ${C.accentBorder}`, borderRadius: 10, fontSize: 12, fontWeight: 500, color: C.accent, lineHeight: 1.45, marginBottom: 10 } }, "You\u2019re recording where a keg went without seeing it. If you\u2019ll be at the other site soon, transfer it in from there instead \u2014 that corrects both sides and you\u2019ll have the keg in your hand."),
          _outKegs.length === 0
            ? /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.textTer, textAlign: "center", padding: "12px 0" } }, "No kegs on hand here to transfer")
            : /*#__PURE__*/React.createElement(React.Fragment, null,
              /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, margin: "0 0 6px" } }, "Which keg?"),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 } },
                _outKegs.map((k, i) => {
                  const sel = _n(transferOutKeg) === _n(k.kegId);
                  const kc = PRODUCT_COLOR[k.product] || C.textTer;
                  return /*#__PURE__*/React.createElement("button", {
                    key: "tk" + i,
                    onClick: () => _keepScroll(() => setTransferOutKeg(sel ? "" : k.kegId)),
                    style: { padding: "7px 11px", fontSize: 12, fontWeight: sel ? 700 : 500, background: sel ? `${kc}18` : C.bg, color: sel ? kc : C.textSec, border: `1.5px solid ${sel ? kc : C.borderLight}`, borderRadius: 20, cursor: "pointer", fontFamily: font, textAlign: "left" }
                  },
                    /*#__PURE__*/React.createElement("span", { style: { fontWeight: 700 } }, k.kegId),
                    /*#__PURE__*/React.createElement("span", { style: { color: sel ? kc : C.textTer, fontWeight: 500 } }, " \xB7 ", PRODUCT_SHORT[k.product] || k.product || "\u2014", " \xB7 ", k._where));
                })),
              /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, margin: "0 0 6px" } }, "Moved to which customer?"),
              /*#__PURE__*/React.createElement("select", {
                value: transferDest,
                onChange: e => { var _y = window.pageYOffset || document.documentElement.scrollTop || 0; setTransferDest(e.target.value); requestAnimationFrame(function() { window.scrollTo(0, _y); }); },
                style: { width: "100%", padding: "10px 12px", fontSize: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: transferDest ? C.text : C.textTer, fontFamily: font, boxSizing: "border-box", marginBottom: 10 }
              },
                /*#__PURE__*/React.createElement("option", { value: "" }, "Select customer\u2026"),
                activeCustomers.filter(c => String(c.id) !== String(customer.id)).slice().sort((a, b) => a.name.localeCompare(b.name)).map(c =>
                  /*#__PURE__*/React.createElement("option", { key: "td" + c.id, value: c.id }, c.name, " \u2014 ", c.suburb))),
              _canOut && /*#__PURE__*/React.createElement(React.Fragment, null,
                /*#__PURE__*/React.createElement(TextArea, { value: transferReason, onChange: setTransferReason, placeholder: "Why did it move, and who told you? (required)", rows: 2 }),
                /*#__PURE__*/React.createElement("button", {
                  onClick: () => { if (_reasonOk && !transferBusy) doTransfer({ kegId: _outSel.kegId, toCustomerId: _destCust.id, toCustomerName: _destCust.name, product: _outSel.product, size: _outSel.size, reason: transferReason.trim(), expectFromCustomerId: customer.id }); },
                  disabled: !_reasonOk || transferBusy,
                  style: { width: "100%", marginTop: 8, padding: "13px", fontSize: 14, fontWeight: 700, fontFamily: font, background: (_reasonOk && !transferBusy) ? _tCol : C.surfaceAlt, color: (_reasonOk && !transferBusy) ? "#fff" : C.textTer, border: "none", borderRadius: 10, cursor: (_reasonOk && !transferBusy) ? "pointer" : "not-allowed" }
                }, transferBusy ? "Transferring\u2026" : "Transfer out to " + _destCust.name))))));
  })(), /*#__PURE__*/React.createElement(TextArea, {
    value: adjustReason,
    onChange: setAdjustReason,
    placeholder: "Reason for adjustment (e.g. maintenance visit, level check)",
    rows: 2
  }), /*#__PURE__*/React.createElement("button", {
    onClick: submitAdjust,
    style: {
      width: "100%",
      padding: "14px",
      fontSize: 14,
      fontWeight: 600,
      position: "sticky",
      bottom: 20,
      zIndex: 10,
      boxShadow: "0 -4px 20px rgba(0,0,0,.1)",
      background: C.blue,
      color: "#fff",
      border: "none",
      borderRadius: 12,
      cursor: "pointer",
      fontFamily: font
    }
  }, "Save Adjustment")), screen === "corflute" && customer && (() => {
    const ProductPicker = ({
      current,
      onSelect,
      label
    }) => /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 6
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        margin: "0 0 6px"
      }
    }, label), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        flexWrap: "wrap",
        gap: 4
      }
    }, PRODUCTS.map(p => /*#__PURE__*/React.createElement("button", {
      key: p,
      onClick: () => onSelect(p),
      style: {
        padding: "6px 10px",
        fontSize: 13,
        fontWeight: 600,
        background: current === p ? `${PRODUCT_COLOR[p]}15` : C.surface,
        color: current === p ? PRODUCT_COLOR[p] : C.textTer,
        border: `1.5px solid ${current === p ? PRODUCT_COLOR[p] : C.borderLight}`,
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: font
      }
    }, PRODUCT_SHORT[p])), /*#__PURE__*/React.createElement("button", {
      onClick: () => onSelect(""),
      style: {
        padding: "6px 10px",
        fontSize: 13,
        fontWeight: 600,
        background: !current ? C.surfaceAlt : C.surface,
        color: C.textTer,
        border: `1.5px dashed ${C.border}`,
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: font
      }
    }, "None")));
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
      label: signReturn === "delivery" ? "Delivery" : customer.name,
      onClick: leaveSignage
    }), /*#__PURE__*/React.createElement("h2", {
      style: {
        fontFamily: font,
        fontSize: 22,
        fontWeight: 600,
        color: C.text,
        margin: "0 0 4px"
      }
    }, "Corflute / A-Frame"), (() => {
      const equip = savedEquipment[customer.id];
      const frameCount = equip?.aframes || 1;
      // Single-sided venues only use face A of each frame.
      const corSingle = String(corDisplayMode || "").indexOf("single") === 0;
      const autoNext = {
        "1A": corSingle ? (frameCount === 2 ? "2A" : null) : "1B",
        "1B": frameCount === 2 ? "2A" : null,
        "2A": corSingle ? null : "2B",
        "2B": null
      };
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 13,
          color: C.textTer,
          margin: "0 0 16px"
        }
      }, "Tap to change designs", frameCount === 2 ? " · 2 A-frames" : ""), /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 13,
          fontWeight: 600,
          color: C.green,
          letterSpacing: .5,
          textTransform: "uppercase",
          margin: "0 0 8px"
        }
      }, frameCount === 2 ? "A-Frame 1" : "On Display"), /*#__PURE__*/React.createElement(AFrameVisual, {
        sideA: corSideA,
        sideB: corSideB,
        productsA: corSideA,
        productsB: corSideB,
        activeA: corSelecting === "1A",
        activeB: corSelecting === "1B",
        singleSide: corSingle,
        onTapA: () => setCorSelecting(corSelecting === "1A" ? null : "1A"),
        onTapB: () => setCorSelecting(corSelecting === "1B" ? null : "1B")
      }), corSelecting === "1A" && /*#__PURE__*/React.createElement(ProductPicker, {
        current: corSideA,
        onSelect: v => {
          setCorSideA(v);
          setCorSelecting(autoNext["1A"]);
        },
        label: corSingle ? "Frame 1" : "Frame 1 \u2014 Side A"
      }), corSelecting === "1B" && !corSingle && /*#__PURE__*/React.createElement(ProductPicker, {
        current: corSideB,
        onSelect: v => {
          setCorSideB(v);
          setCorSelecting(autoNext["1B"]);
        },
        label: "Frame 1 \u2014 Side B"
      }), frameCount === 2 && /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 16
        }
      }, /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 13,
          fontWeight: 600,
          color: C.green,
          letterSpacing: .5,
          textTransform: "uppercase",
          margin: "0 0 8px"
        }
      }, "A-Frame 2"), /*#__PURE__*/React.createElement(AFrameVisual, {
        sideA: cor2SideA,
        sideB: cor2SideB,
        productsA: cor2SideA,
        productsB: cor2SideB,
        activeA: corSelecting === "2A",
        activeB: corSelecting === "2B",
        singleSide: corSingle,
        onTapA: () => setCorSelecting(corSelecting === "2A" ? null : "2A"),
        onTapB: () => setCorSelecting(corSelecting === "2B" ? null : "2B")
      }), corSelecting === "2A" && /*#__PURE__*/React.createElement(ProductPicker, {
        current: cor2SideA,
        onSelect: v => {
          setCor2SideA(v);
          setCorSelecting(autoNext["2A"]);
        },
        label: corSingle ? "Frame 2" : "Frame 2 \u2014 Side A"
      }), corSelecting === "2B" && !corSingle && /*#__PURE__*/React.createElement(ProductPicker, {
        current: cor2SideB,
        onSelect: v => {
          setCor2SideB(v);
          setCorSelecting(autoNext["2B"]);
        },
        label: "Frame 2 \u2014 Side B"
      })), /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 13,
          fontWeight: 600,
          color: C.blue,
          letterSpacing: .5,
          textTransform: "uppercase",
          margin: "20px 0 8px"
        }
      }, "How this venue displays"), /*#__PURE__*/React.createElement("p", {
        style: { fontSize: 12.5, color: C.textTer, margin: "0 0 10px", lineHeight: 1.45 }
      }, "Tells the runs planner how many corflute to send for a new limited. Without this it guesses from the boards in the frame, so a misplaced board makes the next delivery short."),
      /*#__PURE__*/React.createElement("div", {
        style: { display: "flex", gap: 6, marginBottom: corSingle ? 10 : 18 }
      }, [
        { k: "double", label: "Double", hint: "Limited both faces" },
        { k: "split",  label: "Split",  hint: "Limited + Classic" },
        { k: "single", label: "Single", hint: "One face only" },
        { k: "",       label: "Auto",   hint: "Work it out" }
      ].map(function(opt) {
        var on = opt.k === "single" ? corSingle : (corDisplayMode || "") === opt.k;
        return /*#__PURE__*/React.createElement("button", {
          key: opt.k || "auto",
          // Re-tapping Single keeps whichever single option is already set.
          onClick: function() { if (opt.k === "single" && corSingle) return; setCorDisplayMode(opt.k); },
          style: {
            flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer",
            fontFamily: font, textAlign: "center",
            background: on ? C.greenSoft : C.surfaceAlt,
            color: on ? C.green : C.textTer,
            border: "1.5px solid " + (on ? C.greenBorder : C.borderLight)
          }
        }, /*#__PURE__*/React.createElement("div", {
          style: { fontSize: 13, fontWeight: 700 }
        }, (on ? "\u2713 " : "") + opt.label), /*#__PURE__*/React.createElement("div", {
          style: { fontSize: 10.5, marginTop: 2, opacity: .8 }
        }, opt.hint));
      })), corSingle && /*#__PURE__*/React.createElement("div", {
        style: { marginBottom: 18 }
      }, /*#__PURE__*/React.createElement("p", {
        style: { fontSize: 12.5, color: C.textTer, margin: "0 0 8px", lineHeight: 1.45 }
      }, "What goes on the one face?"), /*#__PURE__*/React.createElement("div", {
        style: { display: "flex", gap: 8 }
      }, [
        { k: "single-classic", label: "Classic always", hint: "No limited corflute sent" },
        { k: "single-limited", label: "Change with limiteds", hint: "One board per new limited" }
      ].map(function(opt) {
        var on = corDisplayMode === opt.k;
        return /*#__PURE__*/React.createElement("button", {
          key: opt.k,
          onClick: function() { setCorDisplayMode(opt.k); },
          style: {
            flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer",
            fontFamily: font, textAlign: "center",
            background: on ? C.greenSoft : C.surfaceAlt,
            color: on ? C.green : C.textTer,
            border: "1.5px solid " + (on ? C.greenBorder : C.borderLight)
          }
        }, /*#__PURE__*/React.createElement("div", {
          style: { fontSize: 13, fontWeight: 700 }
        }, (on ? "\u2713 " : "") + opt.label), /*#__PURE__*/React.createElement("div", {
          style: { fontSize: 10.5, marginTop: 2, opacity: .8 }
        }, opt.hint));
      })), corDisplayMode === "single" && /*#__PURE__*/React.createElement("p", {
        style: { fontSize: 12, color: C.accent, fontWeight: 600, margin: "8px 0 0" }
      }, "Pick one \u2014 until then the planner sends one board per new limited.")), /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 13,
          fontWeight: 600,
          color: C.blue,
          letterSpacing: .5,
          textTransform: "uppercase",
          margin: "20px 0 10px"
        }
      }, "Backup Corflute"), corBackups.map(function(bv, bi) {
        return /*#__PURE__*/React.createElement("div", {
          key: "bk-" + bi,
          style: {
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            background: PRODUCT_COLOR[bv] ? PRODUCT_COLOR[bv] + "08" : C.surfaceAlt,
            border: "1.5px solid " + (PRODUCT_COLOR[bv] || C.border),
            borderRadius: 10,
            marginBottom: 6
          }
        }, /*#__PURE__*/React.createElement("span", {
          style: { flex: 1, fontSize: 14, fontWeight: 700, color: PRODUCT_COLOR[bv] || C.text }
        }, PRODUCT_SHORT[bv] || bv), /*#__PURE__*/React.createElement("button", {
          onClick: function() { setCorBackups(function(p) { return p.filter(function(_, i) { return i !== bi; }); }); },
          style: { width: 28, height: 28, borderRadius: 14, border: "none", background: C.red + "12", color: C.red, fontSize: 16, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1, fontFamily: font }
        }, "\u00D7"));
      }), corBackupAdding ? /*#__PURE__*/React.createElement(ProductPicker, {
        current: "",
        onSelect: function(v) {
          if (v) setCorBackups(function(p) { return p.concat([v]); });
          setCorBackupAdding(false);
        },
        label: "Select product for backup board"
      }) : /*#__PURE__*/React.createElement("button", {
        onClick: function() { setCorBackupAdding(true); },
        style: { width: "100%", padding: "12px 16px", fontSize: 13, fontWeight: 600, background: C.surfaceAlt, color: C.blue, border: "1.5px dashed " + C.blue + "40", borderRadius: 10, cursor: "pointer", fontFamily: font, marginBottom: 10 }
      }, "+ Add Backup"), /*#__PURE__*/React.createElement("button", {
        onClick: function() {
          var _single = String(corDisplayMode || "").indexOf("single") === 0;
          var data = {
            sideA: corSideA,
            sideB: _single ? "" : corSideB,
            frame2A: cor2SideA,
            frame2B: _single ? "" : cor2SideB,
            backups: corBackups,
            displayMode: corDisplayMode || ""
          };
          addLog("corflute", data);
          setSavedCorflute(function(prev) { var n = {}; for(var k in prev) n[k]=prev[k]; n[customer.id] = data; return n; });
          syncToSheet("saveCorflute", {
            customerId: customer.id,
            data: {
              ...data,
              cards: savedTapCards[customer.id]?.cards || [],
              backup: savedTapCards[customer.id]?.backup || []
            }
          });
          showToast("Corflute updated");
          leaveSignage();
        },
        style: {
          width: "100%",
          padding: "14px",
          fontSize: 14,
          fontWeight: 600,
          position: "sticky",
          bottom: 20,
          zIndex: 10,
          boxShadow: "0 -4px 20px rgba(0,0,0,.1)",
          background: corSideA || corSideB ? C.green : C.surfaceAlt,
          color: corSideA || corSideB ? "#fff" : C.textTer,
          border: "none",
          borderRadius: 12,
          cursor: "pointer",
          fontFamily: font
        }
      }, "Save Corflute"));
    })());
  })(), screen === "tapcard" && customer && (() => {
    const CardPicker = ({
      index,
      current,
      onSelect,
      label
    }) => /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "10px",
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        marginTop: 6,
        marginBottom: 8
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        margin: "0 0 6px"
      }
    }, label), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        flexWrap: "wrap",
        gap: 4
      }
    }, PRODUCTS.map(p => /*#__PURE__*/React.createElement("button", {
      key: p,
      onClick: () => onSelect(p),
      style: {
        padding: "6px 10px",
        fontSize: 13,
        fontWeight: 600,
        background: current === p ? `${PRODUCT_COLOR[p]}15` : C.surfaceAlt,
        color: current === p ? PRODUCT_COLOR[p] : C.textTer,
        border: `1.5px solid ${current === p ? PRODUCT_COLOR[p] : C.borderLight}`,
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: font
      }
    }, PRODUCT_SHORT[p])), /*#__PURE__*/React.createElement("button", {
      onClick: () => onSelect(""),
      style: {
        padding: "6px 10px",
        fontSize: 13,
        fontWeight: 600,
        background: !current ? C.surfaceAlt : C.surface,
        color: C.textTer,
        border: `1.5px dashed ${C.border}`,
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: font
      }
    }, current ? "Remove" : "None")));
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
      label: signReturn === "delivery" ? "Delivery" : customer.name,
      onClick: leaveSignage
    }), /*#__PURE__*/React.createElement("h2", {
      style: {
        fontFamily: font,
        fontSize: 22,
        fontWeight: 600,
        color: C.text,
        margin: "0 0 4px"
      }
    }, "Tap Cards"), (() => {
      const tapCount = getActiveTapCount(customer.id);
      // Ensure tapCards array matches tap count — trim or pad
      if (tapCards.length !== tapCount) {
        const adjusted = Array(tapCount).fill("").map((_, i) => tapCards[i] || "");
        setTimeout(() => setTapCards(adjusted), 0);
      }
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 13,
          color: C.textTer,
          margin: "0 0 16px"
        }
      }, tapCount, " tap", tapCount > 1 ? "s" : "", " \xB7 tap each card to change", tapBackupCards.some(b => b.onDisplay) ? " \xB7 " + tapBackupCards.filter(b => b.onDisplay).length + " extra on display" : ""), /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 13,
          fontWeight: 600,
          color: C.blue,
          letterSpacing: .5,
          textTransform: "uppercase",
          margin: "0 0 10px"
        }
      }, "On Display (", tapCount + tapBackupCards.filter(b => b.onDisplay).length, ")"), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          justifyContent: "center",
          marginBottom: 4
        }
      }, tapCards.slice(0, tapCount).map((p, i) => /*#__PURE__*/React.createElement(TapCardVisual, {
        key: i,
        product: p,
        active: tapSelecting === i,
        onTap: () => setTapSelecting(tapSelecting === i ? null : i)
      }))), tapSelecting !== null && tapSelecting < tapCount && /*#__PURE__*/React.createElement(CardPicker, {
        index: tapSelecting,
        current: tapCards[tapSelecting],
        label: `Card ${tapSelecting + 1}`,
        onSelect: p => {
          setTapCards(prev => prev.map((x, i) => i === tapSelecting ? p : x));
          // Clearing stays put and closes the picker — auto-advancing after a
          // Remove reads as though the tap was skipped, not emptied.
          if (!p) { setTapSelecting(null); showToast("Tap card removed"); return; }
          setTapSelecting(tapSelecting + 1 < tapCount ? tapSelecting + 1 : null);
        }
      }), /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 16
        }
      }, /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 13,
          fontWeight: 600,
          color: C.textTer,
          letterSpacing: .5,
          textTransform: "uppercase",
          margin: "0 0 10px"
        }
      }, "Backup Cards"), /*#__PURE__*/React.createElement("p", {
        style: { fontSize: 11, color: C.textTer, margin: "-4px 0 10px", textAlign: "center" }
      }, "Mark \u201COn display\u201D if a card is on a tap rather than spare"), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          justifyContent: "center",
          marginBottom: 4
        }
      }, tapBackupCards.map((b, i) => /*#__PURE__*/React.createElement("div", {
        key: i,
        style: {
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4
        }
      }, /*#__PURE__*/React.createElement(TapCardVisual, {
        product: b.product,
        active: tapBackupSelecting === i,
        onTap: () => setTapBackupSelecting(tapBackupSelecting === i ? null : i)
      }), /*#__PURE__*/React.createElement("button", {
        onClick: e => {
          e.stopPropagation();
          _keepScroll(() => setTapBackupCards(prev => prev.map((x, j) => j === i ? { ...x, onDisplay: !x.onDisplay } : x)));
        },
        style: {
          width: 100,
          padding: "4px 0",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: .3,
          borderRadius: 6,
          cursor: "pointer",
          fontFamily: font,
          background: b.onDisplay ? C.greenSoft : C.surfaceAlt,
          color: b.onDisplay ? C.green : C.textTer,
          border: `1.5px solid ${b.onDisplay ? C.greenBorder : C.borderLight}`
        }
      }, b.onDisplay ? "\u2713 On display" : "On display"), /*#__PURE__*/React.createElement("button", {
        onClick: e => {
          e.stopPropagation();
          setTapBackupCards(prev => prev.filter((_, j) => j !== i));
          if (tapBackupSelecting === i) setTapBackupSelecting(null);
        },
        style: {
          position: "absolute",
          top: -6,
          right: -6,
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: C.redSoft,
          border: `1px solid ${C.red}33`,
          color: C.red,
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2
        }
      }, "\xD7"))), /*#__PURE__*/React.createElement("button", {
        onClick: () => setTapBackupCards(prev => [...prev, { product: "", onDisplay: false }]),
        style: {
          width: 100,
          height: 64,
          borderRadius: 8,
          border: `2px dashed ${C.border}`,
          background: "none",
          cursor: "pointer",
          fontSize: 13,
          color: C.textTer,
          fontFamily: font
        }
      }, "+ Add")), tapBackupSelecting !== null && tapBackupSelecting < tapBackupCards.length && /*#__PURE__*/React.createElement(CardPicker, {
        index: tapBackupSelecting,
        current: (tapBackupCards[tapBackupSelecting] || {}).product,
        label: `Backup card ${tapBackupSelecting + 1}`,
        onSelect: p => {
          setTapBackupCards(prev => prev.map((x, i) => i === tapBackupSelecting ? { ...x, product: p } : x));
          if (!p) { setTapBackupSelecting(null); showToast("Backup card cleared"); return; }
          setTapBackupSelecting(tapBackupSelecting + 1 < tapBackupCards.length ? tapBackupSelecting + 1 : null);
        }
      })));
    })(), /*#__PURE__*/React.createElement("button", {
      onClick: () => {
        const data = {
          cards: tapCards,
          backup: tapBackupCards
        };
        addLog("tapcard", data);
        setSavedTapCards(prev => ({
          ...prev,
          [customer.id]: data
        }));
        const corData = savedCorflute[customer.id] || {};
        syncToSheet("saveCorflute", {
          customerId: customer.id,
          data: {
            ...corData,
            cards: tapCards,
            backup: tapBackupCards
          }
        });
        showToast("Tap cards updated");
        leaveSignage();
      },
      style: {
        width: "100%",
        padding: "14px",
        fontSize: 14,
        fontWeight: 600,
        position: "sticky",
        bottom: 20,
        zIndex: 10,
        boxShadow: "0 -4px 20px rgba(0,0,0,.1)",
        background: C.blue,
        color: "#fff",
        border: "none",
        borderRadius: 12,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Save Tap Cards"));
  })(), screen === "equipment" && customer && (() => {
    const ours = C.purple;
    const theirs = "#999";
    const oursLabel = "Ours";
    const theirsLabel = "Theirs";
    const ownerColor = owned => owned === "theirs" ? theirs : ours;
    const ownerBg = owned => owned === "theirs" ? "#f5f5f3" : `${ours}08`;
    const ownerBorder = owned => owned === "theirs" ? "#ddd" : ours;
    const OwnerToggle = ({
      value,
      onChange
    }) => /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 0,
        borderRadius: 6,
        overflow: "hidden",
        border: `1px solid ${C.borderLight}`
      }
    }, [["ours", oursLabel, ours], ["theirs", theirsLabel, theirs]].map(([v, label, col]) => /*#__PURE__*/React.createElement("button", {
      key: v,
      onClick: () => onChange(v),
      style: {
        padding: "4px 10px",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: .3,
        background: value === v ? col : "transparent",
        color: value === v ? "#fff" : C.textTer,
        border: "none",
        cursor: "pointer",
        fontFamily: font
      }
    }, label)));
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
      label: customer.name,
      onClick: () => setScreen("customer")
    }), /*#__PURE__*/React.createElement("h2", {
      style: {
        fontFamily: font,
        fontSize: 22,
        fontWeight: 600,
        color: C.text,
        margin: "0 0 4px"
      }
    }, "Equipment"), /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        color: C.textTer,
        margin: "0 0 6px"
      }
    }, "Tap to configure \xB7 ", /*#__PURE__*/React.createElement("span", {
      style: {
        color: ours,
        fontWeight: 600
      }
    }, "Purple = ours"), " \xB7 ", /*#__PURE__*/React.createElement("span", {
      style: {
        color: theirs,
        fontWeight: 600
      }
    }, "Grey = theirs")), /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 18
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.purple,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "12px 0 10px"
      }
    }, "Keg Fridge"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8
      }
    }, [{
      id: "Series 4",
      label: "Series 4",
      sub: "Fits 3 kegs",
      kegs: 3
    }, {
      id: "Series X",
      label: "Series X",
      sub: "Fits 4 kegs",
      kegs: 4
    }, {
      id: "Custom",
      label: "Custom",
      sub: "Own fridge",
      kegs: 2
    }].map(f => {
      const sel = equipFridge === f.id;
      const owned = formVal.fridgeOwner || "ours";
      const col = sel ? owned === "theirs" ? theirs : ours : C.textTer;
      return /*#__PURE__*/React.createElement("button", {
        key: f.id,
        onClick: () => setEquipFridge(sel ? "" : f.id),
        style: {
          flex: 1,
          padding: "14px 8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          background: sel ? ownerBg(owned) : C.surface,
          border: `1.5px solid ${sel ? ownerColor(owned) : C.borderLight}`,
          borderRadius: 12,
          cursor: "pointer"
        }
      }, /*#__PURE__*/React.createElement("svg", {
        viewBox: "0 0 56 68",
        fill: "none",
        style: {
          width: 40,
          height: 48
        }
      }, /*#__PURE__*/React.createElement("rect", {
        x: "4",
        y: "2",
        width: "48",
        height: "56",
        rx: "4",
        fill: col,
        fillOpacity: sel ? .12 : .04,
        stroke: col,
        strokeWidth: "1.5",
        strokeOpacity: sel ? .6 : .2
      }), /*#__PURE__*/React.createElement("line", {
        x1: "4",
        y1: "22",
        x2: "52",
        y2: "22",
        stroke: col,
        strokeWidth: "1",
        strokeOpacity: ".25"
      }), /*#__PURE__*/React.createElement("rect", {
        x: "42",
        y: "7",
        width: "3",
        height: "9",
        rx: "1",
        fill: col,
        fillOpacity: ".3"
      }), /*#__PURE__*/React.createElement("rect", {
        x: "42",
        y: "27",
        width: "3",
        height: "9",
        rx: "1",
        fill: col,
        fillOpacity: ".3"
      }), Array(Math.min(f.kegs, 4)).fill(0).map((_, j) => {
        const kx = f.kegs <= 3 ? 12 + j * 14 : 8 + j * 11;
        return /*#__PURE__*/React.createElement("rect", {
          key: j,
          x: kx,
          y: "28",
          width: f.kegs <= 3 ? 10 : 8,
          height: "22",
          rx: "2",
          fill: col,
          fillOpacity: sel ? .15 : .06,
          stroke: col,
          strokeWidth: ".8",
          strokeOpacity: ".2"
        });
      }), /*#__PURE__*/React.createElement("rect", {
        x: "10",
        y: "60",
        width: "5",
        height: "5",
        rx: "1.5",
        fill: col,
        fillOpacity: ".15"
      }), /*#__PURE__*/React.createElement("rect", {
        x: "41",
        y: "60",
        width: "5",
        height: "5",
        rx: "1.5",
        fill: col,
        fillOpacity: ".15"
      })), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 13,
          fontWeight: 600,
          color: sel ? ownerColor(owned) : C.textTer
        }
      }, f.label), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 13,
          color: C.textTer
        }
      }, f.sub));
    })), equipFridge && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8,
        padding: "8px 12px",
        background: C.surface,
        borderRadius: 8,
        border: `1px solid ${C.borderLight}`
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: C.textSec
      }
    }, "Ownership"), /*#__PURE__*/React.createElement(OwnerToggle, {
      value: formVal.fridgeOwner || "ours",
      onChange: v => setFormVal(p => ({
        ...p,
        fridgeOwner: v
      }))
    }))), /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 18
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 8
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.purple,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: 0
      }
    }, "Backup Fridge"), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: C.textTer
      }
    }, "(overflow stock)")), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8
      }
    }, ["None", "Series 4", "Series X", "Custom", "Cool Room"].map(f => {
      const sel = (formVal.backupFridge || "None") === f;
      const col = sel && f !== "None" ? ours : C.textTer;
      return /*#__PURE__*/React.createElement("button", {
        key: f,
        onClick: () => setFormVal(p => ({
          ...p,
          backupFridge: f,
          backupFridgeOwner: f === "Cool Room" ? "theirs" : p.backupFridgeOwner
        })),
        style: {
          flex: 1,
          padding: "10px 6px",
          fontSize: 12,
          fontWeight: 600,
          background: sel ? f === "None" ? C.surfaceAlt : `${ours}08` : C.surface,
          color: sel ? f === "None" ? C.textSec : ours : C.textTer,
          border: `1.5px solid ${sel ? f === "None" ? C.border : ours : C.borderLight}`,
          borderRadius: 8,
          cursor: "pointer",
          fontFamily: font
        }
      }, f);
    })), formVal.backupFridge && formVal.backupFridge !== "None" && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 6,
        padding: "8px 12px",
        background: C.surface,
        borderRadius: 8,
        border: `1px solid ${C.borderLight}`
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: C.textSec
      }
    }, "Ownership"), formVal.backupFridge === "Cool Room" ? /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: theirs
      }
    }, "Customer\u2019s") : /*#__PURE__*/React.createElement(OwnerToggle, {
      value: formVal.backupFridgeOwner || "ours",
      onChange: v => setFormVal(p => ({
        ...p,
        backupFridgeOwner: v
      }))
    }))), /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 18
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.purple,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 10px"
      }
    }, "A-Frame"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8
      }
    }, [0, 1, 2].map(n => {
      const sel = equipAframes === n;
      const owned = formVal.aframeOwner || "ours";
      const col = sel ? n === 0 ? C.textSec : ownerColor(owned) : C.textTer;
      return /*#__PURE__*/React.createElement("button", {
        key: n,
        onClick: () => setEquipAframes(n),
        style: {
          flex: 1,
          padding: "14px 8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          background: sel ? n === 0 ? C.surfaceAlt : ownerBg(owned) : C.surface,
          border: `1.5px solid ${sel ? n === 0 ? C.border : ownerColor(owned) : C.borderLight}`,
          borderRadius: 12,
          cursor: "pointer"
        }
      }, /*#__PURE__*/React.createElement("svg", {
        viewBox: "0 0 48 56",
        fill: "none",
        style: {
          width: 36,
          height: 42
        }
      }, n === 0 ? /*#__PURE__*/React.createElement("text", {
        x: "24",
        y: "34",
        textAnchor: "middle",
        fontSize: "14",
        fill: col,
        fontFamily: font,
        fontWeight: "600"
      }, "\u2014") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
        d: n === 1 ? "M10 50 L24 8 L38 50" : "M4 50 L14 8 L24 50",
        stroke: col,
        strokeWidth: "2",
        fill: "none",
        strokeLinejoin: "round",
        strokeOpacity: sel ? .6 : .25
      }), /*#__PURE__*/React.createElement("line", {
        x1: n === 1 ? "14" : "7",
        y1: "38",
        x2: n === 1 ? "34" : "21",
        y2: "38",
        stroke: col,
        strokeWidth: "1.2",
        strokeOpacity: sel ? .4 : .15
      }), n === 2 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
        d: "M24 50 L34 8 L44 50",
        stroke: col,
        strokeWidth: "2",
        fill: "none",
        strokeLinejoin: "round",
        strokeOpacity: sel ? .6 : .25
      }), /*#__PURE__*/React.createElement("line", {
        x1: "27",
        y1: "38",
        x2: "41",
        y2: "38",
        stroke: col,
        strokeWidth: "1.2",
        strokeOpacity: sel ? .4 : .15
      })))), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 12,
          fontWeight: 600,
          color: sel ? col : C.textTer
        }
      }, n === 0 ? "None" : n));
    })), equipAframes > 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8,
        padding: "8px 12px",
        background: C.surface,
        borderRadius: 8,
        border: `1px solid ${C.borderLight}`
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: C.textSec
      }
    }, "Ownership"), /*#__PURE__*/React.createElement(OwnerToggle, {
      value: formVal.aframeOwner || "ours",
      onChange: v => setFormVal(p => ({
        ...p,
        aframeOwner: v
      }))
    }))), /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 18
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.purple,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 10px"
      }
    }, "Tap Tower"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8
      }
    }, ["Single tap tower", "Twin tap tower", "Triple tap tower"].map(t => {
      const n = TOWER_TAP_COUNT[t];
      const sel = equipTower === t;
      const owned = formVal.towerOwner || "ours";
      const col = sel ? ownerColor(owned) : C.textTer;
      return /*#__PURE__*/React.createElement("button", {
        key: t,
        onClick: () => {
          setEquipTower(t);
          setEquipTaps(Array(n).fill("").map((_, i) => equipTaps[i] || {
            type: "",
            spout: ""
          }));
        },
        style: {
          flex: 1,
          padding: "14px 8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          background: sel ? ownerBg(owned) : C.surface,
          border: `1.5px solid ${sel ? col : C.borderLight}`,
          borderRadius: 12,
          cursor: "pointer"
        }
      }, /*#__PURE__*/React.createElement("svg", {
        viewBox: "0 0 48 56",
        fill: "none",
        style: {
          width: 36,
          height: 42
        }
      }, /*#__PURE__*/React.createElement("rect", {
        x: "8",
        y: "44",
        width: "32",
        height: "6",
        rx: "2",
        fill: col,
        fillOpacity: sel ? .2 : .08,
        stroke: col,
        strokeWidth: "1.5",
        strokeOpacity: sel ? .5 : .2
      }), /*#__PURE__*/React.createElement("rect", {
        x: "21",
        y: "10",
        width: "6",
        height: "34",
        rx: "2",
        fill: col,
        fillOpacity: sel ? .15 : .06,
        stroke: col,
        strokeWidth: "1.5",
        strokeOpacity: sel ? .5 : .2
      }), Array(n).fill(0).map((_, j) => {
        const x = n === 1 ? 24 : n === 2 ? 17 + j * 14 : 12 + j * 12;
        return /*#__PURE__*/React.createElement("g", {
          key: j
        }, /*#__PURE__*/React.createElement("rect", {
          x: x - 2,
          y: "4",
          width: "4",
          height: "12",
          rx: "1.5",
          fill: col,
          fillOpacity: sel ? .4 : .15
        }), /*#__PURE__*/React.createElement("circle", {
          cx: x,
          cy: "3",
          r: "2",
          fill: col,
          fillOpacity: sel ? .5 : .2
        }));
      })), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 12,
          fontWeight: 600,
          color: sel ? col : C.textTer
        }
      }, n === 1 ? "Single" : n === 2 ? "Twin" : "Triple"));
    })), equipTower && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8,
        padding: "8px 12px",
        background: C.surface,
        borderRadius: 8,
        border: `1px solid ${C.borderLight}`
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: C.textSec
      }
    }, "Ownership"), /*#__PURE__*/React.createElement(OwnerToggle, {
      value: formVal.towerOwner || "ours",
      onChange: v => setFormVal(p => ({
        ...p,
        towerOwner: v
      }))
    })), equipTower && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textSec,
        margin: "0 0 6px"
      }
    }, "Tower Style"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6
      }
    }, ["Standard", "Cobra", "Slimline"].map(function(s) {
      var curStyle = formVal.towerStyle || "";
      var sel = curStyle === s || (s === "Slimline" && curStyle === "Slimline (W)");
      return /*#__PURE__*/React.createElement("button", {
        key: s,
        onClick: function() {
          var newVal = s;
          if (s === "Slimline" && curStyle === "Slimline (W)") newVal = "Slimline (W)";
          setFormVal(function(p) { return Object.assign({}, p, { towerStyle: newVal }); });
        },
        style: {
          flex: 1,
          padding: "10px 8px",
          fontSize: 13,
          fontWeight: 600,
          background: sel ? ours + "10" : C.bg,
          color: sel ? ours : C.textTer,
          border: "1.5px solid " + (sel ? ours : C.borderLight),
          borderRadius: 8,
          cursor: "pointer",
          fontFamily: font
        }
      }, s);
    })), (formVal.towerStyle || "").indexOf("Slimline") === 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 8,
        marginTop: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      onClick: function() {
        var cur = formVal.towerStyle || "";
        var newVal = cur === "Slimline (W)" ? "Slimline" : "Slimline (W)";
        setFormVal(function(p) { return Object.assign({}, p, { towerStyle: newVal }); });
      },
      style: {
        width: 28,
        height: 28,
        borderRadius: 14,
        border: "1.5px solid " + ((formVal.towerStyle === "Slimline (W)") ? ours : C.borderLight),
        background: (formVal.towerStyle === "Slimline (W)") ? ours + "10" : C.bg,
        color: (formVal.towerStyle === "Slimline (W)") ? ours : C.textTer,
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        fontFamily: font
      }
    }, "W"), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: (formVal.towerStyle === "Slimline (W)") ? ours : C.textTer,
        fontWeight: 600
      }
    }, (formVal.towerStyle === "Slimline (W)") ? "Welded" : "Weld reinforced?"))), equipTower && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 12
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.purple,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 10px"
      }
    }, "Liquid Disconnects"), /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textSec,
        margin: "0 0 6px"
      }
    }, "Connection Type"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        marginBottom: 10
      }
    }, ["Push fit", "Clamped"].map(s => {
      const sel = (formVal.liquidConnection || "") === s;
      return /*#__PURE__*/React.createElement("button", {
        key: s,
        onClick: () => setFormVal(p => ({
          ...p,
          liquidConnection: s
        })),
        style: {
          flex: 1,
          padding: "10px 8px",
          fontSize: 13,
          fontWeight: 600,
          background: sel ? `${ours}10` : C.bg,
          color: sel ? ours : C.textTer,
          border: `1.5px solid ${sel ? ours : C.borderLight}`,
          borderRadius: 8,
          cursor: "pointer",
          fontFamily: font
        }
      }, s);
    })), /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textSec,
        margin: "0 0 6px"
      }
    }, "Disconnect Style"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6
      }
    }, ["Standard", "Kegland new"].map(s => {
      const sel = (formVal.liquidStyle || "") === s;
      return /*#__PURE__*/React.createElement("button", {
        key: s,
        onClick: () => setFormVal(p => ({
          ...p,
          liquidStyle: s
        })),
        style: {
          flex: 1,
          padding: "10px 8px",
          fontSize: 13,
          fontWeight: 600,
          background: sel ? `${ours}10` : C.bg,
          color: sel ? ours : C.textTer,
          border: `1.5px solid ${sel ? ours : C.borderLight}`,
          borderRadius: 8,
          cursor: "pointer",
          fontFamily: font
        }
      }, s);
    })))), equipTower && equipTaps.length > 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 18
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.purple,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 10px"
      }
    }, "Taps & Spouts"), equipTaps.map((tap, i) => {
      const inactive = tap.inactive;
      const _tapBrand = tap.type && tap.type.startsWith("Intertap") ? "Intertap" : tap.type && tap.type.startsWith("Nukatap") ? "Nukatap" : "";
      const _tapBase = _tapBrand ? tap.type.replace(_tapBrand + " ", "") : (tap.type || "");
      const _setTapComposite = function(brand, base) {
        const composite = brand ? (brand + " " + base) : base;
        setEquipTaps(function(p) { return p.map(function(t, j) { return j === i ? Object.assign({}, t, { type: composite }) : t; }); });
      };
      return inactive ? /*#__PURE__*/React.createElement("div", {
        key: i,
        style: {
          padding: "14px",
          background: "#f8f8f8",
          border: "1.5px dashed #ccc",
          borderRadius: 12,
          marginBottom: 6,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 13,
          fontWeight: 600,
          color: "#999"
        }
      }, "Tap ", i + 1), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 12,
          color: "#bbb",
          marginLeft: 8
        }
      }, "\u2014 not in use")), /*#__PURE__*/React.createElement("button", {
        onClick: () => setEquipTaps(p => p.map((t, j) => j === i ? {
          ...t,
          inactive: false
        } : t)),
        style: {
          padding: "6px 14px",
          fontSize: 12,
          fontWeight: 600,
          background: C.greenSoft,
          color: C.green,
          border: `1.5px solid ${C.greenBorder}`,
          borderRadius: 8,
          cursor: "pointer",
          fontFamily: font
        }
      }, "Reactivate")) : /*#__PURE__*/React.createElement("div", {
        key: i,
        style: {
          padding: "12px 14px",
          background: C.surface,
          border: `1px solid ${C.borderLight}`,
          borderRadius: 12,
          marginBottom: 6
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 12,
          fontWeight: 600,
          color: C.textSec,
          marginBottom: 8
        }
      }, "Tap ", i + 1), /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 12,
          fontWeight: 600,
          color: C.textTer,
          margin: "0 0 4px"
        }
      }, "Brand"), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 6,
          marginBottom: 8
        }
      }, ["Intertap", "Nukatap"].map(function(brand) {
        var sel = _tapBrand === brand;
        return /*#__PURE__*/React.createElement("button", {
          key: brand,
          onClick: function() { _setTapComposite(sel ? "" : brand, _tapBase || "Flow control"); },
          style: {
            flex: 1,
            padding: "8px",
            fontSize: 13,
            fontWeight: 600,
            background: sel ? ours + "10" : C.bg,
            color: sel ? ours : C.textTer,
            border: "1.5px solid " + (sel ? ours : C.borderLight),
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: font
          }
        }, brand);
      })), /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 12,
          fontWeight: 600,
          color: C.textTer,
          margin: "0 0 4px"
        }
      }, "Type"), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 6,
          marginBottom: 8
        }
      }, ["Flow control", "Standard"].map(function(type) {
        var sel = _tapBase === type;
        return /*#__PURE__*/React.createElement("button", {
          key: type,
          onClick: function() { _setTapComposite(_tapBrand, type); },
          style: {
            flex: 1,
            padding: "8px",
            fontSize: 13,
            fontWeight: 600,
            background: sel ? ours + "10" : C.bg,
            color: sel ? ours : C.textTer,
            border: "1.5px solid " + (sel ? ours : C.borderLight),
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: font
          }
        }, /*#__PURE__*/React.createElement("svg", {
          viewBox: "0 0 32 24",
          fill: "none",
          style: {
            width: 24,
            height: 18,
            display: "block",
            margin: "0 auto 4px"
          }
        }, /*#__PURE__*/React.createElement("rect", {
          x: "12",
          y: "2",
          width: "8",
          height: "16",
          rx: "2",
          fill: sel ? ours : C.textTer,
          fillOpacity: ".2",
          stroke: sel ? ours : C.textTer,
          strokeWidth: "1.2",
          strokeOpacity: ".4"
        }), type === "Flow control" && /*#__PURE__*/React.createElement("circle", {
          cx: "16",
          cy: "10",
          r: "2",
          fill: sel ? ours : C.textTer,
          fillOpacity: ".4"
        }), /*#__PURE__*/React.createElement("rect", {
          x: "10",
          y: "18",
          width: "12",
          height: "3",
          rx: "1",
          fill: sel ? ours : C.textTer,
          fillOpacity: ".15"
        })), type);
      })), /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 12,
          fontWeight: 600,
          color: C.textTer,
          margin: "0 0 4px"
        }
      }, "Spout"), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 6
        }
      }, ["Standard spout", "Nitro spout"].map(spout => {
        const sel = tap.spout === spout;
        return /*#__PURE__*/React.createElement("button", {
          key: spout,
          onClick: () => setEquipTaps(p => p.map((t, j) => j === i ? {
            ...t,
            spout
          } : t)),
          style: {
            flex: 1,
            padding: "8px",
            fontSize: 13,
            fontWeight: 600,
            background: sel ? `${ours}10` : C.bg,
            color: sel ? ours : C.textTer,
            border: `1.5px solid ${sel ? ours : C.borderLight}`,
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: font
          }
        }, spout.replace(" spout", ""));
      })), /*#__PURE__*/React.createElement("button", {
        onClick: () => setEquipTaps(p => p.map((t, j) => j === i ? {
          ...t,
          inactive: true,
          type: "",
          spout: ""
        } : t)),
        style: {
          marginTop: 10,
          padding: "6px 0",
          width: "100%",
          background: C.redSoft,
          border: `1.5px solid ${C.red}33`,
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          color: C.red,
          cursor: "pointer",
          fontFamily: font
        }
      }, "Mark as not in use"));
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 18
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.purple,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 10px"
      }
    }, "Gas Cylinder & Regulator"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 12,
        justifyContent: "center",
        padding: "16px 0"
      }
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 80 140",
      fill: "none",
      style: {
        width: 80,
        height: 140
      }
    }, /*#__PURE__*/React.createElement("circle", {
      cx: "40",
      cy: "22",
      r: "14",
      fill: equipRegulator ? `${ownerColor(formVal.regOwner || "ours")}12` : `${C.textTer}08`,
      stroke: equipRegulator ? ownerColor(formVal.regOwner || "ours") : C.textTer,
      strokeWidth: "1.5",
      strokeOpacity: equipRegulator ? .5 : .2
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "40",
      cy: "22",
      r: "5",
      fill: "none",
      stroke: equipRegulator ? ownerColor(formVal.regOwner || "ours") : C.textTer,
      strokeWidth: "1",
      strokeOpacity: ".3"
    }), equipRegulator && /*#__PURE__*/React.createElement("text", {
      x: "40",
      y: "25",
      textAnchor: "middle",
      fontSize: "8",
      fill: ownerColor(formVal.regOwner || "ours"),
      fontFamily: font,
      fontWeight: "600"
    }, "N\u2082"), /*#__PURE__*/React.createElement("rect", {
      x: "36",
      y: "34",
      width: "8",
      height: "8",
      rx: "2",
      fill: C.textTer,
      fillOpacity: ".15"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "24",
      y: "42",
      width: "32",
      height: "72",
      rx: "8",
      fill: (formVal.cylOwner || "theirs") === "theirs" ? `${theirs}08` : `${ours}08`,
      stroke: (formVal.cylOwner || "theirs") === "theirs" ? theirs : ours,
      strokeWidth: "1.5",
      strokeOpacity: ".4"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M28 52 Q40 42 52 52",
      stroke: (formVal.cylOwner || "theirs") === "theirs" ? theirs : ours,
      strokeWidth: "1",
      strokeOpacity: ".2"
    }), /*#__PURE__*/React.createElement("text", {
      x: "40",
      y: "80",
      textAnchor: "middle",
      fontSize: "10",
      fill: (formVal.cylOwner || "theirs") === "theirs" ? theirs : ours,
      fontFamily: font,
      fontWeight: "600",
      opacity: ".5"
    }, "BOC"), /*#__PURE__*/React.createElement("text", {
      x: "40",
      y: "94",
      textAnchor: "middle",
      fontSize: "9",
      fill: C.textTer,
      fontFamily: font
    }, "N\u2082"), /*#__PURE__*/React.createElement("rect", {
      x: "22",
      y: "114",
      width: "36",
      height: "6",
      rx: "2",
      fill: C.textTer,
      fillOpacity: ".1"
    }))), /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textSec,
        margin: "0 0 8px"
      }
    }, "Regulator"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        flexDirection: "column",
        gap: 4,
        marginBottom: 8
      }
    }, ["BOC N2 Hospitality", "BOC N2 Commercial", "BOC N2 Commercial New"].map(r => {
      const sel = equipRegulator === r;
      const owned = formVal.regOwner || "ours";
      return /*#__PURE__*/React.createElement("button", {
        key: r,
        onClick: () => setEquipRegulator(sel ? "" : r),
        style: {
          width: "100%",
          padding: "10px 14px",
          textAlign: "left",
          fontSize: 13,
          background: sel ? ownerBg(owned) : C.surface,
          color: sel ? ownerColor(owned) : C.textSec,
          border: `1.5px solid ${sel ? ownerColor(owned) : C.borderLight}`,
          borderRadius: 10,
          cursor: "pointer",
          fontFamily: font,
          fontWeight: sel ? 600 : 400
        }
      }, r);
    })), equipRegulator && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 12px",
        background: C.surface,
        borderRadius: 8,
        border: `1px solid ${C.borderLight}`,
        marginBottom: 10
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: C.textSec
      }
    }, "Regulator ownership"), /*#__PURE__*/React.createElement(OwnerToggle, {
      value: formVal.regOwner || "ours",
      onChange: v => setFormVal(p => ({
        ...p,
        regOwner: v
      }))
    })), /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textSec,
        margin: "0 0 8px"
      }
    }, "Gas Cylinder"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 14px",
        background: C.surface,
        borderRadius: 10,
        border: `1px solid ${C.borderLight}`
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        color: C.text
      }
    }, "BOC N\u2082 Cylinder"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 13,
        color: C.textTer,
        marginTop: 2
      }
    }, (formVal.cylOwner || "theirs") === "ours" ? "We manage & on-charge" : "Customer deals with BOC directly")), /*#__PURE__*/React.createElement(OwnerToggle, {
      value: formVal.cylOwner || "theirs",
      onChange: v => setFormVal(p => ({
        ...p,
        cylOwner: v
      }))
    }))), /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 18
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.purple,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 10px"
      }
    }, "Gas Line"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8
      }
    }, ["Single", "Double", "Triple"].map(g => {
      const sel = equipGasLine === g;
      const lineCount = g === "Single" ? 1 : g === "Double" ? 2 : 3;
      return /*#__PURE__*/React.createElement("button", {
        key: g,
        onClick: () => setEquipGasLine(sel ? "" : g),
        style: {
          flex: 1,
          padding: "14px 8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          background: sel ? `${ours}08` : C.surface,
          border: `1.5px solid ${sel ? ours : C.borderLight}`,
          borderRadius: 12,
          cursor: "pointer"
        }
      }, /*#__PURE__*/React.createElement("svg", {
        viewBox: "0 0 48 40",
        fill: "none",
        style: {
          width: 36,
          height: 30
        }
      }, /*#__PURE__*/React.createElement("rect", {
        x: "2",
        y: "8",
        width: "14",
        height: "26",
        rx: "4",
        fill: sel ? ours : C.textTer,
        fillOpacity: ".12",
        stroke: sel ? ours : C.textTer,
        strokeWidth: "1.5",
        strokeOpacity: sel ? .5 : .2
      }), /*#__PURE__*/React.createElement("rect", {
        x: "6",
        y: "4",
        width: "6",
        height: "4",
        rx: "1.5",
        fill: sel ? ours : C.textTer,
        fillOpacity: ".2"
      }), Array(lineCount).fill(0).map((_, j) => {
        const y = lineCount === 1 ? 20 : lineCount === 2 ? 16 + j * 8 : 13 + j * 7;
        return /*#__PURE__*/React.createElement("line", {
          key: j,
          x1: "16",
          y1: y,
          x2: "42",
          y2: y,
          stroke: sel ? ours : C.textTer,
          strokeWidth: "1.5",
          strokeOpacity: sel ? .4 : .15
        });
      }), Array(lineCount).fill(0).map((_, j) => {
        const y = lineCount === 1 ? 20 : lineCount === 2 ? 16 + j * 8 : 13 + j * 7;
        return /*#__PURE__*/React.createElement("rect", {
          key: `d${j}`,
          x: "38",
          y: y - 3,
          width: "8",
          height: "6",
          rx: "1.5",
          fill: sel ? "#888" : C.textTer,
          fillOpacity: sel ? .3 : .1,
          stroke: sel ? "#888" : C.textTer,
          strokeWidth: "1",
          strokeOpacity: ".3"
        });
      })), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 12,
          fontWeight: 600,
          color: sel ? ours : C.textTer
        }
      }, g));
    })), equipGasLine && GAS_LINE_COMPONENTS[equipGasLine] && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10,
        padding: "10px 14px",
        background: `${ours}06`,
        borderRadius: 10,
        border: `1px solid ${ours}15`
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: ours,
        letterSpacing: .5,
        margin: "0 0 6px"
      }
    }, "INCLUDES"), GAS_LINE_COMPONENTS[equipGasLine].map((c, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 0"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: 4,
        height: 4,
        borderRadius: "50%",
        background: ours,
        opacity: .3
      }
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: C.textSec
      }
    }, c)))), equipGasLine && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textSec,
        margin: "0 0 6px"
      }
    }, "Hose Style"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        marginBottom: 10
      }
    }, [{
      id: "old",
      label: "Old (5mm×9mm)"
    }, {
      id: "new",
      label: "New (6mm×9.5mm)"
    }].map(s => {
      const sel = (formVal.gasHoseStyle || "") === s.id;
      return /*#__PURE__*/React.createElement("button", {
        key: s.id,
        onClick: () => setFormVal(p => ({
          ...p,
          gasHoseStyle: s.id
        })),
        style: {
          flex: 1,
          padding: "10px 8px",
          fontSize: 13,
          fontWeight: 600,
          background: sel ? `${ours}10` : C.bg,
          color: sel ? ours : C.textTer,
          border: `1.5px solid ${sel ? ours : C.borderLight}`,
          borderRadius: 8,
          cursor: "pointer",
          fontFamily: font
        }
      }, s.label);
    })), equipGasLine !== "Single" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textSec,
        margin: "0 0 6px"
      }
    }, "Splitter"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6
      }
    }, ["Steel T-piece", "Push fit splitter"].map(s => {
      const sel = (formVal.gasSplitter || "") === s;
      return /*#__PURE__*/React.createElement("button", {
        key: s,
        onClick: () => setFormVal(p => ({
          ...p,
          gasSplitter: s
        })),
        style: {
          flex: 1,
          padding: "10px 8px",
          fontSize: 13,
          fontWeight: 600,
          background: sel ? `${ours}10` : C.bg,
          color: sel ? ours : C.textTer,
          border: `1.5px solid ${sel ? ours : C.borderLight}`,
          borderRadius: 8,
          cursor: "pointer",
          fontFamily: font
        }
      }, s);
    }))))), /*#__PURE__*/React.createElement(TextArea, {
      value: equipNotes,
      onChange: setEquipNotes,
      placeholder: "Equipment notes (serial numbers, condition, etc)",
      rows: 2
    }), /*#__PURE__*/React.createElement("button", {
      onClick: () => {
        const data = {
          fridge: equipFridge,
          fridgeOwner: formVal.fridgeOwner || "ours",
          backupFridge: formVal.backupFridge || "None",
          backupFridgeOwner: formVal.backupFridgeOwner || "ours",
          tower: equipTower,
          towerOwner: formVal.towerOwner || "ours",
          towerStyle: formVal.towerStyle || "",
          liquidConnection: formVal.liquidConnection || "",
          liquidStyle: formVal.liquidStyle || "",
          taps: equipTaps,
          gasLine: equipGasLine,
          gasHoseStyle: formVal.gasHoseStyle || "",
          gasSplitter: formVal.gasSplitter || "",
          regulator: equipRegulator,
          regOwner: formVal.regOwner || "ours",
          cylOwner: formVal.cylOwner || "theirs",
          aframes: equipAframes,
          aframeOwner: formVal.aframeOwner || "ours",
          notes: equipNotes
        };
        addLog("equipment", data);
        setSavedEquipment(prev => ({
          ...prev,
          [customer.id]: data
        }));
        syncToSheet("saveEquipment", {
          customerId: customer.id,
          data
        });
        // Clear saved tap cards so they re-init from new tower count
        const prevEquip = savedEquipment[customer.id];
        if (prevEquip?.tower !== equipTower) {
          setSavedTapCards(prev => {
            const next = {
              ...prev
            };
            delete next[customer.id];
            return next;
          });
        }
        showToast("Equipment saved");
        setScreen("customer");
      },
      disabled: !equipFridge && !equipTower && !equipGasLine && !equipRegulator,
      style: {
        width: "100%",
        padding: "14px",
        fontSize: 14,
        fontWeight: 600,
        position: "sticky",
        bottom: 20,
        zIndex: 10,
        boxShadow: "0 -4px 20px rgba(0,0,0,.1)",
        background: equipFridge || equipTower || equipGasLine || equipRegulator ? ours : C.surfaceAlt,
        color: equipFridge || equipTower || equipGasLine || equipRegulator ? "#fff" : C.textTer,
        border: "none",
        borderRadius: 12,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Save Equipment"));
  })(), screen === "maint" && customer && (() => {
    // NOT reversed: _idx must match the real maintLog/repairLog array indices
    // used by edit/delete. The history list below re-sorts by ts for display.
    const mLogs = maintLog[customer.id] || [];
    const rLogs = repairLog[customer.id] || [];
    const todayStr = new Date().toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    const todayISO = new Date().toISOString().split('T')[0];
    const selectedDate = formVal.maintDate || todayISO;
    const displayDate = new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    const now = Date.now();

    // Calculate days since last for each type
    // Line clean timer resets on either a Line Clean OR a Full System Clean
    // Most-recent timestamp per type — order-independent, so a freshly logged
    // clean is reflected instantly. A Full System Clean updates BOTH boxes.
    const allMaint = maintLog[customer.id] || [];
    const latestTs = pred => allMaint.reduce((mx, m) => pred(m) && (m.ts || 0) > mx ? (m.ts || 0) : mx, 0);
    const lcTs = latestTs(m => m.type === "Line Clean" || m.type === "Full System Clean");
    const fcTs = latestTs(m => m.type === "Full System Clean");
    const daysLC = lcTs ? Math.floor((now - lcTs) / 86400000) : null;
    const daysFC = fcTs ? Math.floor((now - fcTs) / 86400000) : null;
    const statusColour = (days, greenMax, amberMax) => {
      if (days === null) return {
        bg: C.redSoft,
        col: C.red,
        label: "No record"
      };
      if (days <= greenMax) return {
        bg: C.greenSoft,
        col: C.green,
        label: `${days} days ago`
      };
      if (days <= amberMax) return {
        bg: C.accentSoft,
        col: C.accent,
        label: `${days} days ago — due soon`
      };
      return {
        bg: C.redSoft,
        col: C.red,
        label: `${days} days ago — overdue`
      };
    };
    const lcDue = lcDueDaysFor(savedEquipment[customer.id]);
    // value: a number of days, "NA" (no prompt) or "" (back to the default).
    const saveLcDays = value => {
      const raw = String(value == null ? "" : value).trim();
      const off = /^(n\/?a|none|off|no)$/i.test(raw);
      const n = parseFloat(raw);
      if (raw && !off && !(n >= 1 && n <= 365)) { showToast("Enter days from 1 to 365, or NA"); return; }
      const days = (!raw || off) ? null : Math.round(n);
      const id = customer.id;
      scrollSafe(() => {
        setSavedEquipment(prev => ({ ...prev, [id]: { ...(prev[id] || {}), lineCleanDays: days, lineCleanOff: off } }));
        setLcDaysEdit(false);
      });
      syncToSheet("saveLineCleanDays", { customerId: id, value: off ? "NA" : (days || "") });
      showToast(off ? "Line clean prompt off for " + customer.name
        : days ? "Line clean every " + days + " days" : "Line clean back to every " + LC_DEFAULT_DAYS + " days");
    };
    const lcStatus = lcDue === null
      ? { bg: C.surfaceAlt, col: C.textSec, label: daysLC === null ? "Not tracked for this venue" : `${daysLC} days ago \u2014 not tracked` }
      : (function () {
          const st = lcStatusOf(daysLC, lcDue);
          const col = st.level === "ok" ? C.green : st.level === "due" ? C.accent : C.red;
          const bg = st.level === "ok" ? C.greenSoft : st.level === "due" ? C.accentSoft : C.redSoft;
          return { bg: bg, col: col, label: st.label };
        })();
    const fcStatus = statusColour(daysFC, 240, 270);
    const logMaint = type => {
      const useDate = new Date(selectedDate + 'T00:00:00');
      const dateStr = useDate.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      // Guard against an accidental double-tap logging the same clean twice for
      // the same date. (If you ever need two of the same clean on one day, change
      // the date first, or remove this guard.)
      const already = (maintLog[customer.id] || []).some(m => m.type === type && m.date === dateStr);
      if (already) {
        showToast(`${type} already logged for ${dateStr}`);
        return;
      }
      const record = {
        date: dateStr,
        time: new Date().toLocaleTimeString('en-AU', {
          hour: '2-digit',
          minute: '2-digit'
        }),
        type,
        ts: useDate.getTime(),
        notes: formVal.maintNotes || ""
      };
      // A Full System Clean resets BOTH timers — the days-since calculation
      // treats it as a line clean too — so we only ever store one row here.
      setMaintLog(prev => ({
        ...prev,
        [customer.id]: [record, ...(prev[customer.id] || [])]
      }));
      addLog("maint", record);
      syncToSheet("logMaintenance", {
        data: {
          customerId: customer.id,
          customerName: customer.name,
          category: "Maintenance",
          type,
          notes: formVal.maintNotes || "",
          user: currentUser?.name || "Unknown",
          date: record.date,
          time: record.time
        }
      });
      setFormVal(p => ({
        ...p,
        maintNotes: "",
        maintDate: ""
      }));
      showToast(type === "Full System Clean" ? "Full System Clean recorded" : `${type} recorded`);
    };
    const logRepair = () => {
      if (!formVal.repairTask) return;
      const useDate = new Date(selectedDate + 'T00:00:00');
      const record = {
        date: useDate.toLocaleDateString('en-AU', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }),
        time: new Date().toLocaleTimeString('en-AU', {
          hour: '2-digit',
          minute: '2-digit'
        }),
        task: formVal.repairTask,
        notes: formVal.repairNotes || "",
        ts: useDate.getTime()
      };
      setRepairLog(prev => ({
        ...prev,
        [customer.id]: [record, ...(prev[customer.id] || [])]
      }));
      addLog("repair", record);
      syncToSheet("logMaintenance", {
        data: {
          customerId: customer.id,
          customerName: customer.name,
          category: "Repair",
          task: formVal.repairTask,
          type: formVal.repairTask,
          notes: formVal.repairNotes || "",
          user: currentUser?.name || "Unknown",
          date: record.date,
          time: record.time
        }
      });
      setFormVal(p => ({
        ...p,
        repairTask: "",
        repairNotes: "",
        maintDate: ""
      }));
      showToast("Repair logged");
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
      label: customer.name,
      onClick: () => setScreen("customer")
    }), /*#__PURE__*/React.createElement("h2", {
      style: {
        fontFamily: font,
        fontSize: 22,
        fontWeight: 600,
        color: C.text,
        margin: "0 0 16px"
      }
    }, "Maintenance & Repairs"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 8,
        marginBottom: 20
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "14px",
        background: lcStatus.bg,
        border: `1.5px solid ${lcStatus.col}22`,
        borderRadius: 12
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 11,
        fontWeight: 600,
        color: lcStatus.col,
        textTransform: "uppercase",
        letterSpacing: .5,
        marginBottom: 4
      }
    }, "Line Clean"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: lcStatus.col
      }
    }, lcStatus.label), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 11,
        color: C.textTer,
        marginTop: 4
      }
    }, lcDue === null ? "No prompt (NA)" : ("Target: every " + lcDue + " days" + (lcDue !== LC_DEFAULT_DAYS ? " (set for this venue)" : ""))),
    !lcDaysEdit ? /*#__PURE__*/React.createElement("button", {
      onClick: () => scrollSafe(() => setLcDaysEdit(true)),
      style: { marginTop: 6, padding: 0, background: "none", border: "none", fontSize: 11, fontWeight: 600, color: C.accent, textDecoration: "underline", cursor: "pointer", fontFamily: font }
    }, "Change days") : /*#__PURE__*/React.createElement("div", { style: { marginTop: 8 } },
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6 } },
        /*#__PURE__*/React.createElement("input", { ref: lcDaysRef, type: "number", inputMode: "numeric", min: 1, max: 365,
          defaultValue: lcDue === null ? "" : String(lcDue),
          style: { width: 56, padding: "6px 8px", fontSize: 16, border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: font, background: C.surface, color: C.text, boxSizing: "border-box" } }),
        /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textSec } }, "days")),
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 } },
        /*#__PURE__*/React.createElement("button", { onClick: () => saveLcDays(lcDaysRef.current ? lcDaysRef.current.value : ""),
          style: { padding: "6px 10px", fontSize: 12, fontWeight: 700, background: C.green, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Save"),
        /*#__PURE__*/React.createElement("button", { onClick: () => saveLcDays("NA"),
          style: { padding: "6px 10px", fontSize: 12, fontWeight: 600, background: C.surface, color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer", fontFamily: font } }, "NA"),
        /*#__PURE__*/React.createElement("button", { onClick: () => scrollSafe(() => setLcDaysEdit(false)),
          style: { padding: "6px 4px", fontSize: 12, fontWeight: 600, background: "none", color: C.textTer, border: "none", cursor: "pointer", fontFamily: font, textDecoration: "underline" } }, "Cancel")))), /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "14px",
        background: fcStatus.bg,
        border: `1.5px solid ${fcStatus.col}22`,
        borderRadius: 12
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 11,
        fontWeight: 600,
        color: fcStatus.col,
        textTransform: "uppercase",
        letterSpacing: .5,
        marginBottom: 4
      }
    }, "Full System Clean"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: fcStatus.col
      }
    }, fcStatus.label), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 11,
        color: C.textTer,
        marginTop: 4
      }
    }, "Target: every 12 months"))), /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "16px",
        background: C.surface,
        border: `1.5px solid ${C.border}`,
        borderRadius: 14,
        marginBottom: 16
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.cyan,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 4px"
      }
    }, "Record Clean"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        margin: "0 0 12px"
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        color: selectedDate !== todayISO ? C.accent : C.textTer,
        fontWeight: selectedDate !== todayISO ? 600 : 400,
        margin: 0
      }
    }, displayDate), /*#__PURE__*/React.createElement("input", {
      type: "date",
      value: selectedDate,
      max: todayISO,
      onChange: e => e.target.value && setFormVal(p => ({
        ...p,
        maintDate: e.target.value
      })),
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.blue,
        padding: "4px 10px",
        background: C.blueSoft,
        border: `1px solid ${C.blueBorder}`,
        borderRadius: 6,
        fontFamily: font,
        WebkitAppearance: "none",
        width: 80,
        textAlign: "center"
      }
    }), selectedDate !== todayISO && /*#__PURE__*/React.createElement("button", {
      onClick: () => setFormVal(p => ({
        ...p,
        maintDate: todayISO
      })),
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        background: "none",
        border: "none",
        cursor: "pointer",
        fontFamily: font,
        padding: 0
      }
    }, "Reset")), /*#__PURE__*/React.createElement(TextArea, {
      value: formVal.maintNotes || "",
      onChange: v => setFormVal(p => ({
        ...p,
        maintNotes: v
      })),
      placeholder: "Notes (optional)",
      rows: 2
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8,
        marginTop: 8
      }
    }, SCHED_MAINT.map(type => /*#__PURE__*/React.createElement("button", {
      key: type,
      onClick: () => logMaint(type),
      style: {
        flex: 1,
        padding: "12px 8px",
        fontSize: 13,
        fontWeight: 600,
        background: C.cyan,
        color: "#fff",
        border: "none",
        borderRadius: 10,
        cursor: "pointer",
        fontFamily: font
      }
    }, type === "Line Clean" ? "Line Clean" : "Full System")))), /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "16px",
        background: C.surface,
        border: `1.5px solid ${C.border}`,
        borderRadius: 14,
        marginBottom: 20
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.red,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 4px"
      }
    }, "Log Repair"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        margin: "0 0 12px"
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        color: selectedDate !== todayISO ? C.accent : C.textTer,
        fontWeight: selectedDate !== todayISO ? 600 : 400,
        margin: 0
      }
    }, displayDate), /*#__PURE__*/React.createElement("input", {
      type: "date",
      value: selectedDate,
      max: todayISO,
      onChange: e => e.target.value && setFormVal(p => ({
        ...p,
        maintDate: e.target.value
      })),
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.blue,
        padding: "4px 10px",
        background: C.blueSoft,
        border: `1px solid ${C.blueBorder}`,
        borderRadius: 6,
        fontFamily: font,
        WebkitAppearance: "none",
        width: 80,
        textAlign: "center"
      }
    }), selectedDate !== todayISO && /*#__PURE__*/React.createElement("button", {
      onClick: () => setFormVal(p => ({
        ...p,
        maintDate: todayISO
      })),
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        background: "none",
        border: "none",
        cursor: "pointer",
        fontFamily: font,
        padding: 0
      }
    }, "Reset")), /*#__PURE__*/React.createElement(Select, {
      value: formVal.repairTask || "",
      onChange: v => setFormVal(p => ({
        ...p,
        repairTask: v
      })),
      options: REPAIR_TASKS,
      placeholder: "What was repaired?"
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 8
      }
    }, /*#__PURE__*/React.createElement(TextArea, {
      value: formVal.repairNotes || "",
      onChange: v => setFormVal(p => ({
        ...p,
        repairNotes: v
      })),
      placeholder: "Details",
      rows: 2
    })), /*#__PURE__*/React.createElement("button", {
      onClick: logRepair,
      disabled: !formVal.repairTask,
      style: {
        width: "100%",
        marginTop: 8,
        padding: "12px",
        fontSize: 14,
        fontWeight: 600,
        background: formVal.repairTask ? C.red : C.surfaceAlt,
        color: formVal.repairTask ? "#fff" : C.textTer,
        border: "none",
        borderRadius: 10,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Log Repair")), (mLogs.length > 0 || rLogs.length > 0) && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.textTer,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 12px"
      }
    }, "History"), [...mLogs.map((m, idx) => ({
      ...m,
      _type: "maint",
      _idx: idx
    })), ...rLogs.map((r, idx) => ({
      ...r,
      _type: "repair",
      _idx: idx
    }))].sort((a, b) => (b.ts || 0) - (a.ts || 0)).map((r, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        padding: "12px 14px",
        background: C.surface,
        border: `1px solid ${C.borderLight}`,
        borderRadius: 12,
        marginBottom: 6
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 14,
        fontWeight: 600,
        color: C.text
      }
    }, r._type === "maint" ? r.type : r.task), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 11,
        fontWeight: 600,
        color: r._type === "maint" ? C.cyan : C.red,
        padding: "2px 8px",
        background: r._type === "maint" ? `${C.cyan}12` : C.redSoft,
        borderRadius: 4
      }
    }, r._type === "maint" ? "CLEAN" : "REPAIR")), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 12,
        color: C.textTer,
        marginTop: 2
      }
    }, r.date, " at ", r.time), r.notes && /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 13,
        color: C.textSec,
        marginTop: 4
      }
    }, r.notes), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8,
        marginTop: 8,
        borderTop: `1px solid ${C.borderLight}`,
        paddingTop: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => setEditingLog({
        source: r._type,
        index: r._idx,
        record: {
          ...r
        }
      }),
      style: {
        padding: "4px 12px",
        fontSize: 12,
        fontWeight: 600,
        background: C.blueSoft,
        color: C.blue,
        border: `1px solid ${C.blueBorder}`,
        borderRadius: 6,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Edit"), /*#__PURE__*/React.createElement("button", {
      onClick: () => {
        if (r._type === "maint") {
          const entry = (maintLog[customer.id] || [])[r._idx];
          setMaintLog(prev => ({
            ...prev,
            [customer.id]: (prev[customer.id] || []).filter((_, j) => j !== r._idx)
          }));
          syncToSheet("deleteMaint", {
            data: {
              customerId: customer.id,
              date: entry?.date || "",
              time: entry?.time || "",
              type: entry?.type || "",
              category: "Maintenance"
            }
          });
        } else {
          const entry = (repairLog[customer.id] || [])[r._idx];
          setRepairLog(prev => ({
            ...prev,
            [customer.id]: (prev[customer.id] || []).filter((_, j) => j !== r._idx)
          }));
          syncToSheet("deleteMaint", {
            data: {
              customerId: customer.id,
              date: entry?.date || "",
              time: entry?.time || "",
              type: entry?.task || "",
              category: "Repair"
            }
          });
        }
        showToast("Entry deleted");
      },
      style: {
        padding: "4px 12px",
        fontSize: 12,
        fontWeight: 600,
        background: C.redSoft,
        color: C.red,
        border: `1px solid ${C.red}33`,
        borderRadius: 6,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Delete"))))));
  })(), screen === "gas" && customer && (() => {
    const equip = savedEquipment[customer.id];
    const cylOwner = equip?.cylOwner || "theirs";
    const logs = (gasLog[customer.id] || []).slice();
    const GAS_REASONS = ["Gas empty", "Gas lost"];
    const todayISO = new Date().toISOString().split('T')[0];
    const selectedGasDate = formVal.gasDate || todayISO;
    const displayGasDate = new Date(selectedGasDate + 'T00:00:00').toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    const reasonSelected = formVal.gasReason || "";
    const canRecord = !!reasonSelected && (reasonSelected !== "Gas lost" || (formVal.gasNotes || "").trim());
    const addGasRecord = () => {
      if (!canRecord) return;
      const useDate = new Date(selectedGasDate + 'T00:00:00');
      const record = {
        date: useDate.toLocaleDateString('en-AU', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }),
        time: new Date().toLocaleTimeString('en-AU', {
          hour: '2-digit',
          minute: '2-digit'
        }),
        reason: reasonSelected,
        notes: formVal.gasNotes || ""
      };
      setGasLog(prev => ({
        ...prev,
        [customer.id]: [record, ...(prev[customer.id] || [])]
      }));
      addLog("gas", record);
      syncToSheet("logGas", {
        data: {
          customerId: customer.id,
          customerName: customer.name,
          reason: reasonSelected,
          notes: formVal.gasNotes || "",
          cylOwner: equip?.cylOwner || "",
          user: currentUser?.name || "Unknown",
          date: record.date,
          time: record.time
        }
      });
      setFormVal(p => ({
        ...p,
        gasReason: "",
        gasNotes: "",
        gasDate: ""
      }));
      showToast("Gas cylinder recorded");
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
      label: gasFromSummary ? "Gas Cylinders" : customer.name,
      onClick: () => {
        if (gasFromSummary) {
          setGasFromSummary(false);
          setScreen("gassummary");
        } else {
          setScreen("customer");
        }
      }
    }), /*#__PURE__*/React.createElement("h2", {
      style: {
        fontFamily: font,
        fontSize: 22,
        fontWeight: 600,
        color: C.text,
        margin: "0 0 4px"
      }
    }, "Gas Cylinders"), /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 14,
        fontWeight: 600,
        color: C.textSec,
        margin: "0 0 2px"
      }
    }, customer.name), /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        color: C.textTer,
        margin: "0 0 16px"
      }
    }, "N2 \xB7 D size"), cylOwner === "theirs" && /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "12px 16px",
        background: "#FFF8E1",
        border: "1px solid #FFE082",
        borderRadius: 12,
        marginBottom: 16,
        fontSize: 13,
        color: "#F57F17"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontWeight: 600
      }
    }, customer.name), " manages their own gas. Records below are for emergency or temporary supply."), cylOwner === "ours" && /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "12px 16px",
        background: `${C.purple}08`,
        border: `1px solid ${C.purple}33`,
        borderRadius: 12,
        marginBottom: 16,
        fontSize: 13,
        color: C.purple
      }
    }, "Gas supply managed by ", /*#__PURE__*/React.createElement("span", {
      style: {
        fontWeight: 600
      }
    }, "Filament Coffee"), "."), /*#__PURE__*/React.createElement("div", {
      style: { padding: "14px 16px", background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 14, marginBottom: 16 }
    }, /*#__PURE__*/React.createElement("p", {
      style: { fontSize: 13, fontWeight: 600, color: C.purple, letterSpacing: .5, textTransform: "uppercase", margin: "0 0 10px" }
    }, "BOC Account"),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, margin: "0 0 6px" } }, "Account number"),
      /*#__PURE__*/React.createElement("input", {
        type: "tel",
        inputMode: "numeric",
        pattern: "[0-9]*",
        value: formVal.bocAccount !== undefined ? formVal.bocAccount : (customer.bocAccount || ""),
        onChange: e => { var _v = e.target.value.replace(/\D/g, ""); setFormVal(p => ({ ...p, bocAccount: _v })); },
        placeholder: "e.g. 1234567",
        style: { width: "100%", padding: "10px 12px", fontSize: 16, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: font, boxSizing: "border-box", marginBottom: 12 }
      }),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, margin: "0 0 6px" } }, "Account type"),
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 0, borderRadius: 10, overflow: "hidden", border: `1.5px solid ${C.border}` } },
        ["cash", "credit", "unknown"].map(t => {
          const cur = formVal.paymentType || customer.paymentType || "cash";
          const sel = cur === t;
          return /*#__PURE__*/React.createElement("button", {
            key: t,
            onClick: () => setFormVal(p => ({ ...p, paymentType: t })),
            style: { flex: 1, padding: "12px 0", fontSize: 14, fontWeight: sel ? 700 : 500, fontFamily: font, color: sel ? "#fff" : C.textSec, background: sel ? (t === "credit" ? C.green : t === "unknown" ? C.textSec : C.accent) : C.surface, border: "none", borderRight: t !== "unknown" ? `1px solid ${C.border}` : "none", cursor: "pointer", textTransform: "capitalize" }
          }, t);
        })
      ),
      /*#__PURE__*/React.createElement("p", { style: { fontSize: 11, color: C.textTer, margin: "8px 0 12px" } }, (formVal.paymentType || customer.paymentType || "cash") === "credit" ? "Credit \u2014 pays on account" : (formVal.paymentType || customer.paymentType || "cash") === "unknown" ? "Unknown \u2014 account type not set" : "Cash \u2014 pays when ordering"),
      /*#__PURE__*/React.createElement("button", {
        onClick: () => {
          const acct = formVal.bocAccount !== undefined ? String(formVal.bocAccount).trim() : (customer.bocAccount || "");
          const ptype = formVal.paymentType || customer.paymentType || "cash";
          syncToSheet("saveGasAccount", { data: { customerId: customer.id, bocAccount: acct, paymentType: ptype } });
          setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, bocAccount: acct, paymentType: ptype } : c) : prev);
          setCust(prev => prev && prev.id === customer.id ? { ...prev, bocAccount: acct, paymentType: ptype } : prev);
          showToast("BOC account saved");
        },
        style: { width: "100%", padding: "12px", fontSize: 14, fontWeight: 700, fontFamily: font, color: "#fff", background: C.purple, border: "none", borderRadius: 10, cursor: "pointer" }
      }, "Save Account Details")
    ), /*#__PURE__*/React.createElement(GasGaugeCard, { custId: customer.id, gasLog: gasLog, setGasLog: setGasLog, syncToSheet: syncToSheet, customer: customer, equip: equip, currentUser: currentUser, showToast: showToast, formVal: formVal, setFormVal: setFormVal }), /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "16px",
        background: C.surface,
        border: `1.5px solid ${C.border}`,
        borderRadius: 14,
        marginBottom: 20
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: "#E67E22",
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 4px"
      }
    }, "Record Replacement"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        margin: "0 0 12px"
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        color: selectedGasDate !== todayISO ? C.accent : C.textTer,
        fontWeight: selectedGasDate !== todayISO ? 600 : 400,
        margin: 0
      }
    }, displayGasDate), /*#__PURE__*/React.createElement("input", {
      type: "date",
      value: selectedGasDate,
      max: todayISO,
      onChange: e => e.target.value && setFormVal(p => ({
        ...p,
        gasDate: e.target.value
      })),
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.blue,
        padding: "4px 10px",
        background: C.blueSoft,
        border: `1px solid ${C.blueBorder}`,
        borderRadius: 6,
        fontFamily: font,
        WebkitAppearance: "none",
        width: 80,
        textAlign: "center"
      }
    }), selectedGasDate !== todayISO && /*#__PURE__*/React.createElement("button", {
      onClick: () => setFormVal(p => ({
        ...p,
        gasDate: todayISO
      })),
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        background: "none",
        border: "none",
        cursor: "pointer",
        fontFamily: font,
        padding: 0
      }
    }, "Reset")), /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        margin: "0 0 6px"
      }
    }, "Reason"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        marginBottom: 10
      }
    }, GAS_REASONS.map(r => {
      const sel = reasonSelected === r;
      return /*#__PURE__*/React.createElement("button", {
        key: r,
        onClick: () => setFormVal(p => ({
          ...p,
          gasReason: r,
          gasNotes: r === "Gas empty" ? "" : p.gasNotes || ""
        })),
        style: {
          flex: 1,
          padding: "12px 10px",
          fontSize: 14,
          fontWeight: 600,
          background: sel ? "#E67E2215" : C.bg,
          color: sel ? "#E67E22" : C.textTer,
          border: `1.5px solid ${sel ? "#E67E22" : C.borderLight}`,
          borderRadius: 10,
          cursor: "pointer",
          fontFamily: font
        }
      }, r);
    })), reasonSelected === "Gas lost" && /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 10
      }
    }, /*#__PURE__*/React.createElement(TextArea, {
      value: formVal.gasNotes || "",
      onChange: v => setFormVal(p => ({
        ...p,
        gasNotes: v
      })),
      placeholder: "What happened? (required)",
      rows: 2
    })), /*#__PURE__*/React.createElement("button", {
      onClick: addGasRecord,
      disabled: !canRecord,
      style: {
        width: "100%",
        padding: "14px",
        fontSize: 14,
        fontWeight: 600,
        background: canRecord ? "#E67E22" : C.surfaceAlt,
        color: canRecord ? "#fff" : C.textTer,
        border: "none",
        borderRadius: 10,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Record Cylinder")), logs.length > 0 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: C.textTer,
        letterSpacing: .5,
        textTransform: "uppercase",
        margin: "0 0 12px"
      }
    }, "History (", logs.length, ")"), logs.map((r, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        padding: "12px 14px",
        background: C.surface,
        border: `1px solid ${C.borderLight}`,
        borderRadius: 12,
        marginBottom: 6,
        display: "flex",
        gap: 12,
        alignItems: "flex-start"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: 28,
        height: 28,
        flexShrink: 0,
        marginTop: 2
      }
    }, icons.gas(r.reason === "Gas lost" ? C.red : r.reason === "Level check" && r.kpa != null ? getGasColor(r.kpa) : r.reason === "New cylinder" ? "#1B5E20" : r.reason === "Status update" ? "#2E7D32" : "#E67E22")), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 14,
        fontWeight: 600,
        color: C.text
      }
    }, r.reason === "Level check" && r.kpa != null ? (r.kpa === -1 ? "Can't read" : r.kpa.toLocaleString() + " kPa") : r.reason), r.reason === "Gas lost" && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 11,
        fontWeight: 600,
        color: C.red,
        padding: "2px 8px",
        background: C.redSoft,
        borderRadius: 4
      }
    }, "LOST"), r.reason === "Status update" && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 600, color: "#2E7D32", padding: "2px 8px", background: "#E8F5E9", borderRadius: 4 } }, "\u2713 ACTIONED"), r.reason === "New cylinder" && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 700, color: "#1B5E20", padding: "2px 8px", background: "#E8F5E9", borderRadius: 4 } }, "SWAP"), r.reason === "Level check" && r.kpa != null && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 11,
        fontWeight: 700,
        color: getGasColor(r.kpa),
        padding: "2px 8px",
        background: getGasColor(r.kpa) + "15",
        borderRadius: 4
      }
    }, getGasStatus(r.kpa).label)), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 12,
        color: C.textTer,
        marginTop: 2
      }
    }, r.reason === "Level check" ? "Level check \xB7 " + r.date + " at " + r.time : r.reason === "New cylinder" ? "New cylinder \xB7 " + r.date + " at " + r.time : r.date + " at " + r.time), r.notes && /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 13,
        color: C.textSec,
        marginTop: 4,
        fontStyle: "italic"
      }
    }, r.notes), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8,
        marginTop: 8,
        borderTop: `1px solid ${C.borderLight}`,
        paddingTop: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => setEditingLog({
        source: "gas",
        index: i,
        record: {
          ...r
        }
      }),
      style: {
        padding: "4px 12px",
        fontSize: 12,
        fontWeight: 600,
        background: C.blueSoft,
        color: C.blue,
        border: `1px solid ${C.blueBorder}`,
        borderRadius: 6,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Edit"), /*#__PURE__*/React.createElement("button", {
      onClick: () => {
        const entry = (gasLog[customer.id] || [])[i];
        setGasLog(prev => ({
          ...prev,
          [customer.id]: (prev[customer.id] || []).filter((_, j) => j !== i)
        }));
        syncToSheet("deleteGas", {
          data: {
            customerId: customer.id,
            date: entry?.date || "",
            time: entry?.time || "",
            reason: entry?.reason || ""
          }
        });
        showToast("Entry deleted");
      },
      style: {
        padding: "4px 12px",
        fontSize: 12,
        fontWeight: 600,
        background: C.redSoft,
        color: C.red,
        border: `1px solid ${C.red}33`,
        borderRadius: 6,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Delete")))))), logs.length === 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        padding: 24,
        textAlign: "center",
        color: C.textTer,
        fontSize: 13
      }
    }, "No gas cylinder records yet"));
  })(), screen === "notes" && customer && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackBtn, {
    label: customer.name,
    onClick: () => setScreen("customer")
  }), /*#__PURE__*/React.createElement("h2", {
    style: { fontFamily: font, fontSize: 22, fontWeight: 600, color: C.text, margin: "0 0 4px" }
  }, "Notes & CRM"),
  // --- Tab strip ---
  /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 0, borderBottom: `1px solid ${C.border}`, marginBottom: 12, marginTop: 8 } },
    ["notes", "deliveries", "crm"].map(tab => /*#__PURE__*/React.createElement("button", { key: tab, onClick: () => _keepScroll(() => { setEditingHistoryNote(null); setCrmTab(tab); }), style: { flex: 1, padding: "10px 4px", fontSize: 13, fontWeight: crmTab === tab ? 600 : 400, textAlign: "center", border: "none", background: "none", cursor: "pointer", fontFamily: font, color: crmTab === tab ? C.text : C.textSec, borderBottom: crmTab === tab ? "2px solid " + C.accent : "2px solid transparent" } }, tab === "notes" ? "Notes" : tab === "deliveries" ? "Delivery log" : "CRM"))),
  // ========== NOTES TAB ==========
  crmTab === "notes" && /*#__PURE__*/React.createElement("div", null,
    // --- Open Action Items ---
    (() => {
      const openActions = actionItems.filter(a => a.customerId === customer.id && a.status === "Open");
      if (openActions.length === 0) return null;
      return /*#__PURE__*/React.createElement("div", { style: { marginBottom: 12 } },
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, letterSpacing: .5, textTransform: "uppercase", margin: "0 0 8px" } }, "Open action items"),
        openActions.map((a, i) => /*#__PURE__*/React.createElement("div", { key: "oa-" + i, style: { padding: "10px 14px", background: "#FFF0ED", border: "1px solid #F0B0A0", borderRadius: 10, marginBottom: 6 } },
          /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 4 } },
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: "#C43E2A" } }, "\u26A1 Open"),
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer, marginLeft: "auto" } }, "\u2192 ", a.assignedTo, a.dueDate ? " \xB7 Due: " + a.dueDate : "")),
          /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.text, margin: "0 0 4px", lineHeight: 1.5 } }, a.description),
          /*#__PURE__*/React.createElement("p", { style: { fontSize: 11, color: C.textTer, margin: "0 0 6px" } }, "Created ", a.dateCreated, " by ", a.createdBy),
          /*#__PURE__*/React.createElement("button", { onClick: () => {
            syncToSheet("completeActionItem", { data: { customerId: customer.id, description: a.description, completedBy: currentUser?.name || "" } });
            setActionItems(prev => prev.map(item => item.customerId === customer.id && item.description === a.description && item.status === "Open" ? { ...item, status: "Complete", completedBy: currentUser?.name || "" } : item));
            showToast("Action item completed");
          }, style: { padding: "6px 14px", fontSize: 12, fontWeight: 600, background: "#EEFBF3", border: "1px solid #B8E4CA", borderRadius: 8, cursor: "pointer", fontFamily: font, color: "#2D7A4F" } }, "\u2713 Mark complete"))));
    })(),
    // --- Add note/action form ---
    /*#__PURE__*/React.createElement("div", { style: { marginTop: 8, padding: "12px 14px", border: `1.5px solid ${C.accent}`, borderRadius: 12, background: C.surface } },
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", background: C.surfaceAlt, borderRadius: 8, padding: 3, marginBottom: 10 } },
        /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setNotesTabMode("note")), style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: notesTabMode === "note" ? 600 : 400, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: font, background: notesTabMode === "note" ? "#fff" : "transparent", color: notesTabMode === "note" ? C.text : C.textSec, boxShadow: notesTabMode === "note" ? "0 1px 2px rgba(0,0,0,0.06)" : "none" } }, "Sticky note"),
        /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setNotesTabMode("action")), style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: notesTabMode === "action" ? 600 : 400, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: font, background: notesTabMode === "action" ? "#fff" : "transparent", color: notesTabMode === "action" ? C.text : C.textSec, boxShadow: notesTabMode === "action" ? "0 1px 2px rgba(0,0,0,0.06)" : "none" } }, "Action item")),
      notesTabMode === "note" ? /*#__PURE__*/React.createElement(React.Fragment, null,
        /*#__PURE__*/React.createElement("textarea", { ref: notesTabNoteRef, placeholder: "Create a note to act as a reminder for next time", rows: 2, style: { width: "100%", padding: "10px 12px", fontSize: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box" } }),
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 11, color: C.textTer, margin: "4px 0 0" } }, "Sticks to customer until cleared"),
        /*#__PURE__*/React.createElement("button", { onClick: () => {
          const val = notesTabNoteRef.current ? notesTabNoteRef.current.value.trim() : "";
          if (!val) { showToast("Enter a note"); return; }
          const nowDate = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
          syncToSheet("logDelivery", { data: { customerId: customer.id, customerName: customer.name, delivered: [], collected: [], orderRef: "", note: val, stickyNote: true, user: currentUser?.name || "Unknown" } });
          setActionItems(prev => [...prev, { dateCreated: nowDate, customerId: customer.id, customerName: customer.name, description: val, assignedTo: currentUser?.name || "", dueDate: "", status: "Note", completedDate: "", completedBy: "", createdBy: currentUser?.name || "Unknown" }]);
          setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, activeNote: val, noteCleared: false } : c) : prev);
          setCust(prev => prev && prev.id === customer.id ? { ...prev, activeNote: val, noteCleared: false } : prev);
          if (notesTabNoteRef.current) notesTabNoteRef.current.value = "";
          showToast("Sticky note saved");
        }, style: { width: "100%", padding: "10px", fontSize: 14, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: font, marginTop: 8 } }, "Save sticky note")
      ) : /*#__PURE__*/React.createElement(React.Fragment, null,
        /*#__PURE__*/React.createElement("textarea", { ref: notesTabActionRef, placeholder: "What needs to be done?", rows: 2, style: { width: "100%", padding: "10px 12px", fontSize: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box" } }),
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: "8px 0 4px", fontWeight: 500 } }, "Assign to"),
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", background: C.surfaceAlt, borderRadius: 8, padding: 3, marginBottom: 8 } },
          /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setActionAssignee(currentUser?.name || "")), style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: (actionAssignee === (currentUser?.name || "")) ? 600 : 400, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: font, background: (actionAssignee === (currentUser?.name || "")) ? "#fff" : "transparent", color: (actionAssignee === (currentUser?.name || "")) ? C.text : C.textSec, boxShadow: (actionAssignee === (currentUser?.name || "")) ? "0 1px 2px rgba(0,0,0,0.06)" : "none" } }, (currentUser?.name || "Me"), (actionAssignee === (currentUser?.name || "")) ? " \u2713" : ""),
          /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => setActionAssignee(currentUser?.name === "Brendan" ? "Aaron" : "Brendan")), style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: (actionAssignee !== (currentUser?.name || "")) ? 600 : 400, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: font, background: (actionAssignee !== (currentUser?.name || "")) ? "#fff" : "transparent", color: (actionAssignee !== (currentUser?.name || "")) ? C.text : C.textSec, boxShadow: (actionAssignee !== (currentUser?.name || "")) ? "0 1px 2px rgba(0,0,0,0.06)" : "none" } }, currentUser?.name === "Brendan" ? "Aaron" : "Brendan", (actionAssignee !== (currentUser?.name || "")) ? " \u2713" : "")),
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8, alignItems: "center", marginBottom: 6 } },
          /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: 0, fontWeight: 500 } }, "Due date"),
          /*#__PURE__*/React.createElement("input", { type: "date", value: actionDueDate, onChange: e => setActionDueDate(e.target.value), style: { fontSize: 12, padding: "4px 8px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, color: C.text, fontFamily: font } })),
        /*#__PURE__*/React.createElement("button", { onClick: () => {
          const val = notesTabActionRef.current ? notesTabActionRef.current.value.trim() : "";
          if (!val) { showToast("Enter a description"); return; }
          syncToSheet("logActionItem", { data: { customerId: customer.id, customerName: customer.name, description: val, assignedTo: actionAssignee || currentUser?.name || "", dueDate: actionDueDate || "", createdBy: currentUser?.name || "Unknown" } });
          setActionItems(prev => [...prev, { dateCreated: new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }), customerId: customer.id, customerName: customer.name, description: val, assignedTo: actionAssignee || currentUser?.name || "", dueDate: actionDueDate || "", status: "Open", completedDate: "", completedBy: "", createdBy: currentUser?.name || "Unknown" }]);
          if (notesTabActionRef.current) notesTabActionRef.current.value = "";
          setActionDueDate("");
          showToast("Action item created");
        }, style: { width: "100%", padding: "10px", fontSize: 14, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: font, marginTop: 4 } }, "Save action item"))),
    // --- History log (completed actions + notes + delivery notes, newest first) ---
    (() => {
      const completedActions = actionItems.filter(a => a.customerId === customer.id && a.status === "Complete").map(a => ({ ...a, _type: "action" }));
      var custNotes = actionItems.filter(a => a.customerId === customer.id && a.status === "Note").map(n => ({ ...n, _type: "note" }));
      const activeNote = customer.activeNote && !customer.noteCleared ? customer.activeNote : "";
      if (activeNote && !custNotes.some(n => n.description === activeNote)) {
        custNotes = [{ dateCreated: "Manual", customerId: customer.id, customerName: customer.name, description: activeNote, assignedTo: "", status: "Note", createdBy: "\u2014", _type: "note" }].concat(custNotes);
      }
      const delivNotes = deliveryLogData.filter(d => d.customerId === customer.id && d.note && d.note.trim()).map(d => ({ dateCreated: d.date, description: d.note, createdBy: d.loggedBy, _type: "delivery" }));
      // Deduplicate: if a delivery note's text+date matches a sticky note or completed action, skip the delivery entry
      const _noteTexts = new Set(custNotes.map(n => n.description + "|" + n.dateCreated));
      const _actionTexts = new Set(completedActions.map(a => a.description + "|" + a.dateCreated));
      const dedupedDelivNotes = delivNotes.filter(d => !_noteTexts.has(d.description + "|" + d.dateCreated) && !_actionTexts.has(d.description + "|" + d.dateCreated));
      const _parseHistDate = function(s) { if (!s || s === "Manual") return 0; var months = {Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11}; var m = String(s).match(/(\d{1,2})\s+(\w{3})\s+(\d{4})/); return m ? new Date(Number(m[3]), months[m[2]] || 0, Number(m[1])).getTime() : 0; };
      const allHistory = completedActions.concat(custNotes).concat(dedupedDelivNotes).sort(function(a, b) { return _parseHistDate(b.dateCreated) - _parseHistDate(a.dateCreated); });
      if (allHistory.length === 0) return null;
      return /*#__PURE__*/React.createElement("div", { style: { marginTop: 12 } },
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, letterSpacing: .5, textTransform: "uppercase", margin: "0 0 8px" } }, "History (", allHistory.length, ")"),
        allHistory.map((n, i) => {
          const isNote = n._type === "note";
          const isDelivery = n._type === "delivery";
          const isActive = isNote && customer.activeNote === n.description && !customer.noteCleared;
          const isExpanded = expandedNote && expandedNote.type === "history" && expandedNote.index === i;
          if (isDelivery) {
            return /*#__PURE__*/React.createElement("div", { key: "hl-" + i, onClick: () => _keepScroll(() => setCrmTab("deliveries")), style: { padding: "12px 16px", background: C.surfaceAlt, border: `1px solid ${C.borderLight}`, borderRadius: 12, marginBottom: 8, cursor: "pointer", opacity: 0.7 } },
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 4 } },
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, padding: "2px 10px", background: C.surfaceAlt, border: `1px solid ${C.borderLight}`, borderRadius: 4 } }, "Delivery note"),
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer, marginLeft: "auto" } }, n.createdBy, " \xB7 ", n.dateCreated)),
              /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.textSec, margin: 0, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" } }, n.description));
          }
          return /*#__PURE__*/React.createElement("div", { key: "hl-" + i, onClick: () => _keepScroll(() => { setEditingHistoryNote(null); setExpandedNote(isExpanded ? null : { type: "history", index: i }); }), style: { padding: "12px 16px", background: isActive ? "#FFF8E1" : C.surface, border: `1px solid ${isActive ? "#FFE082" : isExpanded ? C.accent : C.borderLight}`, borderRadius: 12, marginBottom: 8, cursor: "pointer" } },
            /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 6 } },
              isNote ? /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: isActive ? "#F57F17" : "#2D7A4F", padding: "2px 10px", background: isActive ? "#FFF8E1" : "#EEFBF3", border: `1px solid ${isActive ? "#FFE082" : "#B8E4CA"}`, borderRadius: 4 } }, isActive ? "Active" : "Cleared")
                : /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: "#2D7A4F", padding: "2px 10px", background: "#EEFBF3", border: "1px solid #B8E4CA", borderRadius: 4 } }, "\u2713 Complete"),
              /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer, marginLeft: "auto" } }, n.createdBy || n.completedBy, " \xB7 ", n.dateCreated || n.completedDate),
              /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer, marginLeft: 4 } }, isExpanded ? "\u25B2" : "\u25BC")),
            !isExpanded && /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.text, margin: 0, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" } }, n.description),
            isExpanded && /*#__PURE__*/React.createElement("div", { onClick: e => e.stopPropagation() },
              (isNote && editingHistoryNote && editingHistoryNote.index === i) ?
              /*#__PURE__*/React.createElement(React.Fragment, null,
                /*#__PURE__*/React.createElement("textarea", { autoFocus: true, value: editingHistoryNote.text, onChange: function(e) { _keepScroll(function() { setEditingHistoryNote(function(prev) { return { index: prev.index, text: e.target.value }; }); }); }, rows: 3, style: { width: "100%", padding: "10px 12px", fontSize: 14, background: C.surface, border: "1.5px solid " + C.accent, borderRadius: 10, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box", marginBottom: 8 } }),
                /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8 } },
                  /*#__PURE__*/React.createElement("button", { onClick: function() {
                    var _newText = editingHistoryNote.text.trim();
                    if (!_newText) { showToast("Enter a note"); return; }
                    var _oldDesc = n.description;
                    _keepScroll(function() { setActionItems(function(prev) { return prev.map(function(a) { return a.customerId === customer.id && a.description === _oldDesc && a.status === "Note" && a.dateCreated === n.dateCreated ? Object.assign({}, a, { description: _newText }) : a; }); }); });
                    if (isActive) {
                      setRemoteCustomers(function(prev) { return prev ? prev.map(function(c) { return c.id === customer.id ? Object.assign({}, c, { activeNote: _newText }) : c; }) : prev; });
                      setCust(function(prev) { return prev && prev.id === customer.id ? Object.assign({}, prev, { activeNote: _newText }) : prev; });
                      syncToSheet("logDelivery", { data: { customerId: customer.id, customerName: customer.name, delivered: [], collected: [], orderRef: "", note: _newText, stickyNote: true, user: currentUser?.name || "Unknown" } });
                    }
                    setEditingHistoryNote(null);
                    showToast("Note updated");
                  }, style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Save"),
                  /*#__PURE__*/React.createElement("button", { onClick: function() { _keepScroll(function() { setEditingHistoryNote(null); }); }, style: { flex: 1, padding: "8px", fontSize: 13, fontWeight: 600, background: C.surfaceAlt, color: C.textSec, border: "1px solid " + C.border, borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Cancel")))
              :
              /*#__PURE__*/React.createElement(React.Fragment, null,
              /*#__PURE__*/React.createElement("p", { style: { fontSize: 14, color: C.text, margin: "0 0 8px", lineHeight: 1.6 } }, n.description),
              !isNote && n.assignedTo && /*#__PURE__*/React.createElement("p", { style: { fontSize: 11, color: C.textTer, margin: "0 0 8px" } }, "Assigned to: ", n.assignedTo),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8, flexWrap: "wrap" } },
                isNote && isActive && /*#__PURE__*/React.createElement("button", { onClick: () => {
                  syncToSheet("clearCustomerNote", { data: { customerId: customer.id } });
                  setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, activeNote: "", noteCleared: true } : c) : prev);
                  setCust(prev => prev && prev.id === customer.id ? { ...prev, activeNote: "", noteCleared: true } : prev);
                  showToast("Note cleared");
                }, style: { padding: "6px 12px", fontSize: 12, fontWeight: 600, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer", fontFamily: font, color: C.text } }, "Clear note"),
                isNote && /*#__PURE__*/React.createElement("button", { onClick: () => {
                  _keepScroll(() => setEditingHistoryNote({ index: i, text: n.description }));
                }, style: { padding: "6px 12px", fontSize: 12, fontWeight: 600, background: C.blueSoft, color: C.blue, border: `1px solid ${C.blueBorder}`, borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Edit"),
                /*#__PURE__*/React.createElement("button", { onClick: () => {
                  if (isNote) {
                    _keepScroll(() => setActionItems(prev => prev.filter(a => !(a.customerId === customer.id && a.description === n.description && a.status === "Note" && a.dateCreated === n.dateCreated))));
                    if (isActive) { syncToSheet("clearCustomerNote", { data: { customerId: customer.id } }); setRemoteCustomers(prev => prev ? prev.map(c => c.id === customer.id ? { ...c, activeNote: "", noteCleared: true } : c) : prev); setCust(prev => prev && prev.id === customer.id ? { ...prev, activeNote: "", noteCleared: true } : prev); }
                  } else {
                    _keepScroll(() => setActionItems(prev => prev.filter(item => !(item.customerId === customer.id && item.description === n.description && item.status === "Complete"))));
                  }
                  showToast(isNote ? "Sticky note deleted" : "Action item deleted");
                }, style: { padding: "6px 12px", fontSize: 12, fontWeight: 600, background: C.redSoft, color: C.red, border: `1px solid ${C.red}33`, borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Delete")))));
        }));
    })()),
  // ========== DELIVERY LOG TAB ==========
  crmTab === "deliveries" && /*#__PURE__*/React.createElement("div", null,
    (() => {
      const custDeliveries = deliveryLogData.filter(d => d.customerId === customer.id);
      if (custDeliveries.length === 0) return /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.textTer, textAlign: "center", padding: "20px 0" } }, "No deliveries logged yet");
      const reversed = custDeliveries.slice().reverse();
      return /*#__PURE__*/React.createElement(React.Fragment, null,
        reversed.map((d, i) => {
          const isOpen = expandedNote && expandedNote.type === "delivery" && expandedNote.index === i;
          const _hasMoves = d.kegsDelivered > 0 || d.kegsCollected > 0;
          const _summaryParts = [];
          if (d.kegsDelivered > 0) _summaryParts.push(d.kegsDelivered + " delivered");
          if (d.kegsCollected > 0) _summaryParts.push(d.kegsCollected + " collected");
          if (d.orderRef) _summaryParts.push(d.orderRef);
          if (d.note && !_hasMoves) _summaryParts.push(d.note.length > 60 ? d.note.slice(0, 57) + "\u2026" : d.note);
          const isEditing = editingDelivery && editingDelivery.dlIndex === i;
          return /*#__PURE__*/React.createElement("div", { key: "dl-" + i, onClick: () => { if (isEditing) return; _keepScroll(() => setExpandedNote(isOpen ? null : { type: "delivery", index: i })); }, style: { padding: "10px 14px", background: C.surface, border: `1px solid ${isOpen ? C.accent : C.borderLight}`, borderRadius: 10, marginBottom: 5, cursor: isEditing ? "default" : "pointer" } },
            /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" } },
              /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: C.text } }, d.date),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6 } },
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer } }, d.loggedBy),
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer } }, isOpen ? "\u25B2" : "\u25BC"))),
            !isOpen && /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: "2px 0 0" } }, _summaryParts.join(" \xB7 ") || "Sticky note"),
            !isOpen && d.note && _hasMoves && /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: "2px 0 0", fontStyle: "italic" } }, d.note.length > 80 ? d.note.slice(0, 77) + "\u2026" : d.note),
            isOpen && !isEditing && /*#__PURE__*/React.createElement("div", { style: { marginTop: 8, borderTop: `1px solid ${C.borderLight}`, paddingTop: 8 } },
              d.orderRef && /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "4px 0" } },
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textSec } }, "Order ref"),
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: C.text } }, d.orderRef)),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "4px 0" } },
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textSec } }, "Kegs delivered"),
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: C.text } }, d.kegsDelivered || 0)),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "4px 0" } },
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textSec } }, "Kegs collected"),
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: C.text } }, d.kegsCollected || 0)),
              d.note && /*#__PURE__*/React.createElement("div", { style: { padding: "6px 0" } },
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textSec, display: "block", marginBottom: 2 } }, "Note"),
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 500, color: C.text } }, d.note)),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "4px 0" } },
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textSec } }, "Logged by"),
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: C.text } }, d.loggedBy)),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "4px 0" } },
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textSec } }, "Time"),
                /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: C.text } }, d.time || "\u2014")),
              /*#__PURE__*/React.createElement("button", { onClick: e => { e.stopPropagation(); _keepScroll(() => setEditingDelivery({ dlIndex: i, orderRef: d.orderRef || "", kegsDelivered: String(d.kegsDelivered || 0), kegsCollected: String(d.kegsCollected || 0), note: d.note || "", date: d.date, time: d.time, customerId: d.customerId })); }, style: { marginTop: 8, width: "100%", padding: "8px", fontSize: 13, fontWeight: 600, background: C.surfaceAlt, border: "1px solid " + C.border, borderRadius: 8, cursor: "pointer", fontFamily: font, color: C.accent } }, "\u270F\uFE0F Edit delivery"),
              // Undo button — only on the most recent delivery for this customer, and only if a snapshot exists
              i === 0 && d.hasSnapshot && /*#__PURE__*/React.createElement("button", { onClick: async e => {
                e.stopPropagation();
                if (!confirm("Undo this delivery?\n\nThis will reverse all keg movements and restore Kegs on Hand to what it was before this delivery was logged.\n\nGas readings (if any) will need to be reverted manually from the gas log.\n\nThis cannot be undone.")) return;
                const res = await syncToSheet("undoDelivery", { data: { customerId: d.customerId, date: d.date, time: d.time } });
                if (res && res.success) {
                  // Restore liveKegs from the snapshot the backend returned
                  if (res.restoredKegs) {
                    setLiveKegs(prev => ({ ...prev, [d.customerId]: { onTap: res.restoredKegs.onTap || [], backup: res.restoredKegs.backup || [], lastDelivery: (prev[d.customerId] && prev[d.customerId].lastDelivery) || null } }));
                  }
                  // Drop this delivery from the local log so the row vanishes
                  setDeliveryLogData(prev => prev.filter(dl => !(dl.customerId === d.customerId && dl.date === d.date && dl.time === d.time)));
                  _keepScroll(() => setExpandedNote(null));
                  showToast("Delivery undone \u2014 kegs restored");
                } else if (res && res.error) {
                  showToast("Undo failed: " + res.error);
                }
              }, style: { marginTop: 6, width: "100%", padding: "8px", fontSize: 13, fontWeight: 600, background: C.redSoft || "#FFF0ED", border: "1px solid " + (C.red || "#C43E2A") + "55", borderRadius: 8, cursor: "pointer", fontFamily: font, color: C.red || "#C43E2A" } }, "\u21A9 Undo delivery")),
            isOpen && isEditing && /*#__PURE__*/React.createElement("div", { onClick: e => e.stopPropagation(), style: { marginTop: 8, borderTop: "1px solid " + C.borderLight, paddingTop: 10 } },
              /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: C.accent, margin: "0 0 10px" } }, "Edit delivery"),
              /*#__PURE__*/React.createElement("div", { style: { marginBottom: 8 } },
                /*#__PURE__*/React.createElement("label", { style: { fontSize: 12, color: C.textSec, display: "block", marginBottom: 3 } }, "Order ref"),
                /*#__PURE__*/React.createElement("input", { value: editingDelivery.orderRef, onChange: e => _keepScroll(() => setEditingDelivery(prev => ({ ...prev, orderRef: e.target.value }))), style: { width: "100%", padding: "8px 10px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 8, color: C.text, fontFamily: font, boxSizing: "border-box" } })),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8, marginBottom: 8 } },
                /*#__PURE__*/React.createElement("div", { style: { flex: 1 } },
                  /*#__PURE__*/React.createElement("label", { style: { fontSize: 12, color: C.textSec, display: "block", marginBottom: 3 } }, "Kegs delivered"),
                  /*#__PURE__*/React.createElement("input", { type: "number", value: editingDelivery.kegsDelivered, onChange: e => _keepScroll(() => setEditingDelivery(prev => ({ ...prev, kegsDelivered: e.target.value }))), style: { width: "100%", padding: "8px 10px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 8, color: C.text, fontFamily: font, boxSizing: "border-box" } })),
                /*#__PURE__*/React.createElement("div", { style: { flex: 1 } },
                  /*#__PURE__*/React.createElement("label", { style: { fontSize: 12, color: C.textSec, display: "block", marginBottom: 3 } }, "Kegs collected"),
                  /*#__PURE__*/React.createElement("input", { type: "number", value: editingDelivery.kegsCollected, onChange: e => _keepScroll(() => setEditingDelivery(prev => ({ ...prev, kegsCollected: e.target.value }))), style: { width: "100%", padding: "8px 10px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 8, color: C.text, fontFamily: font, boxSizing: "border-box" } }))),
              /*#__PURE__*/React.createElement("div", { style: { marginBottom: 10 } },
                /*#__PURE__*/React.createElement("label", { style: { fontSize: 12, color: C.textSec, display: "block", marginBottom: 3 } }, "Note"),
                /*#__PURE__*/React.createElement("textarea", { value: editingDelivery.note, onChange: e => _keepScroll(() => setEditingDelivery(prev => ({ ...prev, note: e.target.value }))), rows: 3, style: { width: "100%", padding: "8px 10px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 8, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box" } })),
              /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8 } },
                /*#__PURE__*/React.createElement("button", { onClick: e => { e.stopPropagation(); const ed = editingDelivery; syncToSheet("updateDeliveryLog", { data: { customerId: ed.customerId, date: ed.date, time: ed.time, orderRef: ed.orderRef, kegsDelivered: Number(ed.kegsDelivered) || 0, kegsCollected: Number(ed.kegsCollected) || 0, note: ed.note } }); _keepScroll(() => { setDeliveryLogData(prev => prev.map(dl => dl.customerId === ed.customerId && dl.date === ed.date && dl.time === ed.time ? { ...dl, orderRef: ed.orderRef, kegsDelivered: Number(ed.kegsDelivered) || 0, kegsCollected: Number(ed.kegsCollected) || 0, note: ed.note } : dl)); setEditingDelivery(null); }); showToast("Delivery updated"); }, style: { flex: 1, padding: "10px", fontSize: 13, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Save"),
                /*#__PURE__*/React.createElement("button", { onClick: e => { e.stopPropagation(); _keepScroll(() => setEditingDelivery(null)); }, style: { flex: 1, padding: "10px", fontSize: 13, fontWeight: 600, background: C.surfaceAlt, color: C.textSec, border: "1px solid " + C.border, borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Cancel"))));
        }));
    })()),
  // ========== CRM TAB ==========
  crmTab === "crm" && /*#__PURE__*/React.createElement("div", null,
    // --- CONTACTS SECTION ---
    /*#__PURE__*/React.createElement("div", { style: { marginBottom: 16 } },
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 } },
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, letterSpacing: .5, textTransform: "uppercase", margin: 0 } }, "Contacts"),
        crmMode === "view" && /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setCrmMode("addContact"); setCrmEditContact({ contactName: "", role: "", phone: "" }); }), style: { padding: "5px 12px", fontSize: 12, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: font } }, "+ Add")),
      // Contact cards
      (() => {
        const custContacts = contacts.filter(c => String(c.customerId) === String(customer.id) && c.active !== "No");
        if (custContacts.length === 0 && crmMode === "view") return /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.textTer, textAlign: "center", padding: "12px 0" } }, "No contacts added yet");
        return custContacts.map((ct, i) => /*#__PURE__*/React.createElement("div", { key: "ct-" + i, style: { padding: "10px 14px", background: C.surface, border: "1px solid " + C.borderLight, borderRadius: 10, marginBottom: 5, display: "flex", justifyContent: "space-between", alignItems: "center" } },
          /*#__PURE__*/React.createElement("div", { style: { flex: 1 } },
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 14, fontWeight: 600, color: C.text } }, ct.contactName),
            ct.role && /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, color: C.textSec, marginLeft: 8 } }, ct.role),
            ct.phone && /*#__PURE__*/React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" } },
              /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, color: C.textSec, marginRight: 2 } }, normPhone(ct.phone)),
              /*#__PURE__*/React.createElement("a", { href: "tel:" + phoneHref(ct.phone), style: { padding: "5px 12px", fontSize: 12, fontWeight: 700, color: "#fff", background: C.green, borderRadius: 8, textDecoration: "none", fontFamily: font } }, "Call"),
              /*#__PURE__*/React.createElement("a", { href: "sms:" + phoneHref(ct.phone), style: { padding: "5px 12px", fontSize: 12, fontWeight: 700, color: C.blue, background: C.blueSoft, border: "1px solid " + C.blueBorder, borderRadius: 8, textDecoration: "none", fontFamily: font } }, "Text"))),
          /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 6 } },
            /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setCrmMode("addContact"); setCrmEditContact({ contactName: ct.contactName, role: ct.role || "", phone: ct.phone || "", originalName: ct.contactName }); }), style: { padding: "4px 10px", fontSize: 11, fontWeight: 600, background: C.surfaceAlt, border: "1px solid " + C.border, borderRadius: 6, cursor: "pointer", fontFamily: font, color: C.textSec } }, "Edit"),
            /*#__PURE__*/React.createElement("button", { onClick: () => {
              if (!confirm("Remove " + ct.contactName + "?")) return;
              syncToSheet("deleteContact", { data: { customerId: customer.id, contactName: ct.contactName } });
              setContacts(prev => prev.filter(c => !(String(c.customerId) === String(customer.id) && c.contactName === ct.contactName)));
              showToast("Contact removed");
            }, style: { padding: "4px 10px", fontSize: 11, fontWeight: 600, background: C.redSoft || "#FFF0ED", border: "1px solid " + (C.red || "#C43E2A") + "33", borderRadius: 6, cursor: "pointer", fontFamily: font, color: C.red || "#C43E2A" } }, "Remove"))));
      })(),
      // Add/Edit contact form
      crmMode === "addContact" && crmEditContact && /*#__PURE__*/React.createElement("div", { style: { padding: "12px 14px", border: "1.5px solid " + C.accent, borderRadius: 12, background: C.surface, marginTop: 8 } },
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, fontWeight: 600, color: C.text, margin: "0 0 8px" } }, crmEditContact.originalName ? "Edit contact" : "Add contact"),
        /*#__PURE__*/React.createElement("input", { placeholder: "Name", value: crmEditContact.contactName, onChange: e => setCrmEditContact(prev => ({ ...prev, contactName: e.target.value })), style: { width: "100%", padding: "10px 12px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 10, color: C.text, fontFamily: font, boxSizing: "border-box", marginBottom: 6 } }),
        /*#__PURE__*/React.createElement("input", { placeholder: "Role (e.g. Manager, Barista)", value: crmEditContact.role, onChange: e => setCrmEditContact(prev => ({ ...prev, role: e.target.value })), style: { width: "100%", padding: "10px 12px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 10, color: C.text, fontFamily: font, boxSizing: "border-box", marginBottom: 6 } }),
        /*#__PURE__*/React.createElement("input", { placeholder: "Phone (optional)", value: crmEditContact.phone, onChange: e => setCrmEditContact(prev => ({ ...prev, phone: e.target.value })), type: "tel", style: { width: "100%", padding: "10px 12px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 10, color: C.text, fontFamily: font, boxSizing: "border-box", marginBottom: 8 } }),
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8 } },
          /*#__PURE__*/React.createElement("button", { onClick: () => {
            const ct = crmEditContact;
            if (!ct.contactName.trim()) { showToast("Enter a name"); return; }
            const payload = { customerId: customer.id, customerName: customer.name, contactName: ct.contactName.trim(), role: ct.role.trim(), phone: normPhone(ct.phone) };
            if (ct.originalName) payload.originalName = ct.originalName;
            syncToSheet("saveContact", { data: payload });
            if (ct.originalName) {
              setContacts(prev => prev.map(c => String(c.customerId) === String(customer.id) && c.contactName === ct.originalName ? { ...c, contactName: ct.contactName.trim(), role: ct.role.trim(), phone: normPhone(ct.phone) } : c));
            } else {
              setContacts(prev => [...prev, { customerId: String(customer.id), customerName: customer.name, contactName: ct.contactName.trim(), role: ct.role.trim(), phone: normPhone(ct.phone), active: "Yes" }]);
            }
            showToast(ct.originalName ? "Contact updated" : "Contact added");
            setCrmMode("view"); setCrmEditContact(null);
          }, style: { flex: 1, padding: "10px", fontSize: 14, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: font } }, "Save"),
          /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setCrmMode("view"); setCrmEditContact(null); }), style: { flex: 1, padding: "10px", fontSize: 14, fontWeight: 600, background: C.surfaceAlt, color: C.textSec, border: "1px solid " + C.border, borderRadius: 10, cursor: "pointer", fontFamily: font } }, "Cancel")))),

    // --- DIVIDER ---
    /*#__PURE__*/React.createElement("div", { style: { borderTop: "1px solid " + C.borderLight, margin: "8px 0 16px" } }),

    // --- LOG CONVERSATION SECTION ---
    /*#__PURE__*/React.createElement("div", { style: { marginBottom: 12 } },
      /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 } },
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, fontWeight: 600, color: C.textTer, letterSpacing: .5, textTransform: "uppercase", margin: 0 } }, "Conversations"),
        crmMode === "view" && /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setCrmMode("logConvo"); setCrmConvoContact(""); setCrmConvoNote(""); }), style: { padding: "5px 12px", fontSize: 12, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: font } }, "+ Log")),

      // Log conversation form
      crmMode === "logConvo" && /*#__PURE__*/React.createElement("div", { style: { padding: "12px 14px", border: "1.5px solid " + C.accent, borderRadius: 12, background: C.surface, marginBottom: 12 } },
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, fontWeight: 600, color: C.text, margin: "0 0 8px" } }, "Log conversation"),
        // Contact picker
        /*#__PURE__*/React.createElement("p", { style: { fontSize: 12, color: C.textSec, margin: "0 0 4px", fontWeight: 500 } }, "Who did you speak to?"),
        (() => {
          const custContacts = contacts.filter(c => String(c.customerId) === String(customer.id) && c.active !== "No");
          if (custContacts.length === 0) return /*#__PURE__*/React.createElement("input", { placeholder: "Name (no contacts added yet)", value: crmConvoContact, onChange: e => setCrmConvoContact(e.target.value), style: { width: "100%", padding: "10px 12px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 10, color: C.text, fontFamily: font, boxSizing: "border-box", marginBottom: 8 } });
          return /*#__PURE__*/React.createElement(React.Fragment, null,
            /*#__PURE__*/React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 } },
              custContacts.map((ct, i) => /*#__PURE__*/React.createElement("button", { key: "cp-" + i, onClick: () => setCrmConvoContact(ct.contactName), style: { padding: "6px 12px", fontSize: 13, fontWeight: crmConvoContact === ct.contactName ? 600 : 400, border: crmConvoContact === ct.contactName ? "1.5px solid " + C.accent : "1px solid " + C.border, borderRadius: 20, cursor: "pointer", fontFamily: font, background: crmConvoContact === ct.contactName ? "#EBF2FB" : C.surface, color: crmConvoContact === ct.contactName ? C.accent : C.text } }, ct.contactName)),
              /*#__PURE__*/React.createElement("button", { onClick: () => setCrmConvoContact("__other__"), style: { padding: "6px 12px", fontSize: 13, fontWeight: crmConvoContact === "__other__" ? 600 : 400, border: crmConvoContact === "__other__" ? "1.5px solid " + C.accent : "1px solid " + C.border, borderRadius: 20, cursor: "pointer", fontFamily: font, background: crmConvoContact === "__other__" ? "#EBF2FB" : C.surface, color: crmConvoContact === "__other__" ? C.accent : C.textSec } }, "Other")),
            crmConvoContact === "__other__" && /*#__PURE__*/React.createElement("input", { placeholder: "Enter name", onChange: e => { if (e.target.value.trim()) setCrmConvoContact(e.target.value.trim()); }, style: { width: "100%", padding: "10px 12px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 10, color: C.text, fontFamily: font, boxSizing: "border-box", marginBottom: 4 } }));
        })(),
        /*#__PURE__*/React.createElement("textarea", { placeholder: "What was discussed?", value: crmConvoNote, onChange: e => setCrmConvoNote(e.target.value), rows: 3, style: { width: "100%", padding: "10px 12px", fontSize: 16, background: C.surface, border: "1px solid " + C.border, borderRadius: 10, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box", marginBottom: 8 } }),
        /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 8 } },
          /*#__PURE__*/React.createElement("button", { onClick: () => {
            const contactName = crmConvoContact === "__other__" ? "" : crmConvoContact;
            if (!contactName) { showToast("Select who you spoke to"); return; }
            if (!crmConvoNote.trim()) { showToast("Enter what was discussed"); return; }
            const nowDate = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
            syncToSheet("saveConversation", { data: { customerId: customer.id, customerName: customer.name, contactName: contactName, note: crmConvoNote.trim(), loggedBy: currentUser?.name || "Unknown" } });
            setConversations(prev => [...prev, { date: nowDate, customerId: String(customer.id), customerName: customer.name, contactName: contactName, note: crmConvoNote.trim(), loggedBy: currentUser?.name || "Unknown", linkedType: "", linkedDescription: "" }]);
            // Auto-create contact if they don't exist yet
            if (contactName && contactName !== "General") {
              const existingContact = contacts.find(c => String(c.customerId) === String(customer.id) && c.contactName.toLowerCase() === contactName.toLowerCase());
              if (!existingContact) {
                syncToSheet("saveContact", { data: { customerId: customer.id, customerName: customer.name, contactName: contactName, role: "", phone: "" } });
                _keepScroll(() => setContacts(prev => [...prev, { customerId: String(customer.id), customerName: customer.name, contactName: contactName, role: "", phone: "", active: "Yes" }]));
              }
            }
            showToast("Conversation logged");
            setCrmMode("view"); setCrmConvoContact(""); setCrmConvoNote("");
          }, style: { flex: 1, padding: "10px", fontSize: 14, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: font } }, "Save"),
          /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setCrmMode("view"); setCrmConvoContact(""); setCrmConvoNote(""); }), style: { flex: 1, padding: "10px", fontSize: 14, fontWeight: 600, background: C.surfaceAlt, color: C.textSec, border: "1px solid " + C.border, borderRadius: 10, cursor: "pointer", fontFamily: font } }, "Cancel"))),

      // Conversation history
      (() => {
        const custConvos = conversations.filter(c => String(c.customerId) === String(customer.id)).slice().reverse();
        if (custConvos.length === 0 && crmMode !== "logConvo") return /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.textTer, textAlign: "center", padding: "12px 0" } }, "No conversations logged yet");
        return custConvos.map((cv, i) => {
          const cvIdx = conversations.findIndex(c => String(c.customerId) === String(customer.id) && c.date === cv.date && c.note === cv.note && c.contactName === cv.contactName);
          const isEditing = editingConvo && editingConvo.idx === cvIdx;
          return /*#__PURE__*/React.createElement("div", { key: "cv-" + i, style: { padding: "10px 14px", background: C.surface, border: "1px solid " + (isEditing ? C.accent : C.borderLight), borderRadius: 10, marginBottom: 5 } },
          /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 } },
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: C.text } }, cv.contactName),
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer } }, cv.date)),
          isEditing ? /*#__PURE__*/React.createElement(React.Fragment, null,
            /*#__PURE__*/React.createElement("textarea", { value: editConvoNote, onChange: e => setEditConvoNote(e.target.value), rows: 3, style: { width: "100%", padding: "10px 12px", fontSize: 14, background: C.surface, border: "1px solid " + C.border, borderRadius: 10, color: C.text, fontFamily: font, resize: "vertical", boxSizing: "border-box", marginBottom: 6 } }),
            /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 6 } },
              /*#__PURE__*/React.createElement("button", { onClick: () => { const updated = editConvoNote.trim(); if (!updated) { showToast("Enter a note"); return; } _keepScroll(() => { setConversations(prev => prev.map((c, ci) => ci === cvIdx ? { ...c, note: updated } : c)); setEditingConvo(null); setEditConvoNote(""); }); showToast("Conversation updated"); }, style: { flex: 1, padding: "8px", fontSize: 12, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Save"),
              /*#__PURE__*/React.createElement("button", { onClick: () => _keepScroll(() => { setEditingConvo(null); setEditConvoNote(""); }), style: { flex: 1, padding: "8px", fontSize: 12, fontWeight: 600, background: C.surfaceAlt, color: C.textSec, border: "1px solid " + C.border, borderRadius: 8, cursor: "pointer", fontFamily: font } }, "Cancel")))
          : /*#__PURE__*/React.createElement(React.Fragment, null,
          /*#__PURE__*/React.createElement("p", { style: { fontSize: 13, color: C.text, margin: "0 0 4px", lineHeight: 1.5 } }, cv.note),
          /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" } },
            /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, color: C.textTer } }, "Logged by ", cv.loggedBy),
            cv.linkedType && /*#__PURE__*/React.createElement("span", { style: { fontSize: 11, fontWeight: 600, color: cv.linkedType === "Action" ? "#C43E2A" : "#F57F17", background: cv.linkedType === "Action" ? "#FFF0ED" : "#FFF8E1", border: "1px solid " + (cv.linkedType === "Action" ? "#F0B0A0" : "#FFE082"), borderRadius: 10, padding: "1px 7px" } }, cv.linkedType === "Action" ? "\u26A1 Action" : "\uD83D\uDCCB Sticky"),
                /*#__PURE__*/React.createElement("button", { onClick: e => { e.stopPropagation(); _keepScroll(() => { setEditingConvo({ idx: cvIdx }); setEditConvoNote(cv.note); }); }, style: { padding: "3px 8px", fontSize: 11, fontWeight: 600, background: C.surfaceAlt, border: "1px solid " + C.border, borderRadius: 6, cursor: "pointer", fontFamily: font, color: C.textSec } }, "Edit"),
                /*#__PURE__*/React.createElement("button", { onClick: e => { e.stopPropagation(); if (!confirm("Delete this conversation?")) return; syncToSheet("deleteConversation", { data: { customerId: customer.id, date: cv.date, note: cv.note } }); _keepScroll(() => setConversations(prev => prev.filter((c, ci) => ci !== cvIdx))); showToast("Conversation deleted"); }, style: { padding: "3px 8px", fontSize: 11, fontWeight: 600, background: C.redSoft || "#FFF0ED", border: "1px solid " + (C.red || "#C43E2A") + "33", borderRadius: 6, cursor: "pointer", fontFamily: font, color: C.red || "#C43E2A" } }, "Delete"))));
        });
      })()))), editingLog && customer && (() => {
    const r = editingLog.record;
    const isGas = editingLog.source === "gas";
    const isMaint = editingLog.source === "maint";
    const isRepair = editingLog.source === "repair";
    const saveEdit = () => {
      if (isMaint) {
        setMaintLog(prev => ({
          ...prev,
          [customer.id]: (prev[customer.id] || []).map((m, j) => j === editingLog.index ? {
            ...m,
            notes: r.notes,
            type: r.type
          } : m)
        }));
      } else if (isRepair) {
        setRepairLog(prev => ({
          ...prev,
          [customer.id]: (prev[customer.id] || []).map((m, j) => j === editingLog.index ? {
            ...m,
            notes: r.notes,
            task: r.task
          } : m)
        }));
      } else if (isGas) {
        setGasLog(prev => ({
          ...prev,
          [customer.id]: (prev[customer.id] || []).map((m, j) => j === editingLog.index ? {
            ...m,
            notes: r.notes,
            reason: r.reason
          } : m)
        }));
      }
      setEditingLog(null);
      showToast("Entry updated");
    };
    return /*#__PURE__*/React.createElement("div", {
      style: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20
      },
      onClick: () => setEditingLog(null)
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: "100%",
        maxWidth: 420,
        background: C.surface,
        borderRadius: 16,
        padding: "24px 20px",
        boxShadow: "0 20px 60px rgba(0,0,0,.2)"
      },
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("h3", {
      style: {
        fontFamily: font,
        fontSize: 18,
        fontWeight: 600,
        color: C.text,
        margin: "0 0 16px"
      }
    }, "Edit Entry"), isMaint && /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 12
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        margin: "0 0 6px"
      }
    }, "Type"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6
      }
    }, SCHED_MAINT.map(t => /*#__PURE__*/React.createElement("button", {
      key: t,
      onClick: () => setEditingLog(prev => ({
        ...prev,
        record: {
          ...prev.record,
          type: t
        }
      })),
      style: {
        flex: 1,
        padding: "10px",
        fontSize: 13,
        fontWeight: 600,
        background: r.type === t ? `${C.cyan}15` : C.bg,
        color: r.type === t ? C.cyan : C.textTer,
        border: `1.5px solid ${r.type === t ? C.cyan : C.borderLight}`,
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: font
      }
    }, t)))), isRepair && /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 12
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        margin: "0 0 6px"
      }
    }, "Task"), /*#__PURE__*/React.createElement(Select, {
      value: r.task || "",
      onChange: v => setEditingLog(prev => ({
        ...prev,
        record: {
          ...prev.record,
          task: v
        }
      })),
      options: REPAIR_TASKS,
      placeholder: "Task"
    })), isGas && /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 12
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        margin: "0 0 6px"
      }
    }, "Reason"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6
      }
    }, ["Gas empty", "Gas lost"].map(t => /*#__PURE__*/React.createElement("button", {
      key: t,
      onClick: () => setEditingLog(prev => ({
        ...prev,
        record: {
          ...prev.record,
          reason: t
        }
      })),
      style: {
        flex: 1,
        padding: "10px",
        fontSize: 13,
        fontWeight: 600,
        background: r.reason === t ? "#E67E2215" : C.bg,
        color: r.reason === t ? "#E67E22" : C.textTer,
        border: `1.5px solid ${r.reason === t ? "#E67E22" : C.borderLight}`,
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: font
      }
    }, t)))), /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 16
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: C.textTer,
        margin: "0 0 6px"
      }
    }, "Notes"), /*#__PURE__*/React.createElement(TextArea, {
      value: r.notes || "",
      onChange: v => setEditingLog(prev => ({
        ...prev,
        record: {
          ...prev.record,
          notes: v
        }
      })),
      placeholder: "Notes",
      rows: 3
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 12,
        color: C.textTer,
        marginBottom: 16
      }
    }, r.date, " at ", r.time), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => setEditingLog(null),
      style: {
        flex: 1,
        padding: "12px",
        fontSize: 14,
        fontWeight: 600,
        background: C.surfaceAlt,
        color: C.textSec,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      onClick: saveEdit,
      style: {
        flex: 1,
        padding: "12px",
        fontSize: 14,
        fontWeight: 600,
        background: C.blue,
        color: "#fff",
        border: "none",
        borderRadius: 10,
        cursor: "pointer",
        fontFamily: font
      }
    }, "Save Changes"))));
  })(), /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 40,
      paddingTop: 16,
      borderTop: `1px solid ${C.borderLight}`,
      textAlign: "center"
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: LOGO_URL,
    alt: "Filament Coffee",
    style: {
      width: 100,
      height: "auto",
      opacity: 0.25,
      display: "block",
      margin: "0 auto"
    }
  })))));
}
ReactDOM.render(React.createElement(App), document.getElementById("root"));